import { HOME_PAGE_NOTE } from "./pageLabel";
import { pacingSwitchedOff, paceSimulation } from "./simulationPacer";
import { BubbleGlide, cssMoveMs, glideFactor, usesJsGlide } from "./bubbleGlide";
import { useBitmapFor } from "./iconBitmaps";
import { createOriginReader } from "./originSnapshot";
import * as d3 from "d3";
import { isShareView } from "../share/shareView";
import { PAGE_SLOT_COUNT } from "./slotCount";
import { boxesLocked } from "../share/shareLock";
import { each } from "lodash";
import flagIconCountries from "flag-icons/country.json";
import goalStarIconAsset from "../assets/images/goal-star.svg";
import unidentifiedBadgeIconAsset from "../assets/images/visitor_analytics_icons/unidentified.svg";
import {
  data,
  getPagesStatistics,
  handleVisitorMovePagesModel,
  getClusterNumber,
  syncCurrentVisitorsFromPageNames,
  setPages,
  setPagesReplacedListener,
  applySlotChangesInPlace,
  saveSlotReorder,
  unselectPage,
} from "./pagesModel";
import * as slotDrag from "./slotDragService";
import { barFillWidth, computeSlotTotals, slotTotalsText } from "./slotTotals";
import { invertChanges } from "./slotReorder";
import type { SlotChange, SlotPage } from "./slotReorder";
import { sortTarget, stepsToReach } from "./slotSort";
import { layoutDonut } from "./historicalDonut";
import type { SortOrder } from "./slotSort";
import { toastMessageError, toastMessageSuccess } from "../components/utilities/commonToastMessage";

import { session } from "../services/session";
import { eventEmitter } from "../eventEmitters/EventEmitters";
import { happyPathFacts } from "./happyPath";
import { routeToHappyPath } from "./happyRoute";
import { createRouteTally, routesFromWire } from "./routeTally";
import { fetchCommonRoute } from "./commonRoute";
import type { PathAnswer } from "../insights/pathCore";
import { arrivalTooltipHtml, FLOW_TOOLTIP_BACKGROUND, flowPeriodLabel, HAPPY_PATH_TOOLTIP_BACKGROUND, happyPathTooltipHtml, routeTooltipHtml } from "./flowTooltips";
import { getMetricsData, historical } from "./metricsModal";
import storage from "./storage";

import { site } from "../services/site";
import {
  hideHistoricalTooltip,
  hideRealtimeTooltip,
  hideFlowLineTooltip,
  hideSelectedUrlTooltip,
  scheduleSlotHint,
  moveSlotHint,
  hideSlotHint,
  followFlowLineTooltip,
  repositionFlowLineTooltip,
  showFlowLineTooltip,
  showHistoricalTooltip,
  showRealtimeTooltip,
  showSelectedUrlTooltip,
  isCoarsePointer,
  setTouchTooltipAllowed,
} from "./tooltipService";

import {
  DATEPICKER_RANGES,
  INITIAL_FILTERS,
  speedOptions,
} from "../constants/commonConstants";
import { readableLabel, wrapPath } from "./helper";
import { getDomain } from "./commonMethodService";
import { getOnsiteSessions } from "./onsiteReconciliation";
import * as playbackModeService from "./playbackModeService";

// FUNNEL STEP INDICATORS (docs/FUNNEL_STEP_INDICATORS.md): demo and STAGING builds only (REACT_APP_FUNNEL=1 is set in .env.staging and nowhere else), so a production bundle does not contain them.
const funnelHost = (process.env.REACT_APP_DEMO === "1" || process.env.REACT_APP_FUNNEL === "1") ? { layer: require("../funnel/funnelLayer"), state: require("../funnel/funnelState"), playback: require("../funnel/funnelPlayback"), flow: require("../funnel/flowRibbons"), flowSeed: require("../funnel/flowSeed"), canvasZoom: require("./canvasZoom"), slotFocus: require("../funnel/slotFocus"), flowHit: require("../funnel/flowHit") } : null;
import { getGoalForPage, goalsList } from "./goalsModel";
import { getCachedPages } from "./helper";
import * as densityCloud from "./densityCloudService";
import { returnMarkFor } from "./returnMark";
import { loaderStatsText } from "./loaderStats";
import * as ambientLife from "./ambientLife";
import { GOAL_FLAG_PATH } from "./goalFlag";
import { dropAllBadges, dropBadge } from "./pulseBadges";

// Every removal of a reading / watching / idle / away badge goes through pulseBadges, which tells the Sessions drawer in the same breath (a silent removal left a stale glyph on a row).
const emitBadgeEvent = (event: string, payload: Record<string, unknown>) => eventEmitter.emit(event, payload);
import { setPerfContext } from "./perfProbe";
import { badgeIconDataUri, hasBadge, plainIconDataUri } from "./badgeIcon";
import { clusterSpacingForCharge } from "./clusterSpacing";
import { clusterRecentreForce } from "./clusterRecentre";
import { isUnknownBucketName, knownTotal, NO_CATEGORY_DATA } from "./unknownBucket";
import { ALL_BRAND_COLOURS, BRAND_COLOUR_BASE, brandColourHex, brandColourIndex, NO_DATA_COLOUR_INDEX } from "./brandColours";
import { HistoricalRowsOrder, layoutHistoricalRows, ROWS_GAP_X, ROWS_GAP_Y } from "./historicalRows";
import { barLength, layoutHistoricalBarList } from "./historicalBarList";
import { layoutHistoricalColumns } from "./historicalColumns";
import { fitLabel, labelRoom, LABEL_DROP, LABEL_PX, LABEL_ROOM_Y, LABEL_WEIGHT } from "./historicalLabels";
import { ChartHooks, BarListRow, ColumnRow, DonutRow, syncHistoricalDonut, positionHistoricalLabels, pulseChartBar, removeHistoricalCharts, removeHistoricalLabels, syncHistoricalBarList, syncHistoricalColumns, syncHistoricalLabels } from "./historicalDockDraw";
import { textWidth } from "./textWidth";
import { applyFlowSnapshot, FlowSnapshot, readSavedFlowState, takeFlowSnapshot } from "./flowLineSnapshot";
import { clearParkedFlows, currentFingerprint, newParkedFlows, relocateFlows } from "./flowLineRelocation";
import { readSavedRange, resolveSavedRange, saveHistoricalRange, usableTimeOr } from "./historicalRangeStore";
export let availableFilters;

interface FilterItem {
  name: string;

  menuType: string;
}

const initialHistoryDataDateRange = [
  { name: "Today", value: new Date().setHours(0, 0, 0, 0) },
  {
    name: "Last 2 Days",
    value: new Date(
      new Date().setHours(0, 0, 0, 0) - 1 * 24 * 60 * 60 * 1000
    ).getTime(),
  },
  {
    name: "Last 7 Days",
    value: new Date(
      new Date().setHours(0, 0, 0, 0) - 6 * 24 * 60 * 60 * 1000
    ).getTime(),
  },
  {
    name: "Last 30 Days",
    value: new Date(
      new Date().setHours(0, 0, 0, 0) - 29 * 24 * 60 * 60 * 1000
    ).getTime(),
  },
  { name: "All", value: storage.get("userCreatedAt") },
];


const DEFAULT_D3CIRCLE_CONSTRAINT = {
  gravity: 0.2,
  charge: -1,
  // Separate from `charge` - page-cluster (real-time/playback) and
  // historical-cluster simulations previously shared one "spacing" setting,
  // even though they're visually and behaviorally distinct clusters (per
  // direct user request to split them). Same default value as `charge`
  // since there's no reason the two should look different out of the box,
  // just independently adjustable going forward.
  historicalCharge: -1,
  friction: 0.0001,
  speed: 100,
};
const DEFAULT_VALUE = {
  // 12 real page slots now (was 10) - indices 10-11 are the two newly added
  // slots (row 3, columns 0-1), 12 is "All other pages" (was 10) - see
  // getClusterCoords' own comment for the full index remap this pairs with.
  UNKNOWN_URL_CLUSER_INDEX: PAGE_SLOT_COUNT,
  ALL_CLUSTER_INDEX: PAGE_SLOT_COUNT + 2,
  DEFAULT_CLUSTER_CENTER: {
    "menu-nodes": {
      centerIndex: 13,
      cluster: 13,
    },
  },
  SETUP_TIMEOUT: 15000,
  ALL_RANGE_NAME: "All",
  ROW_PER_PAGE: 100,
};

export interface RealtimeItem {
  name: string;
  currentUsers: number;
  totalUsers: number;
  color: number;
  colorInfo: any;
  display: boolean;
}

const siteId = storage.get("siteId");
// Was a plain `let pageViewDisabled = false;` with no persistence, unlike
// useIcons/interactionPulsesEnabled just below - navigating to Settings and
// back is a full hard page reload (confirmed directly: every trip there
// this session required a fresh login/2FA, not just a component remount),
// which reinitializes this whole module from scratch. Every OTHER piece of
// single-cluster-mode state (the visual collapse itself, AnalyticsView's own
// resync-on-mount effect) ultimately reads or reacts to this one flag, so it
// silently resetting to false on reload was the actual root cause of single-
// cluster mode "mixing" back into grid view - some code paths (bubble
// clustering targets, resolved live every tick) kept working because they're
// re-derived fresh after reload anyway, while others (setPages' guard
// against re-filling box labels/fill/goal-flag) read this now-wrong-again
// flag and did the opposite of what the still-open single-cluster view
// needed. Reading the persisted value here, matching useIcons' own pattern,
// fixes it at the source instead of chasing each downstream symptom.
export let pageViewDisabled = storage.get("pageViewDisabled") === "true";
// 12 real page slots now (was 10) - see DEFAULT_VALUE's own comment above
// for the full index remap.
const UNKNOWN_URL_CLUSER_INDEX = PAGE_SLOT_COUNT;
// Was 5 unnamed `const clusterIndex = 11;` literals scattered across
// drawHistoricalBubble and friends - moved here as the historical cluster
// shifted from index 11 to 13 to make room for the 2 new real page slots.
const HISTORICAL_CLUSTER_INDEX = PAGE_SLOT_COUNT + 1;
let clusterTextInstances: any = [];
let clusterRectInstances: any = [];
let svg: any;
// Nearly every redraw path (real-time bubbles on new visitor/page move, and
// the historical cluster on session close) creates its own fresh
// d3.forceSimulation() instance. Under bursty traffic these calls happen far
// faster than a simulation naturally settles, and an un-stopped simulation
// keeps ticking on every animation frame forever - so without explicitly
// stopping the previous one, dozens of overlapping simulations pile up and
// saturate the main thread until the tab freezes. Track the active
// simulation per logical subsystem (real-time vs historical) so each new
// call stops its own predecessor before starting.
const activeSimulations: { [key: string]: any } = {};
const stopPreviousSimulation = (key: "historical", simulation: any) => {
  activeSimulations[key]?.stop();
  activeSimulations[key] = simulation;
};

// Real-time bubbles: ONE persistent simulation, created once and never torn
// down, shared by every real-time redraw reason (new visitor, page move,
// page-selection change). This matches how reference "beeswarm" people-flow
// visualizations (e.g. FlowingData's "A Day in the Life of Americans") work:
// each bubble's target cluster is resolved dynamically, every tick, from its
// own current state (d.pageName) - so redirecting a bubble is just mutating
// that one field on its existing node object, and the ALREADY-RUNNING
// simulation naturally eases it toward the new target on subsequent ticks.
// No simulation ever gets recreated or hard-reset, so no other bubble is
// disturbed by an unrelated bubble's move, and there's only ever one
// simulation instance, so there's no possibility of two independent physics
// processes fighting over the same shared bubble object.
//
// realtimeNodes is the array passed to the simulation - the SAME array
// object for the app's lifetime. Nodes are added/removed via push/splice
// only, never replaced wholesale, which is what lets d3 preserve
// x/y/vx/vy for every bubble that's already in it.
const realtimeNodes: any[] = [];

// Visitors whose slot currently shows a density cloud. Their real bubbles are
// hidden, so they must not keep pushing on the rest of the board (repulsion and
// collision are zeroed; they stay anchored to their own slot): a hundred hidden bubbles in one slot were shoving the
// neighbouring slot's cluster up and out of its box.
let cloudedVisitors = new Set<string>();

// The labels under each page box (the treatment Chris picked from the idle-life mockups, 2026-10-08):
// the page name is bold and dark; the count and its "on page" are regular and one mid grey together
// (it used to be a grey name, a grey number and a lighter "on page", all regular).
const PAGE_LABEL_COLOUR = "#263238";
const PAGE_LABEL_WEIGHT = 600;
const COUNT_LABEL_COLOUR = "#6b7782";
const COUNT_LABEL_WEIGHT = 400;
let realtimeSimulation: any = null;

// Bumped by pauseRealtimeAnimations() to invalidate any in-flight rAF loop
// (currently just confetti) whose progress is tracked by a plain per-frame
// counter, not elapsed wall-clock time - see that function's own comment
// for why this exists (backgrounded-tab rAF suspension).
let animationGeneration = 0;

// "None" filter - collapses every bubble into one shared, fixed category
// instead of reading sessionData[menuType] (which doesn't exist for
// menuType "none", and would otherwise trip the hard gate every other
// filter relies on - see this function's callers). Reusing the normal
// single-category sidebar/spawn-origin machinery this way, rather than a
// special no-sidebar code path, means "None" gets a real, stable spawn
// point (the "All visitors" row) for free instead of a disconnected fixed
// coordinate.
const ALL_VISITORS_CATEGORY = "All visitors";
// Exported for tooltipService.ts's redesigned real-time tooltip header,
// which needs "whatever the currently active filter's value is for this
// bubble" - reuses this instead of duplicating the "none" special-case.
// Bucket for sessions that genuinely have no value for the active filter.
//
// This used to return undefined, and every caller treated that as "skip this
// session entirely" - drawUserBubble bailed before creating a bubble, with no
// error and nothing on screen. The visitor still appeared in the Sessions
// drawer, so the list and the visualization disagreed about who was there.
//
// Confirmed on real production data (2026-07-29): a direct visit stores
// `referrer: ""`, which is falsy, so "By Referral Link" silently dropped it;
// and sessions written before that day's deploy have no `visitor_category`
// field at all, so "By Visitor Type" dropped every one of them. Both read as
// "the dashboard is broken" rather than "these visitors have no value here".
//
// A named bucket says the true thing instead: the visitor exists, this
// particular filter just has nothing to say about them.
export { NO_CATEGORY_DATA };

// "Hide unknown" (Display settings): leave the groups that mean "we do not know" out of the lists and out of the percentages, so what is left adds up to 100% of what IS
// known. Asked for by Chris, 2026-10-16: with By Search Engine, the visits that did not come from a search engine at all were the biggest group, so Google read as
// ~21% when it is ~66% of the search visits that have an engine.
// "Unknown" = no value ((No data...)), the literal Unknown/UNKNOWN some fields carry, and - for search engines only - "Other" (a referrer that is not a known engine).
export const isUnknownBucket = (name: unknown, menuType?: string): boolean => isUnknownBucketName(name, menuType ?? selectedFilter?.menuType);
// Remembered across a refresh like the other display choices; off unless the person turned it on. The PUBLIC DEMO starts with it ON (until the visitor chooses), so its lists
// never open with a "(No data...)" row; the guard folds away in every normal build.
const storedHideUnknown = storage.get("hideUnknownBuckets");
export let hideUnknownBuckets =
  storedHideUnknown === "true" || (process.env.REACT_APP_DEMO === "1" && storedHideUnknown !== "false");
export const shouldHideBucket = (name: unknown): boolean => hideUnknownBuckets && isUnknownBucket(name);
// Hide unknown touches ONLY the historical dock and the percentages. The real-time list and stage always show every visitor, with their icons unchanged (Chris,
// 2026-10-17: fading the live bubbles hurt readability; those visitors must stay visible).
export const setHideUnknownBuckets = (on: boolean) => {
  hideUnknownBuckets = on;
  try {
    storage.set("hideUnknownBuckets", on ? "true" : "false");
  } catch (e) {
    // the choice just will not survive a refresh
  }
  eventEmitter.emit("filterChanged"); // the dock and the lists are rebuilt from the data, without (or with) the unknown groups
};

// ── the historical dock's LAYOUT (Display settings > Historical) ─────────────────────────────────────────────────────────────────
// "cluster" is the physics cloud it has always been. "rows" lays the same bubbles out sorted by their count - the first at the left of the first row, running to the right and wrapping
// onto further rows - and keeps them in order as counts change (historicalRows.ts is the layout; scheduleHistoricalRowsLayout below glides each bubble to its slot).
// "bars" draws the same categories as a BAR CHART of COLUMNS standing side by side, the name under each (historicalColumns.ts: Chris's mock). "list" is the same categories as ranked horizontal bars,
// the name at the left and the count at the end (historicalBarList.ts). `historicalLabels` puts each category's NAME under its bubble in the rows layout. All three are DEMO ONLY for now
// (Chris, 2026-10-19: "Let's build the insights on the demo first"): another build ignores a stored "bars" / "list" and a stored labels switch, and its menu does not offer them.
export type HistoricalLayoutMode = "cluster" | "rows" | "bars" | "list" | "donut";
export const DOCK_EXTRAS: boolean = process.env.REACT_APP_DEMO === "1" || process.env.REACT_APP_DOCK_EXTRAS === "1";
const storedHistoricalLayout = storage.get("historicalLayout");
const storedHistoricalOrder = storage.get("historicalRowsOrder");
export let historicalLayout: HistoricalLayoutMode =
  storedHistoricalLayout === "rows" ? "rows" : DOCK_EXTRAS && (storedHistoricalLayout === "bars" || storedHistoricalLayout === "list" || storedHistoricalLayout === "donut") ? (storedHistoricalLayout as HistoricalLayoutMode) : "cluster";
export let historicalLabels: boolean = DOCK_EXTRAS && storage.get("historicalLabels") === "true";
/** true when the dock places its bubbles itself (rows, bars) and the physics stays out of it */
const dockPlacesItself = (): boolean => historicalLayout !== "cluster";
/** true when the dock is drawn as a chart (bars, list): its bubbles are not drawn, so there is nothing for a departing visitor to fly to */
const dockIsChart = (): boolean => historicalLayout === "bars" || historicalLayout === "list" || historicalLayout === "donut";
export let historicalRowsOrder: HistoricalRowsOrder = storedHistoricalOrder === "asc" ? "asc" : "desc";
export const setHistoricalLayout = (mode: HistoricalLayoutMode) => {
  historicalLayout = mode;
  try {
    storage.set("historicalLayout", mode);
  } catch (e) {
    // the choice just will not survive a refresh
  }
  eventEmitter.emit("filterChanged"); // the dock is rebuilt from the data, and takes whichever layout is now chosen
};
export const setHistoricalLabels = (on: boolean) => {
  historicalLabels = DOCK_EXTRAS && on;
  try {
    storage.set("historicalLabels", historicalLabels ? "true" : "false");
  } catch (e) {
    // the choice just will not survive a refresh
  }
  scheduleHistoricalRowsLayout(); // the rows make room for the names (or give it back) and the labels appear (or go)
  if (historicalLayout === "cluster") eventEmitter.emit("filterChanged"); // the Cluster is rebuilt: its bubbles take room for their names (or give it back) and the names appear (or go)
};
export const setHistoricalRowsOrder = (order: HistoricalRowsOrder) => {
  historicalRowsOrder = order;
  try {
    storage.set("historicalRowsOrder", order);
  } catch (e) {
    // the choice just will not survive a refresh
  }
  scheduleHistoricalRowsLayout(); // only the order changed: the bubbles glide to their new places
};
// "Netscape" here is never a real claim about the browser - it's
// detectBrowser()'s (skydive_tracking_code/helpers/common.js) own fallback
// to navigator.appName when its regex can't identify the browser at all,
// and due to a 25+ year old web-compat quirk that's literally the string
// "Netscape" for almost any real browser landing on that path. Left as-is,
// any "browser"-dimension lookup groups these into their own legitimate-
// looking "Netscape" bucket (complete with a real Netscape logo,
// netscape.svg) as if it were a genuine identity.
//
// Pulled out as its own function (rather than inlined in
// resolveRealtimeCategory alone) after this exact bug showed up a SECOND
// time in getBubbleIconUrl - that function reads d.sessionData[menuType]
// directly rather than going through resolveRealtimeCategory (bubble/dot
// mode never needs an icon at all, so it never called either function;
// icon mode does, and got the raw unnormalized value). Any future call site
// that reads a raw "browser" field value for icon/label purposes should
// route through this instead of re-deriving the same check a third time.
const normalizeBrowserValue = (menuType: string | undefined, value: any) => {
  if (
    menuType === "browser" &&
    typeof value === "string" &&
    value.toLowerCase().startsWith("netscape")
  ) {
    // Capitalized to match every other category label here ("Chrome",
    // "Firefox") - these render as plain text with no capitalization step
    // downstream, and getLocalIconUrl lowercases internally regardless, so
    // this still resolves to unidentified.svg either way.
    return "Unidentified";
  }
  return value;
};

// What a visitor's value for ANY filter is, by the same rules the sidebar and the bubbles use (an empty value is the "no data" bucket; a visit with no referrer is "/"). The active
// filter's answer is resolveRealtimeCategory; the zoomed view asks for every filter's, so it can split a page's visitors by any of them.
const categoryOf = (menuType: string | undefined, sessionData: any): string => {
  if (menuType === "none") {
    return ALL_VISITORS_CATEGORY;
  }
  const value = normalizeBrowserValue(menuType, sessionData?.[menuType as string]);
  // Deliberately catches "" and null as well as undefined - an empty-string
  // referrer is the single most common case here, not an edge case.
  if (value === undefined || value === null || value === "") {
    // A direct visit has no referrer. The backend's mapper stores that as "/" (Helper/mapper.ts) and the historical side groups it there, so the live side must too -
    // otherwise a direct visitor sat in "(No data for this category)" while the dock called the same people "/".
    return menuType === "referrer" ? "/" : NO_CATEGORY_DATA;
  }
  return value;
};
export const resolveRealtimeCategory = (sessionData: any): string => categoryOf(selectedFilter?.menuType, sessionData);

// For the Insights tab's "see it happen" (src/insights/groupSpotlight.ts): the stage's svg, and who is on it, each with the group they belong to for ANY characteristic (the same value the dock and the sidebar
// group by, so a group named in an insight is found here by the same name). Read-only: nothing here changes the stage.
/**
 * The picture for a group's name (a flag, a logo, a badge in the group's colour), for the Insights tab: the same pictures the dock and the sidebar draw with "Show icons", and shown whether or not that is
 * on, EXCEPT the three kinds that are looked up on an outside favicon service (a referrer, a search engine, a social network): those follow the person's choice, so nothing is asked of an outside
 * server for a customer who has not turned icons on. undefined = draw nothing.
 */
export const getGroupIconUrl = (menuType: string, value: string): string | undefined => {
  if (!useIcons && (menuType === "referrer" || menuType === "search_engine" || menuType === "social_network")) return undefined;

  return resolveIconUrl(menuType, value);
};
export const getStageSvgNode = (): SVGSVGElement | null => (svg?.node() as SVGSVGElement | undefined) ?? null;
export const getStageVisitors = (menuType: string): Array<{ id: string; category: string }> =>
  bubblesData.filter((b: any) => b.realTime).map((b: any) => ({ id: String(b.visitor_id), category: String(categoryOf(menuType, b.sessionData)) }));

// The values the zoomed view may split a page's visitors by, one per filter it offers (zoomDimensions.ts).
const ZOOM_ATTR_MENUS = ["country_name", "os", "browser", "device", "traffic_source", "search_engine", "social_network", "referrer", "visitor_category"];
const zoomAttrsOf = (sessionData: any): Record<string, string> => {
  const attrs: Record<string, string> = {};
  for (const menuType of ZOOM_ATTR_MENUS) {
    attrs[menuType] = String(categoryOf(menuType, sessionData));
  }

  return attrs;
};

// "Does this session currently have a live bubble on screen" - NOT the same
// question as session.session_expire_flag. In live mode they usually agree,
// but in playback every fetched session is already a fully-completed
// historical DB record (session_expire_flag: true) regardless of whether
// playback's virtual clock currently has that visitor "onsite" or already
// "departed" in the replay - so session_expire_flag can't tell the Sessions
// tab which rows correspond to a bubble actually on screen right now.
// bubblesData is what both live AND playback dispatch through to represent
// "who's currently rendered as a real-time bubble," so membership there is
// the actual right signal for both modes uniformly (confirmed directly:
// RightMenu.tsx's session-row color bar wasn't showing during playback
// because it was reading session_expire_flag instead of this).
export const isSessionCurrentlyOnsite = (sessionId: string): boolean =>
  bubblesData.some((item: any) => item.realTime && item.visitor_id === sessionId);

// d3-drag suppresses the native click event after an actual drag distance,
// so this only fires for a genuine click.
const handleRealtimeBubbleClick = (d: any) => {
  eventEmitter.emit("selectSessionInDrawer", d.visitor_id);
};

// Historical dock bubbles are an aggregate for a whole category, not one
// session - clicking opens the segment-detail panel instead (RightMenu.tsx),
// scoped by the currently active filter's field name (selectedFilter.menuType,
// e.g. "browser") and this bubble's own category value (d.menuName, e.g.
// "Chrome"). See project_tooltip_and_visualization_ux_ideas memory for the
// full design.
const handleHistoricalBubbleClick = (d: any) => {
  // "none" ("No Filter" - the app's default on load) IS a valid, clickable
  // segment: its one bucket (d.menuName === ALL_VISITORS_CATEGORY) means
  // "the whole site," so the backend treats dimension="none" as "no extra
  // match filter" (see metrics.repo.ts's getSegmentSummary). Was previously
  // excluded here entirely, which silently no-op'd every click on the
  // default filter - the state most people are actually in when they first
  // click a historical bubble.
  if (!selectedFilter?.menuType || !d?.menuName) {
    return;
  }
  // Pass the bubble's OWN already-resolved color along instead of having
  // the panel re-derive it from d.menuName later (getCategoryColor(name)
  // re-hashes/re-reads categoryColorAssignments independently of however
  // this specific bubble actually got colored) - getHistoricalBubbleFill
  // paints every historical bubble via setColor(d.color), where d.color is
  // a cached palette-slot index, not the raw name, so this is the exact
  // same call with the exact same input the bubble's own fill used,
  // guaranteeing the header matches pixel-for-pixel rather than risking a
  // second, independently-computed value drifting from the real one.
  const color = setColor(d.color);
  eventEmitter.emit("openSegmentDetail", {
    dimension: selectedFilter.menuType,
    value: d.menuName,
    color: { r: color.r, g: color.g, b: color.b },
  });
};

const resolveRealtimeClusterCoords = (pageName: string) => {
  if (pageViewDisabled) {
    return clusterCenterCoords?.[DEFAULT_VALUE.ALL_CLUSTER_INDEX];
  }
  const visitedPage = data.find(
    (page: any) => page.selected && page.name === pageName
  );
  if (visitedPage) {
    return clusterCenterCoords?.[visitedPage.slotIndex];
  }
  return clusterCenterCoords?.[DEFAULT_VALUE.UNKNOWN_URL_CLUSER_INDEX];
};

// Reverted: playback previously used a stronger pull + higher friction than
// live mode, tuned across several rounds to try to make the spawn-to-dock
// animation visually complete within playback's compressed real-time
// budget. In practice that never converged - each fix (strength 1, then
// 0.6 + higher velocityDecay) traded one artifact for another (overshoot/
// spring-back, then erratic "flitting" jitter from the stronger spring still
// fighting collision/charge every frame, then bubbles disappearing again).
// Motion physics is now IDENTICAL in both modes - live mode's feel was never
// the problem. The actual fix for playback's compressed timing lives at the
// rendering-decision layer instead: moveBubbleToHistoricalCluster always
// docks silently/instantly in playback mode, so the docking transition never
// has to race a real-time settle window at all. See its own comment.
const REALTIME_TARGET_STRENGTH = 0.3;
// Hidden (clouded) bubbles stay anchored to their slot: with no pull they coast
// out of the box, dragging their badges with them.
const realtimeTargetForceStrength = () => REALTIME_TARGET_STRENGTH;

const ensureRealtimeSimulation = () => {
  if (realtimeSimulation) {
    return realtimeSimulation;
  }
  const bubbleParametersData = returnBubbleParameterData();
  realtimeSimulation = d3
    .forceSimulation(realtimeNodes)
    .force(
      "collision",
      d3.forceCollide().radius((d: any) => (cloudedVisitors.has(d.visitor_id) ? 0.1 : getBubbleRadius(d))).strength(1)
    )
    .force(
      "charge",
      d3
        .forceManyBody()
        .strength((d: any) =>
          cloudedVisitors.has(d.visitor_id)
            ? 0
            : bubbleParametersData && bubbleParametersData.charge
            ? parseInt(bubbleParametersData.charge)
            : DEFAULT_D3CIRCLE_CONSTRAINT.charge
        )
    )
    // No "gravity" force here (unlike the historical-cluster simulations,
    // which deliberately use one to pull every bubble toward the SAME
    // single dock point). Each real-time bubble has its OWN distinct
    // per-page target below, resolved from its own pageName - a
    // system-wide mutual-attraction force pulling every bubble toward
    // every OTHER bubble regardless of target (previously strength 0.2,
    // stronger than the 0.05 per-bubble target pull below) fights that:
    // whenever bubbles elsewhere on the board arrived/departed, it visibly
    // tugged already-settled bubbles off-target ("shuttling"), and kept
    // bubbles from ever fully reaching their own box's center.
    .force(
      "x",
      d3
        .forceX((d: any) => densityCloud.getPendingTarget(d.visitor_id)?.x ?? resolveRealtimeClusterCoords(d.pageName)?.x ?? d.x)
        .strength(realtimeTargetForceStrength)
    )
    .force(
      "y",
      d3
        .forceY((d: any) => densityCloud.getPendingTarget(d.visitor_id)?.y ?? resolveRealtimeClusterCoords(d.pageName)?.y ?? d.y)
        .strength(realtimeTargetForceStrength)
    )
    // each box's cluster stays centred on its own box whatever the spacing (the board-wide repulsion pushes neighbouring clusters off-centre, more the looser the spacing; see clusterRecentre.ts). A bubble on its way
    // somewhere (a pending target) or hidden in the density cloud is not part of its box's cluster.
    .force(
      "recentre",
      clusterRecentreForce<any>((d) => {
        if (cloudedVisitors.has(d.visitor_id) || densityCloud.getPendingTarget(d.visitor_id)) return null;
        const target = resolveRealtimeClusterCoords(d.pageName);

        return target ? { x: target.x, y: target.y, key: `${target.x},${target.y}` } : null;
      }) as any
    )
    .on("tick", paintRealtimeBubblePositions);
  // one repaint per ~16.7 ms however fast the screen refreshes, with the physics at its old pace (see simulationPacer.ts: a 120 Hz screen has half the time per frame)
  // keepPainting: Medium and Slow bubble speeds are glides that go on for seconds after the simulation has cooled (bubbleGlide.ts), and a paint is what moves them
  if (!pacingSwitchedOff()) paceSimulation(realtimeSimulation, undefined, { keepPainting: () => paintGlides(bubbleMoveMsNow()) && bubbleGlide.moving });
  return realtimeSimulation;
};

// Browsers throttle/fully suspend requestAnimationFrame in a backgrounded
// tab - not just the real-time simulation's own d3-timer (itself rAF-based),
// but every in-flight rAF loop (confetti bursts included). None of that
// work is lost while suspended, it just doesn't run - so on a long-enough
// background period, a visitor returning to the tab sees every one of
// those stalled loops all resume and "catch up" in the same instant: the
// simulation jumps through a backlog of ticks at once (bubbles visibly
// snapping through positions), and any confetti bursts still mid-flight
// all resume together, reading as one big simultaneous storm even though
// each was genuinely triggered by a separate real event spread over real
// time. Call pauseRealtimeAnimations() on `visibilitychange` -> hidden and
// resumeRealtimeAnimations() on -> visible (wired in AnalyticsView.tsx) to
// stop the pile-up before it can happen, rather than trying to smooth out
// a burst after the fact.
export const pauseRealtimeAnimations = () => {
  animationGeneration++;
  realtimeSimulation?.stop();
};

export const resumeRealtimeAnimations = () => {
  if (!realtimeSimulation) {
    return;
  }
  // NOT an animated alphaTarget()->restart() nudge - any bubbles that
  // arrived while paused were pushed into realtimeNodes with the
  // simulation stopped, so collision/charge never had a chance to spread
  // them apart; they're sitting stacked on/near their shared target
  // point. Resuming with a live restart() lets the visible animated ticks
  // do that separation in real time, which reads as bubbles "starting in
  // the middle of the box and rapidly expanding outward" - the exact
  // pile-up problem this pause/resume mechanism exists to prevent, just
  // moved from the whole board to individual boxes. settleRealtimeSimulation()
  // (already built for silent playback reconstructions, same shape of
  // problem: converge deterministically before the first paint) fast-forwards
  // the same separation synchronously with no rendered frames in between,
  // so it's already resolved by the time anything is visible again.
  settleRealtimeSimulation();
};

/*
 * The shown position of each real-time bubble, when the paint pass (not a CSS transition) does the glide - see bubbleGlide.ts. `snapNextPaint` is set by anything that rebuilds the scene
 * silently (settleRealtimeSimulation) so that paint puts every bubble on its target instead of gliding to it. `skydive.cssGlide` is a diagnostic switch for measuring against the browser's
 * transition: `localStorage.setItem("skydive.cssGlide", "1")` and reload.
 */
const bubbleGlide = new BubbleGlide();
/** diagnostic switch for measuring with and without the icon bitmaps (iconBitmaps.ts): `localStorage.setItem("skydive.noIconBitmaps", "1")` and reload */
const iconBitmapsSwitchedOff = (() => {
  try {
    return window.localStorage.getItem("skydive.noIconBitmaps") === "1";
  } catch {
    return false;
  }
})();
let snapNextPaint = false;
let lastGlidePaintAt = 0;
const cssGlideForced = (() => {
  try {
    return window.localStorage.getItem("skydive.cssGlide") === "1";
  } catch {
    return false;
  }
})();
// (with the pacing switched off, a diagnostic only, nothing keeps painting after the simulation cools, so the browser does the glide as it used to)
const paintGlides = (moveMs: number): boolean => !cssGlideForced && !pacingSwitchedOff() && usesJsGlide(moveMs);

/*
 * WRITE A POSITION ONLY WHEN IT MOVED (2026-10-18, Chris: "STILL SKIPPING AND CHOPPINESS"). The paint pass below runs on every simulation tick and used to rewrite cx/cy (and x/y, and a
 * transform) of EVERY bubble, icon, ring and badge every time, moved or not. The browser treats each such write as a style change even when the value is identical, so a trace of the live
 * demo showed ~1,100 cx/cy writes a second and ~65 style recalculations a second, almost all for bubbles that were sitting still while the simulation cooled. Positions are now snapped
 * to 0.05 px (a tenth of a device pixel at 2x, so nothing visible changes) and written only when that text differs from what the element already holds. Reading the attribute back, rather
 * than remembering what we wrote last, keeps this right when anything else (a drag, a transition, a redraw) wrote the element in between. A non-finite value is written as it always was.
 */
// 2026-10-20: 0.5 px was 1 device pixel at 2x, and a bubble slowing into its place moved a whole device pixel at a time, standing still for several frames between steps (perf/approach.mjs, a real
// Chrome at 120 Hz: a bubble stood still in the middle of its glide on 6.6% of the frames it was slowing down, and its steps varied by 150%). 0.05 px (a tenth of a device pixel at 2x)
// lets it move a little every paint, and still stops the writes once a bubble is at rest.
const POSITION_SNAP = 20;
const snapPosition = (v: number): number => Math.round(v * POSITION_SNAP) / POSITION_SNAP;
const writePosition = (node: Element, name: string, value: number): void => {
  if (!Number.isFinite(value)) {
    node.setAttribute(name, String(value));
    return;
  }
  const text = String(snapPosition(value));
  if (node.getAttribute(name) !== text) node.setAttribute(name, text);
};
const writeTransform = (node: Element, x: number, y: number, tail = ""): void => {
  const text = Number.isFinite(x) && Number.isFinite(y) ? `translate(${snapPosition(x)}, ${snapPosition(y)})${tail}` : `translate(${x}, ${y})${tail}`;
  if (node.getAttribute("transform") !== text) node.setAttribute("transform", text);
};

// Every d3.Selection method (selectAll/select/each/attr...) allocates its
// own wrapper objects (_groups/_parents arrays, per-call closures) on top of
// whatever the underlying querySelectorAll call itself allocates - fine for
// occasional use, but this function runs on every single force-simulation
// tick (up to 60-120/sec while bubbles are moving), so those wrapper
// allocations become a sustained, real allocation rate on their own,
// regardless of how many elements actually change. Confirmed live: even
// after collapsing the O(n^2) per-sibling DOM queries below to O(n) (see the
// shapeDatumByVisitorId map), heap still grew ~9.5MB/s with many onsite
// bubbles (country/icon mode, dozens of siblings) - a smaller but still very
// real "slows down and goes in spurts" symptom driven by GC pressure from
// this per-tick D3 overhead itself. Rewritten to use plain
// querySelectorAll/setAttribute - the datum D3 binds via `.data()` is a
// plain `__data__` property on the DOM node either way, so it's readable
// without going through a D3 selection at all.
function paintRealtimeBubblePositions() {
  const svgNode = svg?.node() as SVGSVGElement | undefined;
  if (!svgNode) {
    return;
  }
  const shapeNodes = svgNode.querySelectorAll<SVGGraphicsElement>(
    "circle[realTime=true], rect[realTime=true]"
  );
  const shapeDatumByVisitorId = new Map<string, any>();
  // the glide (bubbleGlide.ts): how far each bubble moves toward where the simulation put it, this paint
  const moveMs = bubbleMoveMsNow();
  const glides = paintGlides(moveMs);
  const paintAt = performance.now();
  const sincePaintMs = lastGlidePaintAt ? paintAt - lastGlidePaintAt : 1000;
  lastGlidePaintAt = paintAt;
  const glideStep = glides ? glideFactor(sincePaintMs, moveMs) : 1;
  const snapAll = snapNextPaint;
  snapNextPaint = false;
  if (glides) bubbleGlide.begin();
  else if (bubbleGlide.size) bubbleGlide.clear();
  /*
   * Resolve every shape's position from the LIVE simulation node, not from the
   * element's own bound datum.
   *
   * A DOM element's datum is only correct while it is the same object the rest
   * of the app mutates - and it stops being that after a filter change:
   * handleFilterChange redraws the bubbles (binding today's objects), then the
   * historical refetch/reconcile that follows REPLACES the entries in
   * realtimeNodes/bubblesData with fresh objects from the backend. The rects
   * keep the previous objects, so handleVisitorMove correctly updates the live
   * ones while the screen draws detached copies that nobody ever touches again.
   *
   * Measured live (2026-07-29): before a filter change 31 of 31 rects were bound
   * to the live nodes; immediately after, 0 of 30 were - while realtimeNodes and
   * bubblesData still agreed with each other. Every page move after a filter
   * switch silently did nothing; dragging "worked" only because it mutates the
   * stale copy the rect happens to hold.
   *
   * Keying off realtimeNodes makes the simulation the single source of truth for
   * position, so a stale binding can no longer desynchronise the view. Same
   * reasoning as the icon-image fix below, generalised: read from the authority,
   * not from whatever an element was handed at creation time.
   */
  const liveNodeByVisitorId = new Map<string, any>();
  for (const liveNode of realtimeNodes) {
    if (liveNode?.visitor_id) {
      liveNodeByVisitorId.set(String(liveNode.visitor_id), liveNode);
    }
  }
  for (const node of shapeNodes) {
    const ownVisitorId = node.getAttribute("visitor_id");
    const live = ownVisitorId ? liveNodeByVisitorId.get(ownVisitorId) : null;
    // Rebind so every OTHER consumer of this element's datum (drag, click,
    // tooltip, the decoration loop below) also sees the live object from here
    // on, instead of only fixing position and leaving the rest stale.
    if (live && (node as any).__data__ !== live) {
      (node as any).__data__ = live;
    }
    const d = live || (node as any).__data__;
    if (!d) {
      continue;
    }
    const visitorId = node.getAttribute("visitor_id");
    // where it is SHOWN: the simulation's own position, or - when the paint pass does the glide - a fraction of the way there. A bubble held by the pointer (fx/fy set) follows it exactly.
    const shown = glides && visitorId ? bubbleGlide.follow(visitorId, d.x, d.y, glideStep, snapAll || d.fx != null || d.fy != null) : d;
    writePosition(node, "cx", shown.x);
    writePosition(node, "cy", shown.y);
    if (node.tagName.toLowerCase() === "rect") {
      const width = +node.getAttribute("width") || 0;
      const height = +node.getAttribute("height") || 0;
      const offsetX = +node.getAttribute("data-offset-x") || 0;
      const offsetY = +node.getAttribute("data-offset-y") || 0;
      writePosition(node, "x", shown.x - width / 2 + offsetX);
      writePosition(node, "y", shown.y - height / 2 + offsetY);
    }
    if (visitorId) {
      shapeDatumByVisitorId.set(visitorId, shown);
    }
  }
  if (glides) bubbleGlide.end();
  // Icon images can end up bound to a datum object that's no longer the same
  // reference as their sibling rect/circle's (confirmed live - some path
  // that re-appends an icon, e.g. a page move, isn't rebinding it to the
  // SAME bubblesData object every other update relies on, leaving it behind
  // a divergent/stale copy). Positioning from the image's OWN datum then
  // silently drifts it out of sync with the bubble it's supposed to sit on
  // top of - dragging a bubble (which mutates the rect's datum directly)
  // made this glaring, since only the invisible rect moved while the visible
  // icon stayed put (reported directly - "icons do not have any click and
  // drag capability"). Reading position from the CORRESPONDING shape's own
  // live datum instead of the image's own guarantees the icon always tracks
  // its bubble regardless of any datum-identity divergence elsewhere.
  // Was 3 separate querySelectorAll scans (images, ripple/hover rings,
  // interaction pulses) - each one a full traversal of the SVG subtree,
  // repeated on every single tick (up to 60-120/sec while any bubble is
  // moving). Confirmed live: with test traffic keeping bubbles in near-
  // continuous motion, median frame time sat right at the 16.7ms (60fps)
  // budget with ~39% of frames over it, well below the threshold that shows
  // up as a Long Task but exactly the kind of small, sustained per-tick cost
  // that reads as constant low-level jitter rather than occasional freezes.
  // All three only ever need visitor_id-keyed lookup into the SAME
  // shapeDatumByVisitorId map built above, so one broader query + branching
  // on tag/attributes in a single loop does the same work in one DOM scan
  // instead of three.
  const decorationNodes = svgNode.querySelectorAll<SVGGraphicsElement>(
    "image[realTime=true], circle[data-highlight-ripple=true], circle[data-hover-highlight=true], [data-pulse-click=true], [data-pulse-typing=true], [data-pulse-idle=true], [data-pulse-reading=true], [data-pulse-watching=true], [data-pulse-scroll=true], [data-pulse-away=true]"
  );
  for (const node of decorationNodes) {
    const visitorId = node.getAttribute("visitor_id");
    const shapeDatum = visitorId ? shapeDatumByVisitorId.get(visitorId) : null;
    if (!shapeDatum) {
      continue;
    }
    const tag = node.tagName.toLowerCase();
    if (tag === "image") {
      const width = +node.getAttribute("width") || 0;
      const height = +node.getAttribute("height") || 0;
      const offsetX = +node.getAttribute("data-offset-x") || 0;
      const offsetY = +node.getAttribute("data-offset-y") || 0;
      writePosition(node, "x", shapeDatum.x - width / 2 + offsetX);
      writePosition(node, "y", shapeDatum.y - height / 2 + offsetY);
    } else if (tag === "circle") {
      // Covers both the ripple/hover rings (fixed radius, cx/cy only) and
      // any circle-shaped interaction pulse - identical positioning logic.
      writePosition(node, "cx", shapeDatum.x);
      writePosition(node, "cy", shapeDatum.y);
    } else if (tag === "text") {
      writePosition(node, "x", shapeDatum.x);
      writePosition(node, "y", shapeDatum.y);
    } else if (tag === "rect") {
      const height = +node.getAttribute("height") || 0;
      const width = +node.getAttribute("width") || 0;
      writePosition(node, "x", shapeDatum.x - width / 2);
      writePosition(node, "y", shapeDatum.y - height / 2);
    } else if (tag === "g" && node.hasAttribute("data-badge-dx")) {
      // Corner-pinned badge (currently the away glyph): a fixed offset from
      // the bubble centre, no scaling. Distinguished from the click cursor
      // below by attribute, since both are <g> elements.
      const bdx = +node.getAttribute("data-badge-dx") || 0;
      const bdy = +node.getAttribute("data-badge-dy") || 0;
      writeTransform(node, shapeDatum.x + bdx, shapeDatum.y + bdy);
    } else if (tag === "g") {
      const offset = +node.getAttribute("data-tri-offset") || 0;
      const scale = +node.getAttribute("data-tri-scale") || 1;
      writeTransform(node, shapeDatum.x - offset, shapeDatum.y - offset, ` scale(${scale})`);
    }
  }
}

// Every silent reconstruction (playback seek / 1:1<->compressed mode
// switch) now starts from an identical, fully deterministic input - same
// session set, same insertion order (see drawUserBubble's silent-origin
// fix), same target coords. But left to the normal animated reheat, the
// collision layout still only reaches its FINAL resting position after
// however many real requestAnimationFrame ticks happen to elapse - and how
// many of those actually fire before the user looks (or before the next
// mode switch) is real wall-clock-timing-dependent, not deterministic. Two
// separate reconstructions of the same session set were settling to
// slightly different in-progress positions purely because they'd each been
// running for a different amount of real time when compared - reported
// directly as bubble positions still changing between 1:1 and compressed.
// Fast-forwarding the simulation synchronously (manual .tick() calls, which
// don't fire the "tick" event/repaint DOM on their own) all the way to its
// natural rest state before the very first paint removes that timing
// variable entirely: given identical input, this always converges to the
// same layout. maxTicks mirrors d3's own default alphaMin/alphaDecay
// relationship (alpha decays below the default 0.001 alphaMin in ~300
// ticks) - a safety cap, not a tuning knob, so a stuck/never-converging
// simulation can't hang the reconstruction.
const SETTLE_MAX_TICKS = 300;
export const settleRealtimeSimulation = () => {
  if (!realtimeSimulation) {
    return;
  }
  // The whole batch was pushed via reheatRealtimeSimulation(silent=true),
  // which deliberately never bumped alpha (no restart() = timer never woke
  // up to consume it) - seed it explicitly here, once, right before the
  // one controlled convergence pass.
  realtimeSimulation.alpha(1);
  for (let i = 0; i < SETTLE_MAX_TICKS && realtimeSimulation.alpha() > realtimeSimulation.alphaMin(); i++) {
    realtimeSimulation.tick();
  }
  snapNextPaint = true;   // a silent rebuild: no journey to glide, the bubbles are where they belong
  paintRealtimeBubblePositions();
};

// Cheap spike to test how draggable real-time bubbles feel before building
// the full drag-to-spotlight feature - deliberately minimal, no spotlight
// zone, no drawer, just: grab a bubble, move it, let go, watch it spring
// back. Pinning via d.fx/d.fy is natively understood by every d3-force
// force (forceX/forceY/collision all skip a fixed node) - no changes needed
// anywhere else, the existing forceX/forceY target (this bubble's page-
// cluster center) just takes back over the instant fx/fy are cleared.
// Settings > Application's "Bubble Speed" applies a CSS transition
// (--bubble-move-duration, AnalyticsView.scss) to every realTime-tagged
// element's cx/cy/x/y, so the D3 simulation's own periodic ticks ease
// smoothly between positions instead of jumping. That's the right feel for
// simulation-driven movement, but a drag gesture writes x/y directly on
// EVERY mousemove - dozens of times a second, far more often than a tick -
// so each new write retargets the CSS transition still easing toward the
// PREVIOUS write, producing exactly what was reported live: the dragged
// bubble "stalls and slows, then speeds up and wobbles" instead of tracking
// the pointer, because the browser is constantly re-interpolating toward a
// moving target it can never catch. Saved/restored per-drag (not read from
// settings directly) so this doesn't have to know or care what the user's
// configured speed actually is - whatever was there before the drag comes
// back unchanged once it ends.
let realtimeMoveDurationBeforeDrag: string | null = null;
const suspendBubbleMoveTransition = () => {
  const svgNode = svg?.node() as SVGElement | undefined;
  if (!svgNode) {
    return;
  }
  realtimeMoveDurationBeforeDrag = svgNode.style.getPropertyValue("--bubble-move-duration");
  svgNode.style.setProperty("--bubble-move-duration", "0s");
};
const restoreBubbleMoveTransition = () => {
  const svgNode = svg?.node() as SVGElement | undefined;
  if (!svgNode) {
    return;
  }
  if (realtimeMoveDurationBeforeDrag) {
    svgNode.style.setProperty("--bubble-move-duration", realtimeMoveDurationBeforeDrag);
  }
  realtimeMoveDurationBeforeDrag = null;
};

// A bubble's DOM element can end up bound to a datum object that's no
// longer the same reference the force simulation actually tracks in
// realtimeNodes (confirmed live via direct instrumentation - a rect
// correctly matched realtimeNodes at creation time, but had silently
// diverged by the time it was dragged a few seconds later, despite no
// redraw/rebind happening in between and no other traced code path
// replacing it; root cause not fully isolated after extensive tracing).
// Resolving the canonical object fresh by visitor_id - rather than trusting
// whatever `d` a given event handed us - and reading/writing THAT directly
// sidesteps the question entirely, regardless of which object is currently
// bound to the DOM element. d3-drag was also found to hand "start"/"drag"/
// "end" a datum captured once at gesture-start rather than re-reading the
// element's current binding on every event, so rebinding via `.datum()`
// mid-gesture (an earlier attempt) never actually reached the "drag"/"end"
// handlers - direct DOM attribute writes below don't depend on that at all.
const resolveCanonicalRealtimeNode = (fallback: any) =>
  realtimeNodes.find((node: any) => node.visitor_id === fallback.visitor_id) ??
  fallback;

const realtimeDragBehavior = () =>
  d3
    .drag()
    .on("start", function (this: SVGGraphicsElement, event: any, d: any) {
      // The tooltip is shown/hidden purely by mouseover/mouseout - starting
      // a drag doesn't fire mouseout (the pointer never actually leaves the
      // element), so it stayed stuck on screen for the whole gesture,
      // tracking nothing, instead of disappearing like every other bubble
      // interaction. Hiding it explicitly here is the only place that
      // actually fires once a drag begins.
      hideRealtimeTooltip();
      suspendBubbleMoveTransition();
      const canonical = resolveCanonicalRealtimeNode(d);
      if (canonical !== d) {
        // canonical's own x/y reflect wherever it silently drifted to while
        // orphaned from the DOM (the simulation kept ticking it, just never
        // visibly) - snapping straight to that would jump the bubble out
        // from under the pointer the instant the drag starts, since it can
        // be far from wherever the user actually sees/grabs it on screen.
        // Carrying the currently-RENDERED position over onto canonical
        // instead means the takeover is invisible.
        canonical.x = d.x;
        canonical.y = d.y;
        d3.select(this).datum(canonical);
      }
      if (!event.active) {
        // alphaTarget alone only pulls alpha up by a tiny fraction per tick
        // (alphaTarget * alphaDecay, ~0.007) - fine for a simulation that's
        // still actively ticking, but once it's fully settled (alpha decayed
        // to 0, d3's internal timer stopped itself), that trickle keeps
        // alpha under alphaMin for many frames, so grabbing a bubble that's
        // been still for even a few seconds silently did nothing visible
        // (reported directly - drag stopped working, a refresh "fixed" it
        // only because a fresh mount's bubbles hadn't settled yet). Every
        // OTHER path that reheats this same simulation (reheatRealtimeSimulation,
        // used for arrivals/page moves) explicitly bumps alpha() itself
        // before restart() - matching that here is what actually wakes the
        // timer immediately instead of waiting on the trickle.
        if (realtimeSimulation && realtimeSimulation.alpha() < REHEAT_ALPHA) {
          realtimeSimulation.alpha(REHEAT_ALPHA);
        }
        realtimeSimulation?.nodes(realtimeNodes);
        realtimeSimulation?.alphaTarget(0.3).restart();
      }
      canonical.fx = canonical.x;
      canonical.fy = canonical.y;
    })
    .on("drag", function (this: SVGGraphicsElement, event: any, d: any) {
      const canonical = resolveCanonicalRealtimeNode(d);
      canonical.fx = event.x;
      canonical.fy = event.y;
      canonical.x = event.x;
      canonical.y = event.y;
      if (canonical !== d) {
        d3.select(this).datum(canonical);
      }
      // Write this element's own rendered position directly rather than
      // going through the shared paintRealtimeBubblePositions pass (which
      // repaints from each element's OWN bound datum) - guarantees THIS
      // element visibly tracks the pointer 1:1 on every mousemove regardless
      // of any datum-identity subtlety, while every other bubble's own
      // tick-driven paint continues completely independently.
      const width = +this.getAttribute("width") || 0;
      const height = +this.getAttribute("height") || 0;
      d3.select(this)
        .attr("cx", canonical.x)
        .attr("cy", canonical.y)
        .attr("x", canonical.x - width / 2)
        .attr("y", canonical.y - height / 2);
      const visitorId = this.getAttribute("visitor_id");
      if (visitorId) {
        svg
          ?.select(`image[realTime=true][visitor_id="${visitorId}"]`)
          .each(function (this: SVGImageElement) {
            const imgWidth = +this.getAttribute("width") || 0;
            const imgHeight = +this.getAttribute("height") || 0;
            const offsetX = +this.getAttribute("data-offset-x") || 0;
            const offsetY = +this.getAttribute("data-offset-y") || 0;
            this.setAttribute("x", String(canonical.x - imgWidth / 2 + offsetX));
            this.setAttribute("y", String(canonical.y - imgHeight / 2 + offsetY));
          });
      }
    })
    .on("end", function (this: SVGGraphicsElement, event: any, d: any) {
      const canonical = resolveCanonicalRealtimeNode(d);
      if (canonical !== d) {
        d3.select(this).datum(canonical);
      }
      if (!event.active) {
        realtimeSimulation?.nodes(realtimeNodes);
        realtimeSimulation?.alphaTarget(0).restart();
      }
      canonical.fx = null;
      canonical.fy = null;
      restoreBubbleMoveTransition();
    });

// Same spike, for historical dock bubbles. Unlike real-time's one persistent
// simulation, historical redraws create a brand-new d3.forceSimulation()
// instance every time (drawBubbleNow/redrawHistoricalBubbles/
// drawNewHistoricalBubble) - stopPreviousSimulation already tracks whichever
// one is currently live in activeSimulations["historical"], so reading that
// on every drag event (rather than closing over whatever simulation existed
// when this particular bubble's DOM element was created) always targets the
// actually-running one, not a stale/stopped instance from an earlier redraw.
const historicalDragBehavior = () =>
  d3
    .drag()
    .filter((event: any) => !dockPlacesItself() && !event.ctrlKey && !event.button)   // in rows and bars every bubble has its place: nothing to drag
    .on("start", (event: any, d: any) => {
      // See realtimeDragBehavior's matching comment - drag never fires
      // mouseout, so the tooltip stayed stuck on screen for the whole
      // gesture unless hidden explicitly here.
      hideHistoricalTooltip();
      if (!event.active) {
        // See realtimeDragBehavior's matching comment - a settled simulation
        // needs alpha bumped directly, not just alphaTarget, or the drag is
        // silently inert until the trickle eventually clears alphaMin.
        const historicalSim = activeSimulations["historical"];
        if (historicalSim && historicalSim.alpha() < REHEAT_ALPHA) {
          historicalSim.alpha(REHEAT_ALPHA);
        }
        historicalSim?.alphaTarget(0.3).restart();
      }
      d.fx = d.x;
      d.fy = d.y;
    })
    .on("drag", (event: any, d: any) => {
      d.fx = event.x;
      d.fy = event.y;
    })
    .on("end", (event: any, d: any) => {
      if (!event.active) {
        activeSimulations["historical"]?.alphaTarget(0);
      }
      d.fx = null;
      d.fy = null;
    });

// Gently reheat the persistent simulation instead of resetting alpha to a
// high value: an already-settled bubble whose target hasn't changed barely
// moves (its forces are already near-balanced), while a newly added or
// retargeted bubble gets enough energy to travel to its new position.
//
// This used to sustain an `alphaTarget(0.3)` floor, reset via a 400ms
// "cooldown" timer on every call, that only relaxed back to 0 (letting the
// simulation actually settle) once 400ms passed with no new bubble event.
// Live traffic is sparse enough for that gap to occur naturally. Playback
// (see playbackService.ts) deliberately paces arrivals/departures/pageviews
// every ~30-90ms to stay watchable, so during any active stretch of
// playback a new event always lands before the 400ms cooldown could fire -
// the simulation was held at the 0.3 floor continuously for the whole
// playback, so every bubble kept getting pushed around by charge/collision
// forces and never actually settled onto its target cluster (reported as
// bubbles "fluttering"/"buzzing" and never landing). A one-shot `alpha()`
// bump lets d3's own per-tick decay bring the simulation back to rest after
// each event, no matter how frequently new events arrive - each call only
// raises alpha if it's already decayed below the bump, so it never fights
// its own decay the way a sustained alphaTarget floor does.
const REHEAT_ALPHA = 0.3;
// `silent` (a whole batch of seek/mode-switch reconstruction bubbles being
// pushed one by one across replaySessionStates' many real animation frames)
// skips `.restart()` deliberately. restart() starts d3's own internal timer,
// which ticks the simulation on ITS OWN real-frame schedule independently of
// our code - so with every earlier push calling restart(), the simulation
// was actively, autonomously moving already-pushed bubbles around for
// however much real wall-clock time happened to elapse while the REST of
// the batch was still being pushed (a multi-frame loop). That's a second,
// separate source of real-time-dependent nondeterminism beyond starting
// position - one reconstruction run and another of the identical session
// set could easily take a different number of real frames to finish
// pushing, diverging before settleRealtimeSimulation's deterministic
// convergence pass even runs. Registering the node (so forces are
// initialized against it) without waking the timer keeps the whole batch
// perfectly static until settleRealtimeSimulation ticks it forward
// synchronously, all at once, from a known start state.
// sim.nodes(realtimeNodes) reinitializes every d3 force (collision/charge/x/y)
// against the array's current contents - for forceManyBody specifically that
// rebuilds its Barnes-Hut quadtree, an O(n log n) cost that scales with how
// many bubbles are currently onsite. Was called synchronously once per
// reheatRealtimeSimulation call, i.e. once per drawUserBubble call - fine for
// a single arrival, but a burst of many sessions resyncing at once (see
// AnalyticsView.tsx's "new-visitor" handler - every currently-onsite session
// gets resent this way on every socket reconnect, e.g. after a laptop wakes
// from sleep) called drawUserBubble once per session independently, each one
// paying this full reinit cost separately. Confirmed live: a burst of ~30
// resyncing sessions produced 15 back-to-back Long Tasks, each right around
// 50-52ms - a near-identical, repeated cost matching exactly one
// sim.nodes() call apiece. Coalescing multiple calls arriving in the same
// tick/microtask turn into a single sim.nodes() call (via queueMicrotask,
// which still runs before the next animation frame, so a normal single
// arrival stays effectively instant) turns that burst into one reinit
// instead of thirty.
let realtimeNodesDirty = false;
let realtimeNodesFlushScheduled = false;

const flushRealtimeNodesIfDirty = () => {
  realtimeNodesFlushScheduled = false;
  if (!realtimeNodesDirty) {
    return;
  }
  realtimeNodesDirty = false;
  const sim = ensureRealtimeSimulation();
  sim.nodes(realtimeNodes);
};

const scheduleRealtimeNodesFlush = () => {
  realtimeNodesDirty = true;
  if (realtimeNodesFlushScheduled) {
    return;
  }
  realtimeNodesFlushScheduled = true;
  queueMicrotask(flushRealtimeNodesIfDirty);
};

// `currentVisitors` (the Pages tab's "Current" column) is DERIVED from the
// bubbles actually on screen, never accumulated - see the long comment on
// syncCurrentVisitorsFromPageNames in pagesModel.ts for why a STATE must not
// be incremented across resyncs.
//
// 🔴 The consequence that was missed, and the bug this fixes (2026-08-29,
// reported on production: "1 total visitor onsite" while every page's Current
// read 0, and it survived a refresh):
//
// Resync arrivals call handleVisitorMovePagesModel with countPageview=false so
// a replayed session cannot manufacture a pageview. But that same flag is what
// increments occupancy, so those paths leave `currentVisitors` untouched and
// rely on something deriving it immediately afterwards. `drawExistingBubble`
// did exactly that. `drawUserBubble`'s silent path - which is how
// flushActiveSessions replays EVERY already-onsite session on every socket
// connect, including the one on every page load - did not. So any visitor who
// was already onsite when the dashboard opened was drawn as a bubble (the
// onsite total was right) but counted on no page, permanently: refreshing took
// the identical path and produced the identical result.
//
// It also fixes a latent negative: an uncounted silent arrival that later
// departs ran `previousPage.currentVisitors--` from 0.
//
// Coalesced through a microtask because a reconnect replays every onsite
// session in one burst (20-30+ is normal), and deriving per arrival would walk
// the whole bubble set each time. Same idiom, and same reasoning, as
// scheduleRealtimeNodesFlush above.
let currentVisitorsSyncScheduled = false;

const flushCurrentVisitorsSync = () => {
  currentVisitorsSyncScheduled = false;
  syncCurrentVisitorsFromPageNames(
    bubblesData
      .filter((item: any) => item?.realTime)
      .map((item: any) => item?.pageName)
  );
};

const scheduleCurrentVisitorsSync = () => {
  if (currentVisitorsSyncScheduled) {
    return;
  }
  currentVisitorsSyncScheduled = true;
  queueMicrotask(flushCurrentVisitorsSync);
};
// the page list is replaced from several places (REMOVE, SELECT, a group made, a socket refresh): pagesModel's setPages tells us each time, so the count is derived again however the list was refreshed
setPagesReplacedListener(() => scheduleCurrentVisitorsSync());

const reheatRealtimeSimulation = (silent = false) => {
  const sim = ensureRealtimeSimulation();
  // d3 forces (collision/charge/x/y) are only initialized against whatever
  // array reference was last passed to .nodes() - pushing/splicing
  // realtimeNodes in place doesn't automatically re-initialize forces for
  // the new contents, so this has to be re-called on every change even
  // though the array reference itself never changes.
  scheduleRealtimeNodesFlush();
  if (silent) {
    return;
  }
  if (sim.alpha() < REHEAT_ALPHA) {
    sim.alpha(REHEAT_ALPHA);
  }
  sim.restart();
  applyBubbleMoveSpeed();
  applyClusterSpacing(sim);
};

// Settings > Application's "Bubble Cluster Spacing" (charge) was, like
// bubble speed above, only ever read once - inside ensureRealtimeSimulation's
// one-time construction block - so a settings change made mid-session
// wouldn't take effect until a full page reload created a fresh simulation.
// Re-applying it here on every reheat closes that gap. Unlike the earlier
// alphaDecay attempt, this is a standard, well-supported d3-force operation
// (re-configuring an EXISTING force's strength for future ticks) - it
// doesn't touch alpha/alphaDecay/restart at all, so it can't put the
// simulation into the same kind of broken state that caused the two 1:1
// playback regressions. Applies unconditionally in every mode (live,
// compressed playback, 1:1) - unlike bubble speed, charge/spacing was
// already applied everywhere before this fix, just not reactively.
const applyClusterSpacing = (sim: any) => {
  try {
    const bubbleParametersData = returnBubbleParameterData();
    const charge = parseInt(bubbleParametersData?.charge);
    const base = Number.isFinite(charge) ? charge : DEFAULT_D3CIRCLE_CONSTRAINT.charge;
    sim
      .force("charge")
      ?.strength((d: any) => (cloudedVisitors.has(d.visitor_id) ? 0 : base));
  } catch {
    // Purely a force-parameter tweak - never let a bad setting value break
    // the simulation itself.
  }
};

// Settings > Application's "Bubble Speed" (Fast/Medium/Slow -> 100/1000/
// 5000, see speedOptions in constants/commonConstants.ts) was saved to the
// account but never actually consumed anywhere in this file - a dormant
// setting until now. First attempt at wiring it up went through the D3
// simulation's own alphaDecay (how fast its physics settle) - that broke
// 1:1 playback twice (see git history/PR discussion if revisiting this)
// despite defensive guarding, most likely from touching shared physics
// state on every single reheat call in a simulation this codebase already
// has a documented history of subtle timing bugs with (fluttering/buzzing,
// burst-pacing, seek performance).
//
// This version is deliberately orthogonal to all of that: it never reads
// or writes anything on the simulation itself (no .alpha()/.alphaDecay()/
// .restart() involvement) - it only sets a CSS custom property
// (--bubble-move-duration, see AnalyticsView.scss's own comment) that the
// BROWSER uses to smooth its rendering of successive cx/cy/x/y attribute
// changes. The D3 simulation keeps computing/setting positions exactly as
// it always has, every tick, completely unaware this exists - this can
// only affect how those changes are visually interpolated, not what values
// get computed, so it's structurally incapable of poisoning the physics
// state the way the alphaDecay attempt did.
// An account's saved speed is 0 until it is first saved (that is the backend's schema default), which
// is none of the three choices on Settings > Application, so no radio was selected and the app
// quietly ran with no easing at all. Anything that is not one of the choices is the default.
const BUBBLE_MOVE_MS_DEFAULT = DEFAULT_D3CIRCLE_CONSTRAINT.speed; // Fast
export const savedBubbleMoveMs = () => {
  const speedMs = Number(returnBubbleParameterData()?.speed);
  return speedOptions.some((o) => o.value === speedMs) ? speedMs : BUBBLE_MOVE_MS_DEFAULT;
};
// how long the real bubbles' glide to a new position takes right now (0 where it is not applied).
// Asked every frame for every pending bubble, so the saved setting is read at most twice a second.
let bubbleMoveMsCache = { at: -1e9, ms: 0 };
const bubbleMoveMsNow = () => {
  const t = performance.now();
  if (t - bubbleMoveMsCache.at < 500) return bubbleMoveMsCache.ms;
  let ms = 0;
  try {
    const applyToThisMode =
      playbackModeService.mode !== "playback" || playbackModeService.realTimeMode;
    ms = applyToThisMode ? savedBubbleMoveMs() : 0;
  } catch {
    ms = 0;
  }
  bubbleMoveMsCache = { at: t, ms };
  return ms;
};
const applyBubbleMoveSpeed = () => {
  try {
    (svg?.node() as SVGElement | null)?.style.setProperty(
      "--bubble-move-duration",
      `${paintGlides(bubbleMoveMsNow()) ? cssMoveMs(bubbleMoveMsNow()) : bubbleMoveMsNow()}ms`
    );
  } catch {
    // Purely cosmetic - never let a bad setting value affect rendering.
  }
};

// Ensures every current real-time bubble (from bubblesData) has a
// corresponding entry in the persistent realtimeNodes array, by reference -
// so mutating a bubblesData item's fields (e.g. pageName) is immediately
// visible to the simulation's per-tick target resolution.
const syncRealtimeNodesFromBubblesData = () => {
  const known = new Set(realtimeNodes.map((node) => node.visitor_id));
  bubblesData
    .filter((item: any) => item.realTime && !known.has(item.visitor_id))
    .forEach((item: any) => realtimeNodes.push(item));
};

// 'new-visitor' bubble creation is deliberately deferred 1s (see
// AnalyticsView.tsx) after the socket event arrives. A very short-lived
// session can send its 'disconnect-visitor' event before that deferred draw
// ever runs, so moveBubbleToHistoricalCluster finds no matching bubble yet.
// Since the backend only emits one disconnect notification per session,
// silently no-op-ing there would leave the bubble orphaned onscreen forever
// once it's finally drawn. Remember the session id here so the deferred draw
// can skip creating it at all, matching the end state a same-order arrival
// would have produced.
const pendingDisconnectedSessions = new Set<string>();
// moveBubbleToHistoricalCluster is async and doesn't remove anything from
// bubblesData until after its first await - two near-simultaneous calls for
// the SAME sessionId (confirmed happening in practice, via debug logging,
// for every single departure during a playback run) can both read
// bubblesData.find(...) and both see the bubble as still present before
// either has actually spliced it out, so both proceed - double-counting
// conversion/session totals and racing two docking transitions against each
// other on the same DOM element (reported as bubbles that never finish
// closing). This set is checked/claimed SYNCHRONOUSLY, before any await, so
// the second near-simultaneous call for the same session bails out
// immediately instead of racing the first.
const departingSessionIds = new Set<string>();
// Who is currently away, so a repaint can restore the glyph. See
// applyInteractionPulse's away/return branch for why this has to be tracked
// rather than re-derived - the dashboard only ever hears about TRANSITIONS.
const awaySessionIds = new Set<string>();
export let useIcons = storage.get("useIcons") === "true";
export let interactionPulsesEnabled = storage.get("interactionPulsesEnabled") !== "false";

// end Date selected for custom date range
export let selectedEndDateRange;
export let historyDataDateRange;
export let items: RealtimeItem[] = [];
let initialHistoryFlag = true;
export let onsiteUser = 0;

// const availableFilters: FilterItem[];

// Was uninitialized (undefined) until handleFilterChange first ran, which
// only ever happens from a Sidebar dropdown click - meaning on a fresh page
// load, this stayed undefined while Sidebar.tsx's own local "menuType"
// React state (just for the button's label) independently defaulted to
// INITIAL_FILTERS[0] ("None"). The button visibly read "By None" while
// every data fetch that reads this variable (AnalyticsView.tsx's initial
// historical fetch, resolveRealtimeCategory, etc.) silently fell back to
// "country_name" instead - a real mismatch between what the UI showed and
// what was actually fetched, not just a display quirk.
/*
 * Restored from localStorage so a refresh keeps the filter you were on.
 *
 * VALIDATED against INITIAL_FILTERS rather than trusted, and this matters more
 * than it looks: getHistoricalBubblesData throws on a menuType it doesn't
 * recognise, and fetchData() SWALLOWS that error - so an unrecognised stored
 * value produces no historical cluster and no sidebar list, with nothing in
 * the console to explain it (that exact failure is recorded in the project
 * CLAUDE.md, from when visitor_category was missing from the backend's
 * allowlist). A stale value left behind by a renamed filter would otherwise
 * give someone a permanently blank dashboard whose only cure is clearing site
 * data. Anything unrecognised falls back to INITIAL_FILTERS[0].
 */
const REALTIME_FILTER_STORAGE_KEY = "skydive.realtimeFilter";

const restoreSelectedFilter = (): FilterItem => {
  // a replay clip starts in "No Filter" here and is moved to the owner's filter by presetShareFilter below once the clip is known; the recipient's own choices are never read from, or kept in, their browser
  if (isShareView()) return (INITIAL_FILTERS.find((f: any) => f.menuType === "none") as FilterItem) || INITIAL_FILTERS[0];
  try {
    const stored = window.localStorage.getItem(REALTIME_FILTER_STORAGE_KEY);
    if (!stored) {
      return INITIAL_FILTERS[0];
    }
    const match = INITIAL_FILTERS.find((f: any) => f.menuType === stored);
    return (match as FilterItem) || INITIAL_FILTERS[0];
  } catch {
    // Private browsing / storage disabled - not a reason to break the app.
    return INITIAL_FILTERS[0];
  }
};

export const persistSelectedFilter = (filter?: FilterItem) => {
  if (isShareView()) return;
  try {
    const menuType = (filter as any)?.menuType;
    if (menuType) {
      window.localStorage.setItem(REALTIME_FILTER_STORAGE_KEY, menuType);
    }
  } catch {
    // Ignore - persistence is a convenience, never a requirement.
  }
};

export let selectedFilter: FilterItem = restoreSelectedFilter();

/** a shared clip opens in the real-time filter the owner had (absent or unknown: No Filter); called by the viewer before the dashboard mounts, never writes anything to the browser */
export const presetShareFilter = (menuType?: string) => {
  if (!isShareView()) return;
  const match = INITIAL_FILTERS.find((f: any) => f.menuType === (menuType || "none"));
  selectedFilter = ((match as FilterItem) || (INITIAL_FILTERS.find((f: any) => f.menuType === "none") as FilterItem) || INITIAL_FILTERS[0]);
};
// Memoizes hashNameToColorIndex's result per category name - purely a cache
// now (the hash itself is a pure function of the name, so this could be
// cleared or skipped entirely without changing any color), unlike the
// first-seen-order counter this used to guard against. Left in place mainly
// so resetVisualizationState()/playback seeks don't recompute the hash for
// every category on every redraw.
let categoryColorAssignments: { [name: string]: number } = {};
export let clusterCenterCoords: any;
let bubblesSelection: any;
export let updateBubbles: any;
/*
 * 🔴 THESE WERE `const`, EVALUATED ONCE AT MODULE IMPORT.
 *
 * Measured 2026-09-09 on a live dashboard: the grid never reflowed. At a
 * 1210px viewport the SVG was 852.8px inside a 940px container; widening to
 * 1600px left the SVG at EXACTLY 852.8px with 477px of dead space, and
 * narrowing to 1024px left it 852.8px inside a 746px container, producing a
 * horizontal scrollbar (scrollWidth 1103 > 1024). The container moved; the
 * grid never did.
 *
 * Three `resize` listeners already existed in the app, but all three
 * reposition small things (the icon row, the historical empty state, the
 * scrub bar) - nothing recomputed the grid itself.
 *
 * They are `let` rather than functions deliberately: 18 call sites read them,
 * and turning each into a call would be a large diff through the most
 * performance-sensitive file in the codebase for no behavioural gain. Kept as
 * mutable values refreshed in one place instead.
 */
export let mainSvgHeight = window.innerHeight;
export let mainSvgWidth = window.innerWidth;

/*
 * Re-read the viewport and rebuild the derived cluster geometry.
 *
 * Called from AnalyticsView's debounced resize handler, never on a raw
 * resize event: resize fires at pointer rate while a window is being
 * dragged, and this recomputes every cluster centre. Perf gotcha #7 in
 * CLAUDE.md is exactly this shape - a burst of layout work each rebuilding
 * the force simulation's quadtree.
 */
/*
 * 🔴 THE STRUCTURE OF THE GRID, NOT THE SIZE OF THE WINDOW (2026-09-10).
 *
 * A resize used to rebuild everything whenever innerWidth/innerHeight changed
 * by a single pixel. createStaticComponents() DESTROYS the whole SVG, so that
 * meant every bubble was removed and re-created and the force simulation
 * restarted from scratch.
 *
 * Measured: MINIMISING ANY WINDOW changes the macOS Dock, which changes
 * innerHeight by ~63px, which fired that whole teardown while the dashboard
 * was fully VISIBLE. One 63px change produced -svg/+svg, 16 bubbles destroyed
 * and re-created, and 3,392 cx/cy writes. Reported for hours as "the bubble is
 * flickering every time I minimize ANY window", and no visibility guard could
 * ever have caught it, because nothing was hidden.
 *
 * The grid is QUANTISED by design: the box is a fixed size and the column
 * count steps 2/3/4/5. A 63px Dock nudge cannot change either. So the rebuild
 * is keyed on what actually restructures the layout, and continuous
 * pixel changes that leave the structure identical cost nothing.
 */
export const gridLayoutSignature = () => {
  const host = document.querySelector("#main_svg")?.parentElement as HTMLElement | null;
  const available =
    host && host.clientWidth > 0
      ? host.clientWidth
      : Math.max(window.innerWidth - 250, 600);
  const columns = columnsThatFit(available);

  return [
    columns,
    computeBoxWidth(available),
    Math.ceil(UNKNOWN_URL_CLUSER_INDEX / columns),
  ].join("|");
};

export const refreshMainSvgDimensions = () => {
  const changed =
    mainSvgWidth !== window.innerWidth || mainSvgHeight !== window.innerHeight;

  // The floor applies to the LAYOUT width only - window.innerWidth is still
  // the real viewport. Below the floor the SVG is simply wider than its
  // container and the container scrolls, which is the intended degradation.
  mainSvgWidth = Math.max(GRID_MIN_WIDTH, window.innerWidth);
  mainSvgHeight = window.innerHeight;

  // Recomputed unconditionally - it is two multiplications, and letting it
  // drift behind mainSvgWidth is how the 100->150 box-width bug happened.
  boxWidth = computeBoxWidth(mainSvgWidth);
  boxHalfWidth = boxWidth / 2;
  boxHeight = Math.round(boxWidth / BOX_ASPECT);
  boxHalfHeight = boxHeight / 2;

  if (changed) {
    // Derived from the width, so it MUST be recomputed here rather than left
    // to the lazy fallback in drawHistoricalBubble - otherwise every cluster
    // centre keeps its pre-resize position while the SVG around it changes.
    clusterCenterCoords = getClusterCoords(mainSvgWidth);
  }

  return changed;
};

let gapBetweenBoxes;

export const pageWiseCount = {};
// The range chosen before the last refresh, if any (see historicalRangeStore). Resolved against the initial list here, and again when the account's own list is
// built (restoreSavedDateRange), because the first historical fetch can run before either.
export let selectedDateRange =
  resolveSavedRange(readSavedRange(), initialHistoryDataDateRange) ??
  (historyDataDateRange ? historyDataDateRange[0] : initialHistoryDataDateRange[0]);
const historyIndexRectCenters = {};
// eslint-disable-next-line prefer-const
let pages: any[] = getCachedPages() ?? [];
export let bubblesData = [];

const someThing =
  window.innerHeight * 0.85 > 500 ? window.innerHeight * 0.85 : 500;

// This is the height of the gray "maincluster" background rect behind the
// real-time grid specifically (separate from the svg's own total height,
// which the historical cluster's layout depends on) - reduced from 0.75 so
// the gray box's bottom edge sits closer to the "All other pages" row
// instead of leaving a large empty gap, giving the historical area below it
// more visual room.
const mainClusterHeight = someThing * 0.62;

// Real-time target boxes are 150px wide (75px each side of their own
// center - see the box rect's own comment in createClusterRect for why),
// so this is the intended clear space between a box's outer edge and the
// gray background rect's own edge - used for BOTH the grid's starting
// x-position (getClusterCoords) and the gray rect/SVG's total width
// (createClusterRect), so the two stay in sync with each other by
// construction instead of via two independently-tuned magic numbers.
const CLUSTER_GRID_MARGIN = 20;

/*
 * PAGE-BOX GEOMETRY, SCALED TO THE VIEWPORT (2026-09-09).
 *
 * These were hardcoded 150 / 75 in twelve places. Measured on a 2560px
 * display: every box stayed 150px wide while the GAPS between them grew to
 * 245px - twelve small boxes floating in space, using ~69% of the width. For
 * a product whose whole point is watching bubbles INSIDE those boxes, a
 * bigger screen should mean bigger boxes, not bigger gaps.
 *
 * Derived from the same column spacing getClusterCoords uses, so the two can
 * never disagree - which is exactly the bug the comment in getClusterCoords
 * warns about, where boxes were widened 100 -> 150 without updating a
 * dependent offset and box 0's left edge landed at a negative x.
 *
 * Clamped at both ends: never below the historical 150 (small screens keep
 * today's proven layout), never above 260 (past that a box is mostly empty
 * space and the grid stops reading as a grid).
 */
const BOX_WIDTH_MIN = 150;
const BOX_WIDTH_MAX = 260;
const BOX_GAP_TARGET = 30;

export let boxWidth = BOX_WIDTH_MIN;
export let boxHalfWidth = BOX_WIDTH_MIN / 2;

/*
 * 🔴 HEIGHT SCALES WITH WIDTH (2026-09-09).
 *
 * The box height was pinned at 100px while the width grew with the viewport,
 * so the box got steadily more letterboxed: 1.5:1 at 1280, 2.0:1 at 1600,
 * 2.6:1 at 1920 and beyond.
 *
 * That aspect ratio - not the gaps - is what reads as wrong. Chris approved
 * 1600 (2.0:1) and rejected both 1920 and 2560 (both 2.6:1), which the gap
 * ratio did not predict at all: 1920 measured a perfectly reasonable 32%.
 * His words: "the boxes shouldn't get wider without getting taller."
 *
 * So the shape is held at the ratio he approved and the box simply gets
 * bigger. A page slot is a container for bubbles; keeping it a consistent
 * shape is what makes the grid look the same at every size.
 */
const BOX_ASPECT = 1.75;

export let boxHeight = BOX_WIDTH_MIN / BOX_ASPECT;
export let boxHalfHeight = boxHeight / 2;

/*
 * 🔴 THE BOX IS A FIXED SIZE. SURPLUS SPACE BECOMES MARGIN (2026-09-10).
 *
 * This used to derive the box from the available width, so a bigger monitor
 * got bigger boxes - 157px at 1338, 175px at 1440, 256px at 1920. Reported
 * directly, comparing two screenshots: "the boxes grow so much larger. That
 * doesn't look right at that largest size."
 *
 * It was also inconsistent with the rule the shell already follows: .main-box
 * caps at $max-shell-width and CENTRES, letting the surplus become gutters.
 * Growing the boxes was the same surplus being spent a different way one level
 * down. Now both levels answer it the same way - stop growing, centre, give
 * the rest back as margin.
 *
 * BOX_WIDTH_TARGET is the 1440px size, which is the one confirmed to look
 * right ("that's not bad", and the layout snapshotted as good). It is a single
 * constant precisely so it can be re-tuned without touching this logic.
 *
 * Shrinking is still allowed BELOW the width where the grid fits: a narrow
 * window must reduce the box rather than overflow. That is the floor
 * BOX_WIDTH_MIN has always been, and it is what keeps the narrow and phone
 * layouts working.
 */
/*
 * 🔴 THE BOX AND ITS TEXT NEVER CHANGE SIZE. THE COLUMN COUNT DOES.
 * (2026-09-10)
 *
 * Three attempts got this wrong before the right model turned up, and all
 * three failed the same way - they resized the CONTENT:
 *
 *   1. Grow with the space: 256px boxes at 1920. "The boxes grow so much
 *      larger. That doesn't look right at that largest size."
 *   2. Fix the size and centre: a mid-width window left them marooned.
 *      "Too small and compressed... this sizing just looks so off."
 *   3. Grow to a cap: better, but still squeezing between the breakpoints.
 *
 * The model that works, and it is the customer's own: keep the box, the label
 * and everything else at ONE size at every viewport, and adapt by REFLOWING
 * the columns - 5 when there is room, then 4, then 3, then 2. The phone layout
 * already did exactly this at 2 columns; this generalises it.
 *
 * The payoff is that the viewBox never has to scale the grid down, so the URL
 * labels stay legible at every width instead of shrinking with the boxes.
 *
 * Boxes only shrink if even TWO columns will not fit - the floor that keeps
 * a very narrow phone from overflowing.
 */
const BOX_WIDTH_TARGET = 180;
const COLUMN_GAP_TARGET = 90;
const MAX_COLUMNS = 5;
const MIN_COLUMNS = 2;

/*
 * On a wide display the surplus has to go SOMEWHERE. Two honest choices:
 * outside the grid (gutters) or between the columns (a wider arena).
 *
 * The grid is not just boxes - it is the space the bubbles fly around in - so
 * surplus becomes gap first, up to this cap, and only what is left over
 * becomes gutter. The BOX and its label never change size either way; this
 * only decides where the leftover pixels land.
 *
 * The cap exists so the columns cannot be flung to opposite edges of a very
 * wide monitor; past it the shell's own max-width takes over and the rest is
 * genuine gutter.
 */
const COLUMN_GAP_MAX = 110;

/*
 * The sidebar's width, as a NUMBER the grid math can use. Must stay equal to
 * $sidebar-width in Variables.scss - the two are the halves of one flex row.
 */
const SIDEBAR_WIDTH = 250;

/*
 * 🔴 THE ONE DEFINITION OF WHERE THE LAYOUT STOPS GROWING (2026-09-10).
 *
 * The grid reaches its natural width when it is at MAX_COLUMNS and the gap
 * has reached COLUMN_GAP_MAX. Past that point nothing inside can grow, so the
 * shell caps and the surplus becomes gutters.
 *
 * This USED TO BE A HAND-COMPUTED SASS NUMBER ($max-shell-width), recalculated
 * by hand every time a constant here changed - 1570, then 1750, then 1630,
 * three times in a single session. A comment warned about it, but a comment is
 * not a mechanism, and a layout value defined in two places is the exact bug
 * that produced most of this work.
 *
 * So it is computed HERE, where the constants live, and published to CSS as a
 * custom property. Sass keeps its own value only as a pre-JS fallback.
 *
 * The test that this is genuinely single-source: change COLUMN_GAP_MAX and
 * confirm the shell cap AND the sidebar's flush offset both follow with no
 * Sass edit at all.
 */
export const gridNaturalWidth = () =>
  MAX_COLUMNS * BOX_WIDTH_TARGET +
  (MAX_COLUMNS - 1) * COLUMN_GAP_MAX +
  CLUSTER_GRID_MARGIN * 2;

export const shellMaxWidth = () => SIDEBAR_WIDTH + gridNaturalWidth();

export const publishShellMaxWidth = () => {
  // Guarded: this module is imported by tests and by any non-DOM context.
  if (typeof document === "undefined" || !document.documentElement) {
    return;
  }
  document.documentElement.style.setProperty(
    "--skydive-shell-max",
    `${shellMaxWidth()}px`
  );
};

// At import time, so the property is set before the dashboard first paints.
// The Sass fallback covers the frame before this module loads.
publishShellMaxWidth();

// Surplus goes into the gaps up to COLUMN_GAP_MAX, then stops.
const columnGapFor = (availableWidth: number, columns: number, boxWidth: number) => {
  if (columns < 2) {
    return COLUMN_GAP_TARGET;
  }
  const forGaps =
    availableWidth - CLUSTER_GRID_MARGIN * 2 - columns * boxWidth;
  const even = forGaps / (columns - 1);
  return Math.round(
    Math.max(COLUMN_GAP_TARGET, Math.min(COLUMN_GAP_MAX, even))
  );
};

// How many whole columns fit, at the constant box size.
const columnsThatFit = (availableWidth: number) => {
  const usable = availableWidth - CLUSTER_GRID_MARGIN * 2 + COLUMN_GAP_TARGET;
  const perColumn = BOX_WIDTH_TARGET + COLUMN_GAP_TARGET;
  const fit = Math.floor(usable / perColumn);
  return Math.max(MIN_COLUMNS, Math.min(MAX_COLUMNS, fit));
};

const computeBoxWidth = (availableWidth: number) => {
  const columns = columnsThatFit(availableWidth);
  const needed =
    columns * BOX_WIDTH_TARGET +
    (columns - 1) * COLUMN_GAP_TARGET +
    CLUSTER_GRID_MARGIN * 2;

  if (availableWidth >= needed) {
    return BOX_WIDTH_TARGET;
  }

  // Only reached when even MIN_COLUMNS will not fit at the target size.
  const forBoxes =
    availableWidth - CLUSTER_GRID_MARGIN * 2 - (columns - 1) * COLUMN_GAP_TARGET;
  return Math.round(
    Math.max(BOX_WIDTH_MIN, Math.min(BOX_WIDTH_TARGET, forBoxes / columns))
  );
};

/*
 * THE MIN-WIDTH FLOOR the punch list asked for.
 *
 * Below this, the grid stops shrinking and the container scrolls instead.
 * Measured: at a 1100px viewport the column spacing is 147 for 150px boxes,
 * so adjacent boxes already OVERLAP by 3px. Compressing further makes the
 * grid unreadable, and the page-slot COUNT must never be reduced to
 * compensate - slots are a paid entitlement, not a layout choice.
 */
const GRID_MIN_WIDTH = 1280;

// The grid spans whatever width its container gives it. It does NOT cap or
// centre itself: .main-box caps the whole shell at $max-shell-width and
// centres it (Styles.scss), so the sidebar and the grid travel together and
// the surplus on a large monitor becomes equal gutters.
//
// A grid-level cap was tried first and was wrong twice over. It centred the
// grid inside the DASHBOARD container only, while the sidebar stayed pinned
// to the viewport edge - measured gutters 570px left vs 340px right. And it
// left two independent maximum widths that had to agree with each other,
// which is the same duplicate-definition trap behind the historical
// cluster's +230/+134 and the sidebar's 250px-in-four-places.


// Distance from the grid's bottom edge down to the historical cluster's
// centre. ONE definition on purpose: drawHistoricalBubble derives the
// cluster's y from it, and AnalyticsView.tsx's positionHistoricalEmptyState
// imports it to place the "No sessions" text in the same place. These were
// two independent magic numbers (+230 and +134) that drifted apart the
// moment the box geometry changed.
// 🔴 KNOWN-GOOD GEOMETRY, approved 2026-09-09 after a long tuning session.
// Do not change any of these without a live measurement AND Chris's sign-off -
// three separate attempts to "improve" them were rejected and reverted:
//   BOX_WIDTH_MIN 150 / BOX_WIDTH_MAX 260 / BOX_GAP_TARGET 30
//   BOX_ASPECT 1.75  (boxes must not get wider without getting taller)
//   LABEL_CLEARANCE 44  (minimum row pitch = box height + this)
// The historical cluster is CENTRED in its band rather than offset from the
// grid, so there is deliberately no gap constant here any more.
// The grid's real bottom edge, captured when the historical cluster is drawn.
// This is the FLOOR the historical bubbles may not rise above. The tick
// clamp's top bound used to be effectively zero (radius + 2), so a category
// with many values - Country is the obvious one - could spread upward into
// the page grid. Nothing bounded it; it simply had not been observed with a
// big enough cluster, and moving the cluster up on 2026-09-09 narrowed the
// band above it. 0 reproduces the old unbounded behaviour, which is the right
// fallback for before the grid has been drawn.
export let historicalBandTop = 0;

// 🔴 NARROW GRID: stack the page slots instead of shrinking them.
//
// Below this width the grid drops from 5 columns to 2 and grows downward, so
// every slot keeps its full readable size and the page scrolls VERTICALLY.
// Horizontal scrolling was explicitly rejected ("I have to scroll up and down
// and to the left and right?!") and scaling to fit made the slots unreadable
// (0.73x at 800px, 0.32x at 700px). Vertical scrolling is the one direction
// people already expect.
//
// ALL TWELVE SLOTS SURVIVE - they restack, they are never dropped.
// mainViewSlots is a paid entitlement, so a breakpoint that showed fewer
// would be silently downgrading someone's plan.
//

// The grid's own landmarks, exported so createClusterRect never has to index
// a slot by literal again. It used clusterCenterCoords[4] for the rightmost
// column and [2]/[9] for the wide box's edges - all three assume exactly five
// columns and all three break at two.
export let gridLastColumnX = 0;
export let gridWideBoxStartX = 0;
export let gridWideBoxEndX = 0;
export let gridContentBottomY = 0;
// The grid's true horizontal centre. Was read as clusterCenterCoords[2].x,
// i.e. "the middle column of five".
export let gridCentreX = 0;
// Left edge of the leftmost box, the counterpart to gridWideBoxEndX.
export let gridFirstColumnLeftEdge = 0;
// Left edge of the CENTRED grid within its container. The grey panel must
// start here too, or its left margin swallows the centring offset while the
// right keeps only the base margin.
export let gridLeftEdge = 0;

/*
 * The SVG canvas's logical height.
 *
 * Was `mainSvgHeight - 140` everywhere - i.e. the WINDOW's height, which is
 * right only while the grid fits on one screen. The narrow layout is 7 rows
 * deep and taller than the viewport by design, so the canvas has to be sized
 * from its CONTENT and the page scrolls to reach the rest.
 *
 * NARROW_HISTORICAL_BAND is the room left below the last row for the
 * historical dock, matching the band the wide layout leaves it.
 */
const NARROW_HISTORICAL_BAND = 260;
export const gridCanvasHeight = () => {
  /*
   * 🔴 CONTENT-DERIVED AT EVERY WIDTH, not just the narrow layout
   * (fixed 2026-09-10).
   *
   * This used to fall back to `mainSvgHeight - 140` - the WINDOW's height -
   * for anything that was not the phone layout. That was fine while the grid
   * was always 3 rows: it always fitted.
   *
   * Once the columns started reflowing, the ROW COUNT varies at every width:
   * 3 columns means 4 rows, 2 means 6. At 1280 the taller grid overflowed a
   * canvas still sized to the window, so the historical band was squeezed to
   * nothing AND there was no page height to scroll into. Reported directly:
   * "this is squeezing the historical cluster space down. And I can't scroll
   * down to see it."
   *
   * The canvas is now whichever is TALLER: the window, or the content plus a
   * full band for the historical cluster. Taking the max keeps short grids
   * filling the screen (no dead space beneath a 3-row layout) while letting a
   * tall one grow the page so it can actually be scrolled.
   */
  const contentDriven =
    gridContentBottomY > 0
      ? Math.round(gridContentBottomY + NARROW_HISTORICAL_BAND)
      : 0;

  return Math.max(mainSvgHeight - 140, contentDriven);
};

/*
 * Initialise at module load. Without this, someone OPENING the dashboard on a
 * large display got the 150px fallback until they happened to resize the
 * window - the exact failure this change exists to remove, just deferred.
 *
 * mainSvgWidth is re-floored here too: its own declaration runs before
 * GRID_MIN_WIDTH exists, so it starts as the raw viewport.
 */
mainSvgWidth = Math.max(GRID_MIN_WIDTH, window.innerWidth);
boxWidth = computeBoxWidth(mainSvgWidth);
boxHalfWidth = boxWidth / 2;
boxHeight = Math.round(boxWidth / BOX_ASPECT);
boxHalfHeight = boxHeight / 2;

export const getPagesList = () => {
  pages = getCachedPages() ?? [];
};

// A per-transition gooey ("metaball") burst effect: a brief SVG filter applied
// only to a small, short-lived group - not a standing filter on the whole
// historical cluster (that whole-cluster version was tried and reverted; the
// user wants the effect localized to the moment a bubble is born or docks).
//
// This is appended directly into the main cluster `svg` - the SAME element
// (and therefore the SAME internal coordinate space) the real bubbles live
// in. An earlier version rendered the burst into a separate, viewport-fixed
// overlay `<svg>` on document.body so it could never be clipped/hidden, but
// that required converting between the sidebar's screen coordinates and the
// main svg's internal coordinates (via getScreenCTM) for every burst, and any
// drift between those two conversions - stale reads, sub-pixel rounding,
// re-computing at a different moment than the real bubble's own placement -
// showed up as the burst visibly not lining up with the actual bubble.
// Rendering into the same svg, using the exact same x/y the real bubble
// uses, makes misalignment structurally impossible instead of something to
// keep chasing.
const buildGooeyFilterDef = (id: string, stdDeviation: number) => {
  svg
    .append("defs")
    .append("filter")
    .attr("id", id)
    .attr("x", "-100%")
    .attr("y", "-100%")
    .attr("width", "300%")
    .attr("height", "300%")
    .call((filter) => {
      filter
        .append("feGaussianBlur")
        .attr("in", "SourceGraphic")
        .attr("stdDeviation", stdDeviation)
        .attr("result", "blur");
      filter
        .append("feColorMatrix")
        .attr("in", "blur")
        .attr("mode", "matrix")
        .attr(
          "values",
          "1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 20 -9"
        )
        .attr("result", "goo");
      filter
        .append("feComposite")
        .attr("in", "SourceGraphic")
        .attr("in2", "goo")
        .attr("operator", "atop");
    });
};

// Now that the sidebar row-position race is fixed at its actual source
// (Sidebar.tsx was discarding notifySidebarItemsUpdated's pinned-order
// payload and re-reading the live, unpinned `items` array directly - see
// that fix's comment), the ONE remaining gap is: a burst's blur can extend
// a few pixels past its own row into a NEIGHBORING row, which is free to
// resort/repaint normally (only the actively-bursting row itself is
// pinned). This gives the spawn burst its own, modestly tighter blur so it
// can't bleed into that neighbor - modest specifically because an earlier,
// much more aggressive version of this (stdDeviation 2, tried before the
// row-locking bug was fixed) was reported as looking worse, without the
// row-lock in place to address the other half of the problem.
const ensureGooeyFilter = () => {
  if (!svg) {
    return;
  }
  if (svg.select("#gooey-transition-filter").empty()) {
    // Used by the DOCK burst, which plays in the open space around the
    // historical cluster - no neighboring-row race there, so the original
    // wider blur is fine.
    buildGooeyFilterDef("gooey-transition-filter", 8);
  }
  if (svg.select("#gooey-spawn-filter").empty()) {
    buildGooeyFilterDef("gooey-spawn-filter", 5);
  }
};

// Plays a brief "budding" burst at a fixed viewport point: a small anchor
// blob and a growing blob start overlapping (so the gooey filter melds them
// into one shape) and the growing blob then pulls away and fades, reading as
// the new bubble "oozing out" of its origin point (e.g. the sidebar dot for
// a new spawn, or the historical bubble's own position when a departing
// bubble docks into it).
// SVG filters are expensive per-element (each filtered node needs its own
// offscreen raster pass), so this caps how many gooey bursts can be running
// at once. Real traffic - the actual target for this effect - never gets
// close to this cap; it exists so synthetic load-test traffic (which fires
// far more spawn/dock events per second than any real visitor pattern) can't
// pile up dozens of simultaneous filtered elements and drag the whole page
// down. Bursts beyond the cap are simply skipped, not queued, so the effect
// degrades gracefully instead of building a backlog.
const MAX_CONCURRENT_GOOEY_BURSTS = 12;
let activeGooeyBurstCount = 0;

// items.sort() (called on every arrival, elsewhere in this file) reorders
// the sidebar list immediately in memory. If that new order reached the
// sidebar's React state while a burst was still playing at one row's
// on-screen position, that row's slot could get silently repainted with a
// DIFFERENT category's name/color mid-animation - Sidebar.tsx's row list has
// no `key` prop, so React reconciles by index, meaning a resort doesn't move
// DOM nodes, it just patches each slot's content in place. The burst,
// anchored to a screen position rather than a specific row's identity, had
// no way to know its slot had been repainted - that's what read as "the
// burst is the wrong color."
//
// An earlier attempt fixed this by deferring the ENTIRE list's update
// while any burst was in flight - but playback can fire arrivals faster
// than a single burst's ~800ms lifetime, so back-to-back bursts chained
// into an unbroken deferral, which needed an arbitrary time cap
// (MAX_SIDEBAR_REORDER_FREEZE_CHAIN_MS) to avoid the whole sidebar looking
// frozen during a sustained busy stretch - and that cap reintroduced this
// exact bug every time it tripped mid-burst.
//
// This replaces that with per-row pinning instead of a global freeze:
// whichever specific category currently has a burst playing keeps its
// CURRENT on-screen slot no matter how counts/sort would otherwise move it,
// while every other row updates immediately and normally. Nothing is ever
// globally deferred, so there's no staleness trade-off and no timing gap
// left for a busy stretch to exploit.
const activeBurstRowNames = new Set<string>();
let lastEmittedRowOrder: string[] = [];

// Called right before playing a burst for `name` (spawn or dock), and
// automatically releases the lock once that burst's own animation duration
// has elapsed - matching the effect's actual on-screen lifetime exactly,
// not a shared/guessed timeout.
const lockRowDuringBurst = (name: string, durationMs: number) => {
  activeBurstRowNames.add(name);
  setTimeout(() => {
    activeBurstRowNames.delete(name);
    notifySidebarItemsUpdated();
  }, durationMs);
};

// Called from 9+ places throughout this file - every single arrival,
// departure, dock update, and burst-completion. Each call cascades into a
// React re-render (via the "sideBarRealtimeItemsUpdated" emit below) AND
// schedules further requestAnimationFrame/setTimeout work in AnalyticsView.tsx
// (via refreshHistoricalEmptyState's own "historicalSessionsChanged" emit) -
// confirmed live via a Chrome DevTools CPU profile during a slow playback
// scrub: ~70% of all self time was attributed to setTimeout/requestAnimationFrame
// callback bodies, and notifySidebarItemsUpdated/its downstream
// onHistoricalSessionsChangedReposition each showed ~80% of TOTAL trace time
// despite trivial self time - i.e. nearly the entire slowdown was this
// cascade re-triggering itself once per individual session event instead of
// once per batch, compounding directly with how many events a scrub
// reconstruction processes (worse the longer/more you scrub - exactly the
// reported symptom). Coalescing multiple synchronous calls within the same
// tick into a single queued execution (any call while one is already
// pending is a no-op) cuts an O(n) cascade down to O(1) per batch of
// same-tick events, without changing what any individual caller does.
let notifySidebarItemsUpdatedPending = false;
export const notifySidebarItemsUpdated = () => {
  if (notifySidebarItemsUpdatedPending) {
    return;
  }
  notifySidebarItemsUpdatedPending = true;
  queueMicrotask(() => {
    notifySidebarItemsUpdatedPending = false;
    notifySidebarItemsUpdatedFlush();
  });
};

const notifySidebarItemsUpdatedFlush = () => {
  const freshOrder = items.map((item) => item.name);
  // Rows with an in-flight burst keep their LAST EMITTED index - inserted
  // back at that position after removing them from the freshly-sorted
  // order - so no other row's position is affected by a locked row still
  // being "out of place" relative to its current counts.
  const locked = freshOrder.filter((name) => activeBurstRowNames.has(name));
  const displayOrder = freshOrder.filter((name) => !activeBurstRowNames.has(name));
  for (const name of locked) {
    const priorIndex = lastEmittedRowOrder.indexOf(name);
    const insertAt = priorIndex === -1 ? displayOrder.length : Math.min(priorIndex, displayOrder.length);
    displayOrder.splice(insertAt, 0, name);
  }
  lastEmittedRowOrder = displayOrder;

  const itemsByName = new Map(items.map((item) => [item.name, item]));
  const orderedItems = displayOrder
    .map((name) => itemsByName.get(name))
    .filter((item): item is RealtimeItem => item != null);

  eventEmitter.emit(
    "sideBarRealtimeItemsUpdated",
    JSON.parse(JSON.stringify(orderedItems))
  );

  refreshHistoricalEmptyState();
};

// "No sessions" placeholder where the historical cluster would otherwise
// sit. First attempt drew this as SVG text at clusterCenterCoords[11] -
// wrong twice over: that coordinate is a static default from
// getClusterCoords, but drawHistoricalBubble mutates the SAME object's
// .y at runtime (historicalCoords.y = clusterCenterCoords[10].y + 230,
// its own comment explains why) - if this ran before that mutation ever
// happened (e.g. on first paint, before any real dock bubble existed),
// it captured the stale pre-mutation value and rendered far too high.
// Simpler and more robust: emit a plain event with the current total so
// AnalyticsView.tsx can render an ordinary HTML block (same treatment as
// Sidebar.tsx's own empty state) positioned in normal page flow below the
// grid, instead of fighting this SVG coordinate system a third time. Also
// clears any dock bubble left over in the DOM - a genuinely empty period
// shouldn't show a lingering dot alongside the empty-state text.
const refreshHistoricalEmptyState = () => {
  if (historical.totalSession === 0) {
    svg?.selectAll("circle[historical=true], rect[historical=true], image[historical=true]").remove();
  }
  eventEmitter.emit("historicalSessionsChanged", {
    hasSessions: historical.totalSession > 0,
  });
};

// Single on/off switch (surfaced as a "Display settings" toggle in the
// sidebar) plus every knob for the effect's look/feel, gathered here so both
// are easy to tune without hunting through the animation code below.
// Remembered across a refresh (it is a display choice, like icons and activity). On unless the person turned it off.
export let gooeyEffectEnabled = storage.get("gooeyEffectEnabled") !== "false";
export const setGooeyEffectEnabled = (enabled: boolean) => {
  gooeyEffectEnabled = enabled;
  try {
    storage.set("gooeyEffectEnabled", enabled ? "true" : "false");
  } catch (e) {
    // the choice just will not survive a refresh
  }
};

// HOW A VISITOR LEAVES (Chris, 2026-10-23: "all the closing visualization options ... toggles for all six"). Most of the noise in a replay at speed is visitors flying down to the historical dock. One of six, never
// more than one: fly to the dock (the way it has always been), dim then fly, or close right where the visitor is: shrink, pop into a ring, sink, or vanish. Whatever it is, the counts are the same. Remembered across a
// refresh; clips carry it.
export type LeaveStyle = "fly" | "dim" | "shrink" | "pop" | "sink" | "vanish";
export const LEAVE_STYLES: LeaveStyle[] = ["fly", "dim", "shrink", "pop", "sink", "vanish"];
export const DIM_LEAVING_OPACITY = 0.25;
const readLeaveStyle = (): LeaveStyle => {
  const saved = storage.get("leaveStyle") as LeaveStyle;
  if (LEAVE_STYLES.includes(saved)) return saved;

  // the first version of this was a single "Dim leaving visitors" switch
  return storage.get("dimLeavingEnabled") === "true" ? "dim" : "fly";
};
export let leaveStyle: LeaveStyle = readLeaveStyle();
export const setLeaveStyle = (style: LeaveStyle) => {
  leaveStyle = LEAVE_STYLES.includes(style) ? style : "fly";
  try {
    storage.set("leaveStyle", leaveStyle);
  } catch (e) {
    // the choice just will not survive a refresh
  }
};
/** the styles that close the visitor where it is: no flight to the dock */
const closesInPlace = (): boolean => leaveStyle === "shrink" || leaveStyle === "pop" || leaveStyle === "sink" || leaveStyle === "vanish";

/**
 * The closing animation for the in-place styles. The real bubble (or icon) is removed at once like any silent departure; a copy that nothing else can find (no visitor_id, not a real-time shape, no forces) plays the
 * animation where the bubble was and removes itself. Transform and opacity only, on a handful of short-lived nodes.
 */
const playLeaveGhost = (selector: string, style: LeaveStyle) => {
  if (!svg || style === "vanish" || style === "fly" || style === "dim") return;
  const duration = Math.max(160, Math.min(520, dockTransitionDurationMs() * 0.35));
  const nodes: Element[] = [];
  svg.selectAll(selector).each(function () { nodes.push(this as Element); });
  for (const node of nodes) {
    const parent = node.parentNode;
    if (!parent) continue;
    let ghost: SVGElement;
    if (style === "pop") {
      const box = (node as SVGGraphicsElement).getBBox();
      const fill = node.getAttribute("fill") || "#3d6bb3";
      ghost = document.createElementNS("http://www.w3.org/2000/svg", "circle") as SVGElement;
      ghost.setAttribute("cx", String(box.x + box.width / 2));
      ghost.setAttribute("cy", String(box.y + box.height / 2));
      ghost.setAttribute("r", String(Math.max(4, Math.max(box.width, box.height) / 2)));
      ghost.setAttribute("fill", "none");
      ghost.setAttribute("stroke", fill.startsWith("url") ? "#3d6bb3" : fill);
      ghost.setAttribute("stroke-width", "2");
    } else {
      ghost = node.cloneNode(true) as SVGElement;
      ["visitor_id", "realTime", "id", "name"].forEach((a) => ghost.removeAttribute(a));
    }
    ghost.setAttribute("data-leave-ghost", "true");
    ghost.setAttribute("pointer-events", "none");
    ghost.style.transformBox = "fill-box";
    ghost.style.transformOrigin = "center";
    parent.appendChild(ghost);
    const frames =
      style === "shrink"
        ? [{ transform: "scale(1)", opacity: 1 }, { transform: "scale(0)", opacity: 0 }]
        : style === "sink"
          ? [{ transform: "translate(0,0)", opacity: 1 }, { transform: "translate(0,16px)", opacity: 0 }]
          : [{ transform: "scale(1)", opacity: 0.9 }, { transform: "scale(2.6)", opacity: 0 }];
    const gone = () => ghost.remove();
    try {
      ghost.animate(frames, { duration, easing: style === "pop" ? "ease-out" : "ease-in", fill: "forwards" }).onfinish = gone;
    } catch (e) {
      gone();
    }
    window.setTimeout(gone, duration + 400);
  }
};

// The historical total on each page box (slotTotals.ts): a bar along the box's bottom edge. A build switch (the demo and staging) and a display choice (Display settings, on unless turned off).
export const SLOT_TOTALS: boolean = process.env.REACT_APP_DEMO === "1" || process.env.REACT_APP_SLOT_TOTALS === "1";
export let slotTotalsEnabled: boolean = SLOT_TOTALS && storage.get("slotTotalsEnabled") !== "false";
export const setSlotTotalsEnabled = (on: boolean) => {
  slotTotalsEnabled = SLOT_TOTALS && on;
  try {
    storage.set("slotTotalsEnabled", slotTotalsEnabled ? "true" : "false");
  } catch (e) {
    // the choice just will not survive a refresh
  }
  scheduleSlotTotals();
};

// Experimental "flow lines" feature - a weighted line between two page
// clusters for every session that navigates between them, thickening as
// more sessions travel that same path. Off by default: this is a first,
// cheap prototype to judge whether the idea is worth investing in further,
// not a finished feature - see setFlowLinesEnabled's own toggle in
// Sidebar.tsx's Display settings.
export let flowLinesEnabled = false;
// FLOW LINES AS RIBBONS (src/funnel/flowRibbonMath.ts): a second STYLE of the same flow lines, drawn from the same counts. Only in a demo or staging build (funnelHost exists there and
// nowhere else), where it is the default; "lines" is the dashed-line style that every build has. The choice is remembered in this browser.
const FLOW_STYLE_KEY = "flowLinesStyle";
export const flowRibbonsAvailable = !!funnelHost;
const readFlowStyle = (): "lines" | "ribbons" => {
  if (!funnelHost) return "lines";
  try {
    return storage.get(FLOW_STYLE_KEY) === "lines" ? "lines" : "ribbons";
  } catch (e) {
    return "ribbons";
  }
};
export let flowLinesStyle: "lines" | "ribbons" = readFlowStyle();
// How many flows the ribbon style draws: the busiest 10, 20 or ALL (0). 20 unless the viewer chose otherwise (the options and the default are in src/funnel/flowRibbonMath.ts; they are repeated here
// because nothing outside the funnel folder may import it statically, or it would ship to production).
const FLOW_COUNT_KEY = "flowRibbonsCount";
// The busiest 20, plus every flow that touches the goal page (kept whatever the cap). "All" is one click in Display settings: with the flows seeded from history EVERY connection the site has
// is real, and all of them at once (60+ ribbons on the demo's wandering visitors) is a wall - tried as the default 2026-10-19 and put back.
const FLOW_COUNT_DEFAULT = 20;
const readFlowCount = (): number => {
  try {
    const raw = storage.get(FLOW_COUNT_KEY);
    const v = Number(raw);

    return raw !== undefined && raw !== null && `${raw}` !== "" && [10, 20, 0].includes(v) ? v : FLOW_COUNT_DEFAULT;
  } catch (e) {
    return FLOW_COUNT_DEFAULT;
  }
};
export let flowRibbonCount: number = readFlowCount();
// Keyed either by a canonical (order-independent) "loIndex-hiIndex" string
// for a page-to-page path (A->B and B->A both contribute to the same line,
// rather than drawing two overlapping ones), or "arrival:index" for a
// session's very first landing page - see ARRIVAL_ANCHOR's comment for why
// that one doesn't use a real sidebar-row position as its other endpoint.
const pathWeights = new Map<string, number>();
// Keyed by real, unsorted "fromIndex->toIndex" - unlike pathWeights above,
// A->B and B->A are tracked separately here so the tooltip can show a
// per-direction breakdown even though the two directions still render as
// one merged line.
const directionalPathWeights = new Map<string, number>();
// Snapshot of both endpoints' page labels at the moment a path/arrival was
// recorded, keyed identically to pathWeights/directionalPathWeights
// (shares one map since the two key formats never collide). Lets a stale
// weight self-invalidate whenever a box's page assignment changes, no
// matter which of the several code paths that can reassign a box caused it
// (enablePageView's grid toggle, pagesModel's setPages() on config refetch,
// or any future one) - rather than relying on catching every individual
// reassignment site with its own explicit resetFlowLines() call, which
// proved fragile in practice (see git history/PR discussion if revisiting
// this). A weight only survives past the next prune if its recorded
// endpoint labels still match what's currently on screen.
const pathWeightLabels = new Map<string, string>();
// Keyed by box index - how many sessions' LAST recorded page was this box
// before they departed. Mirrors pathWeights' "arrival:index" entries (which
// track the other end of a session's journey) but was never actually
// recorded until now - the happy-path overlay's stopping point previously
// had no real departure signal to check against, only "the next hop's
// traffic dropped off," which isn't the same claim as "this is genuinely
// where people left."
const departureWeights = new Map<number, number>();
// why a new visitor had no spawn point to fly in from (staging stamp; see densityCloudService's arrivalStats)
const spawnMiss = { noBubble: 0, tooOld: 0, noOrigin: 0, silent: 0 };
// Weights whose pages are not on the grid right now, kept by page NAME until they are (see flowLineRelocation.ts).
const parkedFlows = newParkedFlows();
// True while a zoomed view (a page's card, or the single view) covers the grid: the lines belong to the grid, so they are not drawn then. The numbers behind them keep
// counting and are untouched, so the same lines are there when the grid comes back.
let flowLinesZoomHidden = false;
let flowLinesRedrawPending = false;

// ── what the flow lines have counted survives a refresh, and a trip into playback ──
// It used to live only in these maps, so a refresh (or entering playback, which counts its own day) wiped hours of analysis with no way back.
const FLOW_STATE_KEY = "flowLinesState";
const FLOW_SHOWN_KEY = "flowLinesShown";
const flowStores = () => ({ pathWeights, directionalPathWeights, pathWeightLabels, parked: parkedFlows });
// true while the maps hold the LIVE counts; false while playback has them (the live counts are in liveFlowStash meanwhile)
let flowMapsHoldLive = true;
let liveFlowStash: FlowSnapshot | null = null;
let flowStateDirty = false;
let flowPersistTimer = 0;
const FLOW_PERSIST_DELAY_MS = 8000;

const persistFlowState = () => {
  flowPersistTimer = 0;
  // never save a past day's counts as if they were today's: they are saved only while the maps hold live ones
  if (!flowStateDirty || !flowMapsHoldLive || playbackModeService.mode === "playback") {
    return;
  }
  try {
    storage.set(
      FLOW_STATE_KEY,
      JSON.stringify({ siteId: storage.get("siteId"), savedAt: Date.now(), snapshot: takeFlowSnapshot(flowStores()) })
    );
    flowStateDirty = false;
  } catch (e) {
    // a failed save only means the counts do not survive a refresh
  }
};
const markFlowsDirty = () => {
  flowStateDirty = true;
  if (!flowPersistTimer && typeof window !== "undefined") {
    flowPersistTimer = window.setTimeout(persistFlowState, FLOW_PERSIST_DELAY_MS);
  }
};
// A refresh fires pagehide: save right then, so the last seconds of counting are not lost either.
if (typeof window !== "undefined") {
  window.addEventListener("pagehide", persistFlowState);
}
// Bring back what was counted before the refresh (this site, within a day). Their slot positions are re-derived by pruneStaleFlowLines once the pages are on the grid.
try {
  const restoredFlows = readSavedFlowState(storage.get(FLOW_STATE_KEY), storage.get("siteId"), Date.now());
  if (restoredFlows) {
    applyFlowSnapshot(flowStores(), restoredFlows);
  }
} catch (e) {
  // nothing saved, or unreadable: start empty
}

/**
 * The base for a historical bubble's percentage. Normally everything in the dock; with "Hide unknown" on, only the groups that are known. DERIVED from the bubbles that
 * are actually drawn each time it is asked (never kept as a running total), so live departures and re-loads cannot make it drift.
 */
export const getHistoricalPercentBase = (): number => {
  if (!hideUnknownBuckets) {
    return historical.totalSession;
  }

  return knownTotal(bubblesData, true, selectedFilter?.menuType);
};

/** Read-only view of the flow-line state, for tests and the dev demo's console. */
export const getFlowDebug = () => ({
  paths: pathWeights.size,
  weights: Array.from(pathWeights.entries()),
  parkedPairs: parkedFlows.pairs.size,
  parkedArrivals: parkedFlows.arrivals.size,
  shown: flowLinesEnabled,
  zoomHidden: flowLinesZoomHidden,
  holdsLive: flowMapsHoldLive,
  dirty: flowStateDirty,
});

/** Entering playback: keep the live counts aside (playback counts its own day into the same maps). Safe to call on every playback date change. */
export const stashLiveFlows = () => {
  if (!flowMapsHoldLive) {
    return;
  }
  liveFlowStash = takeFlowSnapshot(flowStores());
  flowMapsHoldLive = false;
};
/** Back in live: the live counts return exactly as they were left (playback's own counts are discarded). */
export const restoreLiveFlows = () => {
  if (flowMapsHoldLive) {
    return;
  }
  if (liveFlowStash) {
    applyFlowSnapshot(flowStores(), liveFlowStash);
  }
  liveFlowStash = null;
  flowMapsHoldLive = true;
  markFlowsDirty();
  scheduleFlowLinesRedraw();
};
// After a refresh the lines are asked for before the grid has its layout (slot positions, page names), so the first draw finds nothing to draw on, and on a quiet
// site nothing else triggers another. Try again each second until the restored lines are on screen (or there is nothing to draw, or it is clearly not coming).
let flowRestoreWatch = 0;
const watchRestoredFlowsUntilDrawn = () => {
  if (flowRestoreWatch || typeof window === "undefined") {
    return;
  }
  let tries = 0;
  flowRestoreWatch = window.setInterval(() => {
    tries++;
    const drawn = !!svg && (!svg.select("#flow-lines-layer").selectAll("path.flow-line").empty() || !svg.select("#flow-ribbons-layer").selectAll("path.flow-ribbon").empty());
    if (drawn || !flowLinesEnabled || pathWeights.size === 0 || tries > 30) {
      window.clearInterval(flowRestoreWatch);
      flowRestoreWatch = 0;

      return;
    }
    redrawFlowLines();
  }, 1000);
};

/** Brings back the "shown" choice from before a refresh, but only for a plan that has flow lines (the caller says). */
export const restoreFlowLinesShown = (allowed: boolean) => {
  try {
    if (allowed && storage.get(FLOW_SHOWN_KEY) === "true") {
      if (!flowLinesEnabled) {
        setFlowLinesEnabled(true);
      }
      watchRestoredFlowsUntilDrawn();
    }
  } catch (e) {
    // leave them hidden
  }
};
// Redraws are deliberately throttled rather than firing per-navigation -
// this is meant to be a cheap, low-priority visual layer, not something
// competing with the bubble animation loop for every frame's budget.
const FLOW_LINES_REDRAW_THROTTLE_MS = 2000;

// Everything the flow lines have drawn, in either style: the dashed lines and their hover targets, and the ribbons with their dots (a layer of their own, under the boxes).
const clearFlowDrawing = () => {
  svg?.select("#flow-lines-layer").selectAll("*").remove();
  funnelHost?.flow.clearFlowRibbons(svg);
  // nothing is left to be hovered: forget it, and take the highlight off the boxes and the card off the screen
  if (hoveredFlowKey) {
    hoveredFlowKey = null;
    highlightFlowBoxes(null);
    hideFlowLineTooltip();
  }
};

/** How many ribbons to draw: the busiest 10, 20 or all (0). Redraws at once. */
export const setFlowRibbonCount = (count: number) => {
  const next = [10, 20, 0].includes(count) ? count : FLOW_COUNT_DEFAULT;
  if (next === flowRibbonCount) return;
  flowRibbonCount = next;
  try {
    storage.set(FLOW_COUNT_KEY, `${next}`);
  } catch (e) {
    // the choice just will not survive a refresh
  }
  redrawFlowLines();
};

/** Dashed lines or ribbons (a demo or staging build only; anywhere else there is just the lines). Redraws at once. */
export const setFlowLinesStyle = (style: "lines" | "ribbons") => {
  const next = funnelHost ? style : "lines";
  if (next === flowLinesStyle) return;
  flowLinesStyle = next;
  try {
    storage.set(FLOW_STYLE_KEY, next);
  } catch (e) {
    // the choice just will not survive a refresh
  }
  clearFlowDrawing();
  redrawFlowLines();
};

export const setFlowLinesZoomHidden = (hidden: boolean) => {
  flowLinesZoomHidden = hidden;
  if (hidden) {
    clearFlowDrawing();
  } else {
    redrawFlowLines();
  }
};

// "Flow lines" in Display settings is SHOW / HIDE. Hiding only stops drawing: the paths keep being counted underneath (see the recording sites), so showing them again
// gives the whole picture up to now. Only resetFlowLines() (the Reset button, or leaving playback/live) forgets what was counted.
export const setFlowLinesEnabled = (enabled: boolean) => {
  flowLinesEnabled = enabled;
  try {
    storage.set(FLOW_SHOWN_KEY, enabled ? "true" : "false");
  } catch (e) {
    // the choice just will not survive a refresh
  }
  if (enabled) {
    redrawFlowLines();
    startFlowSeeding();
  } else {
    clearFlowDrawing();
    stopFlowSeeding();
  }
};

/*
 * THE FLOW COUNTS FROM HISTORY (demo and staging builds; src/funnel/flowSeed.ts). The lines used to be counted only from the moves this dashboard saw while it was open, so a connection
 * existed once somebody had taken it in front of it, and the rare ones (the goal's) almost never (Chris, 2026-10-18: "more ribbon connections missing between these slot boxes?!").
 * While the lines are shown, the counts are REPLACED from the site's history for the range being viewed - when they are switched on, when the range or mode changes, and every minute - and the
 * live counting carries on on top. Derived, never accumulated: what is drawn cannot drift from the data. An answer to a question that has since been overtaken (another was asked, the lines
 * were hidden, playback began) is dropped.
 */
const FLOW_SEED_EVERY_MS = 60000;
let flowSeedTimer = 0;
let flowSeedEpoch = 0;
let flowSeedDebounce = 0;
let flowSeedRetries = 0;

// (a function, so the mode is read afresh after the await below: TypeScript would otherwise carry the first answer through and call the second check impossible)
const inPlaybackNow = (): boolean => playbackModeService.mode === "playback";

// What the seeded flow counts cover when they are NOT the whole range (the server's cap keeps the newest visits): said on every flow tooltip. null when they cover it all.
let flowBasis: string | null = null;

// a group's box is drawn as a stack of pages (pageGroups/groupStack.ts): the demo and staging only, like every page-groups screen
// eslint-disable-next-line @typescript-eslint/no-var-requires
const groupStack = (process.env.REACT_APP_DEMO === "1" || process.env.REACT_APP_PAGE_GROUPS === "1") ? require("../pageGroups/groupStack") : null;

export const seedFlowsFromHistory = async (): Promise<void> => {
  if (!funnelHost || !flowLinesEnabled || inPlaybackNow() || pageViewDisabled) {
    return;
  }
  const pages: string[] = clusterCenterCoords ? funnelHost.flowSeed.seedPages(getBoxLabel, UNKNOWN_URL_CLUSER_INDEX) : [];
  if (!pages.length) {
    // after a refresh the lines are asked for before the grid has its page names: try again shortly
    if (flowSeedRetries++ < 30 && typeof window !== "undefined") {
      window.clearTimeout(flowSeedDebounce);
      flowSeedDebounce = window.setTimeout(seedFlowsFromHistory, 1000);
    }

    return;
  }
  flowSeedRetries = 0;
  const asked = ++flowSeedEpoch;
  const seed = await funnelHost.flowSeed.fetchFlowSeed(pages, {
    startDate: new Date(getEffectiveDateRangeValue()).toISOString(),
    endDate: selectedEndDateRange ? selectedEndDateRange : new Date().toISOString(),
  });
  if (!seed || asked !== flowSeedEpoch || !flowLinesEnabled || inPlaybackNow()) {
    return;
  }
  flowBasis = seed.truncated ? `the newest ${Number(seed.sessions || 0).toLocaleString("en-US")} sessions of this range (it holds more than the limit)` : null;
  funnelHost.flowSeed.seedFlows(flowStores(), seed, getBoxLabel, UNKNOWN_URL_CLUSER_INDEX + 1);
  markFlowsDirty();
  scheduleFlowLinesRedraw();
};

/** Soon, and once however many things ask (the range and the filter change together). */
const seedFlowsSoon = () => {
  if (!funnelHost || !flowLinesEnabled || typeof window === "undefined") {
    return;
  }
  window.clearTimeout(flowSeedDebounce);
  flowSeedDebounce = window.setTimeout(seedFlowsFromHistory, 500);
};

const startFlowSeeding = () => {
  if (!funnelHost || typeof window === "undefined") {
    return;
  }
  flowSeedRetries = 0;
  seedFlowsFromHistory();
  if (!flowSeedTimer) {
    flowSeedTimer = window.setInterval(() => {
      if (!document.hidden) seedFlowsFromHistory();
    }, FLOW_SEED_EVERY_MS);
  }
};

const stopFlowSeeding = () => {
  flowSeedEpoch++; // an answer still on its way is out of date
  if (typeof window === "undefined") {
    return;
  }
  window.clearInterval(flowSeedTimer);
  window.clearTimeout(flowSeedDebounce);
  flowSeedTimer = 0;
};

eventEmitter.addListener("modeChanged", ({ mode }: any) => {
  if (mode !== "playback") seedFlowsSoon();
});
// the range (and the filter, which changes with it) decides which visits the counts are of
eventEmitter.addListener("filterChanged", (info?: { quiet?: boolean }) => {
  if (!info?.quiet) seedFlowsSoon();
});

if (SLOT_TOTALS) {
  // the range, the page list and the mode decide what the bars show
  ["filterChanged", "modeChanged", "pages-changed", "pageGroupsUpdated", "addAPage", "playbackScheduleLoaded", "playbackSeeked"].forEach((name) => eventEmitter.addListener(name, () => scheduleSlotTotals()));
}

const scheduleFlowLinesRedraw = () => {
  if (!flowLinesEnabled || flowLinesZoomHidden || pageViewDisabled || flowLinesRedrawPending) {
    return;
  }
  flowLinesRedrawPending = true;
  setTimeout(() => {
    flowLinesRedrawPending = false;
    redrawFlowLines();
  }, FLOW_LINES_REDRAW_THROTTLE_MS);
};

const recordPathTraversal = (fromIndex: number | null, toIndex: number | null, n = 1) => {
  if (
    flowRecordingSuppressed ||
    fromIndex == null ||
    toIndex == null ||
    fromIndex === toIndex ||
    fromIndex > UNKNOWN_URL_CLUSER_INDEX ||
    toIndex > UNKNOWN_URL_CLUSER_INDEX
  ) {
    // UNKNOWN_URL_CLUSER_INDEX (10) IS a real, fixed box - it's "All other
    // pages," a single consistent location on screen even though it
    // represents many different underlying pages. Only indices PAST it
    // (ALL_CLUSTER_INDEX and beyond, only used in pageViewDisabled mode)
    // are excluded here.
    return;
  }
  const [loIndex, hiIndex] = [fromIndex, toIndex].sort((a, b) => a - b);
  const key = `${loIndex}-${hiIndex}`;
  // A slot may have been given another page since this key was last counted; adding to it now would credit the NEW page with the OLD page's traffic.
  const recorded = pathWeightLabels.get(key);
  if (recorded !== undefined && recorded !== currentFingerprint(key, getBoxLabel)) {
    pruneStaleFlowLines();
  }
  pathWeights.set(key, (pathWeights.get(key) || 0) + n);
  // Fingerprint MUST use the same lo/hi order as the key itself, not the
  // original (fromIndex, toIndex) travel order - currentFingerprint()
  // below reconstructs labels by re-parsing the sorted key, so recording
  // them in unsorted order here made every traversal where fromIndex >
  // toIndex mismatch its own fingerprint on the very next prune pass and
  // get deleted immediately, regardless of whether anything was ever
  // actually reassigned.
  pathWeightLabels.set(key, `${getBoxLabel(loIndex)}|${getBoxLabel(hiIndex)}`);
  const directionalKey = `${fromIndex}->${toIndex}`;
  directionalPathWeights.set(
    directionalKey,
    (directionalPathWeights.get(directionalKey) || 0) + n
  );
  // directionalKey preserves true travel order (fromIndex->toIndex), which
  // matches currentFingerprint()'s parsing of it, so this one stays as-is.
  pathWeightLabels.set(directionalKey, `${getBoxLabel(fromIndex)}|${getBoxLabel(toIndex)}`);
  markFlowsDirty();
  scheduleFlowLinesRedraw();
};

// A session's very FIRST landing page has no real "previous box" to draw
// from - the sidebar is where it visually spawns, but sidebar rows resort
// constantly (see freezeSidebarReorder's whole history), so anchoring a
// persistent line to a specific row's live position would look broken the
// moment that row moves. Using one fixed, shared point instead (not tied to
// any category/row) keeps this stable regardless of sidebar activity, at
// the cost of not distinguishing which category's arrivals contributed to
// it - just "how many first-landings does this page get."
/**
 * While true, nothing is added to the flow-line weights. Playback sets it for an action that was already counted on an earlier
 * pass (play resumed after scrubbing backward), so the lines can STAY as they are across a scrub instead of being wiped and
 * rebuilt, and a re-run still never counts a visitor twice.
 */
let flowRecordingSuppressed = false;
export const setFlowRecordingSuppressed = (on: boolean) => { flowRecordingSuppressed = on; };

const recordArrivalTraversal = (clusterIndex: number | null, n = 1) => {
  if (flowRecordingSuppressed || clusterIndex == null || clusterIndex > UNKNOWN_URL_CLUSER_INDEX) {
    return;
  }
  const key = `arrival:${clusterIndex}`;
  const recordedLabel = pathWeightLabels.get(key);
  if (recordedLabel !== undefined && recordedLabel !== getBoxLabel(clusterIndex)) {
    pruneStaleFlowLines();
  }
  pathWeights.set(key, (pathWeights.get(key) || 0) + n);
  pathWeightLabels.set(key, getBoxLabel(clusterIndex));
  markFlowsDirty();
  scheduleFlowLinesRedraw();
};

/**
 * THE FLOW LINES AS OF A MOMENT (2026-10-21, Chris: "Show the totals at that time when you click to that spot on the timeline"). The lines are "traffic so far", so after a seek they must say what
 * had happened by the moment landed on: wipe them and count every action up to it, in order, the way playing through them would have (an arrival into its landing page's box, each page view as
 * a move from the box the visitor was in, each departure). Nothing is drawn per action here; the one redraw at the end shows the result. Before this the lines simply stayed across a scrub.
 */
export const rebuildFlowCounts = (actions: Array<{ type: string; session?: any; sessionId?: string; pathName?: string }>) => {
  resetFlowLines();   // (this also starts the replay's route again: it is rebuilt from the same actions below)
  const was = flowRecordingSuppressed;
  flowRecordingSuppressed = false;
  const boxOf = new Map<string, number>();
  try {
    addPriorFlowCounts();
    for (const action of actions) {
      if (action.type === "arrival") {
        const id = String(action.session?._id);
        const box = getClusterNumber(action.session?.current_url_name);
        boxOf.set(id, box);
        // a visit already there at a replay clip's start was counted in the day-so-far flows
        if (!action.session?.carried) recordArrivalTraversal(box);
        playbackRoute.arrive(id, action.session?.current_url_name || "");
      } else if (action.type === "pageview") {
        const id = String(action.sessionId);
        if (!boxOf.has(id)) continue;
        const box = getClusterNumber(action.pathName);
        recordPathTraversal(boxOf.get(id) as number, box);
        playbackRoute.move(id, action.pathName || "");
        boxOf.set(id, box);
      } else if (action.type === "departure") {
        const id = String(action.sessionId);
        if (!boxOf.has(id)) continue;
        recordDepartureTraversal(boxOf.get(id) as number);
        playbackRoute.leave(id);
        boxOf.delete(id);
      }
    }
  } finally {
    flowRecordingSuppressed = was;
  }
};

/**
 * A replay clip's day-so-far for the flow lines: the landings, moves and departures that happened before the clip began, added at once (a count per page or pair of pages, not a call per visit). Empty everywhere
 * else. Called after the lines are cleared (the clip opening, every seek), so the ribbons always hold the whole day up to the moment shown, not only what the clip itself has played.
 */
export const addPriorFlowCounts = () => {
  // the red line's route starts from the day before the clip too (a clip's own few minutes rarely hold enough ended visits to name a route)
  if (playbackModeService.priorRoutes) playbackRoute.seed(routesFromWire(playbackModeService.priorRoutes));
  const prior = playbackModeService.priorFlows;
  if (!prior) return;
  const was = flowRecordingSuppressed;
  flowRecordingSuppressed = false;
  try {
    const box = (page: string): number => (page ? getClusterNumber(page) : UNKNOWN_URL_CLUSER_INDEX);
    prior.arrivals.forEach(([page, n]) => recordArrivalTraversal(box(page), n));
    prior.moves.forEach(([from, to, n]) => recordPathTraversal(box(from), box(to), n));
    prior.departures.forEach(([page, n]) => recordDepartureTraversal(box(page), n));
  } finally {
    flowRecordingSuppressed = was;
  }
};


/** the hover of a page label under a box: a rounded tinted rectangle behind the text (the same tint and corner radius as the zoom view's Back link), never an underline. Shared by every label, "All other pages" included. */
function showSlotLabelHover(label: SVGTextElement) {
  if (!label.getAttribute("real-text")) return;
  const bb = label.getBBox();
  const host = label.parentNode as SVGGElement;
  let bg = host.querySelector("rect.slot-label-bg") as SVGRectElement | null;
  if (!bg) {
    bg = document.createElementNS("http://www.w3.org/2000/svg", "rect");
    bg.setAttribute("class", "slot-label-bg");
    bg.setAttribute("rx", "6");
    bg.style.fill = "rgba(55, 71, 79, 0.08)";
    bg.style.pointerEvents = "none";
    host.insertBefore(bg, label);
  }
  bg.setAttribute("x", String(bb.x - 4)); bg.setAttribute("y", String(bb.y - 2));
  bg.setAttribute("width", String(bb.width + 8)); bg.setAttribute("height", String(bb.height + 4));
  bg.style.display = "";
}

function hideSlotLabelHover(label: SVGTextElement) {
  const bg = (label.parentNode as SVGGElement).querySelector("rect.slot-label-bg") as SVGRectElement | null;
  if (bg) bg.style.display = "none";
}

// For the real-browser check (scripts/flow-seek-check.mjs): what the flow lines hold right now. The demo build only.
if (typeof window !== "undefined" && process.env.REACT_APP_DEMO === "1") {
  (window as any).__dockFloor = () => ({ cover: dockCover, measured: measureDockCover(), floor: dockFloorY(), canvas: gridCanvasHeight(), bandTop: historicalBandTop });
  (window as any).__flowTotals = () => {
    let moves = 0;
    let arrivals = 0;
    directionalPathWeights.forEach((n) => { moves += n; });
    pathWeights.forEach((n, key) => { if (key.startsWith("arrival:")) arrivals += n; });
    let departures = 0;
    departureWeights.forEach((n) => { departures += n; });

    return { moves, arrivals, departures };
  };
  // the same lines, one per box pair and named by the box's label (what the screen calls it), for the page-group check: the ribbon into a group is the ribbons that went into its members
  (window as any).__flowPairs = () => {
    const out: Array<{ from: string; to: string; n: number }> = [];
    directionalPathWeights.forEach((n, key) => {
      const [a, b] = key.split("->").map(Number);
      out.push({ from: getBoxLabel(a), to: getBoxLabel(b), n });
    });

    return out;
  };
}

// Mirrors recordArrivalTraversal - the exit-side equivalent. Doesn't need a
// pathWeightLabels entry or a redraw (nothing currently renders a
// departure-only line the way arrivals get one) - this purely backs the
// happy-path overlay's stopping condition below.
const recordDepartureTraversal = (clusterIndex: number | null, n = 1) => {
  if (flowRecordingSuppressed || clusterIndex == null || clusterIndex > UNKNOWN_URL_CLUSER_INDEX) {
    return;
  }
  departureWeights.set(clusterIndex, (departureWeights.get(clusterIndex) || 0) + n);
};

/**
 * WHERE NEW VISITORS APPEAR (Chris, 2026-10-18: "They need to come from a more central place further to the left of the page slot boxes. Somewhere in between the real-time items list
 * and the gray box of the real-time canvas."): the middle of the gutter between the Real Time list's right edge and the canvas's left edge, in the svg's own units (so a negative x: the
 * svg draws outside itself, as the bubbles' own spawn already does). Measured from the page each time (the window can be resized), cached for a quarter of a second because one redraw
 * asks for it dozens of times. Where there is no gutter to speak of (the list is an overlay on a narrow window, or touches the canvas) it is the old place, just inside the grid's edge.
 */
const MIN_SPAWN_GUTTER_PX = 8;
let spawnGutterCache: { at: number; x: number } = { at: -1e9, x: CLUSTER_GRID_MARGIN };
const spawnGutterX = (): number => {
  const now = performance.now();
  if (now - spawnGutterCache.at < 250) return spawnGutterCache.x;
  let x = CLUSTER_GRID_MARGIN;
  try {
    const svgNode = svg?.node?.() as SVGSVGElement | undefined;
    const list = document.querySelector(".real-time");
    const ctm = svgNode?.getScreenCTM?.();
    if (svgNode && list && ctm && ctm.a > 0) {
      const listRight = list.getBoundingClientRect().right;
      const canvasLeft = svgNode.getBoundingClientRect().left;
      if (canvasLeft - listRight >= MIN_SPAWN_GUTTER_PX) x = ((listRight + canvasLeft) / 2 - ctm.e) / ctm.a;
    }
  } catch {
    x = CLUSTER_GRID_MARGIN;
  }
  spawnGutterCache = { at: now, x };

  return x;
};

// Halfway down the grid (vertically centered on the real-time rows, see getClusterCoords) and in the gutter left of it (spawnGutterX) - a point that does not move however the
// sidebar resorts.
const getArrivalAnchor = () => {
  const topRow = clusterCenterCoords?.[0];
  /*
   * Slot 5 is the START OF THE SECOND ROW on the five-column grid, so
   * averaging it with slot 0 lands halfway down a 3-row layout. On the
   * stacked grid slot 5 is row 2 of SIX, which put the arrival anchor in the
   * top third instead of the middle - the same class of five-column
   * assumption as the six already removed. Slot 11 is the last real page slot
   * in both layouts, so the midpoint is the grid's true vertical centre.
   */
  // Slot 11 is the last real page slot in EVERY layout, so the midpoint
  // between it and slot 0 is the grid's true vertical centre whatever the
  // column count. This used to branch on isNarrowGrid and use slot 5 for the
  // wide grid - correct only while "wide" always meant 5 columns and 3 rows.
  const bottomRow = clusterCenterCoords?.[UNKNOWN_URL_CLUSER_INDEX - 1];
  if (!topRow || !bottomRow) {
    return null;
  }
  return {
    x: spawnGutterX(),
    y: (topRow.y + bottomRow.y) / 2,
  };
};

// Boxes are reassigned to whichever pages are currently "top pages" (except
// index UNKNOWN_URL_CLUSER_INDEX, permanently "All other pages" - see its own
// `real-text` assignment above), so a path's endpoint label has to be read
// live off the box's current text rather than cached at record time.
const getBoxLabel = (index: number): string => {
  if (index === UNKNOWN_URL_CLUSER_INDEX) {
    return "All other pages";
  }
  const label = clusterTextInstances[index]?.attr("real-text");
  return label || `Page ${index + 1}`;
};

/**
 * RIDES (funnel, demo only): a visitor who steps from one funnel step to the next travels along the ribbon between the two boxes instead of drifting there by physics.
 * The bubble's simulation node is PINNED (fx/fy, which every force skips) and moved along the path the funnel gives, for about a second, then released: the physics settles it in
 * its new box as for any visitor. ONE loop moves every ride and paints ONCE per frame (paintRealtimeBubblePositions writes the bubble, its icon, badges and pulses), so a crowd of
 * riders costs one paint per frame, not one each. A newer ride for the same visitor takes over; a visitor who has left is dropped.
 */
interface Ride { at: (t: number) => { x: number; y: number }; t0: number; durationMs: number }
const activeRides = new Map<string, Ride>();
let rideFrame = 0;
const runRides = (now: number) => {
  rideFrame = 0;
  let moved = false;
  activeRides.forEach((ride, visitorId) => {
    const node = realtimeNodes.find((n: any) => String(n.visitor_id) === visitorId);
    if (!node) {
      activeRides.delete(visitorId);

      return;
    }
    const t = Math.min(1, (now - ride.t0) / ride.durationMs);
    const p = ride.at(t);
    node.fx = node.x = p.x;
    node.fy = node.y = p.y;
    moved = true;
    if (t >= 1) {
      node.fx = null;
      node.fy = null;
      activeRides.delete(visitorId);
    }
  });
  if (moved) {
    paintRealtimeBubblePositions();
  }
  if (activeRides.size) {
    rideFrame = requestAnimationFrame(runRides);
  } else if (moved) {
    reheatRealtimeSimulation(false);   // the last rider has been let go: the physics settles them in their box
  }
};
/** `plan` is given where the bubble is now and says how to move it (`at(0..1)`) and for how long. */
const rideBubble = (visitorId: string, plan: (from: { x: number; y: number }) => { at: (t: number) => { x: number; y: number }; durationMs: number }) => {
  const node = realtimeNodes.find((n: any) => String(n.visitor_id) === visitorId);
  if (!node || typeof node.x !== "number" || typeof node.y !== "number") {
    return;
  }
  const { at, durationMs } = plan({ x: node.x, y: node.y });
  activeRides.set(visitorId, { at, durationMs, t0: performance.now() });
  if (!rideFrame) {
    rideFrame = requestAnimationFrame(runRides);
  }
};

// An effect (burst, confetti) that belongs to a visitor the funnel's spotlight has dimmed is drawn dimmed too: the same opacity the stylesheet gives that visitor's own elements.
const FUNNEL_DIM_EFFECT_OPACITY = 0.2;
const effectDim = (visitorId: string | undefined, page: string | undefined, leaving = false): boolean => !!funnelHost && !!visitorId && funnelHost.layer.dimsEffectsFor(visitorId, page, leaving);

// visitor id -> the circle/rect that draws it (see setElementPositionProvider)
const visitorShapeCache = new Map<string, SVGGraphicsElement>();
let rootRectCache: DOMRect | null = null;
let rootRectAt = -Infinity;
// the last reading per visitor, reused for POS_TTL_MS: "has it landed" does not need 60 readings a second, and a reading up to 40 ms old can only be FARTHER from the spot than the
// bubble really is (it is on its way there), so an arrival is noticed a frame or two late, never early
const POS_TTL_MS = 40;
const visitorPosCache = new Map<string, { x: number; y: number; at: number }>();
const elementScreenPosition = (root: SVGSVGElement | undefined, visitorId: string): { x: number; y: number } | null => {
  if (!root) return null;
  const nowMs = performance.now();
  const recent = visitorPosCache.get(visitorId);
  if (recent && nowMs - recent.at < POS_TTL_MS) return { x: recent.x, y: recent.y };
  let shape = visitorShapeCache.get(visitorId);
  if (!shape || !shape.isConnected) {
    visitorShapeCache.delete(visitorId);
    const els = root.querySelectorAll<SVGGraphicsElement>(`[visitor_id="${visitorId}"]`);
    if (!els.length) return null;
    shape = Array.from(els).find((e) => e.tagName === "circle" || e.tagName === "rect") ?? els[0];
    visitorShapeCache.set(visitorId, shape);
    if (visitorShapeCache.size > 600) {
      visitorShapeCache.forEach((el, id) => {
        if (!el.isConnected) visitorShapeCache.delete(id);
      });
    }
  }
  const r = shape.getBoundingClientRect();
  if (!rootRectCache || nowMs - rootRectAt > 8) {
    rootRectCache = root.getBoundingClientRect();
    rootRectAt = nowMs;
  }
  const at = { x: r.left + r.width / 2 - rootRectCache.left, y: r.top + r.height / 2 - rootRectCache.top, at: nowMs };
  visitorPosCache.set(visitorId, at);
  if (visitorPosCache.size > 800) {
    visitorPosCache.forEach((v, id) => {
      if (nowMs - v.at > 1000) visitorPosCache.delete(id);
    });
  }

  return { x: at.x, y: at.y };
};

/** The funnel step indicators draw from the page boxes as they are NOW: where each one is, which page is in it, and whether there is a grid to hang a funnel from at all. */
let funnelZoomHidden = false;
const getFunnelContext = () => {
  if (!svg || !clusterCenterCoords) {
    return null;
  }
  const slots: Array<{ key: number; page: string; x: number; y: number }> = [];
  for (let key = 0; key < UNKNOWN_URL_CLUSER_INDEX; key++) {
    const centre = clusterCenterCoords[key];
    const page = clusterTextInstances[key]?.attr("real-text");
    const rect = document.getElementById("rect_" + key);
    if (centre && page && rect?.getAttribute("selected") === "true") {
      slots.push({ key, page, x: centre.x, y: centre.y });
    }
  }

  return {
    svg,
    slots,
    boxW: boxWidth,
    boxH: boxHeight,
    otherKey: UNKNOWN_URL_CLUSER_INDEX,
    travel: rideBubble,
    bubbles: () => bubblesData.filter((b: any) => b.realTime).map((b: any) => ({ id: b.visitor_id, page: b.pageName })),
    // no grid in the single-cluster view or the zoomed view
    hidden: pageViewDisabled || funnelZoomHidden,
    // a replayed day is on the grid: the numbers are that whole day's, and a replayed visitor's path comes from the playback (a bubble knows only the page it is on)
    playback: playbackModeService.mode === "playback" && !!playbackModeService.playbackDate,
    pathOf: playbackModeService.pathSoFar,
    dateParams: () =>
      playbackModeService.mode === "playback" && playbackModeService.playbackDate
        ? funnelHost.playback.withClock(funnelHost.playback.dayRange(playbackModeService.playbackDate), playbackModeService.playbackClockMs())
        : {
            startDate: new Date(getEffectiveDateRangeValue()).toISOString(),
            endDate: selectedEndDateRange ? selectedEndDateRange : new Date().toISOString(),
          },
  };
};

export const resetFlowLines = () => {
  clearParkedFlows(parkedFlows);
  pathWeights.clear();
  directionalPathWeights.clear();
  pathWeightLabels.clear();
  departureWeights.clear();
  playbackRoute.reset();
  clearFlowDrawing();
  markFlowsDirty(); // an explicit reset in live saves the empty state too, so a refresh does not resurrect what was cleared
};

// Recomputes each recorded weight's CURRENT endpoint-label fingerprint and
// drops any entry whose labels have drifted from what was recorded at the
// time - i.e. the box(es) it points at/from have since been reassigned to a
// different page by any code path, not just the specific ones that call
// resetFlowLines() explicitly. Self-healing rather than relying on an
// exhaustive list of "clear flow lines here too" call sites.
const pruneStaleFlowLines = () => {
  // Used to DELETE every path whose page names no longer matched its slots. Now a path follows its pages (or is parked until they are back on the grid).
  relocateFlows(
    { pathWeights, directionalPathWeights, pathWeightLabels, parked: parkedFlows },
    getBoxLabel,
    UNKNOWN_URL_CLUSER_INDEX + 1
  );
};

// THE FLOW BEING HOVERED (a ribbon or a dashed line), remembered across redraws. The picture is redrawn every couple of seconds while traffic is counted, and a redraw used to put the hovered
// ribbon back to its resting colour (and leave the card showing numbers that had moved on): so the hover is STATE, applied again after every redraw, until the pointer leaves.
let hoveredFlowKey: string | null = null;
let hoveredFlowAt = { x: 0, y: 0 };
let happyPathHover = false;
// Where the pointer really is, as a fact about the page, not an inference from enter/leave events (those are lost when a redraw replaces the element under the pointer, and the geometry test used to
// keep a ribbon "hovered" just past its end, inside a box): the flows' cards (Chris, 2026-10-23: "sometimes the ribbons ... get stuck and stay open") close whenever the pointer is not on a flow.
let pointerOnFlow = false;
let flowHoverClear: (() => void) | null = null;
if (typeof document !== "undefined") {
  document.addEventListener(
    "mousemove",
    (e: MouseEvent) => {
      const target = e.target as Element | null;
      pointerOnFlow = !!target?.closest?.("path.flow-line-hit, path.happy-path-hit");
      if (pointerOnFlow) return;
      if (hoveredFlowKey) flowHoverClear?.();
      if (happyPathHover) {
        happyPathHover = false;
        hideFlowLineTooltip();
      }
    },
    { passive: true, capture: true }
  );
  const pointerGone = () => {
    pointerOnFlow = false;
    if (hoveredFlowKey) flowHoverClear?.();
    if (happyPathHover) {
      happyPathHover = false;
      hideFlowLineTooltip();
    }
  };
  document.addEventListener("mouseleave", pointerGone);
  window.addEventListener("blur", pointerGone);
}
/** the canvas zoom of the scene that is on the page (demo and staging builds only: null in production) */
let canvasZoomHandle: { destroy: () => void; reset: () => void; state: () => { scale: number } } | null = null;

// FOCUS ONE PAGE BOX (demo and staging builds; src/funnel/slotFocus.ts). Chris, 2026-10-19: "double click a page slot box and it 'highlights' that box to show only the ribbons/lines coming from it
// ... We would need to dim all other boxes at that point." The page is remembered by NAME (the boxes can be dragged into another order), looked up in its slot on every redraw.
let focusedPage: string | null = null;
/** how long a single click on a box waits to learn whether it is the first half of a double-click (a single click zooms into the box) */
const BOX_DOUBLE_CLICK_WAIT_MS = 320;
/** what the hint on a box adds in a build that has the focus (empty in production) */
const boxHintSuffix = (page: string): string => (!funnelHost ? "" : focusedPage && page === focusedPage ? " · double-click to show every flow" : " · double-click to show only its flows");
const boxClickGate = funnelHost ? funnelHost.slotFocus.createClickGate(BOX_DOUBLE_CLICK_WAIT_MS, { set: (fn: () => void, ms: number) => window.setTimeout(fn, ms), clear: (id: number) => window.clearTimeout(id) }) : null;
/** the slot the focused page is in now, or null when nothing is focused or that page is no longer on the grid */
const focusedSlotNow = (): number | null => {
  if (!focusedPage) return null;
  for (let i = 0; i <= UNKNOWN_URL_CLUSER_INDEX; i++) {
    if (getBoxLabel(i) === focusedPage) return i;
  }

  return null;
};
/** Dims every box but the focused one (the ribbons run behind the boxes, so they still show where they end) and outlines the focused one. (It used to say so in a banner as well; Chris, 2026-10-19: "just noise at this point".) */
const applySlotFocus = () => {
  const root = svg?.node() as SVGSVGElement | undefined;
  if (!root || !funnelHost) return;
  let slot = focusedSlotNow();
  if (focusedPage && slot === null) focusedPage = null; // the page left the grid: nothing to focus
  slot = focusedSlotNow();
  root.querySelectorAll(":scope > g[rectIndex]").forEach((box) => {
    box.classList.toggle("slot-dim", slot !== null && Number(box.getAttribute("rectIndex")) !== slot);
  });
  root.querySelectorAll("rect.slot-focus-box").forEach((r) => r.classList.remove("slot-focus-box"));
  if (slot !== null) root.querySelector(`#rect_${slot}`)?.classList.add("slot-focus-box"); // the focused page's own box is outlined
};
const setSlotFocus = (page: string | null) => {
  focusedPage = page;
  // a hover belongs to a ribbon that may no longer be drawn
  hoveredFlowKey = null;
  highlightFlowBoxes(null);
  hideFlowLineTooltip();
  applySlotFocus();
  redrawFlowLines();
};
/** a double-click on a page's box (or on the same box again) */
const toggleSlotFocus = (page: string) => {
  hideSlotHint();
  setSlotFocus(funnelHost.slotFocus.toggleFocus(focusedPage, page));
};
/**
 * The same highlight a double-click gives, asked for by name (the Insights tab's "see it on the canvas" for a landing or exit page): every other box dims and only that page's ribbons are drawn; null
 * leaves it. Returns whether it did anything: false when this build has no highlight or the page is not in one of the boxes (the page list can name a page that has no box).
 */
export const focusPageOnCanvas = (page: string | null): boolean => {
  if (!funnelHost) return false;
  if (page === null) {
    setSlotFocus(null);

    return true;
  }
  for (let i = 0; i <= UNKNOWN_URL_CLUSER_INDEX; i++) {
    if (getBoxLabel(i) === page) {
      setSlotFocus(page);

      return true;
    }
  }

  return false;
};
export const getFocusedPageOnCanvas = (): string | null => (focusedSlotNow() === null ? null : focusedPage);
if (typeof window !== "undefined" && funnelHost) {
  window.addEventListener("keydown", (event) => {
    if (event.key === "Escape" && focusedPage && !densityCloud.isZoomed()) setSlotFocus(null);
  });
}
const FLOW_BOX_CLASS = "flow-hover-box";
/** the boxes a flow joins: the two ends of a route, or the landing page of an arrival */
const flowKeyBoxes = (key: string): number[] => {
  const arrival = /^arrival:(\d+)$/.exec(key);
  if (arrival) return [Number(arrival[1])];
  const pair = /^(\d+)-(\d+)$/.exec(key);

  return pair ? [Number(pair[1]), Number(pair[2])] : [];
};
/** An outline round the page boxes the hovered flow joins, so it is clear which pages it is about (null takes every outline off). */
const highlightFlowBoxes = (key: string | null) => {
  d3.selectAll(`rect.${FLOW_BOX_CLASS}`).classed(FLOW_BOX_CLASS, false);
  if (!key) return;
  flowKeyBoxes(key).forEach((index) => d3.select(`#rect_${index}`).classed(FLOW_BOX_CLASS, true));
};

/** The grid slots whose page is the goal (today's goal live, the replayed day's in playback): the flows that touch them are always drawn (layoutFlow's `keep`). */
const goalSlotSet = (): Set<number> => {
  const out = new Set<number>();
  for (let i = 0; i < UNKNOWN_URL_CLUSER_INDEX; i++) {
    const label = clusterTextInstances[i]?.attr("real-text");
    if (label && (playbackModeService.mode === "playback" ? playbackConvertedUrls.has(label) : !!getGoalForPage(label))) out.add(i);
  }

  return out;
};

// Where new visitors appear (the start of every arrival ribbon): a thin bar in the gutter between the Real Time list and the canvas (getArrivalAnchor), halfway down.
const flowSpawnBox = () => {
  const anchor = getArrivalAnchor();

  return anchor ? { key: "spawn", x: anchor.x, y: anchor.y, w: 2, h: boxHeight * 1.2 } : null;
};

// The hover targets sit above the boxes, so a ribbon (which runs BEHIND them) used to be hoverable inside a page box, where nothing of it shows. They are clipped to everything that is NOT a box:
// a clip path with a hole for every box (clip-rule evenodd), which pointer events respect. Rebuilt on every redraw, since boxes move when the window is resized.
const FLOW_HIT_CLIP_ID = "flow-hit-clip";
const ensureFlowHitClip = (layer: any) => {
  let defs = layer.select("defs.flow-hit-defs");
  if (defs.empty()) {
    defs = layer.insert("defs", ":first-child").attr("class", "flow-hit-defs");
    defs.append("clipPath").attr("id", FLOW_HIT_CLIP_ID).append("path").attr("clip-rule", "evenodd");
  }
  // The hole is the box AND the strip under it that holds its page name and its "n on page" count: those have their own hover (the full name, "Click for page metrics") and a ribbon over them
  // would take it away.
  const LABEL_STRIP = 18;
  let d = "M-100000,-100000H100000V100000H-100000Z";
  for (let i = 0; i <= UNKNOWN_URL_CLUSER_INDEX; i++) {
    const b = flowBoxFor(i);
    if (b) d += `M${(b.x - b.w / 2).toFixed(1)},${(b.y - b.h / 2).toFixed(1)}h${b.w.toFixed(1)}v${(b.h + LABEL_STRIP).toFixed(1)}h${(-b.w).toFixed(1)}Z`;
  }
  defs.select("clipPath path").attr("d", d);

  return `url(#${FLOW_HIT_CLIP_ID})`;
};

// A box as the ribbons need it: its centre on the grid and its real size (the "All other pages" box is wider than the rest).
const flowBoxFor = (index: number) => {
  const centre = clusterCenterCoords?.[index];
  if (!centre) return null;
  const rect = document.getElementById("rect_" + index);
  const w = Number(rect?.getAttribute("width")) || boxWidth;
  const h = Number(rect?.getAttribute("height")) || boxHeight;

  return { key: String(index), x: centre.x, y: centre.y, w, h };
};

const redrawFlowLines = () => {
  // pageViewDisabled = the single-cluster view ("All pages"): the grid the lines belong to is not there, so they are not drawn (and its slots have no page names, so
  // every path is parked until the grid is back)
  if (!flowLinesEnabled || flowLinesZoomHidden || pageViewDisabled || !svg || !clusterCenterCoords) {
    return;
  }
  pruneStaleFlowLines();
  let layer = svg.select("#flow-lines-layer");
  if (layer.empty()) {
    // Inserted right after the LAST box's `<g rectIndex>` wrapper - above
    // every box (so lines read cleanly against the box backgrounds instead
    // of being hidden behind them), but still below every bubble, since
    // bubbles are appended dynamically as sessions arrive/depart (always
    // later in DOM order than the boxes, which are all created once up
    // front) - this insertion point is queried once, the first time a line
    // is ever drawn, and every bubble appended from then on lands after it
    // automatically. Falls back to right after the gray "#maincluster"
    // background rect if no boxes exist yet (shouldn't normally happen,
    // but avoids inserting before an opaque rect and being invisible).
    //
    // MUST be `:scope > g[rectIndex]` (direct children only) - each box's
    // inner <rect>/<text> is ALSO tagged with rectIndex (see
    // createClusterRect), so the plain descendant selector "[rectIndex]"
    // matched those nested elements too. The LAST such match was often one
    // of those nested nodes, not the top-level <g> wrapper - its
    // `.nextSibling` was then some OTHER nested element inside that same
    // <g>, not a child of the svg at all, which is exactly what threw
    // "the node before which the new node is to be inserted is not a
    // child of this node" every time the toggle was flipped on.
    const boxNodes = svg.selectAll(":scope > g[rectIndex]").nodes();
    const lastBoxNode = boxNodes[boxNodes.length - 1];
    const mainClusterNode = svg.select("#maincluster").node();
    const anchorNode = lastBoxNode || mainClusterNode;
    layer = anchorNode
      ? svg.insert("g", () => anchorNode.nextSibling)
      : svg.insert("g", ":first-child");
    layer.attr("id", "flow-lines-layer");
  }
  const allEntries = Array.from(pathWeights.entries());
  // ONE focused page: only the flows that touch it (all of them, however small), drawn against the busiest of THOSE
  const focusSlot = funnelHost ? focusedSlotNow() : null;
  const entries = focusSlot === null ? allEntries : funnelHost.slotFocus.focusEntries(allEntries, focusSlot);
  // RIBBON STYLE (demo and staging builds): the busiest routes drawn as ribbons behind the boxes with dots flowing along them (src/funnel/flowRibbons.ts). The dashed lines are then not
  // drawn at all (their join below gets no entries), and the hover targets, which stay above the boxes, follow the ribbons instead.
  const ribbonMode = flowLinesStyle === "ribbons" && !!funnelHost;
  // the flows that touch the goal page are drawn whatever their rank: they are the rarest, and exactly what a top-N cut
  const goalSlots = ribbonMode ? goalSlotSet() : new Set<number>();
  const ribbons: any[] = ribbonMode
    ? funnelHost.flow.layoutFlow({
        entries,
        directional: directionalPathWeights,
        boxFor: flowBoxFor,
        spawn: flowSpawnBox(),
        boxH: boxHeight,
        topN: focusSlot === null ? flowRibbonCount : 0,
        // one page focused: its flows are drawn as wide, and as dark, as they were before (against the whole site's busiest flow), not blown up against the busiest of their own
        widthMax: focusSlot === null ? undefined : allEntries.reduce((m, [, w]) => Math.max(m, w), 0),
        allBoxes: Array.from({ length: UNKNOWN_URL_CLUSER_INDEX + 1 }, (_, i) => flowBoxFor(i)).filter((b) => !!b),
        keep: (flow: { from: number | null; to: number }) => focusSlot === null && (goalSlots.has(flow.to) || (flow.from !== null && goalSlots.has(flow.from))),
      })
    : [];
  const ribbonByKey = new Map<string, any>(ribbons.map((r: any) => [r.key, r]));
  if (ribbonMode) {
    funnelHost.flow.renderFlowRibbons(svg, ribbons, hoveredFlowKey);
  } else {
    funnelHost?.flow.clearFlowRibbons(svg);
  }
  const lineEntries = ribbonMode ? [] : entries;
  const hitEntries: Array<[string, number]> = ribbonMode ? ribbons.map((r: any) => [r.key, r.weight] as [string, number]) : entries;
  // the share of ALL traffic, also while one page is focused (a card saying "% of traffic" means of the site's, not of the page's)
  // the times visitors went between boxes and the visits that started on a page share one map: together they are all the traffic the flows count, which is what a card's percentage is of
  const totalTraffic = allEntries.reduce((sum, [, w]) => sum + w, 0) || 1;
  // a landing card's whole is the visits that landed (Insights' "61% start at /pricing" is of the same): landings plus moves would count one visit once per page it saw
  const totalLandings = allEntries.reduce((sum, [k, w]) => sum + (k.startsWith("arrival:") ? w : 0), 0) || 1;
  const maxWeight = entries.reduce((max, [, w]) => Math.max(max, w), 1);
  const widthScale = d3.scaleLinear().domain([1, maxWeight]).range([1, 10]).clamp(true);
  const lineGenerator = d3
    .line<{ x: number; y: number }>()
    .x((d) => d.x)
    .y((d) => d.y)
    .curve(d3.curveBasis);

  // Shared by both the visible line and its invisible hit-path below, so
  // hovering the wide hit-path always lines up with what's actually drawn.
  const pathFor = (key: string): string | null => {
    let from: { x: number; y: number } | null;
    let to: { x: number; y: number } | null;
    if (key.startsWith("arrival:")) {
      from = getArrivalAnchor();
      to = clusterCenterCoords[Number(key.slice("arrival:".length))];
    } else {
      // The merged key (loIndex-hiIndex) only exists to combine both
      // directions' traffic into one line's WIDTH - it says nothing about
      // which way more of that traffic actually went. Look up each
      // direction's own count (directionalPathWeights, tracked separately
      // in recordPathTraversal but previously unused for rendering) and
      // draw the line's points in whichever order matches the majority
      // direction, so the marching-ants animation (which always crawls
      // along the path's own from->to point order) reads as "this is where
      // most of the traffic is going," not an arbitrary index sort. Ties
      // (or a direction with zero recorded traversals) fall back to the
      // stable lo->hi order.
      const [loIndex, hiIndex] = key.split("-").map(Number);
      const loToHi = directionalPathWeights.get(`${loIndex}->${hiIndex}`) || 0;
      const hiToLo = directionalPathWeights.get(`${hiIndex}->${loIndex}`) || 0;
      const [fromIndex, toIndex] =
        hiToLo > loToHi ? [hiIndex, loIndex] : [loIndex, hiIndex];
      from = clusterCenterCoords[fromIndex];
      to = clusterCenterCoords[toIndex];
    }
    if (!from || !to) {
      return null;
    }
    // A slight midpoint bow (rather than a straight line) so two lines
    // between the same pair of boxes going through a busy grid read as a
    // distinct path, not just an overlapping straight segment lost among
    // the grid's own horizontal/vertical lines.
    const midX = (from.x + to.x) / 2;
    const midY = (from.y + to.y) / 2 - 20;
    return lineGenerator([
      { x: from.x, y: from.y },
      { x: midX, y: midY },
      { x: to.x, y: to.y },
    ]);
  };

  const FLOW_LINE_COLOR = "rgba(80, 120, 200, 0.35)";
  const FLOW_LINE_HIGHLIGHT_COLOR = "rgba(40, 80, 200, 0.9)";

  layer
    .selectAll("path.flow-line")
    .data(lineEntries, (d: any) => d[0])
    .join("path")
    .attr("class", "flow-line")
    .attr("fill", "none")
    .attr("stroke", (d) => (d[0] === hoveredFlowKey ? FLOW_LINE_HIGHLIGHT_COLOR : FLOW_LINE_COLOR))
    .attr("stroke-width", (d) => widthScale(d[1]) + (d[0] === hoveredFlowKey ? 2 : 0))
    .attr("pointer-events", "none")
    // Cheap spike: "marching ants" - a dashed stroke whose offset animates
    // via a plain CSS keyframe (see .flow-line in Styles.scss), no JS
    // per-frame work needed. The dash pattern crawls in the same direction
    // the path's own points were drawn (from -> mid -> to, see pathFor
    // above), so it reads as traffic actually flowing from -> to rather than
    // an arbitrary direction.
    .attr("stroke-dasharray", "6 4")
    .attr("d", (d) => pathFor(d[0]));

  // The card for one flow, with the numbers as they are NOW (also used to refresh it after a redraw while it is hovered).
  // what the numbers cover, the way the Historical filter names it (a custom range reads "Since Oct 1"; a played day, what the dock says)
  const periodLabel = (): string => flowPeriodLabel({ inPlayback: inPlaybackNow(), priorLabel: playbackModeService.priorLabel, playbackDate: playbackModeService.playbackDate, range: selectedDateRange as any });
  const tooltipFor = (key: string, weight: number): string => {
    if (key.startsWith("arrival:")) {
      // Arrivals are inherently one-way (New visitors -> a landing page) - no reverse direction exists to break out.
      return arrivalTooltipHtml({ to: getBoxLabel(Number(key.slice("arrival:".length))), sessions: weight, allTraffic: totalLandings, period: periodLabel(), ...(flowBasis ? { basedOn: flowBasis } : {}) });
    }
    // The visual line merges both directions (see pathWeights' own comment), so the tooltip breaks them back out via directionalPathWeights rather than implying all `weight`
    // sessions moved the same way. The busier direction reads first, the way the line is drawn (a tie reads low to high).
    const [loIndex, hiIndex] = key.split("-").map(Number);
    const loToHi = directionalPathWeights.get(`${loIndex}->${hiIndex}`) || 0;
    const hiToLo = directionalPathWeights.get(`${hiIndex}->${loIndex}`) || 0;
    const [fromIndex, toIndex] = hiToLo > loToHi ? [hiIndex, loIndex] : [loIndex, hiIndex];

    return routeTooltipHtml({
      from: getBoxLabel(fromIndex),
      to: getBoxLabel(toIndex),
      forward: directionalPathWeights.get(`${fromIndex}->${toIndex}`) || 0,
      backward: directionalPathWeights.get(`${toIndex}->${fromIndex}`) || 0,
      total: weight,
      allTraffic: totalTraffic,
      period: periodLabel(),
      ...(flowBasis ? { basedOn: flowBasis } : {}),
    });
  };
  // the visible line or ribbon of a key, drawn strong (on) or back to rest (off)
  const strengthen = (key: string, on: boolean) => {
    if (ribbonMode) {
      funnelHost.flow.highlightFlowRibbon(svg, key, on);
    } else {
      layer
        .selectAll("path.flow-line")
        .filter((lineD: any) => lineD[0] === key)
        .attr("stroke", on ? FLOW_LINE_HIGHLIGHT_COLOR : FLOW_LINE_COLOR)
        .attr("stroke-width", (lineD: any) => widthScale(lineD[1]) + (on ? 2 : 0));
    }
  };

  // A separate, wider, fully transparent path laid on top purely for hovering - the visible line above is often only 1-2px wide (a single traversal), far too thin to reliably hover, so
  // hit-testing is decoupled from the line's actual rendered weight. CLIPPED to everything that is not a page box (ensureFlowHitClip): inside a box the flow is hidden (ribbons) or
  // belongs to the box (its own hover and click), so hovering there is not wanted.
  const hitClip = ensureFlowHitClip(layer);
  // Ribbon style: WHICH flow the pointer is on is decided from the geometry (src/funnel/flowHit.ts), not by whichever invisible target is on top: where ribbons run side by side the targets
  // overlap, and a small ribbon's target used to take the pointer from the bigger ribbon it lay over (Chris, 2026-10-19: "I am never able to hover on the ribbons that connect these 2 pages
  // because there are so many others there covering it up"). The elements below only tell us the pointer is over a flow at all.
  const hitRibbons = ribbonMode ? ribbons.map((r: any) => ({ key: r.key, weight: r.weight, width: r.width, centre: r.shape.centre })) : [];
  const weightByKey = new Map<string, number>(hitEntries);
  const flowKeyAt = (event: MouseEvent, own: string | null): string | null => {
    if (!ribbonMode) return own;
    const matrix = (layer.node() as SVGGraphicsElement | null)?.getScreenCTM?.();
    if (!matrix) return own;
    const point = new DOMPoint(event.clientX, event.clientY).matrixTransform(matrix.inverse());

    return funnelHost.flowHit.pickRibbonAt(hitRibbons, point.x, point.y);
  };
  // for the real-browser checks (scripts/canvas-zoom-focus-check.mjs): which flow the page itself would pick for a pointer at this screen position
  if (typeof window !== "undefined" && ribbonMode) (window as any).__pickFlow = (clientX: number, clientY: number) => flowKeyAt({ clientX, clientY } as MouseEvent, null);
  // The flow the pointer is on now (null: none). Idempotent: called on every enter, move and leave.
  // the hovered ribbon as small rectangles along its centre (window coordinates): what the card keeps off, after the page boxes (util/flowCardSpot.ts)
  const ribbonRects = (key: string): Array<{ left: number; top: number; right: number; bottom: number }> => {
    const el = (layer.selectAll("path.flow-line-hit").filter((d: any) => d[0] === key).node() as SVGPathElement | null);
    const matrix = el?.getScreenCTM?.();
    if (!el || !matrix || typeof el.getTotalLength !== "function") return [];
    const length = el.getTotalLength();
    const half = Math.max(6, (ribbonMode ? (ribbonByKey.get(key)?.width ?? 0) : widthScale(weightByKey.get(key) ?? 1)) / 2) * Math.abs(matrix.a || 1);
    const rects: Array<{ left: number; top: number; right: number; bottom: number }> = [];
    for (let d = 0; d <= length; d += 10) {
      const p = el.getPointAtLength(d);
      const x = p.x * matrix.a + p.y * matrix.c + matrix.e;
      const y = p.x * matrix.b + p.y * matrix.d + matrix.f;
      rects.push({ left: x - 6, top: y - half, right: x + 6, bottom: y + half });
    }

    return rects;
  };
  // the boxes a flow joins: a route's two (the key is "lo-hi"), a landing line's one ("arrival:N")
  const linkedSlotsOf = (key: string): number[] => (key.startsWith("arrival:") ? [Number(key.slice(8))] : key.split("-").map(Number)).filter((n) => Number.isFinite(n));
  let hoveredRibbon: Array<{ left: number; top: number; right: number; bottom: number }> = [];
  const hoverFlow = (key: string | null, mouse: MouseEvent) => {
    const event = mouse as MouseEvent & { layerX?: number; layerY?: number };
    if (key !== hoveredFlowKey) {
      if (hoveredFlowKey) {
        strengthen(hoveredFlowKey, false);
        highlightFlowBoxes(null);
        hideFlowLineTooltip();
      }
      hoveredFlowKey = key;
      if (key) {
        hoveredFlowAt = { x: event.layerX ?? event.offsetX, y: event.layerY ?? event.offsetY };
        // the hit-path's own data is keyed identically to its visible counterpart, so a filter on that same key finds the one visible line or ribbon it sits on
        strengthen(key, true);
        highlightFlowBoxes(key);
        hoveredRibbon = ribbonRects(key);
        showFlowLineTooltip(tooltipFor(key, weightByKey.get(key) ?? 0), FLOW_TOOLTIP_BACKGROUND, hoveredFlowAt.x, hoveredFlowAt.y, { pointer: { x: event.clientX, y: event.clientY }, ribbon: hoveredRibbon, linkedSlots: linkedSlotsOf(key) });
      }

      return;
    }
    if (key) {
      // the same ribbon under the pointer: the card is weighed again as the pointer slides along it, and moves only when its place has become poor (a pointer held still never moves it)
      hoveredFlowAt = { x: event.layerX ?? event.offsetX, y: event.layerY ?? event.offsetY };
      followFlowLineTooltip({ pointer: { x: event.clientX, y: event.clientY }, ribbon: hoveredRibbon, linkedSlots: linkedSlotsOf(key) });
    }
  };
  flowHoverClear = () => hoverFlow(null, {} as MouseEvent);
  layer
    .selectAll("path.flow-line-hit")
    .data(hitEntries, (d: any) => d[0])
    .join("path")
    .attr("class", "flow-line-hit")
    .attr("fill", "none")
    .attr("stroke", "transparent")
    .attr("clip-path", hitClip)
    // a ribbon is wide, so its hover target is as wide as the ribbon plus a margin (easier to hit than a 1-10 px line, which is half the point of the style). The margin is SMALL (3 px a side,
    // 12 px at least): a target 8 px wider than the ribbon on each side lay over its neighbours in a crowded channel and took their hover away (Chris, 2026-10-19)
    .attr("stroke-width", (d) => (ribbonMode ? Math.max(12, (ribbonByKey.get(d[0])?.width ?? 0) + 6) : 16))
    .attr("d", (d) => (ribbonMode ? (ribbonByKey.has(d[0]) ? funnelHost.flow.centrePath(ribbonByKey.get(d[0]).shape.centre) : null) : pathFor(d[0])))
    .style("cursor", "pointer")
    .on("mouseenter", (event: MouseEvent, d: any) => hoverFlow(flowKeyAt(event, d[0]), event))
    // The tooltip follows the pointer: when the pointer is still on the same flow that only repositions the card already showing (see repositionFlowLineTooltip's own comment for why rebuilding
    // it on every move was the actual cause of the reported hover slowdown). In ribbon style the flow under the pointer is worked out again on every move, since the pointer can slide from one
    // ribbon onto another that lies under the same target.
    .on("mousemove", (event: MouseEvent, d: any) => hoverFlow(flowKeyAt(event, d[0]), event))
    // Leaving a target ends the hover, full stop. The one exception is sliding straight onto another flow's target (ribbons overlap): THAT target's own "enter" decides which flow it is. (This used to
    // work the flow out from the geometry, which still said "on the ribbon" just past its end, inside a box, and left the card open.)
    .on("mouseleave", (event: MouseEvent) => {
      const to = event.relatedTarget as Element | null;
      if (to?.closest?.("path.flow-line-hit")) return;
      hoverFlow(null, event);
    });

  // Stacked like the ribbons they sit on: the busiest at the back and the smallest in front. A hover target is hit from the top, and a small ribbon under a big one's target could never be reached.
  if (ribbonMode) layer.selectAll("path.flow-line-hit").sort((a: any, b: any) => b[1] - a[1] || (a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0));

  // The spawn point, marked: a thin bar and "New visitors", so the lines that start there say where they start (only while an arrival line is drawn). Chris, 2026-10-18: the arrivals
  // "need to come from a more central place further to the left of the page slot boxes, somewhere in between the real-time items list and the gray box".
  const spawnAnchor = hitEntries.some(([key]) => key.startsWith("arrival:")) ? getArrivalAnchor() : null;
  const spawnData = spawnAnchor ? [spawnAnchor] : [];
  const spawnHalf = (boxHeight * 1.2) / 2;
  const spawnMark = layer.selectAll("rect.flow-spawn-mark").data(spawnData);
  spawnMark.exit().remove();
  spawnMark
    .enter()
    .append("rect")
    .attr("class", "flow-spawn-mark")
    .attr("pointer-events", "none")
    .attr("width", 3)
    .attr("rx", 1.5)
    .attr("fill", "rgba(80, 120, 200, 0.6)")
    .merge(spawnMark as any)
    .attr("x", (d: any) => d.x - 1.5)
    .attr("y", (d: any) => d.y - spawnHalf)
    .attr("height", spawnHalf * 2);
  const spawnLabel = layer.selectAll("text.flow-spawn-label").data(spawnData);
  spawnLabel.exit().remove();
  spawnLabel
    .enter()
    .append("text")
    .attr("class", "flow-spawn-label")
    .attr("pointer-events", "none")
    .attr("text-anchor", "middle")
    .style("font-size", "11px")
    .style("font-weight", "600")
    .style("fill", "#6b7a99")
    .text("New visitors")
    .merge(spawnLabel as any)
    .attr("x", (d: any) => d.x)
    .attr("y", (d: any) => d.y - spawnHalf - 6);

  // A redraw must not undo a hover that is still going on: the same flow is drawn strong again (renderFlowRibbons and the line join above already did that), its boxes stay outlined, and the
  // card shows the numbers as they are now. A flow that has gone (it fell out of the busiest, or was reset) takes its hover with it.
  if (hoveredFlowKey) {
    const hovered = hitEntries.find((entry) => entry[0] === hoveredFlowKey);
    // only for a pointer that is still on a flow: a redraw never opens a card for one that has gone
    if (hovered && pointerOnFlow) {
      highlightFlowBoxes(hoveredFlowKey);
      showFlowLineTooltip(tooltipFor(hovered[0], hovered[1]), FLOW_TOOLTIP_BACKGROUND, hoveredFlowAt.x, hoveredFlowAt.y, undefined, true);
    } else {
      hoveredFlowKey = null;
      highlightFlowBoxes(null);
      hideFlowLineTooltip();
    }
  }
  if (focusedPage) applySlotFocus(); // the focused page may have moved to another slot, or left the grid
  if (happyPathEnabled) {
    drawHappyPathOverlay(); // the red line follows the lines it is read from
  }
};

// Cheap spike (same "try it before building it properly" approach as the
// drag/bounce/velocity-stretch/comet-trail spikes above) - a "happy path"
// overlay: one bold, distinctly-colored line tracing the single most
// common route through the flow-line diagram, greedily walked from the
// most common entry point. Reuses the exact same underlying data as the
// marching-ants direction fix (directionalPathWeights) and the auto-arrange
// idea's algorithm sketch - draws it instead of using it to reorder boxes.
// Triggered once via the "playbackEnded" event (see playbackService.ts's
// seekTo) rather than live/continuously, matching the "must be an explicit,
// after-the-fact action" principle agreed for auto-arrange.
const HAPPY_PATH_COLOR = "#f4364c";
const MAX_HAPPY_PATH_STEPS = UNKNOWN_URL_CLUSER_INDEX + 1;
// THE RED LINE IS THE MOST COMMON ROUTE (2026-10-25: it used to be a second, different walk over the flow counts, which have no "ended here" numbers for history, so it told another story from the Insights card).
// There is ONE definition (insights/pathCore.ts): the server answers it for the range being viewed (getCommonRoute) and a replay works it out as the day plays (routeTally.ts, fed where the flow counts are).
const playbackRoute = createRouteTally();
let liveRoute: PathAnswer | null = null;
let liveRouteAsked = false;
let liveRouteEpoch = 0;
let liveRouteTimer = 0;
let liveRouteDebounce = 0;
const LIVE_ROUTE_EVERY_MS = 60000;

/** a replay (or a shared clip) counts the visits that have ended so far; everything else asks the server for the range shown */
const routeInPlayback = (): boolean => playbackModeService.mode === "playback";

const tallyRoute = (kind: "arrive" | "move" | "leave", id: string | undefined, page?: string) => {
  if (flowRecordingSuppressed || !id || !routeInPlayback()) return;
  if (kind === "arrive") playbackRoute.arrive(String(id), page || "");
  else if (kind === "move") playbackRoute.move(String(id), page || "");
  else playbackRoute.leave(String(id));
};

const refreshLiveRoute = async () => {
  if (!happyPathEnabled || routeInPlayback()) return;
  const asked = ++liveRouteEpoch;
  liveRouteAsked = true;
  try {
    const route = await fetchCommonRoute(new Date(getEffectiveDateRangeValue()).toISOString(), selectedEndDateRange ? selectedEndDateRange : new Date().toISOString());
    if (asked !== liveRouteEpoch || !happyPathEnabled || routeInPlayback()) return;   // overtaken: another was asked, the line was switched off, or a replay began
    liveRoute = route;
    drawHappyPathOverlay();
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn("[skydive] the most common route could not be loaded:", error);
  }
};

const refreshLiveRouteSoon = () => {
  if (!happyPathEnabled || typeof window === "undefined") return;
  window.clearTimeout(liveRouteDebounce);
  liveRouteDebounce = window.setTimeout(refreshLiveRoute, 500);
};

const startLiveRoute = () => {
  if (typeof window === "undefined") return;
  refreshLiveRoute();
  if (!liveRouteTimer) liveRouteTimer = window.setInterval(() => { if (!document.hidden) refreshLiveRoute(); }, LIVE_ROUTE_EVERY_MS);
};

const stopLiveRoute = () => {
  liveRouteEpoch++;   // an answer still on its way is out of date
  if (typeof window === "undefined") return;
  window.clearInterval(liveRouteTimer);
  window.clearTimeout(liveRouteDebounce);
  liveRouteTimer = 0;
};

// the range, the mode, the page list and the groups decide which route it is
["filterChanged", "modeChanged", "pages-changed", "pageGroupsUpdated"].forEach((name) => eventEmitter.addListener(name, () => refreshLiveRouteSoon()));

const happyPathNow = () => {
  if (!routeInPlayback() && !liveRouteAsked && happyPathEnabled) startLiveRoute();   // switched on by a remembered choice: nothing has asked yet

  return routeToHappyPath(routeInPlayback() ? playbackRoute.answer() : liveRoute, (page) => getClusterNumber(page));
};

// What the numbers cover ("playback up to 3:55 PM"); playback registers it, live says nothing more than "counted so far".
let happyPathCoverage: () => string = () => "";
/** what the line is counted from, in words (the tooltip's "Based on") */
const happyPathBasis = (): string => (routeInPlayback() ? `visits that ended so far${happyPathCoverage() ? ` (${happyPathCoverage()})` : ""}` : `people's visits in ${selectedDateRange?.name ?? "the range shown"}`);
export const setHappyPathCoverageProvider = (fn: () => string) => {
  happyPathCoverage = fn;
};

// ON: the red line is drawn now and redrawn whenever the flow lines are (at most every FLOW_LINES_REDRAW_THROTTLE_MS), so it forms and shifts as traffic is counted. It used to be drawn once,
// at the end of a playback day, which meant waiting for the end of the day to see it at all (Chris, 2026-10-13).
// The person's own choice comes back after a refresh. Only the Display settings switch saves it (remember = true): the end of a playback day also turns the line on,
// and that must not become a standing preference.
export let happyPathEnabled = storage.get("happyPathEnabled") === "true";
export const setHappyPathEnabled = (enabled: boolean, remember = false) => {
  happyPathEnabled = enabled;
  if (remember) {
    try {
      storage.set("happyPathEnabled", enabled ? "true" : "false");
    } catch (e) {
      // the choice just will not survive a refresh
    }
  }
  if (enabled) {
    if (!routeInPlayback()) startLiveRoute();
    drawHappyPathOverlay();
  } else {
    stopLiveRoute();
    svg?.select("#flow-lines-layer").selectAll(".happy-path-part").remove();
  }
};

export const drawHappyPathOverlay = () => {
  if (!flowLinesEnabled || !svg || !clusterCenterCoords) {
    return;
  }
  const layer = svg.select("#flow-lines-layer");
  if (layer.empty()) {
    return;
  }
  layer.selectAll(".happy-path-part").remove();
  const found = happyPathNow();
  if (!found) {
    return;
  }
  const path = found.steps;
  // Starts from the same fixed spawn origin the "arrival:" lines already
  // use (getArrivalAnchor), not from the first box's own center - so the
  // happy path visually reads as "spawn -> box -> box -> ..." matching how
  // every other line in this diagram already anchors an arrival, instead of
  // implying the journey started mid-air at whichever box happened to be
  // the most common landing page.
  const arrivalAnchor = getArrivalAnchor();
  const boxPoints = path
    .map((index) => clusterCenterCoords[index])
    .filter((point): point is { x: number; y: number } => !!point);
  const points = arrivalAnchor ? [arrivalAnchor, ...boxPoints] : boxPoints;
  if (points.length < 2) {
    return;
  }
  // Straight linear segments, not curveBasis - curveBasis is an
  // APPROXIMATING spline (deliberately cuts corners rather than touching
  // most of its points), which reads fine for the regular flow-lines' single
  // 3-point bow but visibly skips past each box's real center once chained
  // across several hops - exactly the "doesn't connect straight from box to
  // box" problem reported. curveLinear passes through every point exactly.
  const lineGenerator = d3
    .line<{ x: number; y: number }>()
    .x((d) => d.x)
    .y((d) => d.y)
    .curve(d3.curveLinear);
  const d = lineGenerator(points);
  layer
    .append("path")
    .attr("class", "happy-path happy-path-part")
    .attr("fill", "none")
    .attr("stroke", HAPPY_PATH_COLOR)
    .attr("stroke-width", 4)
    .attr("stroke-linecap", "round")
    .attr("pointer-events", "none")
    .attr("d", d);
  // Where the route ends: a dot on the last box, so "where it stops" is visible without hovering.
  const last = points[points.length - 1];
  layer
    .append("circle")
    .attr("class", "happy-path-part happy-path-end")
    .attr("cx", last.x)
    .attr("cy", last.y)
    .attr("r", 7)
    .attr("fill", HAPPY_PATH_COLOR)
    .attr("stroke", "#fff")
    .attr("stroke-width", 2)
    .attr("pointer-events", "none");
  // Hovering the line (a wide invisible copy, so it is easy to hit) explains it, in the flow lines' own tooltip.
  const explain = () => happyPathTooltipHtml(happyPathFacts(found, getBoxLabel, happyPathBasis()));
  layer
    .append("path")
    .attr("class", "happy-path-part happy-path-hit")
    .attr("fill", "none")
    .attr("stroke", "transparent")
    .attr("stroke-width", 18)
    .attr("clip-path", ensureFlowHitClip(layer))   // not hoverable inside a page box, like the flows it is read from
    .attr("d", d)
    .on("mouseenter", (event: any) => {
      happyPathHover = true;
      showFlowLineTooltip(explain(), HAPPY_PATH_TOOLTIP_BACKGROUND, event.layerX ?? event.offsetX, event.layerY ?? event.offsetY);
    })
    .on("mousemove", (event: any) => {
      repositionFlowLineTooltip(event.layerX ?? event.offsetX, event.layerY ?? event.offsetY);
    })
    .on("mouseout", () => {
      happyPathHover = false;
      hideFlowLineTooltip();
    });
};

eventEmitter.addListener("playbackEnded", () => {
  setHappyPathEnabled(true); // as before, the end of a day shows it; now it also stays on and keeps updating
});

// Real, browser-local wall-clock duration of the bubble-to-dock transition
// played on every non-silent departure (see moveBubbleToHistoricalCluster).
// Live mode keeps the original, unhurried 1600ms. Playback runs the exact
// same animation - same easing, same visual "closing" journey - just
// compressed to match its own faster pace, rather than gating on whether a
// fixed-length animation could complete in time (that approach, tried
// first, kept resolving to "not enough time" for realistic visit lengths
// and skipped the animation entirely - reported as bubbles "disappearing
// instead of closing").
const DOCK_TRANSITION_DURATION_MS_LIVE = 1600;
const DOCK_TRANSITION_DURATION_MS_PLAYBACK = 500;
const dockTransitionDurationMs = () =>
  playbackModeService.mode === "playback"
    ? DOCK_TRANSITION_DURATION_MS_PLAYBACK
    : DOCK_TRANSITION_DURATION_MS_LIVE;
// how long a visitor whose group "Hide unknown" leaves out of the dock takes to fade away where they are (they have nowhere to fly to)
const HIDDEN_GROUP_CLOSE_FADE_MS = 350;

const GOOEY_TUNING = {
  // Shortened from 450+350=800ms. Row-position locking (activeBurstRowNames)
  // keeps THIS burst's own row pinned for its whole lifetime, but at high
  // playback speed a category's NEIGHBORING sidebar rows can still resort
  // multiple times during that 800ms window - each resort is a fresh chance
  // for a differently-colored row to land immediately next to the still-
  // playing burst, which is what read as "the burst is the wrong color"
  // specifically at fast speeds. A shorter window gives fewer resorts a
  // chance to land while the burst is still visible, without changing the
  // pinning logic itself.
  spawnDuration: 250,
  spawnFadeDuration: 200,
  dockPulseDuration: 300,
  dockSettleDuration: 250,
  // A real bubble is often only 4-7px radius - far too small to read as a
  // distinct "burst" against the rest of the dashboard. The effect exists to
  // draw the eye, so it deliberately overshoots the actual bubble size and
  // shrinks back down at the very end, rather than matching it exactly.
  // Spawn and dock are tuned separately - spawn happens right next to the
  // dense sidebar list, so it needs to stay noticeably smaller than the dock
  // burst (which has open space around the historical cluster to play in).
  // spawnMinPeakRadius was previously trimmed (12->9) alongside the shorter
  // duration above, to leave less time/reach for a neighboring row's resort
  // to visually intrude on. Brought back up (9->12) after that read as too
  // small to register as a burst at all.
  spawnMinAnchorRadius: 5,
  spawnMinPeakRadius: 12,
  dockMinPeakRadius: 25,
};

// The neutral fallback used for both burst types whenever the real bubble
// will render as an icon rather than a solid accent color (see
// spawnBurstColor's own comment) - was #b0b0b0 at full opacity, reported as
// too heavy/dark against the icon it surrounds. Lightened a step and started
// at reduced opacity (the fade-to-0 transition at the end of each burst
// still animates FROM whatever opacity the circle starts at, so this makes
// the whole swell read as softer throughout, not just at the very end).
// Deliberately NOT applied to the colored-accent case (a real category
// color) - only this specific neutral-gray fallback was reported as too
// strong.
const GOOEY_NEUTRAL_BURST_COLOR = "#eaeaea";
const GOOEY_NEUTRAL_BURST_OPACITY = 0.55;

const playGooeySpawnBurst = (
  x: number,
  y: number,
  color: string,
  bubbleRadius: number,
  dim = false
) => {
  if (
    !gooeyEffectEnabled ||
    !svg ||
    x == null ||
    y == null ||
    Number.isNaN(x) ||
    Number.isNaN(y) ||
    activeGooeyBurstCount >= MAX_CONCURRENT_GOOEY_BURSTS
  ) {
    return;
  }
  activeGooeyBurstCount++;
  ensureGooeyFilter();

  const group = svg
    .append("g")
    .attr("filter", "url(#gooey-spawn-filter)")
    .attr("opacity", dim ? FUNNEL_DIM_EFFECT_OPACITY : null);   // (a group's opacity is applied AFTER its filter, so the gooey effect itself is untouched)

  const fillColor = color || "#00bfff";
  const peakRadius = Math.max(
    bubbleRadius * 2.3,
    GOOEY_TUNING.spawnMinPeakRadius
  );

  // Earlier versions had a second circle ("bud") grow while translating away
  // from the origin, meant to read as the new bubble budding off - but any
  // lateral motion here reads as "the burst is offset from the bubble" long
  // before it reads as organic growth, since the origin point is small and
  // sits right next to a dense sidebar list. So this is now a single circle
  // that only ever grows and shrinks IN PLACE, exactly on top of the real
  // bubble's spawn point - a swell, not a moving blob - which makes
  // misalignment structurally impossible since nothing here ever moves.
  const pulse = group
    .append("circle")
    .attr("cx", x)
    .attr("cy", y)
    .attr("r", GOOEY_TUNING.spawnMinAnchorRadius)
    .style("fill", fillColor)
    .style(
      "opacity",
      fillColor === GOOEY_NEUTRAL_BURST_COLOR ? GOOEY_NEUTRAL_BURST_OPACITY : 1
    );

  // SVG paints children in DOM order - a bubble spawned (and appended into
  // this same svg) later during the burst's ~450ms lifetime would render on
  // top of it purely by being a more recent sibling, with no relation to
  // where either one is on screen. At high playback speed, many new bubbles
  // append in quick succession, so re-raising this group to the front at
  // the start of BOTH animation stages keeps it topmost for its entire
  // visible lifetime, not just the instant it was created.
  group.raise();

  pulse
    .transition()
    .duration(GOOEY_TUNING.spawnDuration)
    .ease(d3.easeCubicOut)
    .on("start", () => group.raise())
    .attr("r", peakRadius)
    .transition()
    .duration(GOOEY_TUNING.spawnFadeDuration)
    .ease(d3.easeCubicIn)
    .on("start", () => group.raise())
    // Fade out WITHOUT shrinking any further. A radius this small (down at
    // the real bubble's own 4-7px size) is smaller than the filter's own
    // blur radius, so the gooey filter can't resolve a clean solid fill at
    // that size - it washes out into a translucent, desaturated haze right
    // where it overlaps the real (fully-opaque, unfiltered) bubble
    // underneath, which is what read as "the burst is a different color."
    // Fading in place at peakRadius keeps the filter output solid the whole
    // time, so the only thing that changes is opacity, not color.
    .style("opacity", 0)
    .on("end", () => {
      group.remove();
      activeGooeyBurstCount--;
    });
};

// Confetti celebration on conversion - deliberately its own primitive, not
// a reuse of playGooeySpawnBurst above (that's a single swelling circle,
// reads as "this bubble grew," not as a celebration). Fast-follow idea
// recorded early in the goal-tracking work, built once the core mechanics
// were solid.
//
// Physics ported directly from canvas-confetti (catdad/canvas-confetti,
// the library behind confetti.js.org), not approximated - mocked up and
// compared against several earlier from-scratch attempts (CSS keyframes,
// simple drag+gravity) that all read as fake. The two things that actually
// make it look real: velocity DECAYS multiplicatively each frame (not
// fought against by drag), and each piece is drawn as a skewed
// quadrilateral (four corners offset by a rotating tilt value, not a
// scaled rect) - that's what fakes the 3D tumble. See that mockup session
// for the full comparison.
let lastConfettiBurstAt = 0;
const CONFETTI_BURST_COOLDOWN_MS = 600;
const CONFETTI_COLORS = ["#FFD166", "#EF476F", "#06D6A0", "#118AB2", "#8338EC", "#FF9F1C"];
const CONFETTI_PIECE_COUNT = 32;
// canvas-confetti's own defaults, angle/spread re-oriented for "burst up
// and out of the flag" instead of the library's default straight-up cone;
// startVelocity/gravity/scalar scaled down from the library's full-window
// defaults to suit this small, fixed-position burst.
const CONFETTI_ANGLE_DEG = 90;
const CONFETTI_SPREAD_DEG = 130;
// Slowed down further still (third pass) - same direction again: less
// initial velocity/gravity, decay closer to 1 again, more ticks for the
// gentler motion to play out over.
const CONFETTI_START_VELOCITY = 2.9;
const CONFETTI_DECAY = 0.965;
const CONFETTI_GRAVITY = 0.08;
const CONFETTI_SCALAR = 0.55;
const CONFETTI_TOTAL_TICKS = 200;

// wrapperNode is the goal page-box's own <g rectIndex> group (same parent
// the flag badge itself lives in - see renderClusterGoalBadge) - inserting
// the confetti group there, immediately BEFORE the flag's own
// g[data-goal-page-badge] element, puts it earlier in DOM paint order so it
// renders behind the flag (SVG paints later siblings on top of earlier
// ones) - reads as the confetti bursting out from behind the flag, not on
// top of it.
const playConfettiBurst = (x: number, y: number, wrapperNode: Element, dim = false) => {
  if (!wrapperNode || x == null || y == null || Number.isNaN(x) || Number.isNaN(y)) {
    return;
  }
  // A hidden tab doesn't stop real conversions from happening - it just
  // means nobody's watching. Bailing out here (before ever scheduling the
  // first requestAnimationFrame) is what actually matters, not the
  // animationGeneration check inside tick(): that only invalidates a burst
  // that was ALREADY running when the tab went hidden. A conversion that
  // happens for the first time WHILE hidden still calls this function and
  // still calls its own fresh requestAnimationFrame(tick) - the browser
  // just queues that callback rather than dropping it, so every such call
  // made during the whole time the tab was backgrounded fires back-to-back
  // the instant it's foregrounded again. That's a real burst of genuinely
  // separate conversions, not a re-fired/duplicated one, so it's not
  // caught by (or a bug in) the resync/manualFlag fix - it needs this
  // separate guard.
  if (document.hidden) {
    return;
  }
  const now = Date.now();
  // Throttled, not queued - a wave of near-simultaneous conversions skips
  // extra bursts rather than piling dozens of overlapping particle sets on
  // screen at once, same "stay celebratory, not chaotic" reasoning the
  // existing click-pulse/burst-pacing fixes already apply elsewhere.
  if (now - lastConfettiBurstAt < CONFETTI_BURST_COOLDOWN_MS) {
    return;
  }
  lastConfettiBurstAt = now;

  const group = d3
    .select(wrapperNode)
    .insert("g", "g[data-goal-page-badge]")
    .attr("pointer-events", "none")
    .attr("opacity", dim ? FUNNEL_DIM_EFFECT_OPACITY : null)
    .node() as SVGGElement;

  const radAngle = (CONFETTI_ANGLE_DEG * Math.PI) / 180;
  const radSpread = (CONFETTI_SPREAD_DEG * Math.PI) / 180;

  const particles = Array.from({ length: CONFETTI_PIECE_COUNT }, (_, i) => {
    const el = document.createElementNS("http://www.w3.org/2000/svg", "path");
    el.setAttribute("fill", CONFETTI_COLORS[i % CONFETTI_COLORS.length]);
    group.appendChild(el);
    return {
      el,
      x: 0,
      y: 0,
      wobble: Math.random() * 10,
      wobbleSpeed: Math.min(0.11, Math.random() * 0.1 + 0.05),
      velocity: CONFETTI_START_VELOCITY * 0.5 + Math.random() * CONFETTI_START_VELOCITY,
      angle2D: -radAngle + (0.5 * radSpread - Math.random() * radSpread),
      tiltAngle: (Math.random() * 0.5 + 0.25) * Math.PI,
      tick: 0,
    };
  });

  const myGeneration = animationGeneration;
  const tick = () => {
    // Tab was backgrounded (or another pause) since this burst started -
    // rAF was suspended mid-burst, not "no ticks happened." Abandoning
    // outright here (rather than fast-forwarding to completion) is safe:
    // particles are already fading/scattering by any point mid-burst, so
    // there's no jarring "snap to finished" state to avoid - see
    // pauseRealtimeAnimations()'s own comment.
    if (myGeneration !== animationGeneration) {
      group.remove();
      return;
    }
    let alive = false;
    for (const p of particles) {
      p.tick++;
      if (p.tick >= CONFETTI_TOTAL_TICKS) {
        p.el.style.opacity = "0";
        continue;
      }
      alive = true;
      p.x += Math.cos(p.angle2D) * p.velocity;
      p.y += Math.sin(p.angle2D) * p.velocity;
      p.velocity *= CONFETTI_DECAY;
      p.y += CONFETTI_GRAVITY * 3;

      p.wobble += p.wobbleSpeed;
      const wobbleX = p.x + 10 * CONFETTI_SCALAR * Math.cos(p.wobble);
      const wobbleY = p.y + 10 * CONFETTI_SCALAR * Math.sin(p.wobble);

      p.tiltAngle += 0.1;
      const tiltSin = Math.sin(p.tiltAngle);
      const tiltCos = Math.cos(p.tiltAngle);
      const rnd = Math.random() * 1.4 + 2.6;
      const x1 = p.x + rnd * tiltCos;
      const y1 = p.y + rnd * tiltSin;
      const x2 = wobbleX + rnd * tiltCos;
      const y2 = wobbleY + rnd * tiltSin;

      const ox = x + p.x;
      const oy = y + p.y;
      const owx = x + wobbleX;
      const owy = y + wobbleY;
      const ox1 = x + x1;
      const oy1 = y + y1;
      const ox2 = x + x2;
      const oy2 = y + y2;
      p.el.setAttribute("d", `M${ox},${oy} L${owx},${oy1} L${ox2},${oy2} L${ox1},${owy} Z`);
      p.el.style.opacity = String(Math.max(0, 1 - p.tick / CONFETTI_TOTAL_TICKS));
    }
    if (alive) {
      requestAnimationFrame(tick);
    } else {
      group.remove();
    }
  };
  requestAnimationFrame(tick);
};

const playGooeyDockBurst = (
  x: number,
  y: number,
  color: string,
  targetRadius: number,
  dim = false
) => {
  if (
    !gooeyEffectEnabled ||
    !svg ||
    x == null ||
    y == null ||
    Number.isNaN(x) ||
    Number.isNaN(y) ||
    activeGooeyBurstCount >= MAX_CONCURRENT_GOOEY_BURSTS
  ) {
    return;
  }
  activeGooeyBurstCount++;
  ensureGooeyFilter();

  const group = svg
    .append("g")
    .attr("filter", "url(#gooey-transition-filter)")
    .attr("opacity", dim ? FUNNEL_DIM_EFFECT_OPACITY : null);

  // Unlike the spawn burst (whose group ends up behind the real bubble
  // essentially by accident of timing - the real bubble icon is appended
  // to the SVG AFTER the burst starts, so it naturally lands on top in DOM
  // paint order), the dock target's icon already exists in the DOM before
  // this burst is ever created. A freshly-appended group has nothing after
  // it yet, so it painted ON TOP of that already-existing icon - reported
  // directly as the historical burst covering the icon, unlike the spawn
  // burst which doesn't. Lowering it to the back of its parent right away
  // puts it behind the icon, matching the spawn burst's look.
  group.lower();

  const fillColor = color || "#00bfff";
  const peakRadius = Math.max(targetRadius * 2.7, GOOEY_TUNING.dockMinPeakRadius);
  const burstOpacity =
    fillColor === GOOEY_NEUTRAL_BURST_COLOR ? GOOEY_NEUTRAL_BURST_OPACITY : 1;

  // The target (the historical bubble being docked into) briefly swells,
  // then a bud already overlapping it collapses to nothing - the gooey
  // filter keeps them connected the entire time, so this reads as the
  // arriving bubble melding into and being absorbed by its target, rather
  // than two independent shapes.
  const target = group
    .append("circle")
    .attr("cx", x)
    .attr("cy", y)
    .attr("r", targetRadius)
    .style("fill", fillColor)
    .style("opacity", burstOpacity);

  const bud = group
    .append("circle")
    .attr("cx", x)
    .attr("cy", y)
    .attr("r", targetRadius * 0.8)
    .style("fill", fillColor)
    .style("opacity", burstOpacity);

  target
    .transition()
    .duration(GOOEY_TUNING.dockPulseDuration)
    .ease(d3.easeCubicOut)
    .attr("r", peakRadius)
    .transition()
    .duration(GOOEY_TUNING.dockSettleDuration)
    .ease(d3.easeCubicIn)
    // Fade out in place rather than shrinking back down to targetRadius
    // first - at the real historical bubble's own (often quite small) size,
    // the gooey filter's blur can't resolve a clean solid fill, so it washes
    // out into a lighter/desaturated haze right where it overlaps the real,
    // unfiltered bubble underneath - looking like a color mismatch even
    // though the fill color itself is identical.
    .style("opacity", 0)
    .on("end", () => {
      group.remove();
      activeGooeyBurstCount--;
    });

  bud
    .transition()
    .duration(GOOEY_TUNING.dockPulseDuration)
    .attr("r", 0.1)
    .style("opacity", 0);
};

export const createStaticComponents = async () => {
  return new Promise(async (resolve) => {
    // Remove any svg/tooltip left over from a prior invocation (e.g. React 18 StrictMode
    // double-invokes mount effects in development) so #main_svg never ends up with duplicate
    // cluster grids stacked on top of each other.
    d3.select("#main_svg").selectAll("svg").remove();
    d3.select("#main_svg").selectAll(".historical-cluster-tooltip").remove();

    d3.select("#main_svg")
      .append("div")
      .attr("class", "historical-cluster-tooltip")
      .attr("id", "tooltip")
      .style("opacity", 0);

    svg = d3
      .select("#main_svg")
      .append("svg")
      .attr("width", mainSvgWidth - 10)
      .attr("height", gridCanvasHeight())
      .attr("class", "svg-main-window")
      .attr("background-color", "red")
      .attr("border", 1)
      // <svg> clips its own content by default (overflow: hidden), and the
      // sidebar sits to the left of this svg's own coordinate origin, so a
      // bubble's spawn coordinate (and the gooey burst that plays at that
      // same point) is routinely negative-x - meaning it was being silently
      // clipped out of view this whole time, never actually rendering,
      // rather than positioned wrong. The parent #main_svg div already
      // allows visible overflow, so this just lets that intent reach the
      // svg itself instead of being clipped one level in.
      .style("overflow", "visible");

    // ZOOM (demo and staging builds): the mouse wheel / a trackpad pinch over the canvas zooms it, a drag on the empty canvas moves it (src/util/canvasZoom.ts). The scene is a NEW svg here,
    // so the previous one's zoom (its listeners and label) is let go first. It keeps out of the way of the page-box zoom view and the single view, which draw their own canvas.
    canvasZoomHandle?.destroy();
    focusedPage = null; // the boxes are rebuilt
    canvasZoomHandle = funnelHost
      ? funnelHost.canvasZoom.installCanvasZoom(svg.node() as SVGSVGElement, {
          blocked: () => densityCloud.isZoomed() || pageViewDisabled,
          onChange: () => sidebarOriginReader.reset(), // the canvas is somewhere else on screen now, and so is every row's dot in it
        })
      : null;

    // a freshly built svg starts with the activity setting already applied (it used to
    // start with pulses showing whatever the saved setting said)
    svg.classed("pulses-hidden", !interactionPulsesEnabled);

    densityCloud.destroy();
    densityCloud.init(svg.node() as unknown as SVGElement);
    // a rebuilt grid gets its page boxes draggable again (docs/DRAG_DROP_SLOTS_SCOPE.md); this drops the old svg's wiring first
    slotDrag.attachSlotDrag(slotDragHost);
    if (!cloudVisibilityTimer) {
      cloudVisibilityTimer = window.setInterval(() => {
        if (cloudedVisitors.size) syncCloudCounts();
      }, 500);
    }
    densityCloud.setDotReleaseHandler(releaseDotBatched);
    // how the zoomed view names and colours the groups of a filter: as everywhere else (the sidebar's name, the bubble's colour). Optional call: a stand-in for the service need not have it.
    densityCloud.setZoomCategoryProvider?.({
      label: (menuType, value) => readableLabel(menuType, value),
      hide: (menuType, value) => hideUnknownBuckets && isUnknownBucket(value, menuType),
      color: (menuType, value) => String(setColor(colourIndexFor(menuType, value))),
      universe: (menuType) => {
        // only the filters whose values are a known list of lowercase names get every one of them a region up front (the rest - countries, links, systems, browsers - show what is there)
        if (menuType === "search_engine" || menuType === "social_network") {
          return [...ALL_BRAND_COLOURS().filter((b) => b.key.startsWith(`${menuType}:`)).map((b) => b.key.slice(menuType.length + 1)), NO_CATEGORY_DATA];
        }
        if (menuType === "traffic_source") {
          return ["direct", "organic_search", "social", "referral", "ai"];
        }
        if (menuType === "visitor_category") {
          return ["human", "search_engine_bot", "ai_agent", "ai_crawler", "other_bot", "unidentified"];
        }

        return undefined;
      },
    });
    // a bubble flying into a cloud is aimed at its reserved honeycomb spot; the
    // force targets are only re-read on (re)initialise, so nudge the simulation
    densityCloud.setPendingTargetsHandler(() => reheatRealtimeSimulation(false));
    // the moment a bubble has arrived, hide it synchronously (not on the next frame)
    densityCloud.setBubbleHideHandler(() => applyCloudBubbleVisibility());
    // the single-cluster view is one cloud holding every visitor, whatever page they are on
    densityCloud.setPageSlotProvider((pageName) => {
      if (pageViewDisabled) return DEFAULT_VALUE.ALL_CLUSTER_INDEX;
      const pg = data.find((p: any) => p?.selected && p.name === pageName);
      return pg && pg.slotIndex != null ? pg.slotIndex : DEFAULT_VALUE.UNKNOWN_URL_CLUSER_INDEX;
    });
    // While the zoomed view is open every real bubble is hidden (the view draws them),
    // EXCEPT a leaving bubble once it is below the grey stage: its drop into the
    // historical dock is real and has to be seen, the dock burst follows it.
    // clicking a neighbour's pill in a page's card pans the stage over to that page
    // the card's big page title opens the page's detail drawer, the same as the page's label in the grid
    densityCloud.setPageTitleHandler((pageName) => {
      eventEmitter.emit("openSegmentDetail", { dimension: "page", value: pageName });
    });
    densityCloud.setPanHandler((slot, dir) => {
      const rectEl = (svg?.node() as SVGSVGElement | undefined)?.querySelectorAll("rect");
      const el = rectEl && Array.from(rectEl).find((r) => r.getAttribute("rectIndex") === String(slot));
      if (!el) return;
      const name = slot === DEFAULT_VALUE.UNKNOWN_URL_CLUSER_INDEX ? "All other pages" : data.find((p: any) => p?.selected && p.slotIndex === slot)?.name;
      const x = parseFloat(el.getAttribute("x") || "0"), y = parseFloat(el.getAttribute("y") || "0");
      const w = parseFloat(el.getAttribute("width") || "0"), h = parseFloat(el.getAttribute("height") || "0");
      if (!name || !(w > 0 && h > 0)) return;
      densityCloud.panToSlot(slot, name, neighborSlotPages(slot), x + w / 2, y + h / 2, w, h, dir);
    });
    // the single view's card is a state read from here, never an event (see reconcileSingleCard)
    densityCloud.setSingleViewProvider(() => pageViewDisabled);
    // choosing a grouping in the single view with no cloud yet: re-send the counts so a chosen grouping opens the cloud view at once
    densityCloud.setSingleCloudRequestHandler(() => syncCloudCounts());
    densityCloud.setSingleCountProvider(() => {
      let n = 0;
      for (const b of bubblesData) if (b.realTime && !departingSessionIds.has(b.visitor_id)) n++;
      return n;
    });
    densityCloud.setZoomChangeHandler((open) => {
      eventEmitter.emit("densityZoomChanged", { open, page: densityCloud.isPageZoomOpen() });
      if (open) canvasZoomHandle?.reset(); // that view lays itself over the stage at 100%
      setFlowLinesZoomHidden(open); // hidden while zoomed; redrawn from the SAME counts when the grid is back
      funnelZoomHidden = open;
      if (!open) {
        if (zoomLeaverRaf) cancelAnimationFrame(zoomLeaverRaf);
        zoomLeaverRaf = 0;
        (svg?.node() as SVGSVGElement | undefined)?.querySelectorAll("[data-show-leaver]").forEach((el) => el.removeAttribute("data-show-leaver"));
        return;
      }
      const loop = () => {
        zoomLeaverRaf = requestAnimationFrame(loop);
        const root = svg?.node() as SVGSVGElement | undefined;
        const stage = root?.querySelector("#maincluster") as SVGGraphicsElement | null;
        if (!root || !stage) return;
        const stageBottom = stage.getBoundingClientRect().bottom;
        for (const vid of densityCloud.getLeavingVisitorIds()) {
          const els = root.querySelectorAll<SVGGraphicsElement>(`[visitor_id="${vid}"]`);
          if (!els.length) continue;
          const shape = Array.from(els).find((e) => e.tagName === "circle" || e.tagName === "rect") ?? els[0];
          // hand over to the real element when the bubble's CENTRE crosses the stage's bottom
          // edge (the canvas dot is cut off there), so there is no gap between the two
          const sr = shape.getBoundingClientRect();
          const below = sr.top + sr.height / 2 >= stageBottom - 1;
          els.forEach((e) => {
            if (below) e.setAttribute("data-show-leaver", "1"); else e.removeAttribute("data-show-leaver");
          });
        }
      };
      zoomLeaverRaf = requestAnimationFrame(loop);
    });
    // Icon mode: a bubble is an icon ~2.7x its radius wide, so a cloud's dots start
    // at the icon's own half-width (and spread to match), not the smaller radius.
    densityCloud.setClusterSpacingProvider(() => clusterSpacingForCharge(returnBubbleParameterData()?.charge));
    densityCloud.setBubbleRadiusProvider(() =>
      useIcons ? getRectSizeFromRadius(getBubbleRadius()).width / 2 : getBubbleRadius()
    );
    // colour AND (icon mode) category icon, in one lookup
    densityCloud.setBubbleLookProvider((visitorId) => {
      const b = bubblesData.find((n) => n.realTime && n.visitor_id === visitorId);
      if (!b) return null;
      const c: any = setColor(b.color);
      const color = c && typeof c.r === "number" ? `rgb(${c.r},${c.g},${c.b})` : c ? String(c) : null;
      const icon = getBubbleIconUrl(b);
      return { color, icon, aspect: icon ? getActiveIconImageAspect() : undefined };
    });
    // Asked for every visitor still gliding toward a cloud, on every frame (densityCloudService.syncSlotParticles). MEASURED 2026-10-18 under campaign traffic with the funnel OFF: ~88 ms of every
    // second, because each call searched the whole grid for the visitor's element (querySelectorAll over ~800 nodes) and measured the grid's own rectangle again. Now the element is
    // remembered (found again only if it was replaced, e.g. after a rebuild) and the grid's rectangle is read once per frame. The answer is the same.
    densityCloud.setElementPositionProvider((visitorId) => elementScreenPosition(svg?.node() as SVGSVGElement | undefined, visitorId));
    // Where a closing visitor's bubble docks, in client px, and how long the app's own move takes.
    // Mirrors moveBubbleToHistoricalCluster: the existing history bubble of the visitor's category,
    // or, when there is none yet, the historical cluster's own spot. (The visitor is still in
    // bubblesData when this is asked: the departure path notifies the clouds first.)
    densityCloud.setLeaveTargetProvider((visitorId) => {
      const root = svg?.node() as SVGSVGElement | undefined;
      const b = bubblesData.find((n) => n.visitor_id === visitorId);
      if (!root || !b) return null;
      const box = root.getBoundingClientRect();
      const dockNode = svg
        ?.select(`circle[name='${b.name}'], rect[name='${b.name}'], image[name='${b.name}']`)
        .node() as SVGGraphicsElement | null;
      let cx: number, cy: number;
      if (dockNode) {
        const c = getShapeCenter(dockNode);
        cx = c.cx; cy = c.cy;
      } else {
        const h = clusterCenterCoords?.[HISTORICAL_CLUSTER_INDEX];
        if (!h) return null;
        cx = h.x; cy = h.y;
      }
      return { x: box.left + cx, y: box.top + cy, durationMs: dockTransitionDurationMs() };
    });
    densityCloud.setSlotCenterProvider((slot) => {
      const c = clusterCenterCoords?.[slot];
      return c ? { x: c.x, y: c.y } : null;
    });
    // the spawn point of a bubble that has JUST spawned, in client px (older ones: none)
    densityCloud.setSpawnPointProvider((visitorId) => {
      const root = svg?.node() as SVGSVGElement | undefined;
      const b = bubblesData.find((n) => n.realTime && n.visitor_id === visitorId);
      if (!root || !b) { spawnMiss.noBubble++; return null; }
      if (b.spawnSilent) { spawnMiss.silent++; return null; }
      // where the bubble spawned; if that was never resolved (the row was not painted yet),
      // where its category's row is now
      let sx: number | null = b.spawnX, sy: number | null = b.spawnY;
      // an old bubble is not a new arrival (it flies from nowhere); one whose spawn point was never
      // resolved falls back to where its category's row is now
      if (sx != null && performance.now() - b.spawnAt > 6000) { spawnMiss.tooOld++; return null; }
      if (sx == null || sy == null) {
        const o = getSidebarOriginCoords(b.name);
        sx = o ? o.x : null; sy = o ? o.y : null;
      }
      if (sx == null || sy == null) { spawnMiss.noOrigin++; return null; }
      const ctm = root.getScreenCTM?.();
      if (!ctm) { spawnMiss.noOrigin++; return null; }
      const pt = root.createSVGPoint();
      pt.x = sx; pt.y = sy;
      const c = pt.matrixTransform(ctm);
      return { x: c.x, y: c.y, at: b.spawnAt };
    });
    // a bubble is not 'there' until it has finished gliding to where the simulation put it
    densityCloud.setLandingLagProvider(bubbleMoveMsNow);
    // a past day has no interaction states (they are live-only), so the zoom's grouping by them is off there
    densityCloud.setInteractionStatesProvider(() => playbackModeService.mode !== "playback");
    // the zoomed card carries the goal page's flag too, decided exactly as the grid's flag is (renderClusterGoalBadge)
    densityCloud.setGoalPageProvider((pageName) =>
      playbackModeService.mode === "playback" ? playbackConvertedUrls.has(pageName) : !!getGoalForPage(pageName)
    );
    setPerfContext("onsite", () => `${getOnsiteVisitors().size} onsite`);
    densityCloud.setStampExtra(() => `${stuckBubbleReport()} · ${loaderStatsText()} · spawn point missing: no bubble ${spawnMiss.noBubble}, older than 6s ${spawnMiss.tooOld}, no sidebar row ${spawnMiss.noOrigin}, a seek's reconstruction ${spawnMiss.silent}`);
    densityCloud.setActivityProvider(() => interactionPulsesEnabled);
    densityCloud.setPeriodicHook(() => updateSlotCountLabels(lastSlotCounts));
    densityCloud.setBubbleColorProvider((visitorId) => {
      const b = bubblesData.find((n) => n.realTime && n.visitor_id === visitorId);
      if (!b) return null;
      // the same call getBubbleFill makes, deliberately unguarded: right
      // after a refresh d.color can be undefined and the bubble still paints
      // via setColor's fallback - skipping it here gave a teal cloud over
      // orange bubbles
      const c: any = setColor(b.color);
      if (c && typeof c.r === "number") return `rgb(${c.r},${c.g},${c.b})`;
      return c ? String(c) : null;
    });
    densityCloud.setBubblePositionProvider((visitorId) => {
      const b = bubblesData.find((n) => n.realTime && n.visitor_id === visitorId);
      return b && typeof b.x === "number" && typeof b.y === "number" ? { x: b.x, y: b.y } : null;
    });
    densityCloud.setBubbleHandlers({
      onHover: (visitorId, x, y) => {
        const d = bubblesData.find((b) => b.realTime && b.visitor_id === visitorId);
        if (!d) return;
        // same tooltip a real bubble shows; it reads the datum off event.target
        showRealtimeTooltip({ target: { __data__: d }, layerX: x, layerY: y });
        const tip = document.getElementById("tooltip");
        if (tip) tip.style.zIndex = "1003";
      },
      onLeave: () => hideRealtimeTooltip(),
      onClick: (visitorId) => {
        hideRealtimeTooltip();
        eventEmitter.emit("selectSessionInDrawer", visitorId);
      },
    });

    // Historical bubbles are mostly static once laid out (unlike real-time
    // bubbles, which move continuously), so a drop shadow here is cheap - it's
    // only real-time bubbles where re-rendering a filter every animation
    // frame would be a performance concern, and this filter is never applied
    // to those.
    svg
      .append("defs")
      .append("filter")
      .attr("id", "historical-bubble-shadow")
      .attr("x", "-50%")
      .attr("y", "-50%")
      .attr("width", "200%")
      .attr("height", "200%")
      .append("feDropShadow")
      .attr("dx", 0)
      .attr("dy", 1)
      .attr("stdDeviation", 1.2)
      .attr("flood-opacity", 0.35);

    bubblesSelection = svg?.selectAll("circle[data-bubble], rect[data-bubble]");

    svg
      .append("rect")
      .attr("id", "maincluster")
      .attr("width", mainSvgWidth - 10)
      .attr("height", mainClusterHeight)
      .style("fill", "#f0f0f0")
      .style("stroke", "#f0f0f0");

    const svgNodeForTouch = svg?.node() as SVGSVGElement | undefined;
    if (svgNodeForTouch) {
      attachTouchTooltips(svgNodeForTouch);
    }

    await createClusterRect(svg, mainSvgWidth);
    if (funnelHost) {
      funnelHost.state.setGoalProvider(() => goalsList.find((g) => g.pageName)?.pageName ?? null);
      funnelHost.layer.startFunnelLayer(getFunnelContext);
    }

    /*
     * 🔴 SIZE THE GREY BACKGROUND TO WHAT IT CONTAINS (2026-09-09).
     *
     * `mainClusterHeight` is `someThing * 0.62` where someThing is derived
     * from window.innerHeight AT MODULE IMPORT - another frozen constant, the
     * same class of bug as mainSvgWidth/mainSvgHeight. Measured live: the grey
     * rect was 421.6 tall because the window happened to be 800px when the
     * module loaded, while the bottom row of boxes ended at 426.7 and its
     * labels at 441.7. The last row and its labels hung OUTSIDE the box that
     * is supposed to contain them.
     *
     * Chris: "The gray bounding box at the bottom here is gone. We need a
     * little to show more so it looks like the white tracking page boxes are
     * contained within it. It should match the top margin space a little also
     * to look balanced."
     *
     * So it is no longer a percentage of anything. It is measured from the
     * grid that was just laid out: the lowest slot centre, plus half a box,
     * plus room for that row's label, plus a bottom margin equal to the top
     * one. Balanced by construction rather than by tuning a multiplier, and
     * it cannot drift when the row pitch or box height changes.
     *
     * Runs after createClusterRect because that is what populates
     * clusterCenterCoords.
     */
    const slotCentres = (clusterCenterCoords || [])
      .slice(0, UNKNOWN_URL_CLUSER_INDEX + 1)
      .filter((c: any) => c && typeof c.y === "number");

    if (slotCentres.length) {
      const BOX_HALF_HEIGHT = boxHalfHeight;
      const LABEL_ROOM = 28;
      /*
       * Deliberately LESS than the 50px top margin, not equal to it.
       *
       * Matching it exactly measured 63px of visible grey below the last
       * label - the label sits above its own baseline, so an equal margin
       * reads as MORE space at the bottom than the top. Chris: "you didn't
       * need to make the bottom gray space so much."
       *
       * 26 lands the visible gap just under the top one, which is what
       * "balanced" actually looks like for a panel whose last element is
       * text rather than a box.
       */
      const BOTTOM_MARGIN = 26;
      const lowestCentreY = Math.max(...slotCentres.map((c: any) => c.y));
      const contentBottom = lowestCentreY + BOX_HALF_HEIGHT + LABEL_ROOM;

      svg
        .select("#maincluster")
        .attr("height", Math.round(contentBottom + BOTTOM_MARGIN));
    }

    resolve("ok");
  });
};


/*
 * LONG-PRESS TOOLTIPS FOR TOUCH (2026-09-10).
 *
 * On a touch device the hover tooltip is suppressed outright (see
 * tooltipService's isCoarsePointer note - a tap synthesises mouseover but
 * never mouseout, so the tooltip stuck forever). This gives that information
 * back deliberately: press and hold a bubble for LONG_PRESS_MS to see it,
 * which is the native convention for "show me more without committing".
 *
 * Bound ONCE on the svg by delegation rather than on each bubble. Bubbles are
 * created and destroyed constantly across six different binding sites, and
 * per-bubble listeners would have to be re-attached at every one of them -
 * exactly the kind of thing that gets missed when a seventh is added.
 *
 * A press that becomes a long-press SUPPRESSES the click that follows it, so
 * one gesture does one thing: tap opens the drawer, long-press shows the
 * tooltip. Without that, a long-press would do both, which is the bug this
 * exists to fix arriving by a different route.
 */
const LONG_PRESS_MS = 450;
/*
 * 🔴 RAISED 10 -> 25 (2026-09-10). 10px was set from a synthetic pointer,
 * which does not move at all - so the emulator passed and a real finger did
 * not. Holding still for 450ms on a phone routinely wanders further than
 * 10px, which cancelled the press every time. Reported directly: "long press
 * does not work to bring up the tooltip."
 *
 * 25px is the usual threshold for distinguishing a press from a drag. Past it
 * this is a scroll or a drag and the page must keep the gesture - which
 * matters, because the narrow layout is taller than the viewport by design.
 */
const LONG_PRESS_MOVE_TOLERANCE_PX = 25;

const findBubbleTarget = (node: EventTarget | null): Element | null => {
  let el = node as Element | null;
  while (el && el !== document.body) {
    if (
      el.getAttribute &&
      (el.getAttribute("realTime") === "true" ||
        el.getAttribute("historical") === "true")
    ) {
      return el;
    }
    el = el.parentElement;
  }
  return null;
};

/*
 * 🔴 EVERY LISTENER HERE IS ON THE CAPTURE PHASE (2026-09-10).
 *
 * These are delegated on the svg, so in the normal bubbling phase they run
 * AFTER the target's own handlers - and every bubble carries
 * `.call(realtimeDragBehavior())`. d3-drag calls stopImmediatePropagation()
 * when a gesture begins, so a press on a bubble never reached this code at
 * all and the long press silently did nothing.
 *
 * That is also why dragging a bubble worked perfectly while long-press did
 * not: drag was consuming the very events long-press needed. Confirmed on a
 * real device - "Press and drag DOES work", "long press does not".
 *
 * Capture runs from the root DOWN, before the target's handlers, so this sees
 * the press first. It does not interfere with drag: nothing here calls
 * preventDefault or stopPropagation, so the event continues to d3 exactly as
 * before.
 */
const attachTouchTooltips = (svgNode: SVGSVGElement) => {
  if (!isCoarsePointer()) {
    return;
  }
  let timer: ReturnType<typeof setTimeout> | null = null;
  let startX = 0;
  let startY = 0;
  let suppressNextClick = false;

  const cancel = () => {
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
  };

  const dismiss = () => {
    setTouchTooltipAllowed(false);
    hideRealtimeTooltip();
    hideHistoricalTooltip();
  };

  const beginPress = (target: Element) => {
    cancel();
    timer = setTimeout(() => {
      timer = null;
      suppressNextClick = true;
      // The show functions ignore coarse pointers unless this is set, which is
      // what keeps a synthesised mouseover from reaching them.
      setTouchTooltipAllowed(true);
      /*
       * 🔴 THE EVENT MUST CARRY layerX/layerY (2026-09-10).
       *
       * showRealtimeTooltip positions itself with
       * `bubbleData.layerX + radius + GAP`, which are MOUSE-EVENT properties.
       * A synthetic `{ target }` has neither, so that arithmetic produced NaN
       * and `left: NaNpx` was silently dropped as invalid CSS. The tooltip was
       * genuinely being shown - opacity 1, correct content - parked at
       * whatever position it last held, off-screen.
       *
       * That is why every earlier attempt at this appeared to do nothing: the
       * on-screen diagnostic reported "LONG PRESS FIRED" while the tooltip sat
       * somewhere invisible.
       *
       * layerX/layerY are relative to the nearest positioned ancestor, which
       * is #main_svg - the same element #tooltip lives in - so the bubble's
       * own centre measured against that container is the faithful
       * equivalent of where a cursor would have been.
       */
      const bubbleRect = target.getBoundingClientRect();
      const tooltipHost = document.getElementById("main_svg");
      const hostRect = tooltipHost
        ? tooltipHost.getBoundingClientRect()
        : ({ left: 0, top: 0 } as DOMRect);
      const fakeEvent = {
        target,
        layerX: bubbleRect.left + bubbleRect.width / 2 - hostRect.left,
        layerY: bubbleRect.top + bubbleRect.height / 2 - hostRect.top,
      } as any;
      if (target.getAttribute("historical") === "true") {
        showHistoricalTooltip(fakeEvent);
      } else {
        showRealtimeTooltip(fakeEvent);
      }
      setTouchTooltipAllowed(false);
    }, LONG_PRESS_MS);
  };

  svgNode.addEventListener("pointerdown", (event: PointerEvent) => {
    if (event.pointerType === "mouse") {
      return;
    }
    const target = findBubbleTarget(event.target);
    if (!target) {
      // A press on empty canvas is how a tooltip gets dismissed.
      dismiss();
      return;
    }
    startX = event.clientX;
    startY = event.clientY;
    beginPress(target);
  }, true);

  svgNode.addEventListener("pointermove", (event: PointerEvent) => {
    if (!timer) {
      return;
    }
    if (
      Math.abs(event.clientX - startX) > LONG_PRESS_MOVE_TOLERANCE_PX ||
      Math.abs(event.clientY - startY) > LONG_PRESS_MOVE_TOLERANCE_PX
    ) {
      cancel();
    }
  }, true);

  /*
   * TOUCH EVENTS AS WELL AS POINTER EVENTS.
   *
   * Pointer events over SVG children are not uniformly reliable across mobile
   * browsers, and this could not be verified on the real device from here.
   * Touch events are the older, universally supported path; binding both
   * costs nothing because the timer is idempotent - whichever arrives first
   * starts it, and any of the enders cancels it.
   */
  svgNode.addEventListener("touchstart", (event: TouchEvent) => {
    if (timer) {
      return;
    }
    const touch = event.touches && event.touches[0];
    const target = findBubbleTarget(event.target);
    if (!touch || !target) {
      if (!target) {
        dismiss();
      }
      return;
    }
    startX = touch.clientX;
    startY = touch.clientY;
    beginPress(target);
  }, { passive: true, capture: true });

  svgNode.addEventListener("touchmove", (event: TouchEvent) => {
    if (!timer) {
      return;
    }
    const touch = event.touches && event.touches[0];
    if (!touch) {
      return;
    }
    if (
      Math.abs(touch.clientX - startX) > LONG_PRESS_MOVE_TOLERANCE_PX ||
      Math.abs(touch.clientY - startY) > LONG_PRESS_MOVE_TOLERANCE_PX
    ) {
      cancel();
    }
  }, { passive: true, capture: true });

  svgNode.addEventListener("touchend", cancel, true);

  svgNode.addEventListener("pointerup", cancel, true);
  svgNode.addEventListener("pointercancel", () => {
    // Only drop a PENDING press. This used to dismiss the tooltip as well,
    // which is wrong on iOS: the platform emits pointercancel whenever it
    // takes a gesture over, so a tooltip that had just been shown was torn
    // down again immediately. Dismissal belongs to an explicit press on empty
    // canvas, not to the platform reclaiming the pointer.
    cancel();
  }, true);

  // Capture phase, so the suppressed click never reaches the bubble's own
  // handler and opens the drawer on top of the tooltip.
  svgNode.addEventListener(
    "click",
    (event: MouseEvent) => {
      if (suppressNextClick) {
        suppressNextClick = false;
        event.stopPropagation();
        event.preventDefault();
      }
    },
    true
  );
};

// The page in each direction from a slot, by where the slots sit in the grid.
const neighborSlotPages = (slot: number): densityCloud.SlotNeighbors => {
  // The page lying in each of the EIGHT directions from this slot (the nearest one in that direction), by name, with its slot so the
  // zoomed view can show its live count. A direction is horizontal or vertical when one offset clearly dominates (1.6x the other), and
  // diagonal otherwise: a 5-by-3 grid has its diagonal neighbours at about equal offsets.
  const here = clusterCenterCoords?.[slot];
  const out: densityCloud.SlotNeighbors = {};
  if (!here) return out;
  // offsets are measured in grid cells (the columns are wider apart than the rows), so a diagonal neighbour is about one cell each way
  const cw = Math.abs((clusterCenterCoords?.[1]?.x ?? 0) - (clusterCenterCoords?.[0]?.x ?? 0)) || 1;
  const rh = Math.abs((clusterCenterCoords?.[5]?.y ?? 0) - (clusterCenterCoords?.[0]?.y ?? 0)) || 1;
  const names: Partial<Record<string, string>> = {}, slots: Partial<Record<string, number>> = {}, best: Record<string, number> = {};
  for (const page of data) {
    if (!page?.selected || page.slotIndex == null || page.slotIndex === slot ||
        page.slotIndex >= DEFAULT_VALUE.UNKNOWN_URL_CLUSER_INDEX) continue;
    const c = clusterCenterCoords?.[page.slotIndex];
    if (!c) continue;
    const dx = c.x - here.x, dy = c.y - here.y, ax = Math.abs(dx) / cw, ay = Math.abs(dy) / rh;
    let dir: string;
    if (ax > 1.6 * ay) dir = dx < 0 ? "left" : "right";
    else if (ay > 1.6 * ax) dir = dy < 0 ? "top" : "bottom";
    else dir = (dy < 0 ? "t" : "b") + (dx < 0 ? "l" : "r");
    const dist = Math.hypot(ax, ay);
    if (best[dir] === undefined || dist < best[dir]) { best[dir] = dist; names[dir] = page.name; slots[dir] = page.slotIndex; }
  }
  // "All other pages" is a neighbour too: one wide box under columns 3 to 5, so a page above any of those has it straight below
  const U = DEFAULT_VALUE.UNKNOWN_URL_CLUSER_INDEX, uc = clusterCenterCoords?.[U];
  if (slot !== U && uc) {
    const x2 = clusterCenterCoords?.[2]?.x, x4 = clusterCenterCoords?.[4]?.x;
    const cx = x2 !== undefined && x4 !== undefined ? Math.min(Math.max(here.x, x2), x4) : uc.x;
    const dx = cx - here.x, dy = uc.y - here.y, ax = Math.abs(dx) / cw, ay = Math.abs(dy) / rh;
    let dir: string;
    if (ax > 1.6 * ay) dir = dx < 0 ? "left" : "right";
    else if (ay > 1.6 * ax) dir = dy < 0 ? "top" : "bottom";
    else dir = (dy < 0 ? "t" : "b") + (dx < 0 ? "l" : "r");
    const dist = Math.hypot(ax, ay);
    if (best[dir] === undefined || dist < best[dir]) { best[dir] = dist; names[dir] = "All other pages"; slots[dir] = U; }
  }
  Object.assign(out, names);
  out.slots = slots as densityCloud.SlotNeighbors["slots"];
  return out;
};

const createClusterRect = (svg, mainSvgWidth) => {
  return new Promise(async (resolve) => {
    if (SLOT_TOTALS) {
      if (slotTotalsTimer) clearInterval(slotTotalsTimer);
      // the totals move with live arrivals (a few seconds is plenty for a bar) and with a replay as it plays (every second)
      let slotTotalsBeat = 0;
      slotTotalsTimer = setInterval(() => { if (playbackModeService.mode === "playback" || ++slotTotalsBeat % 4 === 0) updateSlotTotals(); }, 1000);
    }
    clusterTextInstances = [];

    clusterRectInstances = [];

    clusterCenterCoords = await getClusterCoords(mainSvgWidth);

    clusterCenterCoords.forEach((center, key) => {
      if (key < UNKNOWN_URL_CLUSER_INDEX) {
        const svgInstance = svg.append("g").attr("rectIndex", key);
        addSlotShadow(svgInstance, center.x - boxHalfWidth, center.y - boxHalfHeight, boxWidth, boxHeight, false);
        const element = document.getElementById("select_a_page");

        const rectInstance = svgInstance
          .append("rect")
          // Width is 150 (a box's true left edge is 75px, not 50px, before
          // its center) - this box used to be 100 wide (where a ±50 offset
          // correctly centered it, matching the y/height pairing right
          // below, which IS still correctly centered) and was widened to
          // 150 without updating this offset to match. That left a
          // permanent ~25px gap between where a real-time bubble's force
          // target actually sits (center.x, resolved via
          // resolveRealtimeClusterCoords) and this rect's true rendered
          // center (center.x + 25) - bubbles were converging exactly where
          // they were told to, just not to the visual middle of the box.
          .attr("x", center.x - boxHalfWidth)
          .attr("y", center.y - boxHalfHeight)
          .attr("width", boxWidth)
          .attr("height", boxHeight)
          .attr("rectIndex", key)
          .attr("selected", false)
          .attr("id", "rect_" + key)
          .style("fill", d3.rgb(227, 231, 231))
          .style("stroke", d3.rgb(227, 231, 231))
          .on("mouseover", function () {
            const clusterIndex = parseInt(this.getAttribute("rectIndex"));

            const clusterSelected = this.getAttribute("selected");

            // Skipped specifically while flow lines are visible in playback
            // - the "Select a Page to Show +" overlay this shows/positions
            // competed with hovering flow lines for the same screen space
            // (reported directly). Deliberately NOT gated on playback mode
            // as a whole (an earlier version of this fix was) - that left
            // no way to get the overlay back short of fully exiting to live,
            // even once playback had ended/reset and flow lines weren't
            // actually competing for anything anymore (reported directly).
            // Gating on flowLinesEnabled instead means toggling flow lines
            // off (already an existing control) immediately restores this,
            // and it solves the original conflict at its actual source
            // rather than the whole playback session.
            // A recipient of a shared clip cannot add a page (the pages are the clip's own), so an empty box offers nothing to select
            if (
              clusterIndex &&
              clusterSelected === "false" &&
              !isShareView() &&
              !pageViewDisabled &&
              !slotDrag.isSlotDragActive() &&
              !(playbackModeService.mode === "playback" && flowLinesEnabled)
            ) {
              const ele = this.getBoundingClientRect();

              element.style.left = ele.left + "px";
              element.style.top = ele.top + "px";
              element.style.right = ele.right + "px";
              element.style.width = ele.width + "px";
              element.style.height = ele.height + "px";
              element.style.display = "flex";
              element.setAttribute("rectIndex", clusterIndex.toString());
            }
          })
          .on("mouseout", () => {
            element.style.display = "none";
          })
          // Only a slot with a real page assigned (selected===true) zooms
          // into its interaction rivers - an empty/gray slot has no page to
          // show, and already has its own click surface (the "Select a Page
          // to Show +" overlay above) for assigning one. The box ALWAYS
          // zooms, busy or quiet (zoomSlot forms a cloud for a quiet one);
          // the page's metrics drawer is opened from its LABEL, below.
          .on("mouseenter.hint", function (e) { if (!pageViewDisabled && !slotDrag.isSlotDragActive() && this.getAttribute("selected") === "true") scheduleSlotHint((slotDrag.slotIsDraggable(key) ? "Click to zoom in or drag to reorder" : "Click to zoom in") + boxHintSuffix(d3.select(this.parentNode as Element).select("text").attr("real-text") || ""), e.clientX, e.clientY); })
          .on("mousemove.hint", function (e) { moveSlotHint(e.clientX, e.clientY); })
          .on("mouseleave.hint", () => hideSlotHint())
          .on("click", function (event: MouseEvent) {
            hideSlotHint();
            if (this.getAttribute("selected") !== "true") {
              return;
            }
            const pathName = d3
              .select(this.parentNode)
              .select("text")
              .attr("real-text");
            if (!pathName) {
              return;
            }
            // "Start a new funnel" is waiting for clicks: this click picks the page, it does not zoom into it
            if (funnelHost && funnelHost.state.pickPage(pathName)) {
              return;
            }
            const zoomIn = () => densityCloud.zoomSlot(key, pathName, neighborSlotPages(key), center.x, center.y, boxWidth, boxHeight);
            // demo and staging: a double-click focuses the page, so the zoom waits to see whether a second click follows
            if (boxClickGate) boxClickGate.click(event.detail, zoomIn, () => toggleSlotFocus(pathName));
            else zoomIn();
          });

        const textInstance = svgInstance

          .append("text")
          .text("")
          // True left edge is center.x - 75, not center.x - 50 (see the
          // matching rect's own comment above - this box was widened
          // 100->150px/±50->±75 offset at some point and the rect was
          // updated to match, but this label's x was left on the old ±50
          // offset, leaving it visibly shifted ~25px right of the box's
          // actual left edge ever since). Flush with the box's left edge
          // (no inset) per direct request - was +10, matching "All other
          // pages"' own label positioning below, which is flush for the
          // same reason.
          .attr("x", center.x - boxHalfWidth)
          .attr("y", center.y + boxHalfHeight + 15)
          .attr("fill", "#fff")
          .attr("text-anchor", "start")
          .attr("real-text", "")
          .attr("is-wrap", false)
          .style("font-size", "10px")
          .style("font-weight", PAGE_LABEL_WEIGHT)
          .style("fill", PAGE_LABEL_COLOUR)
          .style("z-index", "999999999")
          // The label opens the page's metrics drawer (the box zooms). A
          // transparent stroke with pointer-events:all widens the hit area
          // past the thin glyphs, so it is not a pixel hunt at 10px.
          .attr("class", "slot-label-link")
          .style("stroke", "transparent")
          .style("stroke-width", 8)
          .style("pointer-events", "all")
          .style("width", "100px")
          // No tooltip on the label (it is a plain link, and its text is already on screen). Its hover state is a
          // rounded background rectangle behind the text, not an underline.
          .on("mouseover", function () { showSlotLabelHover(this); })
          .on("mouseout", function () { hideSlotLabelHover(this); })
          .on("click", function () {
            const pathName = this.getAttribute("real-text");
            if (!pathName) {
              return;
            }
            eventEmitter.emit("openSegmentDetail", { dimension: "page", value: pathName });
          });

        // Visitors onsite in this slot, outside the box (right-aligned on the label's
        // baseline) so it never competes with the bubbles or the density cloud.
        svgInstance
          .append("text")
          .attr("class", "slot-count")
          .attr("data-slot", key)
          .attr("x", center.x + boxHalfWidth)
          .attr("y", center.y + boxHalfHeight + 15)
          .attr("text-anchor", "end")
          .style("font-size", "10px")
          .style("font-weight", COUNT_LABEL_WEIGHT)
          .style("fill", COUNT_LABEL_COLOUR)
          .style("pointer-events", "none");

        appendSlotTotalBar(svgInstance, rectInstance, key, center.x - boxHalfWidth, boxWidth, center.y + boxHalfHeight);
        clusterTextInstances.push(textInstance);
        clusterRectInstances.push(rectInstance);
        syncCloudCounts(); // a rebuilt grid starts with blank count labels

        gapBetweenBoxes =
          clusterCenterCoords[1].x - (clusterCenterCoords[0].x + 100);
        resolve("ok");
      }

      if (key === UNKNOWN_URL_CLUSER_INDEX) {
        // rectIndex on the wrapping <g> itself (not just the inner <rect>
        // below, which already has it) - every OTHER box's <g> wrapper
        // carries this, and redrawFlowLines's `:scope > g[rectIndex]`
        // selector uses it to find the last box to insert the flow-lines
        // layer after. Without it here, this specific box's own opaque
        // white background got appended AFTER that layer (it's created
        // near the end of this same loop), painting over every line that
        // ran into or out of it.
        const svgInstance = svg.append("g").attr("rectIndex", key);
        addSlotShadow(svgInstance, gridWideBoxStartX, center.y - boxHalfHeight, gridWideBoxEndX - gridWideBoxStartX, boxHeight, true);
        const backColor = d3.rgb(255, 255, 255);

        // "All other pages" now spans only the rightmost 3 columns (was all
        // 5) - left edge at clusterCenterCoords[2] (the third-from-right
        // column), freeing columns 0-1 for two new REAL page slots (indices
        // 10-11, drawn by the `key < UNKNOWN_URL_CLUSER_INDEX` branch above
        // like any other slot - see UNKNOWN_URL_CLUSER_INDEX's own comment).
        // Still visually the largest box on the grid, keeping the "this is
        // the broad catch-all" reading intact even at the smaller size.
        const rectInstance = svgInstance
          .append("rect")
          // Same ±75 (not ±50) true half-width as the individual page boxes
          // above (see that rect's comment).
          // Was clusterCenterCoords[2] / [9] - both five-column assumptions.
          // Derived from the column count now, so this box spans columns 2-4
          // on the wide grid and both columns on the narrow one.
          .attr("x", gridWideBoxStartX)
          .attr("y", center.y - boxHalfHeight)
          .attr(
            "width",
            // 🔴 The right edge was `clusterCenterCoords[9].x + 75`. That 75
            // is a stale literal from when every box was 150px wide, but
            // boxWidth now scales (up to BOX_WIDTH_MAX 260, i.e. a half-width
            // of 130), so this box's right edge fell SHORT of the boxes above
            // it by boxHalfWidth - 75 - about 53px at a 1920px viewport.
            // boxHalfWidth is the same value those boxes use, so the edges
            // line up at every width now.
            gridWideBoxEndX - gridWideBoxStartX
          )
          .attr("rectIndex", key)
          .attr("height", boxHeight)
          .style("fill", backColor)
          .style("stroke", backColor)
          .style("cursor", "pointer")
          .on("mouseenter.hint", (e) => { if (!pageViewDisabled) scheduleSlotHint("Click to zoom in" + boxHintSuffix("All other pages"), e.clientX, e.clientY); })
          .on("mousemove.hint", (e) => moveSlotHint(e.clientX, e.clientY))
          .on("mouseleave.hint", () => hideSlotHint())
          .on("click", (event: MouseEvent) => {
            hideSlotHint();
            // Zooms busy or quiet, like every assigned page box. It has no
            // metrics drawer: "All other pages" is a group, not a page.
            const zoomIn = () => densityCloud.zoomSlot(
              key, "All other pages", neighborSlotPages(key),
              center.x, center.y, gridWideBoxEndX - gridWideBoxStartX, boxHeight
            );
            if (boxClickGate) boxClickGate.click(event.detail, zoomIn, () => toggleSlotFocus("All other pages"));
            else zoomIn();
          });

        const textInstance = svgInstance

          .append("text")
          .text("All other pages")
          // the label opens the list of the pages that are not in a box (RightMenu's OtherPagesPanel)
          .attr("class", "other-pages-label")
          .attr("data-other-pages-label", "1")
          .style("cursor", "pointer")
          .on("mouseover", function () { showSlotLabelHover(this as SVGTextElement); })
          .on("mouseout", function () { hideSlotLabelHover(this as SVGTextElement); })
          .on("click", () => { hideSlotHint(); eventEmitter.emit("openOtherPages"); })
          // Flush with the box's left edge (no inset) per direct request -
          // was +10, matching the individual page boxes' own label
          // positioning above, which is flush for the same reason.
          // Same left edge as the rect above, from the same landmark.
          .attr("x", gridWideBoxStartX)
          .attr("y", center.y + boxHalfHeight + 15)
          .attr("real-text", "All other pages")
          .attr("fill", PAGE_LABEL_COLOUR)
          .attr("is-wrap", false)
          .style(
            "stroke-width",

            1,

            { "font-size": "10px", "z-index": "999999999" },

            "fill",

            d3.rgb(105, 105, 105)
          )
          .style(
            "font-size",

            "10px"
          )
          .style("font-weight", PAGE_LABEL_WEIGHT);

        svgInstance
          .append("text")
          .attr("class", "slot-count")
          .attr("data-slot", key)
          .attr("x", gridWideBoxEndX)
          .attr("y", center.y + boxHalfHeight + 15)
          .attr("text-anchor", "end")
          .style("font-size", "10px")
          .style("font-weight", COUNT_LABEL_WEIGHT)
          .style("fill", COUNT_LABEL_COLOUR)
          .style("pointer-events", "none");

        appendSlotTotalBar(svgInstance, rectInstance, key, gridWideBoxStartX, gridWideBoxEndX - gridWideBoxStartX, center.y + boxHalfHeight);
        clusterTextInstances.push(textInstance);
        clusterRectInstances.push(rectInstance);
        syncCloudCounts();

        resolve("ok");
      }

      // for svg width

      // Was `+ 52 + 150` (202px past the rightmost column's own center) -
      // the rightmost box's true right edge is only 75px past its center
      // (see the box rect's own comment), so this left ~127px of excess
      // gray space past that edge with nothing symmetric on the left (see
      // initialWidth's comment above for that half of the mismatch).
      // Matching the same box-half-width + CLUSTER_GRID_MARGIN used to
      // start the grid keeps both edges' clearance equal.
      // Was clusterCenterCoords[4] - slot 4 happens to be the rightmost
      // column ONLY when there are five of them. gridLastColumnX is derived
      // from the real column count, so it is correct at 2 as well.
      const gridRightEdge = gridLastColumnX + boxHalfWidth + CLUSTER_GRID_MARGIN;
      d3.select("#main_svg").attr("width", gridRightEdge);
      svg.attr("width", gridRightEdge);

      /*
       * 🔴 SCALE TO FIT, never scroll (2026-09-09).
       *
       * A min-width floor alone was the wrong degradation: below it the grid
       * simply overflowed and the user had to scroll horizontally AND
       * vertically to see one screen. Chris, on seeing it: "I have to scroll
       * up and down and to the left and right?!"
       *
       * A viewBox fixes that properly. The logical layout keeps the geometry
       * computed above - same slot count, same proportions, no reflow of the
       * force simulation's coordinate space - while the ELEMENT is told to
       * fit whatever width the container actually has. Narrow screens get a
       * smaller grid rather than a clipped one.
       *
       * This is also why the page-slot count never varies by breakpoint:
       * scaling preserves all twelve slots, where a breakpoint that dropped
       * a column would silently take away something the customer pays for.
       */
      const logicalWidth = gridRightEdge;
      const logicalHeight = gridCanvasHeight();
      const container = document.querySelector("#main_svg") as HTMLElement | null;
      const availableWidth = container ? container.clientWidth : logicalWidth;

      svg
        .attr("viewBox", `0 0 ${logicalWidth} ${logicalHeight}`)
        .attr("preserveAspectRatio", "xMinYMin meet");

      // Only ever scale DOWN. Stretching the grid to fill a very wide monitor
      // would blow the bubbles up with it; box width already scales for that
      // case (computeBoxWidth), which is the right lever.
      if (availableWidth > 0 && availableWidth < logicalWidth) {
        svg.attr("width", availableWidth);
        svg.attr("height", logicalHeight * (availableWidth / logicalWidth));
        d3.select("#main_svg").attr("width", availableWidth);
      }
      /*
       * 🔴 THE PANEL MUST START AT THE GRID'S LEFT EDGE (2026-09-10).
       *
       * It used to start at 0 and run to gridRightEdge. Once the grid began
       * CENTRING itself, that offset was absorbed entirely into the panel's
       * left margin - measured ~130px of grey to the left of the first box
       * against ~20px to the right of the last. Reported directly: "the boxes
       * are all shifted right (more margin on the left inside the gray
       * bounding box)."
       *
       * The panel is the boxes' backdrop, so it belongs to the grid, not to
       * the container: it starts where the grid starts and is exactly as wide.
       * The surplus then falls OUTSIDE it as gutters, which is the point.
       */
      d3.select("#maincluster")
        .attr("x", gridLeftEdge)
        .attr("width", gridRightEdge - gridLeftEdge);

      // end
    });

    eventEmitter.emit("createStaticComponents", {
      clusterTextInstances,
      clusterRectInstances,
    });

    // Covers the very first paint, before any departure/fetch has had a
    // chance to call notifySidebarItemsUpdated (which handles every
    // subsequent update) - otherwise a genuinely empty day/site showed
    // nothing at all where the historical cluster would sit until the
    // first real event fired.
    refreshHistoricalEmptyState();

    resolve("ok");
  });
};

const getClusterCoords = (mainSvgWidth: any) => {
  /*
   * 🔴 MEASURED, not window.innerWidth.
   *
   * This was `window.innerWidth < NARROW_GRID_MAX_WIDTH` and it did not work:
   * measured live at a 700px viewport, every other width in the document read
   * 700 (innerWidth, outerWidth, documentElement.clientWidth, body,
   * visualViewport, .main-box) while the value this line saw was 1440, so the
   * narrow layout never engaged below the breakpoint.
   *
   * Rather than chase why, this now reads the same source the rest of this
   * function already trusts. getClusterCoords' own availableWidth comment
   * spells out the rule: measure the element, do not infer from the window.
   * documentElement.clientWidth is the laid-out viewport and it agreed with
   * every other measurement.
   */
  const viewportWidth =
    document.documentElement.clientWidth || window.innerWidth;

  // Measured before the column count, which depends on it.
  const hostForColumns = (document.querySelector("#main_svg")
    ?.parentElement as HTMLElement | null) || null;
  const rawAvailableWidthForColumns =
    hostForColumns && hostForColumns.clientWidth > 0
      ? hostForColumns.clientWidth
      : Math.max(viewportWidth - 250, 600);
  // 2 columns x 6 rows holds all twelve slots, plus a final row for the
  // full-width "All other pages" box.
  /*
   * Columns come from what FITS at the constant box size, not from a
   * breakpoint table: 5 when there is room, then 4, 3, 2.
   */
  const columnsAmount = columnsThatFit(rawAvailableWidthForColumns);

  /*
   * Rows needed for the twelve slots, plus the "All other pages" box.
   *
   * When the slots do not divide evenly into the columns, the wide box takes
   * the REMAINING columns of the final part-filled row - which is exactly what
   * the five-column layout has always done (slots 10 and 11, then the wide box
   * across the other three). When they do divide evenly it gets a row of its
   * own, as it already did at two columns.
   */
  const slotRows = Math.ceil(UNKNOWN_URL_CLUSER_INDEX / columnsAmount);
  const slotsOnLastRow = UNKNOWN_URL_CLUSER_INDEX % columnsAmount;
  const wideBoxSharesLastRow = slotsOnLastRow > 0;
  const rowsAmount = wideBoxSharesLastRow ? slotRows : slotRows + 1;

  /*
   * 🔴 SPAN THE ACTUAL CONTAINER (2026-09-09).
   *
   * This was `(mainSvgWidth * 0.85 - 200) / 5`, i.e. the grid claimed ~85%
   * of the WINDOW minus a fixed 200px. Two problems, both measured:
   *
   *   - it used the window, not the space the grid actually has. The sidebar
   *     is 250px wide, so the formula was always ~250px optimistic.
   *   - the 0.85 and the 200 left the rest as gutter. At 1440px the SVG came
   *     out 1034 inside a 1170 container; at 2560px, 1880 inside 2290 -
   *     410px of dead space on the right.
   *
   * Chris: "There's such big margins/gutters on the left and right. I want
   * that space filled up. Skydive needs to take up the entire view space."
   *
   * So the spacing is now derived from the room genuinely available: measure
   * the container, subtract the margins the grid needs at each edge, and
   * distribute what is left across the columns. The last box's right edge
   * lands one margin from the container's right edge by construction, rather
   * than wherever a tuned constant happened to put it.
   *
   * Falls back to the window minus the sidebar when the container has not
   * been laid out yet (first paint), which is the only case where
   * clientWidth reads 0.
   */
  const container = document.querySelector("#main_svg") as HTMLElement | null;

  /*
   * 🔴 MEASURE THE CONTAINER'S PARENT, NOT #main_svg ITSELF.
   *
   * createClusterRect sets `#main_svg`'s own width to the grid's right edge.
   * So reading #main_svg.clientWidth here reads back THE GRID'S OWN WIDTH, not
   * the space the grid has - a feedback loop in which available space always
   * equals content width.
   *
   * That did not matter while the grid stretched to fill (the two were the
   * same by construction). It matters now that the box is a fixed size and
   * the surplus is supposed to become margin: the surplus computed as ZERO
   * every time, so the grid stayed pinned left with all the empty space
   * collected on the right instead of split into equal gutters.
   *
   * The parent is a plain layout div that nothing here resizes, so it reports
   * the real available width.
   */
  const layoutHost = (container?.parentElement as HTMLElement | null) || null;
  const measured =
    layoutHost && layoutHost.clientWidth > 0
      ? layoutHost.clientWidth
      : container && container.clientWidth > 0
      ? container.clientWidth
      : 0;
  const rawAvailableWidth =
    measured > 0 ? measured : Math.max(mainSvgWidth - SIDEBAR_WIDTH, 600);

  // Space the columns must share: the layout width, less a margin at each
  // edge, less the width of the final box (whose LEFT edge is the last
  // column centre minus half a box).
  const usable = rawAvailableWidth - CLUSTER_GRID_MARGIN * 2 - boxWidth;
  /*
   * 🔴 CAP THE COLUMN GAP.
   *
   * `usable / (columnsAmount - 1)` spreads the columns evenly across the full
   * container, which is right at five columns (it divides by four) and badly
   * wrong at two: it divides by ONE, so the entire usable width becomes the
   * gap and the two columns are flung to opposite edges. Measured at 700px:
   * columns at x=218 and x=583 with 150px boxes.
   *
   * The cap is expressed the same way BOX_GAP_TARGET already is - a gap
   * measured from the box's own width - so the narrow grid sits together as a
   * block instead of being stretched to fill.
   */
  /*
   * A CONSTANT gap, not one that stretches to fill. Together with a fixed box
   * width this gives the grid a FIXED TOTAL WIDTH, which is what lets the
   * surplus become margin instead of being absorbed into ever-wider columns.
   */
  const marginWidth =
    boxWidth + columnGapFor(rawAvailableWidth, columnsAmount, boxWidth);

  /*
   * 🔴 THE GRID IS LEFT-ALIGNED. DO NOT CENTRE IT HERE (2026-09-10).
   *
   * It was briefly centred within its container, so that surplus width became
   * equal margins. That broke alignment with everything else anchored to the
   * container's left - most visibly the "total visitors onsite" counter card,
   * which sat ~90px left of the grey panel's new edge. Reported directly:
   * "the entire bounding box shifts to the right - look how off alignment it
   * is from the overall site counter."
   *
   * There is already ONE centring in this layout and it belongs at the shell:
   * .main-box caps at $max-shell-width and centres, which is what turns
   * surplus into gutters on a large monitor. Centring the grid again inside
   * that was a second, competing centring - and the two do not agree, because
   * the shell contains the sidebar and the counter while the grid does not.
   *
   * One centring, at the shell. Everything inside it stays left-aligned and
   * therefore stays aligned with each other.
   */
  gridLeftEdge = 0;
  const gridCentringOffset = 0;
  // Was `marginWidth - 130` (~52.5 for the example above) - tuned back when
  // boxes were 100px wide (a ±50 offset from center correctly centered
  // them then). Once boxes were widened to 150px without updating this
  // starting offset, box 0's true left edge (center.x - 75) landed at a
  // NEGATIVE x - hanging off the left edge of the gray background rect
  // entirely instead of sitting inside it with a clean margin. Anchored
  // directly to the box's own half-width + a fixed margin instead, so this
  // is always correct regardless of viewport width.
  let initialWidth = gridCentringOffset + boxHalfWidth + CLUSTER_GRID_MARGIN;
  const centerX: any = [];

  for (let i = 0; i < columnsAmount; i++) {
    centerX[i] = {
      x: initialWidth,
      exteraX: initialWidth * (50 / 36) + 250, // (382.53-250) *(50/36)+250 =
    };
    initialWidth = initialWidth + marginWidth;
  }

  const centerY = [];
  const marginHeight =
    ((window.innerHeight * 0.85 > 500 ? window.innerHeight * 0.85 : 500) -
      200) /
      3 +
    10;

  let initialHeight = 100;
  // Both row gaps (1st -> 2nd, and 2nd -> 3rd/"All other pages") are
  // tightened by the same amount, to bring the whole grid up.
  const rowGapReduction = 60;

  /*
   * 🔴 MINIMUM ROW PITCH (2026-09-09).
   *
   * A row must clear its own box AND the page-name label printed beneath it.
   * Without this floor the label is drawn UNDERNEATH the next row's boxes.
   *
   * Measured on a live dashboard at an 800px-tall viewport: rows landed at
   * y = 50 / 160 / 270 - a pitch of 110 for a 100px box, leaving 10px for a
   * label whose baseline sits 15px below the box. Rows 1 and 2 had their
   * labels clipped by the row below; row 3 rendered fine only because
   * nothing follows it. That is exactly what "the labels look wrong" was.
   *
   * It gets worse as the window shortens: at 600px tall the formula yields a
   * pitch of 53 for a 100px box, so the BOXES would overlap by 47px.
   *
   * rowGapReduction was tuned "to bring the whole grid up" and is kept - it
   * is right whenever there is room. This only stops it eating the label.
   */
  const BOX_HEIGHT = boxHeight;
  /*
   * Raised 28 -> 52 (2026-09-09). 28 was the bare minimum that stopped the
   * label being painted under the next row - correct, but only just: it left
   * 13px of clear space while the HORIZONTAL gap between boxes was 70px. The
   * grid read as cramped vertically against generous horizontal spacing.
   * 52 brings the two closer together without the rows drifting apart.
   */
  const LABEL_CLEARANCE = 44;
  const MIN_ROW_PITCH = BOX_HEIGHT + LABEL_CLEARANCE;

  for (let i = 0; i < rowsAmount; i++) {
    centerY[i] = {
      y: initialHeight,
      exteraY: (initialHeight - 250) * (50 / 36) + 250,
    };
    /*
     * marginHeight is `(usable height - 200) / 3` - a hardcoded THREE rows.
     * The narrow layout has seven, so each row inherited a gap sized for a
     * three-row grid and the columns marched far off the bottom of the page.
     *
     * A stacked grid does not want to fill the window's height anyway: it is
     * already taller than the viewport and scrolls. A constant, compact pitch
     * is what reads correctly - the box plus its label plus a little air.
     */
    /*
     * A CONSTANT row pitch, like the constant box and column gap.
     *
     * The wide layout used to derive this from the WINDOW's height (divided by
     * three, because it was always three rows). Column reflow makes the row
     * count vary at every width, so a window-derived pitch spreads a 4-row
     * grid using spacing computed for 3 - the same class of mistake as sizing
     * the canvas from the window.
     *
     * Box height + label clearance + a little air, at every width. That yields
     * the ~60px gap already confirmed as right on the five-column layout.
     */
    const ROW_BREATHING_ROOM = 16;
    const gapToNextRow = MIN_ROW_PITCH + ROW_BREATHING_ROOM;
    initialHeight = initialHeight + gapToNextRow;
  }

  const mainCenters = new Array(UNKNOWN_URL_CLUSER_INDEX + 3);

  // Row 3 (keyY===2) used to hold ONLY "All other pages" (a single wide
  // box spanning all 5 columns). Per direct request, that box now only
  // spans columns 2-3-4 (still the visually largest box, just not
  // full-width), freeing columns 0-1 for two new REAL, fully-interactive
  // page slots (indices 10-11) - not placeholders, they go through the
  // exact same `key < UNKNOWN_URL_CLUSER_INDEX` draw/select/hover path as
  // slots 0-9 in createClusterRect, entirely because UNKNOWN_URL_CLUSER_INDEX
  // itself moved from 10 to 12 (see that constant's own comment) rather than
  // needing any separate wiring.
  /*
   * ONE mapping for every column count (2, 3, 4 or 5).
   *
   * Slots fill left-to-right, top-to-bottom, so their INDICES keep their
   * order and only their positions change. Nothing downstream cares how many
   * columns there are - every draw, select, hover and flow-line path keys off
   * the slot index, never its coordinates.
   *
   * "All other pages" takes the REMAINING columns of the final part-filled
   * row, or a row of its own when the slots divide evenly. That reproduces
   * both layouts that already existed: at five columns it lands beside slots
   * 10 and 11 spanning the other three, and at two columns it gets its own
   * row spanning both.
   */
  let slotIndex = 0;
  for (let row = 0; row < rowsAmount; row++) {
    for (let col = 0; col < columnsAmount; col++) {
      if (slotIndex >= UNKNOWN_URL_CLUSER_INDEX) {
        break;
      }
      mainCenters[slotIndex] = {
        x: centerX[col].x,
        y: centerY[row].y,
        exteraX: centerX[col].exteraX,
        exteraY: centerY[row].exteraY,
      };
      slotIndex++;
    }
  }

  // Where the wide box sits: the leftover columns of the last slot row, or a
  // fresh row beneath when nothing is left over.
  const wideBoxRow = wideBoxSharesLastRow ? slotRows - 1 : slotRows;
  const wideBoxFirstCol = wideBoxSharesLastRow ? slotsOnLastRow : 0;
  const wideBoxLastCol = columnsAmount - 1;

  mainCenters[UNKNOWN_URL_CLUSER_INDEX] = {
    x: (centerX[wideBoxFirstCol].x + centerX[wideBoxLastCol].x) / 2,
    y: centerY[wideBoxRow].y,
    exteraX:
      (centerX[wideBoxFirstCol].exteraX + centerX[wideBoxLastCol].exteraX) / 2,
    exteraY: centerY[wideBoxRow].exteraY,
  };

  /*
   * The grid's landmarks, derived from the ACTUAL column count rather than
   * read back out of fixed slot indices. createClusterRect used
   * clusterCenterCoords[4] (rightmost column), [2] and [9] (the wide box's
   * edges) - every one of those is a five-column assumption.
   */
  gridLastColumnX = centerX[columnsAmount - 1].x;
  gridWideBoxStartX = centerX[wideBoxFirstCol].x - boxHalfWidth;
  gridWideBoxEndX = centerX[columnsAmount - 1].x + boxHalfWidth;
  gridContentBottomY =
    centerY[rowsAmount - 1].y + boxHalfHeight + LABEL_CLEARANCE;
  gridCentreX = (centerX[0].x + centerX[columnsAmount - 1].x) / 2;
  gridFirstColumnLeftEdge = centerX[0].x - boxHalfWidth;

  const someCondition = window.innerHeight * 0.9 > 500;

  const someCalculatedValue = someCondition ? window.innerHeight * 0.85 : 500;

  // Historical cluster (index 13, was 11 - shifted to make room for the 2
  // new real page slots above). x was a hardcoded magic number (250+225 =
  // 475) unrelated to the grid's own layout math - centerX[2].x is the
  // grid's true horizontal center (the middle of 5 columns, already used by
  // "All other pages" above, and by extension the gray real-time bounding
  // box's own center - see getClusterCoords's gridRightEdge comment), so
  // aligning to it instead puts the historical cluster on the same vertical
  // center line as both, per direct request.
  // y nudged up from 500 - the bottom control bar (especially playback's
  // PlaybackControlBar + ScrubBar, both taller than the original simple
  // footer this value predates) was covering the lowest historical bubbles.
  // Approximate/tunable - "y: 500" was never derived from anything
  // responsive (unlike centerY above, which scales with window.innerHeight),
  // so there's no principled "correct" value here, just less overlap than
  // before. Revisit with real visual feedback if this isn't enough, or too
  // much.
  mainCenters[UNKNOWN_URL_CLUSER_INDEX + 1] = {
    // Was centerX[2].x - "the middle of five columns", which is undefined at
    // two and threw here, taking createClusterRect down with it.
    x: gridCentreX,

    y: 430,

    exteraX: someCalculatedValue + 225 * 0.9,

    exteraY: (someCalculatedValue * 0.9 - 250) * (50 / 36) + 250,
  };

  // Combined "collapsed" cluster (index 14, was 13 - shifted for the same
  // reason as the historical cluster above), used when pageViewDisabled -
  // the "arrow" toggle in AnalyticsView.tsx. x had an arbitrary +80 offset
  // shifting it off the grid's true horizontal center - same class of bug
  // as the historical cluster's own fix above (x: centerX[2].x, not a magic
  // number), just not caught at the time. Removed so the collapsed cluster
  // lands on the same center line as the grid/gray bounding box, matching
  // "All other pages" and the historical cluster.
  mainCenters[UNKNOWN_URL_CLUSER_INDEX + 2] = {
    // Nudged up from +80 per direct request. Same "approximate/tunable,
    // not derived from anything responsive" caveat as the historical
    // cluster's own y above - revisit with real visual feedback if this
    // isn't enough, or too much.
    // Was mainCenters[2].x. That slot only happens to sit on the grid's
    // centre line in the five-column layout; at two columns it is row 1's
    // left-hand box. The centre is what this actually wants.
    x: gridCentreX,

    y: (someCalculatedValue * 0.85 - 300) / 2 + 30,

    exteraX: mainCenters[2].exteraX,

    exteraY: ((someCalculatedValue * 0.85 - 300) / 2) * (50 / 36),
  };

  return mainCenters;
};

// ---------------------------------------------------------------------------------------------------------------------------------
// A cloud's dot becoming a real bubble again ("released": its page changed slot, the visitor moved page, or the cloud closed).
//
// 🔴 BATCHED, because one frame can release a WHOLE cloud (a reorder moves a page's visitors to another slot at once). Done per dot - an
// element lookup, a full repaint of every bubble, a forced layout and a reheat EACH - it was QUADRATIC: measured with a cloud swapped with a
// light page, one frame froze the page for 0.6s at 400 visitors and 3.8s at 1000 (CPU profile: getBoundingClientRect, setAttribute,
// querySelectorAll and paintRealtimeBubblePositions). Now one pass finds the elements, and there is one repaint, one forced layout and
// one reheat per batch. Only the cheap state change stays synchronous (the simulation's forces must stop treating the bubble as clouded).
const dotReleases: { visitorId: string; x: number; y: number }[] = [];
let dotReleaseFlushQueued = false;

const flushDotReleases = () => {
  dotReleaseFlushQueued = false;
  const batch = dotReleases.splice(0);

  if (!batch.length) {
    return;
  }

  const wanted = new Map(batch.map((release) => [release.visitorId, release] as const)); // a repeated id: the last release wins
  const bubbleById = new Map<string, any>();

  for (const n of bubblesData) {
    if (n.realTime && wanted.has(n.visitor_id)) {
      bubbleById.set(n.visitor_id, n);
    }
  }

  // the real bubble reappears exactly where its dot was, and the app's own movement (to another slot, or down to close) takes over
  // from there; only visitors that still HAVE a bubble are touched, as before
  const touched: SVGElement[] = [];

  (svg?.node() as SVGSVGElement | undefined)?.querySelectorAll<SVGElement>("[visitor_id]").forEach((el) => {
    const id = el.getAttribute("visitor_id");

    if (!id || !bubbleById.has(id)) {
      return;
    }

    el.removeAttribute("data-cloud-hidden");
    el.style.visibility = "";
    el.style.opacity = "";
    el.style.pointerEvents = "";
    el.style.transition = "none"; // jump to the dot's spot with no easing...
    touched.push(el);
  });

  bubbleById.forEach((b, id) => {
    const release = wanted.get(id)!;

    b.x = release.x;
    b.y = release.y;
    b.vx = 0;
    b.vy = 0;
  });

  // the app's own closing/moving animation starts from where the bubble was last DRAWN, so draw it at the dot's spot now, not on the next tick
  paintRealtimeBubblePositions();

  if (touched.length) {
    void touched[0].getBoundingClientRect(); // ONE forced layout commits every jump (it was one per element)
  }

  for (const el of touched) {
    el.style.transition = ""; // ...then hand the app's own easing back
  }

  reheatRealtimeSimulation(false);
};

const releaseDotBatched = (visitorId: string, svgX: number, svgY: number) => {
  cloudedVisitors.delete(visitorId);
  dotReleases.push({ visitorId, x: svgX, y: svgY });

  if (!dotReleaseFlushQueued) {
    dotReleaseFlushQueued = true;
    queueMicrotask(flushDotReleases);
  }
};

// ---------------------------------------------------------------------------------------------------------------------------------
// DRAG-AND-DROP PAGE SLOT REORDER (docs/DRAG_DROP_SLOTS_SCOPE.md). slotDragService.ts owns the pointer and slotReorder.ts owns the
// rules; this is what a DROP does to the page data, the boxes and the bubbles.
//
// TRAVEL (Chris, 2026-10-11: the bubbles TRAVEL, they do not snap). Every real bubble is pulled toward the centre of ITS PAGE'S slot, and
// that target is resolved fresh from live `data` on every tick (resolveRealtimeClusterCoords) - the same way a visitor navigating to
// another page makes their bubble drift to another box. So a reorder needs only: the page data changed, each affected bubble's own slot
// fields updated (the counts, clouds and flow lines read those), and one reheat.
//
// A SECOND reorder while bubbles are still travelling simply retargets them: targets are a function of current state, never a queue of
// animations, so nothing can stack or double. A HIDDEN tab applies the net result instantly (CLAUDE.md perf note 9).

// Each real bubble's slot fields, from the page data as it is NOW. `movedNames` narrows the pass to the pages that moved; without it
// every real bubble is checked (used after a conflict, when the server's truth may differ from what was moved). Returns how many changed.
const retargetBubblesToCurrentSlots = (movedNames?: Set<string>): number => {
  let changed = 0;

  for (const item of bubblesData) {
    if (!item.realTime || (movedNames && !movedNames.has(item.pageName))) {
      continue;
    }

    const page = data?.find((p: any) => p?.selected && p.name === item.pageName);
    const slot =
      page && page.slotIndex != null && page.slotIndex < UNKNOWN_URL_CLUSER_INDEX ? page.slotIndex : UNKNOWN_URL_CLUSER_INDEX;

    if (item.cluster !== slot || item.id !== slot) {
      item.id = slot;
      item.cluster = slot;
      changed++;
    }
  }

  return changed;
};

// Redraws the page boxes, retargets the bubbles and lets them travel. Idempotent: a box is only redrawn when what it shows differs from
// what the page data says, so calling it with nothing changed touches nothing.
export const applySlotLayoutFromData = (movedNames?: Set<string>) => {
  for (let slot = 1; slot < UNKNOWN_URL_CLUSER_INDEX; slot++) {
    const page = data?.find((p: any) => p?.selected && p.slotIndex === slot);
    const wanted = page ? page.name : "";
    const shown = clusterTextInstances[slot]?.attr("real-text") || "";

    if (wanted === shown) {
      continue;
    }

    if (page) {
      fillClusterSelected(slot, page.name);
    } else {
      fillClusterEmpty(slot);
    }
  }

  retargetBubblesToCurrentSlots(movedNames);

  try {
    densityCloud.refreshMembership(); // which slot each visitor belongs to is derived from the page data (setPageSlotProvider)
  } catch (error) {
    console.error("[slot-reorder] refreshMembership failed", error);
  }

  // Flow weights are keyed by slot index, and a reorder moves pages between slots. This used to WIPE them; the paths now follow their pages (flowLineRelocation.ts),
  // so the analysis survives. Redraw so the lines land on the new positions.
  scheduleFlowLinesRedraw();

  syncRealtimeNodesFromBubblesData();

  if (document.hidden) {
    reheatRealtimeSimulation(true);
    // d3's forceX/forceY read their targets only when (re)initialised, and a silent reheat defers that to a microtask. Settling before it
    // would converge on the OLD targets and move nothing (measured: 580px from the new slot, one frame after the tab came back).
    flushRealtimeNodesIfDirty();
    settleRealtimeSimulation(); // nobody is watching: land the net result at once rather than queue an animation
  } else {
    reheatRealtimeSimulation();
  }

  syncCloudCounts();
};

// ONE valid drop. Optimistic: the boxes and bubbles move NOW, the save follows, and a failure puts them back.
export const commitSlotReorder = (changes: SlotChange[]) => {
  const siteId = storage.get("siteId");

  if (!siteId || !changes.length) {
    return;
  }

  const moved = new Set(changes.map((change) => change.pageName));

  applySlotChangesInPlace(changes);
  applySlotLayoutFromData(moved);

  saveSlotReorder(changes, siteId).then((outcome) => {
    if (outcome.status === "ok" || outcome.status === "noop") {
      return;
    }

    if (outcome.status === "error") {
      // nothing is known about the server: put the page(s) back. Matched on name AND the slot they now hold, so a later reorder is not undone.
      applySlotChangesInPlace(invertChanges(changes));
      toastMessageError("That page couldn't be moved, so it was put back.", "slot-reorder-failed");
    }

    // 'conflict': saveSlotReorder already adopted the server's pages into `data`. Either way, draw from what `data` now says.
    applySlotLayoutFromData();
  });
};

// SORTING THE PAGE BOXES (Chris, 2026-10-24). By page views: the busiest page (or the quietest) in the first box, the rest after it; the homepage keeps slot 0 and a locked page keeps its box. Two ways:
//   - "Sort boxes now": one deliberate rearrangement, saved like a drag (the server takes a move or a swap, so it is the few swaps that reach the order, one after another).
//   - "Auto sort": while it is on, the boxes follow the numbers as they change (a replay's page views so far, or today's live ones), re-checked every few seconds. It is a VIEW only: nothing is saved, and turning
//     it off puts the boxes back where they were. A page nearly level with its neighbour keeps its place, so two pages trading the lead do not shuffle.
export let slotSortOrder: SortOrder = storage.get("slotSortOrder") === "asc" ? "asc" : "desc";
export const setSlotSortOrder = (order: SortOrder) => {
  slotSortOrder = order === "asc" ? "asc" : "desc";
  try {
    storage.set("slotSortOrder", slotSortOrder);
  } catch (e) {
    // the choice just will not survive a refresh
  }
  if (autoSortSlots) tickAutoSort(true);
};
const pageViewsForSort = (): Map<string, number> => {
  const out = new Map<string, number>();
  const list: Array<{ name: string; overallVisitors?: number }> = playbackModeService.mode === "playback" ? playbackModeService.playbackPageViews() : ((data || []) as any[]);
  list.forEach((p) => out.set(p.name, Number(p.overallVisitors) || 0));

  return out;
};
/** sorts the boxes once and saves it; returns how many steps it took (0 = already in order) */
export const sortSlotBoxesNow = (): number => {
  const pages = (data || []) as SlotPage[];
  const steps = stepsToReach(pages, sortTarget(pages, pageViewsForSort(), slotSortOrder));
  steps.forEach((step) => commitSlotReorder(step));

  return steps.length;
};
export let autoSortSlots = false;
let autoSortBase: Map<string, number> | null = null;
let autoSortTimer: ReturnType<typeof setInterval> | null = null;
const applyStepsInView = (steps: SlotChange[][]) => {
  if (!steps.length) return;
  steps.forEach((step) => applySlotChangesInPlace(step));
  applySlotLayoutFromData(new Set(steps.flat().map((c) => c.pageName)));
};
const tickAutoSort = (strict = false) => {
  if (!autoSortSlots || pageViewDisabled || document.hidden || slotDrag.isSlotDragActive() || densityCloud.isZoomed()) return;
  const pages = (data || []) as SlotPage[];
  const views = pageViewsForSort();
  const top = Math.max(0, ...Array.from(views.values()));
  // a page must be ahead of its neighbour by a few percent of the busiest page's views before it is put ahead (or by 1% on a deliberate re-sort)
  applyStepsInView(stepsToReach(pages, sortTarget(pages, views, slotSortOrder, strict ? 0 : Math.max(2, top * 0.02))));
};
export const setAutoSortSlots = (on: boolean) => {
  if (on === autoSortSlots) return;
  autoSortSlots = on;
  if (autoSortTimer) { clearInterval(autoSortTimer); autoSortTimer = null; }
  if (on) {
    autoSortBase = new Map(((data || []) as SlotPage[]).filter((p) => p.selected && typeof p.slotIndex === "number" && p.slotIndex > 0).map((p) => [p.name, p.slotIndex as number]));
    autoSortTimer = setInterval(() => tickAutoSort(), 2500);
    tickAutoSort(true);
  } else if (autoSortBase) {
    // back to the layout the boxes had when it was switched on (what the page's own saved layout still is)
    applyStepsInView(stepsToReach((data || []) as SlotPage[], autoSortBase));
    autoSortBase = null;
  }
};

/**
 * DRAG TO REMOVE (Chris, 2026-10-23): a page dragged down into the strip at the bottom of the grey stage and let go comes off the boxes: exactly what the Pages panel's REMOVE does (the page stays in the
 * list and keeps its history), and a toast says so.
 */
export const removePageFromSlot = async (slot: number) => {
  const siteId = storage.get("siteId");
  const pageName = (clusterTextInstances[slot]?.attr("real-text") as string) || "";

  if (!siteId || !pageName) {
    return;
  }

  await unselectPage({ pageName, siteId });
  eventEmitter.emit("removeAPage");
  eventEmitter.emit("d3componentUpdate");
  // a shared clip has no Pages list to point at
  if (!isShareView()) toastMessageSuccess(`${pageName} is off the page boxes. It is still in the Pages list.`, "slot-removed");
};

/** the strip a dragged page is dropped in to remove it: BELOW every box (and the room their labels take), down to the grey panel's own bottom edge */
export const slotRemoveZone = (): { x: number; y: number; width: number; height: number } | null => {
  const node = svg?.node() as SVGSVGElement | undefined;
  const panel = node?.querySelector("#maincluster");

  if (!node || !panel) {
    return null;
  }

  const bottoms = Array.from(node.querySelectorAll("rect[rectIndex]"))
    .map((rect) => Number(rect.getAttribute("y")) + Number(rect.getAttribute("height")))
    .filter(Number.isFinite);
  const panelTop = Number(panel.getAttribute("y")) || 0;
  const panelBottom = panelTop + Number(panel.getAttribute("height"));
  const width = Number(panel.getAttribute("width"));

  if (!bottoms.length || ![panelBottom, width].every(Number.isFinite) || width <= 0) {
    return null;
  }

  const top = Math.max(Math.max(...bottoms) + SLOT_LABEL_ROOM, panelBottom - REMOVE_STRIP_HEIGHT);

  return { x: Number(panel.getAttribute("x")) || 0, y: top, width, height: Math.max(panelBottom - top, REMOVE_STRIP_MIN_HEIGHT) };
};
const SLOT_LABEL_ROOM = 22; // the name and the "on page" count are drawn under a box
const REMOVE_STRIP_HEIGHT = 56;
const REMOVE_STRIP_MIN_HEIGHT = 30;

const slotDragHost: slotDrag.SlotDragHost = {
  svgNode: () => (svg?.node() as SVGSVGElement | null) ?? null,
  boxes: () =>
    clusterRectInstances
      .slice(0, UNKNOWN_URL_CLUSER_INDEX)
      .map((rect: any, slot: number) => ({
        slot,
        x: Number(rect?.attr("x")),
        y: Number(rect?.attr("y")),
        width: Number(rect?.attr("width")),
        height: Number(rect?.attr("height")),
      }))
      .filter((box: any) => [box.x, box.y, box.width, box.height].every(Number.isFinite)),
  pages: () => data || [],
  // Not in the single view (the boxes are inert), not in a page's zoom. PLAYBACK IS ALLOWED (Chris, 2026-10-11): it was disabled at first on
  // the guess that "the pages are a day's history", which the code does not support. There is ONE layout, the site's saved pages; playback
  // does not carry a per-day copy, it replays the day against today's layout (entering it only flips a mode flag, and both modes redraw the
  // grid from the same cached pages). So a reorder in playback IS a reorder in live, and nothing needs mirroring.
  allowed: () => !pageViewDisabled && !densityCloud.isZoomed() && !boxesLocked(),
  label: (slot: number) => clusterTextInstances[slot]?.attr("real-text") || "",
  commit: (changes: SlotChange[]) => commitSlotReorder(changes),
  removeZone: () => slotRemoveZone(),
  remove: (slot: number) => {
    removePageFromSlot(slot);
  },
  onDragStart: () => {
    hideSlotHint();
    const overlay = document.getElementById("select_a_page");

    if (overlay) {
      overlay.style.display = "none";
    }
  },
};

export const enablePageView = (pages: any,numberOfPagesToBeSelected:number) => {
  // Was a hardcoded 10 - "All other pages"' own index moved to 12 (see
  // UNKNOWN_URL_CLUSER_INDEX's own comment for the full remap).
  const allPagesClusterIndex = UNKNOWN_URL_CLUSER_INDEX;

  // Flow-line weights are keyed by clusterIndex and rendered under
  // whatever page CURRENTLY occupies that box (getBoxLabel reads real-text
  // live) - capture the assignment before it's cleared below so a genuine
  // reassignment can be detected afterward. Doing this check inside
  // fillClusterSelected itself doesn't work here: fillClusterEmpty (below)
  // always clears real-text to "" first, so by the time fillClusterSelected
  // runs its own previousPathName read is always falsy and that guard never
  // fires - this was the actual reason the first attempt at this fix didn't
  // resolve the reported bug.
  const previousLabels = clusterTextInstances.map(
    (instance: any) => instance?.attr("real-text") || ""
  );

  clusterTextInstances.forEach((clusterTextInstance: any, index: any) =>
    fillClusterEmpty(index)
  );

  const pagesData = pages.filter((page: any) => page.hasOwnProperty("slotIndex"));
    for(let i =0;i<pagesData.length;i++){
      if(numberOfPagesToBeSelected>0 && pagesData[i].selected){


          numberOfPagesToBeSelected--;
          fillClusterSelected(pagesData[i].slotIndex, pagesData[i].name);
      }
    }

    // .forEach((page: any) => fillClusterSelected(page.slotIndex, page.name));

  fillClusterSelected(allPagesClusterIndex, "All other pages");

  if (playbackModeService.mode === "playback") {
    const reassigned = clusterTextInstances.some((instance: any, index: number) => {
      const newLabel = instance?.attr("real-text") || "";
      return previousLabels[index] && previousLabels[index] !== newLabel;
    });
    if (reassigned) {
      resetFlowLines();
    }
  }

  pageViewDisabled = false;
  storage.set("pageViewDisabled", "false");
  scheduleFlowLinesRedraw(); // the grid is back: redraw the lines from the same counts
  densityCloud.reconcileSingleCard();   // the single view's card goes with the view, not up to a pass later
  ambientLife.setSingleView(false); // the page boxes are back, so there is something for the glow to sit behind
  try {
    densityCloud.dissolveAll();   // the single cluster's cloud turns back into its bubbles
    densityCloud.refreshMembership();
  } catch (error) {
    console.error("[density-cloud] dissolveAll failed while leaving the single-cluster view", error);
  }
  syncCloudCounts(); // the labels come back, and slots over the threshold re-form their clouds

  // Real-time conversion star badges are no longer removed on the way into
  // single-view mode (see disablePageView's own comment), so there's
  // nothing left to re-add here on the way back out - re-adding them
  // unconditionally would have created a duplicate badge for every
  // already-visible one.

  // Page selection changed which cluster each pageName resolves to -
  // reheating the persistent simulation is all that's needed, since its
  // per-node target is resolved fresh from live `data`/`pageViewDisabled`
  // state on every tick.
  syncRealtimeNodesFromBubblesData();
  reheatRealtimeSimulation();
};

const findForceCenter = (pageName: string, cordinate: string) => {
  let centerCordinates: number;
  const visitedPage = data.find((page: any) => {
    return page.name === pageName;
  });
  if (visitedPage) {
    const bubbleCordinates =
      clusterCenterCoords[
        visitedPage.slotIndex ?? DEFAULT_VALUE.UNKNOWN_URL_CLUSER_INDEX
      ];
    if (cordinate === "x") {
      centerCordinates = bubbleCordinates?.x;
    } else {
      centerCordinates = bubbleCordinates?.y;
    }
  }
  return centerCordinates;
};
export const disablePageView = () => {
  clearFlowDrawing(); // hidden in the single view; the counts are untouched and come back with the grid
  clusterTextInstances.forEach((clusterTextInstance) => {
    clusterTextInstance.text("");
    clusterTextInstance.attr("is-wrap", false);
    clusterTextInstance.attr("real-text", "");
  });

  clusterRectInstances.forEach((clusterRectInstance) => {
    clusterRectInstance.style("fill", "#f0f0f0");
    clusterRectInstance.style("stroke", "#f0f0f0");
  });

  // The goal-destination badge is per-slot-box (renderClusterGoalBadge,
  // keyed by clusterIndex) - collapsing every box down to this single
  // combined view blanks their text/fill above but never touched these, so
  // a goal's flag icon stayed pinned to its now-blank box's coordinates,
  // floating with no visible box underneath it. There's no single
  // "collapsed" page for it to badge instead, so it's just removed while
  // collapsed (enablePageView below restores it).
  // Selector was stale - renderClusterGoalBadge was redesigned to a
  // <g data-goal-page-badge> wrapper (flag + knockout star) rather than a
  // plain <image>, so this never matched anything and the flag stayed
  // visible in single-cluster view no matter what. g[data-goal-page-badge]
  // is the same selector already fixed for the equivalent modeChanged
  // playback-entry cleanup elsewhere in this file.
  svg?.selectAll("g[data-goal-page-badge]").remove();

  // Real-time conversion star badges (appendGoalBadge) are intentionally
  // NOT removed here anymore - per direct request, they're a persistent
  // per-session marker that should keep showing whether the view is
  // collapsed into one cluster or not. (Previously removed here and
  // re-added by enablePageView on the way back out - reversed together
  // with appendGoalBadge's own gate, see that function's comment.)

  pageViewDisabled = true;
  canvasZoomHandle?.reset(); // the single view draws its own canvas at 100%
  storage.set("pageViewDisabled", "true");
  hideSlotHint();                       // a hint already showing for a box that is no longer there
  densityCloud.reconcileSingleCard();   // ... and arrives with it
  ambientLife.setSingleView(true); // no page boxes in this view: nothing for a glow or a light to belong to

  // No page boxes in this view: no "N on page" labels, and no density clouds
  // (each turns back into its real bubbles where it is).
  syncCloudCounts();
  updateSlotCountLabels(lastSlotCounts); // hide them now, not on the next frame
  try {
    densityCloud.dissolveAll();   // each page cloud turns back into its bubbles; the single cluster forms its own
    densityCloud.refreshMembership();
  } catch (error) {
    // a failure here must not stop the bubbles collapsing into the single cluster
    console.error("[density-cloud] dissolveAll failed while entering the single-cluster view", error);
  }

  // Same as enablePageView: pageViewDisabled is read live by the persistent
  // simulation's per-node target resolver, so reheating is all that's
  // needed to pull every real-time bubble into the single "all pages"
  // cluster.
  syncRealtimeNodesFromBubblesData();
  reheatRealtimeSimulation();
};

export const drawExistingBubble = async() => {
  const realTimeBubble = bubblesData.filter((item) => item.realTime);

  // The cloud service is torn down and re-initialised with the svg (a resize rebuilds the grid;
  // coming back from Settings builds it again), and it only learns a visitor from the arrival
  // and page-change events as they happen. Without this, every visitor already onsite was
  // unknown to its cloud afterwards: the cloud formed with nobody in it and the real bubbles
  // stayed in the open.
  for (const b of realTimeBubble) {
    if (departingSessionIds.has(b.visitor_id)) continue;
    densityCloud.notifyVisitorData(b.visitor_id, {
      browser: b.sessionData?.browser,
      visitor_type: b.sessionData?.visitor_type,
      goalReached: !!b.goalReached,
      unidentified: (b.visitor_category ?? b.sessionData?.visitor_category) === "unidentified",
      attrs: zoomAttrsOf(b.sessionData),
    });
    const page = b.currentUrlName ?? b.pageName;
    if (page) densityCloud.notifyVisitorPage(b.visitor_id, page);
  }

  syncRealtimeNodesFromBubblesData();
  reheatRealtimeSimulation();

  svg
    ?.selectAll("circle[data-bubble], rect[data-bubble]")
    .data(realTimeBubble, (d: any) => d.visitor_id)
    .enter()
    .append(getBubbleShapeTag())
    .attr("data-bubble", true)
    .attr("width", (d: any) => getRectSizeFromRadius(getBubbleRadius(d), getActiveIconAspect()).width)
    .attr("height", (d: any) => getRectSizeFromRadius(getBubbleRadius(d), getActiveIconAspect()).height)
    .attr("realTime", true)
    .attr("r", (d: any) => getBubbleRadius(d))
    /*
     * 🔴 POSITION AT CREATION. THIS IS THE "COMES IN FROM THE TOP" (2026-09-10).
     *
     * This enter() join set width/height/r and NEVER cx/cy. An SVG circle with
     * no cx/cy defaults to 0,0 - THE TOP-LEFT CORNER - and stayed there until
     * paintRealtimeBubblePositions ran on the next simulation tick. So every
     * repaint created every bubble in the corner and then visibly moved it into
     * its slot.
     *
     * Reported over and over, and described exactly: "the bubble comes in from
     * the TOP of the screen back into its position". It survived every earlier
     * fix because those all addressed WHEN a redraw happens; this is about HOW
     * the element is created, and no amount of suppressing redraws removes a
     * flash that is baked into the append itself.
     *
     * Positions come from the same live simulation node, and use the same
     * convention, as paintRealtimeBubblePositions - so the first painted frame
     * is already the correct one. A node with no position yet (a genuinely new
     * bubble) starts at the centre of the board rather than the corner, where
     * the forces then place it.
     */
    .attr("cx", (d: any) => (Number.isFinite(d?.x) ? d.x : mainSvgWidth / 2))
    .attr("cy", (d: any) => (Number.isFinite(d?.y) ? d.y : mainSvgHeight / 2))
    .attr("x", (d: any) => {
      const cx = Number.isFinite(d?.x) ? d.x : mainSvgWidth / 2;
      return cx - getRectSizeFromRadius(getBubbleRadius(d), getActiveIconAspect()).width / 2;
    })
    .attr("y", (d: any) => {
      const cy = Number.isFinite(d?.y) ? d.y : mainSvgHeight / 2;
      return cy - getRectSizeFromRadius(getBubbleRadius(d), getActiveIconAspect()).height / 2;
    })
    .attr("id", bubblesData.length - 1)
    .style("fill", (d: any) => getBubbleFill(d))
    .style("stroke", (d: any) => getBubbleStroke(d))
    .attr("visitor_id", (d) => d.visitor_id)
    .attr("show-menu", (d) => d.name)
    .attr("remove", "no")
    .attr("createdAt", (d) => d.createdAt)
    .attr("pageName", (d) => d.pageName)
    .on("mouseover", (d) => showRealtimeTooltip(d))
    .on("mouseout", () => hideRealtimeTooltip())
    .on("click", (event, d) => handleRealtimeBubbleClick(d))
    .call(realtimeDragBehavior())
    .each(function (d: any) {
      const iconUrl = getBubbleIconUrl(d);
      if (iconUrl) {
        appendIconImage(
          this,
          d,
          iconUrl,
          getRectSizeFromRadius(getBubbleRadius(d), getActiveIconAspect()).width,
          getActiveIconImageAspect(),
          "realTime",
          "visitor_id",
          d.visitor_id
        );
      }
      if (d.goalReached) {
        appendGoalBadge(this, d, "realTime", "visitor_id", d.visitor_id);
      }
      if (d.visitor_category === "unidentified") {
        appendUnidentifiedBadge(this, d, "realTime", "visitor_id", d.visitor_id);
      }
      appendReturnMark(this, d, d.visitor_id);
    });
  if (realTimeBubble && realTimeBubble.length > 0) {
    // This loop used to pass the default countPageview=true, so every full
    // redraw of the existing bubbles credited a fresh pageview to EVERY
    // onsite visitor - nobody navigated, we just repainted. Combined with
    // reconcileOnsiteVisitors running on a timer and on every socket
    // reconnect, an overnight tab accumulated ~11k phantom pageviews (28k
    // shown vs 17k real, the latter confirmed by the count snapping back on
    // refresh, which refetches from the backend).
    // Seed-only here, then derive true occupancy from the bubbles themselves.
    for (let i = 0; i < realTimeBubble.length; i++) {
      await handleVisitorMovePagesModel(null, realTimeBubble[i]?.pageName, false);
    }
    syncCurrentVisitorsFromPageNames(
      realTimeBubble.map((bubble: any) => bubble?.pageName)
    );
  }
  /*
   * 🔴 RESTORE THE AWAY GLYPH AFTER A REPAINT (2026-09-10).
   *
   * This function re-appends every bubble through a d3 enter() join, so the
   * DOM it produces is brand new and carries none of the interaction state the
   * old elements had. Away is the only one of those states that PERSISTS - the
   * rest are moments that clear themselves - and nothing ever re-sends it,
   * because the dashboard is only ever told about TRANSITIONS.
   *
   * The visible result was reported directly: "I have the away glyph on and
   * then change the viewport/window sizing and the flicker comes back" - the
   * resize rebuilt the grid and the glyph was simply gone, with no event left
   * to bring it back until the visitor happened to return and leave again.
   *
   * Restored from awaySessionIds, which is maintained at the single point the
   * state actually changes. Same principle as the tracking script's
   * reconcile-on-connect: A STATE MUST BE RE-ASSERTED ACROSS A BOUNDARY, NEVER
   * ASSUMED TO HAVE SURVIVED IT. A repaint is a boundary.
   */
  realTimeBubble.forEach((bubble: any) => {
    const id = bubble?._id || bubble?.visitor_id;
    if (id && awaySessionIds.has(id)) {
      applyInteractionPulse({ sessionId: id, type: "away" });
    }
  });

  eventEmitter.emit("drawExistingUserBubble");
};

// A soft, layered shadow under every page box (Chris, 2026-10-24: "that beautiful smooth drop shadow", after joshwcomeau.com/css/designing-shadows). The recipe: several layers whose offset and blur DOUBLE, each
// pulled in a little (negative spread) so the shadow stays tight under the box, all falling downward (light from above) and tinted with the stage's own dark blue, not black. It is painted ONCE per box size
// into a bitmap with a real blur and shown as an <image> under the box: this page repaints constantly, so a live SVG filter would be paid for every frame (docs/PERFORMANCE_120HZ.md), a bitmap costs nothing.
// An empty slot has none; a slot that gets a page gets it.
const SLOT_SHADOW_PAD = 44;
const SLOT_SHADOW_LAYERS = [
  { y: 1, blur: 1.5, spread: 0, a: 0.1 },
  { y: 2, blur: 3, spread: -1, a: 0.09 },
  { y: 4, blur: 6, spread: -2, a: 0.08 },
  { y: 8, blur: 12, spread: -3, a: 0.065 },
  { y: 16, blur: 24, spread: -5, a: 0.05 },
];
const slotShadowCache = new Map<string, string>();
const slotShadowBitmap = (w: number, h: number): string => {
  const key = `${Math.round(w)}x${Math.round(h)}`;
  const cached = slotShadowCache.get(key);
  if (cached !== undefined) return cached;
  let url = "";
  try {
    const scale = 2;
    const canvas = document.createElement("canvas");
    canvas.width = Math.ceil((w + 2 * SLOT_SHADOW_PAD) * scale);
    canvas.height = Math.ceil((h + 2 * SLOT_SHADOW_PAD) * scale);
    const ctx = canvas.getContext("2d");
    if (ctx) {
      const off = canvas.width + 400;   // the shape itself is drawn off the canvas; only its shadow lands on it
      SLOT_SHADOW_LAYERS.forEach((l) => {
        ctx.save();
        ctx.shadowColor = `rgba(30, 36, 52, ${l.a})`;
        ctx.shadowBlur = l.blur * scale;
        ctx.shadowOffsetX = off;
        ctx.shadowOffsetY = l.y * scale;
        const inset = -l.spread;
        ctx.fillRect((SLOT_SHADOW_PAD + inset) * scale - off, (SLOT_SHADOW_PAD + inset) * scale, (w - 2 * inset) * scale, (h - 2 * inset) * scale);
        ctx.restore();
      });
      url = canvas.toDataURL("image/png");
    }
  } catch (e) {
    url = "";   // no canvas (a test, a locked-down browser): the box simply has no shadow
  }
  slotShadowCache.set(key, url);

  return url;
};
const addSlotShadow = (group: any, x: number, y: number, w: number, h: number, visible: boolean) => {
  const url = slotShadowBitmap(w, h);
  if (!url) return;
  group
    .insert("image", ":first-child")
    .attr("class", "slot-shadow")
    .attr("href", url)
    .attr("x", x - SLOT_SHADOW_PAD)
    .attr("y", y - SLOT_SHADOW_PAD)
    .attr("width", w + 2 * SLOT_SHADOW_PAD)
    .attr("height", h + 2 * SLOT_SHADOW_PAD)
    .attr("pointer-events", "none")
    .attr("display", visible ? null : "none");
};
const setSlotShadow = (clusterIndex: number, on: boolean) => {
  const group = clusterRectInstances[clusterIndex]?.node?.()?.parentNode as Element | null | undefined;
  const layer = group?.querySelector(":scope > .slot-shadow");
  if (!layer) return;
  if (on) layer.removeAttribute("display"); else layer.setAttribute("display", "none");
};

export const fillClusterEmpty = (clusterIndex: any) => {
  const clusterTextInstance = clusterTextInstances[clusterIndex];
  const clusterRectInstance = clusterRectInstances[clusterIndex];
  clusterTextInstance?.text("");
  const hadPage = !!clusterTextInstance?.attr("real-text");
  clusterTextInstance?.attr("real-text", "");
  clusterTextInstance?.attr("is-wrap", false);
  clusterRectInstance?.style("fill", d3.rgb(227, 231, 231));
  setSlotShadow(clusterIndex, false);
  if (hadPage) seedFlowsSoon();
  if (hadPage && groupStack) groupStack.syncGroupStacks();
  clusterRectInstance?.attr("selected", false);
  removeClusterGoalBadge(clusterIndex);
  syncCloudCounts(); // the slot just lost its page: its count label goes with it
};

// A goal's own destination page-box badge - independent of any bubble/
// visitor, so it's keyed by clusterIndex (which box) rather than
// visitor_id/name like appendGoalBadge's bubble badges. Boxes are static-
// positioned (not force-simulated, see createClusterRect), so this is a
// plain one-time append with no tick-sync needed, unlike the bubble
// treatment.
const removeClusterGoalBadge = (clusterIndex: any) => {
  clusterRectInstances[clusterIndex]
    ?.node()
    ?.parentNode?.querySelectorAll("g[data-goal-page-badge]")
    .forEach((node: Element) => node.remove());
  clusterRectInstances[clusterIndex]
    ?.style("stroke", d3.rgb(227, 231, 231))
    .style("stroke-width", 1);
};

// Playback's own day-scoped "conversions actually happened on this URL,
// this day" set - see setPlaybackConvertedUrls's own comment. Deliberately
// separate from goalsList (live goal config) - the two answer genuinely
// different questions and must never be conflated.
let playbackConvertedUrls: Set<string> = new Set();

// Called by playbackService.ts's loadDate() once per day loaded, with the
// backend's getPlaybackConvertedUrls result (derived from the PERSISTED
// per-session goalUrl - a pure historical record, independent of today's
// live goal config). Re-renders every currently-filled box's badge against
// the new set, same as goalsUpdated's own listener does for live mode.
export const setPlaybackConvertedUrls = (urls: string[]) => {
  playbackConvertedUrls = new Set(urls);
  (data || []).forEach((page: any) => {
    if (page.selected && page.slotIndex != null && page.slotIndex < UNKNOWN_URL_CLUSER_INDEX) {
      renderClusterGoalBadge(page.slotIndex, page.name);
    }
  });
  // RightMenu.tsx's Pages tab drawer shows the same historical-record star
  // next to each URL during playback (not the live goal-config star) - it
  // has no direct access to the module-private set above, so notify it to
  // re-derive via isPlaybackConvertedUrl below.
  eventEmitter.emit("playbackConvertedUrlsUpdated");
};

// RightMenu.tsx's own playback-mode star (see playbackConvertedUrlsUpdated
// above) - kept as a plain read function rather than exporting the Set
// directly so the module retains control over its own internal state shape.
export const isPlaybackConvertedUrl = (pathName: string): boolean =>
  playbackConvertedUrls.has(pathName);

const renderClusterGoalBadge = (clusterIndex: any, pathName: string) => {
  removeClusterGoalBadge(clusterIndex);
  // Two entirely different questions depending on mode: live asks "is this
  // CURRENTLY configured as a goal" (goalsList); playback asks "did a
  // conversion actually HAPPEN on this URL, on the specific day being
  // viewed" (playbackConvertedUrls, a pure historical record unaffected by
  // later goal edits/deletes - see setPlaybackConvertedUrls's own comment).
  // Conflating them would let editing today's goals silently rewrite what
  // a past day appears to show.
  const isConverted =
    playbackModeService.mode === "playback"
      ? playbackConvertedUrls.has(pathName)
      : !!getGoalForPage(pathName);
  if (!isConverted) {
    return;
  }
  const rectNode = clusterRectInstances[clusterIndex]?.node();
  const wrapperNode = rectNode?.parentNode as Element;
  if (!rectNode || !wrapperNode) {
    return;
  }
  const rectX = +rectNode.getAttribute("x");
  const rectY = +rectNode.getAttribute("y");
  const rectWidth = +rectNode.getAttribute("width");
  const rectHeight = +rectNode.getAttribute("height");
  // Went through several rounds (solid gold outline + gold flag, a circular
  // badge, a notch/cutout with a tangent-rounded corner, gold-on-white) -
  // all flagged directly once seen live (too much added color, a
  // mismatched/wrong-way curve, gold just didn't work as a badge color at
  // all). Settled on going back toward the original treatment: no border
  // change, a plain white flag+star sitting in the bottom-right corner
  // (allowed to hang slightly outside the box, per the approved mockup),
  // just larger than the original near-invisible version and with a drop
  // shadow for definition against the white box.
  const width = 34;
  const scale = width / 15;
  const height = 19 * scale;
  // Pulled in from the box's exact bottom-right corner - direct request to
  // nudge it up and to the left "just a little" rather than hanging right
  // off the corner point. (8px was tried first and flagged as too far.)
  const iconCx = rectX + rectWidth - 4;
  const iconCy = rectY + rectHeight - 4;
  const iconGroup = d3
    .select(wrapperNode)
    .append("g")
    .attr("data-goal-page-badge", true)
    .attr(
      "transform",
      `translate(${iconCx - width / 2}, ${iconCy - height / 2}) scale(${scale})`
    )
    .attr("pointer-events", "none")
    .style("filter", "drop-shadow(0 1px 2px rgba(0, 0, 0, 0.35))");
  // This is the SAME "Combined-Shape" path from goal-star-flag.svg's own
  // source file, in full - the flag outline as one subpath, followed by a
  // SECOND subpath (a star shape) in the same `d` attribute. Confirmed
  // directly: this second subpath is what makes the star appear at all - a
  // real knocked-out cutout through the flag (fill-rule="nonzero" with the
  // two subpaths wound in opposite directions), not a separate shape
  // sitting on top. An earlier version only used the first subpath (a
  // plain solid flag, no star) and tried to fake the star back in as a
  // separate overlaid white shape - genuinely invisible against a white
  // flag, which is what prompted re-checking the source file and finding
  // this. The empty "noun-star" group elsewhere in that source file is
  // vestigial/unused - the star was never a separate element to begin
  // with.
  iconGroup
    .append("path")
    .attr("d", GOAL_FLAG_PATH)
    .attr("fill", "#fff")
    .attr("fill-rule", "nonzero");
};

// Fires the confetti burst (see playConfettiBurst) at the goal page-box's
// OWN flag position, not the converting bubble's - same icon-corner math
// renderClusterGoalBadge itself uses, so it lines up exactly with wherever
// the flag is actually rendered. Also flashes the box's own white border
// gold (see .goal-slot-flash in AnalyticsView.scss) alongside it - mocked
// up together, a flag-only flash was tried and dropped in favor of this.
const celebrateConversionAtGoalFlag = (pathName: string, visitorId?: string) => {
  // Same reasoning as disablePageView's own removal of the page-box goal
  // flag (g[data-goal-page-badge]) - single-cluster/compressed view blanks
  // every page-slot box down to one undifferentiated cluster, so there's no
  // real goal box left for a burst/flash to anchor to. Was still firing at
  // whatever coordinates the now-blank box happened to sit at, reported
  // directly as the confetti/border-flash still playing there. Real-time
  // conversion star badges are NOT gated this way (see appendGoalBadge) -
  // that's a persistent state marker that should stay visible collapsed or
  // not, unlike this transient celebration effect.
  if (pageViewDisabled) {
    return;
  }
  const page = (data || []).find(
    (p: any) => p.name === pathName && p.selected
  );
  if (!page || page.slotIndex == null || page.slotIndex >= UNKNOWN_URL_CLUSER_INDEX) {
    return;
  }
  const rectNode = clusterRectInstances[page.slotIndex]?.node();
  const wrapperNode = rectNode?.parentNode as Element;
  if (!rectNode || !wrapperNode) {
    return;
  }
  const rectX = +rectNode.getAttribute("x");
  const rectY = +rectNode.getAttribute("y");
  const rectWidth = +rectNode.getAttribute("width");
  const rectHeight = +rectNode.getAttribute("height");
  const iconCx = rectX + rectWidth - 4;
  const iconCy = rectY + rectHeight - 4;
  playConfettiBurst(iconCx, iconCy, wrapperNode, effectDim(visitorId, pathName));

  // Toggling the class (not leaving it permanently) so a second, later
  // conversion on the same box can retrigger the same CSS animation -
  // browsers don't replay an animation from a class that's already
  // applied, only on a fresh add. getBoundingClientRect forces the reflow
  // needed for remove+add to register as two separate changes.
  rectNode.classList.remove("goal-slot-flash");
  void rectNode.getBoundingClientRect();
  rectNode.classList.add("goal-slot-flash");
};

// A goal created/edited/deleted anywhere (the standalone modal, the inline
// per-row page-list button, or a future admin/other-tab surface) should
// update every currently-filled box's badge without needing a full page-
// list refetch - re-derive from `data` (pagesModel.ts's already-current
// selected-pages list), not from a stale snapshot.
eventEmitter.addListener("goalsUpdated", () => {
  (data || []).forEach((page: any) => {
    if (page.selected && page.slotIndex != null && page.slotIndex < UNKNOWN_URL_CLUSER_INDEX) {
      renderClusterGoalBadge(page.slotIndex, page.name);
    }
  });
});

// Entering playback should immediately drop any goal-page badges already
// showing from live mode - renderClusterGoalBadge now derives playback
// badges from playbackConvertedUrls instead, which playbackService.ts's
// loadDate() populates asynchronously shortly after (via
// setPlaybackConvertedUrls) once that day's history is fetched. Clearing
// here avoids a stale live badge flashing on the wrong page in the gap
// before that resolves.
eventEmitter.addListener("modeChanged", ({ mode }: any) => {
  if (mode === "playback") {
    svg?.selectAll("g[data-goal-page-badge]").remove();
  } else {
    // Coming back to live - forget the last playback day's converted-URL
    // set (irrelevant now) and restore badges for whatever's currently
    // selected, same derivation goalsUpdated's own listener above uses.
    playbackConvertedUrls = new Set();
    (data || []).forEach((page: any) => {
      if (page.selected && page.slotIndex != null && page.slotIndex < UNKNOWN_URL_CLUSER_INDEX) {
        renderClusterGoalBadge(page.slotIndex, page.name);
      }
    });
  }
});

export const fillClusterSelected = (clusterIndex: any, pathName: any) => {
  const clusterTextInstance = clusterTextInstances[clusterIndex];
  const clusterRectInstance = clusterRectInstances[clusterIndex];
  const { isWrap: wrapped, pathName: wrappedName } = wrapPath(pathName);

  // Covers callers that reassign a box WITHOUT clearing it first (e.g.
  // pagesModel.ts's setPages(), invoked whenever the page-slot config is
  // (re)fetched - including the playback-entry refresh added alongside this
  // fix) - real-text still holds the box's actual previous page here, so
  // this reassignment check works. enablePageView (clusterService.ts) is a
  // separate caller that DOES clear every box's real-text to "" first via
  // fillClusterEmpty before reassigning, which defeats this exact check (a
  // previously-cleared "" always reads as falsy) - that path has its own
  // equivalent check done before the clear happens, see enablePageView's
  // own comment.
  const previousPathName = clusterTextInstance?.attr("real-text");
  if (
    playbackModeService.mode === "playback" &&
    previousPathName &&
    previousPathName !== pathName
  ) {
    resetFlowLines();
  }

  // "/" is the homepage's path - not obviously so at a glance sitting next
  // to every other box's readable path name (e.g. "/pricing"). Same muted-
  // qualifier treatment as the Referral Link filter's own "/" row and the
  // Pages tab (see Sidebar.tsx/RightMenu.tsx) - a plain-text .text() call
  // can't mix two colors in one SVG <text> element, so this one case builds
  // it out of two <tspan>s instead; every other path keeps the normal
  // single .text() call unchanged.
  if (pathName === "/") {
    clusterTextInstance?.text("");
    clusterTextInstance?.append("tspan").text("/");
    clusterTextInstance
      ?.append("tspan")
      .attr("fill", "#9a9a9a")
      .text(` ${HOME_PAGE_NOTE}`);
  } else {
    clusterTextInstance?.text(wrappedName);
  }
  clusterTextInstance?.attr("real-text", pathName);
  clusterTextInstance?.attr("is-wrap", wrapped);
  clusterRectInstance?.style("fill", d3.rgb(255, 255, 255));
  setSlotShadow(clusterIndex, true);
  clusterRectInstance?.attr("selected", true);
  renderClusterGoalBadge(clusterIndex, pathName);
  syncCloudCounts(); // the slot just got a page: show its "N on page" even at 0
  // the boxes are what the flow counts are asked for BY NAME, so a box that now holds another page (a group took a slot, a group was renamed) is counted again at once; waiting for the
  // minute timer left the ribbon into a new group box tiny for up to a minute (Chris, 2026-10-21). Once, however many boxes change together (seedFlowsSoon debounces).
  if (previousPathName !== pathName) seedFlowsSoon();
  if (groupStack) groupStack.syncGroupStacks();
};

export const updatedClusterIndex = () => {
  return { clusterTextInstances, clusterRectInstances };
};

export const addOrRemovePageBubble = (eventName: string, pageName: string) => {
  return new Promise((resolve) => {
    // Both branches used to build their own one-off simulation scoped to
    // just the affected page's bubbles. Since the persistent simulation's
    // per-node target is resolved live from data's selected/slotIndex state
    // on every tick, a page being added/removed from the selection just
    // needs a reheat - every affected bubble (not only the ones on this one
    // page) gets pulled to its correct cluster automatically.
    syncRealtimeNodesFromBubblesData();
    reheatRealtimeSimulation();
    resolve("ok");
  });
};
// bubble data

// THE DOCK STOPS WHERE THE BOTTOM BARS START (Chris, 2026-10-24: "squeeze the historical docks' height up and down as these bottom structures move up and down"). The metrics bar, the scrub bar and the
// little collapse tab are fixed to the bottom of the window and slide (the footer collapses, the scrub bar appears in a replay), and the dock used to run underneath them. `dockCover` is how much of
// the page's bottom they cover right now; the dock's floor is the canvas bottom minus that. It is watched for: a transition on one of the bars follows them frame by frame, and a slow check catches
// the rest (a bar appearing, a window resize); the dock is re-laid out whenever the number changes. In a replay clip's viewer there are no such bars (its strip is outside the stage): the cover is 0.
let dockCover = 0;
const DOCK_BREATHING_ROOM = 17;   // the gap the dock keeps above the metrics bar on a tall window, where the canvas ends that far above it
const DOCK_COVER_SELECTORS = [".footer-main", ".scrub-bar", ".hide-footer-btn"];
const measureDockCover = (): number => {
  if (typeof document === "undefined") return 0;
  let top = Infinity;
  for (const sel of DOCK_COVER_SELECTORS) {
    const e = document.querySelector(sel);
    if (!e) continue;
    const r = e.getBoundingClientRect();
    if (r.height > 0 && r.width > 0 && r.top < window.innerHeight) top = Math.min(top, r.top);
  }
  if (!Number.isFinite(top)) return 0;
  const root = document.scrollingElement || document.documentElement;
  const scrolls = root.scrollHeight > root.clientHeight + 2;                      // a page that scrolls ends at the window's bottom
  const svgBottom = (svg?.node() as SVGSVGElement | undefined)?.getBoundingClientRect().bottom ?? window.innerHeight;

  // A page that does not scroll: the bars' top, measured from the canvas's bottom. NEGATIVE when the bars have slid BELOW the canvas (the metrics bar collapsed): the dock then runs past the canvas's bottom, down to just
  // above the bars, because the drawing does not clip (its names already hang past the bottom). The canvas itself is never made taller: that made the page scroll, which narrowed it and shifted everything left.
  if (scrolls) return Math.max(0, Math.round(window.innerHeight - top));
  const raw = Math.round(svgBottom - top);

  // (with the bars just below the canvas, as the open metrics bar is on a tall window, the dock still stops at the canvas's bottom, a few pixels above them: only a bar well below it, the collapsed one, lets the dock run on)
  return raw >= 0 ? raw : Math.min(0, raw + DOCK_BREATHING_ROOM);
};
/** the lowest y a dock bubble (or bar, or row) may reach: the canvas bottom less what the bottom bars cover, never closer than a usable band to the grid above */
export const dockFloorY = (): number =>
  Math.max(historicalBandTop + 90, gridCanvasHeight() - dockCover - (DOCK_EXTRAS && historicalLabels && historicalLayout === "cluster" ? LABEL_DROP + 6 : 0));   // (the names under a Cluster's bubbles hang below them)
const onDockCoverChanged = () => {
  refreshHistoricalCoords();
  const sim: any = activeSimulations.historical;
  const coords = clusterCenterCoords && clusterCenterCoords[HISTORICAL_CLUSTER_INDEX];
  if (sim && coords && historicalLayout === "cluster") {
    sim.force("y", d3.forceY(coords.y).strength(0.05));
    sim.alpha(Math.max(sim.alpha(), 0.25)).restart();
  }
  scheduleHistoricalRowsLayout();
};
let coverWatchUntil = 0;
let coverRaf = 0;
const coverStep = () => {
  coverRaf = 0;
  const c = measureDockCover();
  if (c !== dockCover) {
    dockCover = c;
    onDockCoverChanged();
  }
  if (performance.now() < coverWatchUntil) coverRaf = window.setTimeout(coverStep, 16) as unknown as number;   // (a timer, not a frame: a tab in the background still gets the right floor)
};
const bumpCoverWatch = (ms = 900) => {
  coverWatchUntil = Math.max(coverWatchUntil, performance.now() + ms);
  if (!coverRaf) coverRaf = window.setTimeout(coverStep, 16) as unknown as number;
};
if (typeof window !== "undefined" && typeof document !== "undefined") {
  document.addEventListener("transitionrun", (e) => { if ((e.target as Element | null)?.closest?.(DOCK_COVER_SELECTORS.join(","))) bumpCoverWatch(); }, true);
  window.addEventListener("resize", () => bumpCoverWatch(400));
  window.setInterval(() => { if (measureDockCover() !== dockCover) bumpCoverWatch(600); }, 400);
}

/**
 * Where the historical dock sits: its centre line, the band it lives in and the canvas height, from the grid and the window AS THEY ARE NOW. Every path that draws a dock bubble calls this first, because the dock's centre
 * is read from here and the numbers go stale the moment the grid or the window changes (a resize rebuilds the grid; the bulk and the live-departure paths used to draw at the OLD centre, which put the dock against, or
 * under, the grey stage - found 2026-10-23 in the replay clip viewer, where a resize wiped the dock and its rebuild drew it at the pre-resize position).
 */
let lastCoverMeasure = 0;
const refreshHistoricalCoords = () => {
  // the floor follows the bottom bars as they are right now (read at most four times a second: this runs for every dock bubble drawn)
  if (performance.now() - lastCoverMeasure > 250) { lastCoverMeasure = performance.now(); dockCover = measureDockCover(); }
  if (!clusterCenterCoords) {
    clusterCenterCoords = getClusterCoords(mainSvgWidth);
  }
  const historicalCoords = clusterCenterCoords[HISTORICAL_CLUSTER_INDEX];
  if (!historicalCoords) return;
  historicalCoords.x = gridCentreX;
  const gridBottomEdge = Number(svg?.select("#maincluster").attr("height")) || 0;
  historicalBandTop = gridBottomEdge;
  // CENTRED in the historical band, not pinned a fixed distance below the
  // grid. A fixed offset (the old +230, then briefly a +55 gap) puts the
  // cluster near the TOP of the band whenever the band is tall, which is
  // most viewports - reported directly as "not centered vertically in the
  // historical space". The band runs from the grid's real bottom edge to the
  // bottom of the SVG, and the cluster's centre belongs in the middle of it.
  historicalCoords.y =
    gridBottomEdge > 0
      ? (gridBottomEdge + dockFloorY()) / 2
      : clusterCenterCoords[UNKNOWN_URL_CLUSER_INDEX].y + 230;
  historicalCoords.exteraY =
    ((window.innerHeight * 0.9 > 500 ? window.innerHeight * 0.85 : 500) *
      0.9 -
      250) *
      (50 / 36) +
    (bubblesData.length > 20 ? 400 : 250);
  svg?.attr("height", gridCanvasHeight());
};

export const drawHistoricalBubble = async (record) => {
  if (hideUnknownBuckets && Array.isArray(record)) {
    record = record.filter((entry) => !isUnknownBucket(entry?.bubbleName));
  }
  const clusterIndex = HISTORICAL_CLUSTER_INDEX;
  if (!clusterCenterCoords) {
    clusterCenterCoords = getClusterCoords(mainSvgWidth);
  }
  const historicalCoords =
    clusterCenterCoords && clusterCenterCoords[clusterIndex];

  // This unconditional override runs on every historical-bubble draw (i.e.
  // on almost every departure), which means it's the value that ACTUALLY
  // determines the historical cluster's on-screen position - an earlier
  // attempt to fix the position by editing getClusterCoords's initial
  // mainCenters[11] had no visible effect because this always overwrites it
  // immediately afterward. Was `clusterCenterCoords[ALL_CLUSTER_INDEX].x-40`
  // (ALL_CLUSTER_INDEX=13, mainCenters[13].x = mainCenters[2].x+80) - net
  // +40px right of the grid's true center, confirmed by direct pixel
  // measurement. Briefly aliased to
  // clusterCenterCoords[UNKNOWN_URL_CLUSER_INDEX].x ("All other pages"' own
  // center) on the assumption that box WAS the grid's true horizontal
  // center - true before "All other pages" was resized to only span columns
  // 2-4, but that resize moved ITS OWN center off the true grid center too,
  // dragging the historical cluster right along with it (confirmed directly
  // - "it's off"). clusterCenterCoords[2].x (column 2, row 1's box - the
  // actual middle of all 5 columns, unaffected by that resize) is the real
  // fixed point to align to, matching the gray real-time bounding box's own
  // true center.
  // Was clusterCenterCoords[2].x - correct as "the middle of five columns",
  // meaningless at two. gridCentreX is the midpoint of the real column range,
  // so the historical cluster stays on the grid's true centre line in both
  // layouts.
  refreshHistoricalCoords();
  // historicalCoords.y/.exteraY used to be set INSIDE the per-record loop
  // below, so they only got corrected when `record` was a non-empty array.
  // Whichever code path first calls this with an empty/no array (playback's
  // startup sequence, confirmed via live inspection) leaves
  // clusterCenterCoords[11].y stuck at getClusterCoords's raw default
  // (y: 500) for the rest of that session - the real-time grid's "Last 2
  // Days" filter change always passes a real, non-empty array, so it's the
  // first call to actually reach this assignment, which is why switching to
  // it "fixed" the position: it was the first time this ever ran. Hoisted
  // out so it runs unconditionally on every call, matching how
  // historicalCoords.x already behaves - this is the formula itself,
  // unmodified, just no longer conditional on non-empty input.
  // Offset reduced from +300 - direct pixel measurement showed ~155px of
  // empty gray space between the real-time grid's bottom edge and the
  // historical cluster's top, with the bottom control bar covering the
  // lowest bubbles. First pass (+180) went up too far per direct feedback -
  // +230 pulls it back down partway.
  // Was a hardcoded [10] - see historicalCoords.x's own comment above for
  // why this now reads UNKNOWN_URL_CLUSER_INDEX (12) instead.
  // 2026-09-09: the +230 below was measured against ONE box geometry and
  // silently broke when the row pitch changed - raising the boxes' height
  // pushed the whole historical zone down with it, until the empty-state
  // text was 39px off the footer. It is now derived from the grid's REAL
  // bottom edge (#maincluster's own height, which createClusterRect sizes
  // from the content) plus one named gap, so any future geometry change
  // moves the grid without dragging the historical cluster along.
  // The old formula stays as the fallback for the case it was written for:
  // this running before the grey panel has been drawn at all.
  // (the centre, band and canvas height are refreshed by refreshHistoricalCoords() above)
  /*
   * ONE shared scale across every historical bubble on screen - not the max of
   * whatever payload happens to be arriving.
   *
   * This used to read only from `record`, so an incremental update carrying a
   * SINGLE category made that category its own maximum and therefore the
   * largest bubble on screen, regardless of its real count. Reported as
   * countries with 11 and 20 sessions dwarfing one with 52: each had simply
   * been drawn in its own update. Relative size is the entire point of sizing
   * these bubbles, and it is meaningless unless every bubble is measured
   * against the same denominator.
   *
   * getMaxHistoricalUserValue() reads the live bubblesData, so it accounts for
   * categories already drawn; the payload is folded in for any arriving value
   * that exceeds them.
   */
  const payloadMax = Math.max(
    ...record.map((item: any) => (Number.isFinite(+item.userValue) ? +item.userValue : 0)),
    1
  );

  /*
   * A FULL refresh defines the scale by itself; an INCREMENTAL update must not.
   *
   * Two different callers reach here. A full refresh (the backend's whole
   * getHistoricalBubblesData payload) carries every category, so its own max IS
   * the true maximum - and it must be used alone, because bubblesData can still
   * hold entries from the PREVIOUS filter whose counts are much larger. Folding
   * those in measured the new filter's bubbles against a stale inflated maximum
   * and collapsed every one of them to the floor ("no large historical icons"
   * after switching to Country - a regression from the first version of this
   * fix, which folded them in unconditionally).
   *
   * A single-category incremental update is the opposite problem: on its own it
   * would make that one category its own maximum and therefore the largest
   * bubble on screen regardless of its real count (Mexico at 11 dwarfing
   * Singapore at 52). That case genuinely needs the existing bubbles' max.
   *
   * record.length is what distinguishes them: one entry means an incremental
   * seed/update, more than one means a real refresh.
   */
  const isFullRefresh = record.length > 1;
  const maxUserValue = isFullRefresh
    ? payloadMax
    : Math.max(payloadMax, getMaxHistoricalUserValue());
  for (let i = 0; i < record.length; i++) {
    const { bubbleName, userValue, converted } = record[i];

    const radius = getHistoricalBubbleRadius(userValue, maxUserValue);

    const currentBubble = getItemByName(bubbleName);
    bubblesData = bubblesData.filter((value, index, self) => {
      return self.indexOf(value) === index;
    });
    // Same duplicate-dock-circle race drawNewHistoricalBubble guards against
    // (see its comment) - skip the push if this category's placeholder
    // already exists instead of adding a second bubblesData entry with the
    // same menuName, which the d3 data-join keys on and would otherwise
    // render as a second, permanently-colliding DOM circle.
    if (
      bubblesData.some(
        (item) => item?.historical === true && item.menuName === bubbleName
      )
    ) {
      continue;
    }
    const bubble = {
      x: historicalCoords?.x,
      y: historicalCoords?.y,
      radius,
      color: currentBubble?.color,
      colorInfo: currentBubble?.colorInfo,
      cluster: clusterIndex,
      historical: true,
      id: clusterIndex,
      menuName: bubbleName,
      user: userValue,
      converted: converted ?? 0,
      currentUrlName: "menu-nodes",
    };
    bubblesData.push(bubble);
  }

  drawBubbleNow(historicalCoords);

  /*
   * One icon per historical bubble. Enforced at the call site because
   * drawBubbleNow ends inside a d3 chain, and because this is the path a filter
   * change and every data refresh actually take - earlier placements in
   * redrawHistoricalBubbles/drawHistoricalBubble never ran (measured: Country ->
   * OS -> Country still gave 159 images for 101 shapes, and a single freshly
   * drawn bubble arrived with two icons).
   *
   * Matters for rendering cost, not tidiness: while dragging, the per-frame
   * expense is browser style/layout/paint over every moving element - zero long
   * tasks were recorded despite 90ms frames - and each surplus <image> is one
   * more flag re-rasterised every tick.
   */
  dedupeHistoricalIcons();
};

// Shared by both historical-dock force simulations below (drawBubbleNow and
// drawNewHistoricalBubble each spin up their own d3.forceSimulation, but tick
// identical logic) - same rationale as paintRealtimeBubblePositions' own
// rewrite: a fresh d3.selectAll()/.attr() chain allocates its own Selection
// wrapper every tick regardless of element count, a real sustained cost
// while a dock reflow's ticks are firing. Plain querySelectorAll/setAttribute
// does the identical DOM update without that per-tick wrapper allocation.
/*
 * Guarantee ONE icon per historical bubble, after the fact.
 *
 * appendIconImage already removes a previous icon before appending, and all
 * three historical call sites pass identical identifiers ("historical", "name",
 * d.menuName) - yet the DOM still ends up with exactly two images per category
 * (measured 2026-07-30: 159 images for 101 shapes, 60 names doubled). The cause
 * of the miss has not been found, so this enforces the invariant at the end of
 * the draw instead of trusting the append path.
 *
 * Worth fixing for rendering cost, not tidiness: the per-frame expense while
 * dragging is browser style/layout/paint over every moving element (zero long
 * tasks were recorded despite 90ms frames), and each surplus <image> is another
 * flag re-rasterised on every tick. 58 of 159 is roughly a third of the image
 * work for free.
 *
 * Runs once per historical draw, never per tick.
 */
const dedupeHistoricalIcons = () => {
  const svgNode = svg?.node() as SVGSVGElement | undefined;
  if (!svgNode) {
    return 0;
  }
  const seen = new Set<string>();
  const surplus: Element[] = [];
  const images = Array.from(
    svgNode.querySelectorAll("image[data-bubble-flag][historical]")
  );
  // Walk backwards so the LAST appended icon (the freshest, correctly sized for
  // the current mode) is the one kept.
  for (let i = images.length - 1; i >= 0; i--) {
    const name = images[i].getAttribute("name") || "";
    if (!name) {
      continue;
    }
    if (seen.has(name)) {
      surplus.push(images[i]);
    } else {
      seen.add(name);
    }
  }
  surplus.forEach((el) => el.remove());
  return surplus.length;
};

const tickHistoricalBubblePositions = () => {
  const svgNode = svg?.node() as SVGSVGElement | undefined;
  if (!svgNode) {
    return;
  }
  const nodes = svgNode.querySelectorAll<SVGGraphicsElement>(
    "circle[historical=true], rect[historical=true], image[historical=true]"
  );
  for (const node of nodes) {
    const d = (node as any).__data__;
    if (!d) {
      continue;
    }
    // Clamp the simulation's own coordinate in place (not just the rendered
    // attr) so the *next* tick's collision force resolves overlaps using the
    // same bounded position - otherwise the clamp and the physics fight each
    // other and bubbles stack at the edge.
    d.x = clampBubbleCenter(d.x, d.radius, mainSvgWidth - 10);
    d.y = clampBubbleCenterRange(d.y, d.radius, historicalBandTop, dockFloorY());
    node.setAttribute("cx", d.x);
    node.setAttribute("cy", d.y);
    const width = +node.getAttribute("width") || 0;
    const height = +node.getAttribute("height") || 0;
    const offsetX = +node.getAttribute("data-offset-x") || 0;
    const offsetY = +node.getAttribute("data-offset-y") || 0;
    node.setAttribute("x", String(d.x - width / 2 + offsetX));
    node.setAttribute("y", String(d.y - height / 2 + offsetY));
  }
  // the names under a Cluster's bubbles follow them while they settle; once they have, the labels are re-made for the new sizes and the ones that would cover another bubble or label are left off
  if (historicalLayout === "cluster" && DOCK_EXTRAS && historicalLabels) {
    if (dockLabelsLive) positionHistoricalLabels(svgNode);
    scheduleClusterLabelWork();
  }
};

/** with names under the bubbles, a bubble needs room for its name: half the name's width, where that is more than the bubble's own radius (so the names do not sit on their neighbours) */
const clusterLabelExtra = (d: any): number =>
  DOCK_EXTRAS && historicalLabels && historicalLayout === "cluster" ? Math.max(0, Math.min(58, DOCK_LABEL_MEASURE(fitLabel(dockCategoryName(d), 112, DOCK_LABEL_MEASURE)) / 2 + 4) - (+d.radius || 0)) : 0;

let clusterLabelTimer: ReturnType<typeof setTimeout> | null = null;
const scheduleClusterLabelWork = () => {
  if (clusterLabelTimer) clearTimeout(clusterLabelTimer);
  clusterLabelTimer = setTimeout(() => { clusterLabelTimer = null; syncClusterDockLabels(); }, 280);
};
/** the names under the Cluster's bubbles (Display settings > Labels): one per category, hung under its bubble, and only the ones that sit clear of every other bubble and label */
const syncClusterDockLabels = () => {
  const svgNode = svg?.node() as SVGSVGElement | undefined;
  if (!svgNode) return;
  if (!(DOCK_EXTRAS && historicalLabels && historicalLayout === "cluster")) {
    if (historicalLayout === "cluster" && dockLabelsLive) { removeHistoricalLabels(svgNode); dockLabelsLive = false; }

    return;
  }
  const nodes = bubblesData.filter((item) => item?.historical === true && item.menuName);
  syncHistoricalLabels(
    svgNode,
    nodes.map((d) => ({ key: String(d.menuName), datum: d, name: dockCategoryName(d), halfHeight: dockBubbleHalfHeight(d), room: 112 })),
    DOCK_LABEL_MEASURE,
    LABEL_DROP
  );
  dockLabelsLive = true;
  declutterClusterLabels(svgNode, nodes);
};
const declutterClusterLabels = (svgNode: SVGSVGElement, nodes: any[]) => {
  const texts = Array.from(svgNode.querySelectorAll<SVGTextElement>("g.historical-labels text"));
  texts.forEach((t) => t.removeAttribute("display"));
  const circles = nodes.map((d) => ({ d, x: Number(d.x) || 0, y: Number(d.y) || 0, r: Math.max(4, +d.radius || 0) }));
  const boxes = texts
    .map((t) => ({ t, key: t.getAttribute("data-key") || "", box: t.getBBox() }))
    .sort((a, b) => (circles.find((c) => String(c.d.menuName) === b.key)?.r || 0) - (circles.find((c) => String(c.d.menuName) === a.key)?.r || 0));
  const placed: Array<{ x0: number; x1: number; y0: number; y1: number }> = [];
  for (const { t, key, box } of boxes) {
    const r = { x0: box.x - 2, x1: box.x + box.width + 2, y0: box.y - 1, y1: box.y + box.height + 1 };
    const hitsLabel = placed.some((p) => r.x0 < p.x1 && r.x1 > p.x0 && r.y0 < p.y1 && r.y1 > p.y0);
    const hitsBubble = circles.some((c) => {
      if (String(c.d.menuName) === key) return false;
      const nx = Math.max(r.x0, Math.min(c.x, r.x1));
      const ny = Math.max(r.y0, Math.min(c.y, r.y1));

      return (nx - c.x) ** 2 + (ny - c.y) ** 2 < c.r * c.r;
    });
    if (hitsLabel || hitsBubble) t.setAttribute("display", "none");
    else placed.push(r);
  }
};

const drawBubbleNow = (historicalCoords) => {
  const bubbleParametersData = returnBubbleParameterData();

  const historicalBubbleData = bubblesData.filter(
    (item) => item?.historical === true && item.menuName
  );

  const historicalSimulation = d3
    .forceSimulation()
    .force(
      "collision",
      d3
        .forceCollide()
        .radius(
          (d: any) =>
            getHistoricalCollisionRadius(+d.radius) +
            getHistoricalCollisionPadding() +
            clusterLabelExtra(d)
        )
        .strength(1)
        .iterations(getHistoricalCollisionIterations())
    )
    .force(
      "charge",
      d3
        .forceManyBody()
        // Historical cluster's own spacing setting, independent of the
        // page-cluster "charge" setting (see DEFAULT_D3CIRCLE_CONSTRAINT's
        // own comment).
        .strength(
          bubbleParametersData && bubbleParametersData.historicalCharge
            ? parseInt(bubbleParametersData.historicalCharge)
            : DEFAULT_D3CIRCLE_CONSTRAINT.historicalCharge
        )
    )
    .force("gravity", d3.forceManyBody().strength(0.2))
    .force("x", d3.forceX(historicalCoords?.x).strength(0.05))
    .force("y", d3.forceY(historicalCoords?.y).strength(0.05))
    .on("tick", () => tickHistoricalBubblePositions());

  stopPreviousSimulation("historical", historicalSimulation);
  historicalSimulation.nodes(historicalBubbleData).alpha(0.9).restart();
  if (dockPlacesItself()) {
    historicalSimulation.stop();   // the rows and bars layouts place the bubbles themselves; the physics stays out of it
    scheduleHistoricalRowsLayout();
  }

  svg
    ?.selectAll("circle[historical=true], rect[historical=true]")
    .data(historicalBubbleData, (d: any) => d.menuName)
    .enter()
    .append(getBubbleShapeTag())
    .attr("data-bubble", true)
    .attr("width", (d: any) => getRectSizeFromRadius(d.radius, getActiveIconAspect()).width)
    .attr("height", (d: any) => getRectSizeFromRadius(d.radius, getActiveIconAspect()).height)
    .attr("cx", (d) => d.x)
    .attr("cy", (d) => d.y)
    .attr("r", (d) => d.radius)
    .attr("x", rectXFromCenter((d: any) => d.x))
    .attr("y", rectYFromCenter((d: any) => d.y))
    .attr("rx", 1)
    .attr("id", historicalBubbleData.length - 1)
    .attr("historical", true)
    .style("filter", useIcons ? null : "url(#historical-bubble-shadow)")   // icon mode: this shape is transparent (the icon image carries the shadow), so its filter paints nothing and only costs frames - see the note above DOCK_SHADOW

    .attr("stroke-width", useIcons ? 0 : 1)
    .attr("class", "sub_circle")
    .attr("show-menu", (d) => d.name)
    .attr("user", (d) => d.user)
    .attr("sub-menu-flag", true)
    .attr("remove", "no")
    .style("stroke", (d) => getBubbleStroke(d))
    .attr("name", (d) => d.menuName)
    .style("fill", (d: any) => getHistoricalBubbleFill(d))
    .on("mouseover", (d) => showHistoricalTooltip(d))
    .on("mouseout", () => hideHistoricalTooltip())
    .on("click", (event, d) => handleHistoricalBubbleClick(d))
    .call(historicalDragBehavior())
    .each(function (d: any) {
      const iconUrl = getHistoricalIconUrl(d);
      if (iconUrl) {
        appendIconImage(
          this,
          d,
          iconUrl,
          getRectSizeFromRadius(d.radius, getActiveIconAspect()).width,
          getActiveIconImageAspect(),
          "historical",
          "name",
          d.menuName
        );
      }
      // Mirrors the real-time bubble's own star badge (drawUserBubble et al)
      // - a historical dock category with one or more conversions gets the
      // same star, sized off this bubble's own d.radius (see
      // resolveGoalBadgeRadius) since dock bubbles vary in size by visitor
      // count, unlike real-time bubbles' fixed per-mode radius.
      if (d.converted > 0) {
        appendGoalBadge(this, d, "historical", "name", d.menuName);
      }
    });
  if (historicalLayout === "cluster") scheduleClusterLabelWork();
};
// d3.schemeCategory10 alone is only 10 colors - a "by country" list with
// more than 10 distinct values (common, easily 15-20+) silently started
// reusing colors past that point, reported as clusters becoming visually
// indistinguishable once the list got long. The user pointed directly at
// Tableau's classic "Tableau 20" categorical palette as the reference for
// what a good one looks like - the first 10 entries here are its
// dark/saturated half (identical to d3.schemeTableau10, each only
// minimally darkened as needed to clear 3:1 contrast against white). Just
// those 10 weren't enough headroom on their own (reported directly: with a
// realistic ~15-20 country list, all 10 slots fill up and the
// collision-bumping logic in createRealtimeListItem runs out of room,
// producing real repeats again) - the second 10 are Tableau 20's other
// half, each darkened from its original pastel tint to the SAME lightness
// tier as its paired dark color above (not left pastel, which is why it
// was excluded the first time) so every hue family now contributes two
// genuinely distinguishable-by-lightness shades. Two additional near-black
// grayscale entries were tried and dropped - too harsh/heavy next to the
// rest of the palette's mid-range tones.
const EXTENDED_COLOR_PALETTE: string[] = [
  /*
   * 🔴 THE BRAND BLUE, NOT TABLEAU'S (2026-09-11).
   *
   * Was #1f77b4 - Tableau 10's blue, inherited when this palette was lifted
   * wholesale. Reported directly: "the country blue is DIFFERENT than all other
   * blues" - and it was. Skydive already had three blues ($primary #0b506e,
   * $sky #2f6eba, $link-color #337ab7) and this was a fourth, sitting a few
   * degrees off all of them. Close enough to read as a mistake rather than a
   * choice.
   *
   * #2f6eba is $sky from Variables.scss, so every blue in the product now comes
   * from the brand set. It is also BETTER contrast than what it replaces:
   * 5.17:1 on white versus 4.82:1.
   *
   * $primary was rejected: at 8.8:1 it is nearly the footer metrics bar, and a
   * category swatch must not read as chrome. $link-color was rejected as too
   * close to the Tableau blue to fix what was actually being seen.
   *
   * This is slot 0, which ALL_VISITORS_CATEGORY is pinned to - so "No Filter",
   * the most-seen swatch in the product, changes with it. Deliberate.
   */
  /*
   * Slot 0 is what ALL_VISITORS_CATEGORY ("No Filter") is pinned to. It holds
   * the NAVY, and the brand blue sits at slot 8 - see that slot's comment for
   * why the two were swapped rather than one of them re-picked.
   */
  "#14477d", // navy
  "#d2650b", // orange (darkened from #ff7f0e for contrast)
  "#2ca02c", // green
  "#d62728", // red
  "#9467bd", // purple
  "#8c564b", // brown
  "#c05aa4", // pink (darkened from #e377c2 for contrast)
  "#7f7f7f", // gray
  /*
   * 🔴 WAS OLIVE (#8f9018). REPLACED 2026-09-10.
   *
   * Reported twice, the second time emphatically: the olive swatch looked bad
   * wherever it landed - bubble, sidebar dot, tooltip header, segment panel -
   * and because it sits early in the palette it landed often. Replaced rather
   * than avoided, so it is gone from EVERY surface that reads the palette
   * instead of being special-cased at one call site.
   *
   * Navy: contrast 9.4:1 on white (the olive it replaces was 3.4:1), and it is
   * separated from the other blues by LIGHTNESS, which is how this palette
   * already distinguishes its paired hue families - #1f77b4 mid, #6b8fb0
   * muted, this one dark.
   */
  /*
   * 🔴 THE BRAND BLUE LIVES HERE ON PURPOSE (2026-09-11).
   *
   * This slot was olive, then navy, and is now $sky #2f6eba - because this is
   * the slot "United States" hashes to, and on a real customer's board the
   * dominant country is the swatch they look at all day.
   *
   * Colours are assigned by hashing the category NAME, which is what makes them
   * stable across reloads - so WHICH slot a category gets cannot be chosen. The
   * only lever is what each slot CONTAINS. Putting the brand blue at slot 0 was
   * therefore the wrong fix: slot 0 is "All visitors", and "United States" kept
   * the heavy navy. Reported directly: "I thought you were going to fix that
   * dark blue for the USA in the country list?"
   *
   * Swapped rather than re-picked, so the palette keeps exactly the same 18
   * colours and nothing else moves. Navy took slot 0, where it suits the
   * single-swatch No-Filter view.
   */
  "#2f6eba", // brand blue ($sky)
  "#0f8f9c", // cyan (darkened from #17becf for contrast)
  "#6b8fb0", // steel blue (Tableau 20's light blue, darkened)
  "#a9793f", // tan (Tableau 20's light orange, darkened)
  "#5f9c5b", // sage green (Tableau 20's light green, darkened)
  "#c05a5a", // dusty rose (Tableau 20's light red, darkened)
  "#8272a8", // mauve (Tableau 20's light purple, darkened)
  "#9c7e77", // taupe (Tableau 20's light brown, darkened)
  "#c25f9e", // rose pink (Tableau 20's light pink, darkened)
  /*
   * WAS OLIVE DRAB (#8c8d3a) - the second olive, replaced for the same reason.
   * Azure at 4.0:1, a clear step lighter than the navy above and darker than
   * the steel blue, so all four blues stay tellable apart.
   */
  "#2e86c1", // azure (replaced olive drab)
];

// "No Filter" collapses every bubble into the single ALL_VISITORS_CATEGORY
// ("All visitors") - hashNameToColorIndex previously assigned it whatever
// slot that string happened to hash to (landed on the brown swatch above),
// which read as an arbitrary/unpolished default since it's the ONLY
// category shown in this mode, not one of many. Pinned to the palette's own
// "blue" swatch (index 0, "#1f77b4" above) instead of relying on the hash.
const ALL_VISITORS_COLOR_INDEX = 0;

// A session genuinely missing the currently-selected filter's field (e.g.
// no country_name under "By Country") gets no sidebar category at all - but
// its bubble still needs SOME color, and leaving its previous filter's
// name/color cached was the actual bug (reported as a phantom "All
// visitors" blue entry surviving into "By Country", and more generally as
// "filters mixing" - any stale name from a prior filter could survive the
// same way, not just "All visitors"). Palette's own neutral gray swatch,
// not a hash-derived color, so it reads as "uncategorized" rather than
// coincidentally matching some real category's color.
const UNCATEGORIZED_COLOR_INDEX = 7;

// A REAL, stable string, not undefined - bubble.name isn't just a sidebar
// label, moveBubbleToHistoricalCluster uses it as the actual grouping key
// for the historical dock (DOM selector `circle[name='...']`, `item.menuName
// === userBubble.name`, per-category conversion tallying). Setting it to
// undefined (an earlier version of this fix) made every uncategorized
// bubble's departure collapse into one bogus shared dock bucket keyed by the
// literal string "undefined" - reported directly as bubbles not closing
// properly and conversion/metric totals coming out wrong. Deliberately NOT
// added to realtimeMenuItems/the visible sidebar list (matching the
// original "shouldn't get its own fake category" intent) - this is purely
// an internal bookkeeping key so the dock still groups these bubbles
// correctly, just without a corresponding sidebar row.
const UNCATEGORIZED_CATEGORY = "Uncategorized";

// Deterministic string hash (djb2 variant) - same name always produces the
// same index into EXTENDED_COLOR_PALETTE, with no dependency on WHEN or in
// what ORDER that name was first encountered. This is what actually fixes
// color stability: the previous scheme assigned colors by first-seen order
// (see categoryColorAssignments' own comment for the half of this bug that
// was already patched for playback seeks specifically), which meant the
// same country got a different color depending on live socket-arrival
// order vs playback's precomputed schedule order vs which order the
// sidebar happened to discover categories in after a filter switch -
// reported directly as colors changing "all the time" across modes/views.
// A pure function of the name has no such dependency to begin with.
const hashNameToColorIndex = (name: string): number => {
  let hash = 5381;
  for (let i = 0; i < name.length; i++) {
    hash = (hash * 33) ^ name.charCodeAt(i);
  }
  return Math.abs(hash) % EXTENDED_COLOR_PALETTE.length;
};

export const setColor = (colorKey: any) => {
  // a brand's own colour (brandColours.ts): indexes at or past BRAND_COLOUR_BASE are not slots of the main palette
  if (typeof colorKey === "number" && colorKey >= BRAND_COLOUR_BASE) {
    const brandHex = brandColourHex(colorKey);
    if (brandHex) {
      return d3.rgb(brandHex);
    }
  }
  const index =
    typeof colorKey === "string"
      ? hashNameToColorIndex(colorKey)
      : ((colorKey % EXTENDED_COLOR_PALETTE.length) + EXTENDED_COLOR_PALETTE.length) %
        EXTENDED_COLOR_PALETTE.length;
  return d3.rgb(EXTENDED_COLOR_PALETTE[index]);
};

// The ambient dock rings (ambientLife.ts) need a visible colour for an ICON-mode dock shape, whose own fill is "transparent": the category colour, resolved exactly as the
// bubble's fill would have been.
ambientLife.setDockColourResolver((datum: any) => (datum && datum.color !== undefined && datum.color !== null ? String(setColor(datum.color)) : ""));

// Reads the SAME collision-probed slot the sidebar assigned a category
// (categoryColorAssignments), rather than re-hashing the name from scratch -
// setColor(name) alone would drift from a bubble's actual on-screen color
// whenever that name's raw hash lost a collision and got probed to a
// different slot. Falls back to the raw hash only for a name that hasn't
// been assigned a slot yet (e.g. resolving a session row's color before its
// category has ever appeared in the currently-built sidebar).
// Shared by createRealtimeListItem (building a sidebar row) and
// getCategoryColor (resolving a bubble's fill) - both need to agree on
// exactly the same slot for a given name, or a bubble can render one color
// while its sidebar row swatch shows another. This used to only live inside
// createRealtimeListItem; getCategoryColor had its own separate fallback
// (a raw, uncached hash) for any name not yet in categoryColorAssignments,
// which diverges from this collision-probed value the moment two names hash
// to the same slot - reported directly as "bubbles are a different color"
// than what the Traffic Source sidebar list showed right after a filter
// switch, before the sidebar had gotten a chance to populate the cache
// first.
const assignColorForCategory = (name: string): number => {
  // "no value" / "Unknown" is always the one slate grey (brandColours.ts), whatever the filter
  if (name === NO_CATEGORY_DATA || /^unknown$/i.test(name)) {
    return NO_DATA_COLOUR_INDEX;
  }
  // a search engine or social network wears its own brand colour; never cached, so the same word in another filter is unaffected
  const brandIndex = brandColourIndex(selectedFilter?.menuType, name);
  if (brandIndex !== undefined) {
    return brandIndex;
  }
  let assignedColor = categoryColorAssignments[name];
  if (assignedColor == null && name === ALL_VISITORS_CATEGORY) {
    assignedColor = ALL_VISITORS_COLOR_INDEX;
    categoryColorAssignments[name] = assignedColor;
  }
  if (assignedColor == null) {
    /*
     * 🔴 HASH THE NAME. STABILITY IS THE POINT (settled 2026-09-11).
     *
     * A category's colour is derived from its NAME, so "United States" is the
     * same colour on every reload, in every session, on every machine. Chris,
     * deciding between the two: "we need stability in color, for sure."
     *
     * This briefly became order-of-appearance assignment instead, to guarantee
     * the first category got the brand blue. That fixed the wrong problem. The
     * complaint was never the hashing - it was that one of the 18 slots was an
     * ugly OLIVE and common names kept landing on it. Olive is now deleted from
     * the palette (replaced by navy and azure), so every slot a name can hash to
     * is a colour that has been approved. Order-based assignment also cost
     * cross-reload stability, which is worse: a colour that only means something
     * within one page load does not really mean anything.
     *
     * WHAT THIS DOES NOT FIX, deliberately: with more than 18 categories there
     * are more names than slots, so the tail repeats. Stability and uniqueness
     * are separate problems and only the second one needs a bigger palette. A
     * user-selectable palette is the eventual direction (Chris, 2026-09-11) -
     * see the punch list.
     */
    const activeColors = new Set<number>([
      ...items.map((item) => item.color),
      // Everything already handed out, not just what the sidebar currently
      // lists - `items` can lag behind a filter switch, and probing against a
      // stale set is how two visible categories ended up sharing a swatch.
      ...Object.values(categoryColorAssignments),
    ]);
    let candidate = hashNameToColorIndex(name);
    let attempts = 0;
    while (activeColors.has(candidate) && attempts < EXTENDED_COLOR_PALETTE.length) {
      candidate = (candidate + 1) % EXTENDED_COLOR_PALETTE.length;
      attempts++;
    }
    assignedColor = candidate;
    categoryColorAssignments[name] = assignedColor;
  }
  // remembered per filter, and kept across filter switches (categoryColorAssignments is cleared on a switch): lets a place that draws another filter's icon (the drawer's
  // breakdown, the Sessions rows) use the colour that category really has in its own filter
  if (selectedFilter?.menuType) {
    menuColourRecord[`${selectedFilter.menuType}:${name}`] = assignedColor;
  }
  return assignedColor;
};

const menuColourRecord: { [menuAndName: string]: number } = {};
/**
 * The colour index of a category as it appears IN ITS OWN FILTER, wherever it is drawn: the active filter's live assignment; for another filter, the brand / "not there" /
 * remembered colour, else the plain hash of the name (what the assignment starts from before it probes past a clash).
 */
const colourIndexFor = (menuType: string | undefined, name: string): number => {
  if (!menuType || menuType === selectedFilter?.menuType) {
    return assignColorForCategory(name);
  }
  if (name === NO_CATEGORY_DATA || /^unknown$/i.test(name)) {
    return NO_DATA_COLOUR_INDEX;
  }

  return brandColourIndex(menuType, name) ?? menuColourRecord[`${menuType}:${name}`] ?? hashNameToColorIndex(name);
};

export const getCategoryColor = (name: string | undefined) => {
  if (!name) {
    return setColor(0);
  }
  return setColor(assignColorForCategory(name));
};

// Historical bubbles grouped "by country" are keyed by country display name
// (e.g. "United States"), not ISO code, so we need a name -> code lookup to
// reuse the same flag-icons CDN pattern used for real-time bubbles.
//
// Built from the flag-icons package's own country.json (already a direct
// dependency - it's the exact same data source getFlagIconUrl's CDN URLs
// point at) instead of a hand-maintained list: that previously covered only
// 52 countries, so most real-world traffic outside a handful of large
// markets silently fell back to a plain dot. This covers all 267 ISO
// entries the package ships. Keyed lowercased since resolveIconUrl also
// lowercases before looking up.
const FLAG_ICON_COUNTRY_NAME_TO_CODE: { [name: string]: string } = {};
(flagIconCountries as Array<{ name: string; code: string }>).forEach(
  (entry) => {
    FLAG_ICON_COUNTRY_NAME_TO_CODE[entry.name.toLowerCase()] =
      entry.code.toUpperCase();
  }
);

// A handful of common real-world geo-IP display names that don't exactly
// match flag-icons' own (more formal/official) naming convention - e.g. the
// backend's geo-IP provider (UtilityApi/Services/location.ts) returns
// "United States", but flag-icons' own entry is "United States of America".
// Layered on top of the full ISO list above so the common everyday spelling
// still resolves without needing to guess every possible provider's exact
// wording for every country.
const COUNTRY_NAME_ALIASES: { [name: string]: string } = {
  "united states": "US",
  usa: "US",
  "ivory coast": "CI",
  brunei: "BN",
  swaziland: "SZ",
  burma: "MM",
  "cape verde": "CV",
  vatican: "VA",
  "vatican city": "VA",
};

export const COUNTRY_NAME_TO_CODE: { [name: string]: string } = {
  ...FLAG_ICON_COUNTRY_NAME_TO_CODE,
  ...COUNTRY_NAME_ALIASES,
};

// Flag-mode bubbles are <rect> elements (so they visually match the flag's
// rectangular aspect ratio) while normal bubbles stay <circle>. Anything that
// reads a bubble's center point off the DOM (e.g. the session-close transition
// into the historical cluster) needs to work for either shape.
const getShapeCenter = (node: any) => {
  if (!node) {
    return { cx: 0, cy: 0 };
  }
  if (node.hasAttribute("cx")) {
    return {
      cx: +node.getAttribute("cx"),
      cy: +node.getAttribute("cy"),
    };
  }
  const x = +node.getAttribute("x") || 0;
  const y = +node.getAttribute("y") || 0;
  const width = +node.getAttribute("width") || 0;
  const height = +node.getAttribute("height") || 0;
  return { cx: x + width / 2, cy: y + height / 2 };
};

// Every tick handler positions bubbles via cx/cy (for <circle>); these two
// wrap the same accessor so <rect> bubbles (flag mode) stay centered on the
// exact same coordinate via x/y. Setting cx/cy on a <rect> (or x/y on a
// <circle>) is simply ignored by the browser, so applying all four attrs on
// a mixed circle+rect selection is safe.
// data-offset-x/y let a specific element (e.g. the icon-mode goal badge,
// see appendGoalBadge) sit off-center from d.x/d.y every tick instead of
// dead-center - optional, defaults to 0 so every other rect/circle/image
// this already positions is unaffected.
const rectXFromCenter = (xFn: (d: any) => number) =>
  function (this: SVGGraphicsElement, d: any) {
    const width = +this.getAttribute("width") || 0;
    const offsetX = +this.getAttribute("data-offset-x") || 0;
    return xFn(d) - width / 2 + offsetX;
  };
const rectYFromCenter = (yFn: (d: any) => number) =>
  function (this: SVGGraphicsElement, d: any) {
    const height = +this.getAttribute("height") || 0;
    const offsetY = +this.getAttribute("data-offset-y") || 0;
    return yFn(d) - height / 2 + offsetY;
  };

// Historical bubbles vary in size (bigger for higher-traffic countries), and
// the force simulation's collision/gravity forces don't know about the SVG's
// own edges - with enough bubbles or a large enough radius, some can get
// pushed past the bottom of the visible canvas. Clamping the rendered
// position (not the simulation's own d.x/d.y, so the physics keeps running
// normally) guarantees every bubble stays fully inside its container.
// Same as clampBubbleCenter but with a real lower bound as well as an upper
// one. Pure arithmetic, no DOM access: it runs per node, per tick, on the
// historical simulation's hot path (see this file's perf gotcha #5).
const clampBubbleCenterRange = (
  value: number,
  radius: number,
  min: number,
  max: number
) => {
  const padding = radius + 2;
  if (max - min <= padding * 2) {
    // 🔴 This used to `return (min + max) / 2`, copied from clampBubbleCenter
    // where a single max makes that harmless. With a RANGE it is not: every
    // node gets the SAME y, so the whole historical cluster collapses onto
    // one line - observed directly as the dock rendering as a straight
    // horizontal row of bubbles during a reflow, when the band is briefly
    // too short to clamp into.
    //
    // A clamp that cannot be satisfied must not invent a position. Leave the
    // simulation's own value alone and let the physics resolve it; the band
    // is transient and the next tick clamps normally once it has real height.
    return value;
  }
  return Math.min(Math.max(value, min + padding), max - padding);
};

const clampBubbleCenter = (value: number, radius: number, max: number) => {
  const padding = radius + 2;
  if (max <= padding * 2) {
    return max / 2;
  }
  return Math.min(Math.max(value, padding), max - padding);
};

// SVG <pattern> fills kept distorting icons no matter how the internal
// coordinate system was described (objectBoundingBox content either stretched
// to a square or, when sized in absolute pixels, escaped the tile and only a
// tiny corner showed through as a solid color). A real <image> element sized
// with the icon's true aspect ratio - the exact same technique the sidebar
// list and sessions drawer already use via <img>/react-country-flag - has no
// ambiguity: it just renders like a normal image. Each bubble that resolves
// to an icon gets one of these as a sibling, kept positioned on top of it.
//
// Was a live cdn.jsdelivr.net URL - every single country-flag bubble
// (any onsite session in "By Country" icon mode) depended on a real external
// network round-trip, unlike every other filter's icons (os/browser/device/
// etc.), which are already bundled locally via getLocalIconUrl. On a cold
// cache this meant up to one external fetch per distinct country PER
// reconstruction (every playback scrub wipes and redraws every bubble from
// scratch - see resetVisualizationState), and made the whole feature
// fragile against CDN latency/downtime/ad-blockers for something that should
// be instant. `flag-icons` (the same package already used above for
// flagIconCountries' name/code data) ships the actual SVGs locally, so
// bundling them the exact same require()-based way getLocalIconUrl already
// does removes the external dependency entirely - same pattern, same
// try/catch-as-fallback, just a different source directory.
export const getFlagIconUrl = (countryCode: string): string | undefined => {
  try {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    return require(`flag-icons/flags/4x3/${countryCode.toLowerCase()}.svg`);
  } catch (error) {
    return undefined;
  }
};

const getFaviconUrl = (domain: string) =>
  `https://www.google.com/s2/favicons?sz=128&domain=${domain}`;
const getDuckFaviconUrl = (domain: string) => `https://icons.duckduckgo.com/ip3/${domain}.ico`;

// ── no blurry globes, no coloured squares: every filter value shows a CRISP icon ──
// A domain's icon comes from the first of these that is big enough to look sharp; nothing is trusted until it has been LOADED and MEASURED:
//   1. Google's favicon service at 128px. MEASURED 2026-10-16: reddit.com and github.com 128px; news.ycombinator.com only 18px; an unknown domain answers HTTP 404 with a
//      16x16 generic globe (the blurry globe, once scaled up). A browser reports that 404 image as LOADED, so the only tell is its size: accept >= 32.
//   2. DuckDuckGo's icon service. MEASURED: news.ycombinator.com 256px (Google gave 18); reddit/github only 32; an unknown domain gets a 48x48 generic placeholder. So accept
//      only >= 64 and never the 48 placeholder.
//   3. Otherwise the bundled vector icon. Nothing else is fetched: loading images straight from arbitrary referrer domains would expose the dashboard owner to spam
//      referrers, and both services above already see only the domain (Google already did).
// Bubbles are redrawn with the real icon when a lookup passes.
const FAVICON_MIN_PX = 32;
const DDG_MIN_PX = 64;
const DDG_PLACEHOLDER_PX = 48;
const faviconResolved = new Map<string, string | null | "pending">();
let faviconRedrawTimer = 0;
const scheduleFaviconRedraw = () => {
  if (faviconRedrawTimer || typeof window === "undefined") {
    return;
  }
  faviconRedrawTimer = window.setTimeout(() => {
    faviconRedrawTimer = 0;
    if (useIcons) {
      redrawRealtimeBubbles();
      redrawHistoricalBubbles();
      eventEmitter.emit("displaySettingsChanged"); // the sidebar's list follows too
    }
    eventEmitter.emit("faviconResolved"); // the drawer's search engine / network / referrer rows show their logo whether or not the icons switch is on
  }, 400);
};
const measureImageWidth = (url: string): Promise<number> =>
  new Promise((resolve) => {
    const probe = new Image();
    const timer = window.setTimeout(() => resolve(0), 8000);
    probe.onload = () => {
      window.clearTimeout(timer);
      resolve(probe.naturalWidth);
    };
    probe.onerror = () => {
      window.clearTimeout(timer);
      resolve(0);
    };
    probe.src = url;
  });
const resolveFavicon = async (domain: string): Promise<string | null> => {
  if ((await measureImageWidth(getFaviconUrl(domain))) >= FAVICON_MIN_PX) {
    return getFaviconUrl(domain);
  }
  const duckWidth = await measureImageWidth(getDuckFaviconUrl(domain));

  return duckWidth >= DDG_MIN_PX && duckWidth !== DDG_PLACEHOLDER_PX ? getDuckFaviconUrl(domain) : null;
};
const getVerifiedFaviconUrl = (domain: string, fallbackIcon = "referred"): string => {
  const known = faviconResolved.get(domain);
  if (typeof known === "string" && known !== "pending") {
    return known;
  }
  if (known === undefined && typeof Image !== "undefined" && typeof window !== "undefined") {
    faviconResolved.set(domain, "pending");
    resolveFavicon(domain)
      .then((url) => {
        faviconResolved.set(domain, url);
        if (url) {
          scheduleFaviconRedraw();
        }
      })
      .catch(() => faviconResolved.set(domain, null));
  }

  return getLocalIconUrl(fallbackIcon) ?? getLocalIconUrl("unknown") ?? "";
};

// The domain whose icon stands for each social network (the slugs the backend stores - Common/Helper/trafficSource.ts SOCIAL_NETWORKS). Looked up through the same measured
// favicon chain as every other domain; until it passes (or if it never does) the bundled "social" badge is shown, never a generic question mark.
const SOCIAL_NETWORK_DOMAINS: { [slug: string]: string } = {
  facebook: "facebook.com",
  x: "x.com",
  linkedin: "linkedin.com",
  reddit: "reddit.com",
  instagram: "instagram.com",
  pinterest: "pinterest.com",
  tiktok: "tiktok.com",
  threads: "threads.net",
  snapchat: "snapchat.com",
  youtube: "youtube.com",
  rumble: "rumble.com",
  substack: "substack.com",
  whatsapp: "whatsapp.com",
  telegram: "telegram.org",
  discord: "discord.com",
  tumblr: "tumblr.com",
  mastodon: "joinmastodon.org",
  bluesky: "bsky.app",
  quora: "quora.com",
  medium: "medium.com",
  twitch: "twitch.tv",
  vk: "vk.com",
  weibo: "weibo.com",
  wechat: "wechat.com",
  line: "line.me",
  truthsocial: "truthsocial.com",
  gab: "gab.com",
  flickr: "flickr.com",
  nextdoor: "nextdoor.com",
};

// This app's own bundled OS/browser/device icons (src/assets/images/visitor_analytics_icons)
// - same require() pattern RightMenu.tsx already uses for the sessions drawer.
const getLocalIconUrl = (value: string): string | undefined => {
  try {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    return require(`../assets/images/visitor_analytics_icons/${value.toLowerCase()}.svg`);
  } catch (error) {
    return undefined;
  }
};

// Matches backend SEARCH_ENGINE_PROVIDER (src/SessionApi/Constant/providers.constant.ts)
const SEARCH_ENGINE_DOMAINS: { [slug: string]: string } = {
  google: "google.com",
  yahoo: "yahoo.com",
  bing: "bing.com",
  baidu: "baidu.com",
  ask: "ask.com",
  aol: "aol.com",
  excite: "excite.com",
  duckduckgo: "duckduckgo.com",
  yandex: "yandex.com",
  brave: "search.brave.com",
  ecosia: "ecosia.org",
  startpage: "startpage.com",
  qwant: "qwant.com",
  naver: "naver.com",
  seznam: "seznam.cz",
  sogou: "sogou.com",
  mojeek: "mojeek.com",
  dogpile: "dogpile.com",
  lycos: "lycos.com",
  swisscows: "swisscows.com",
};

// Search engines whose favicon Google's favicon service (getFaviconUrl)
// doesn't reliably resolve - confirmed directly: fetching
// google.com/s2/favicons?domain=bing.com returns a generic placeholder
// magnifying-glass icon, not Bing's actual logo, while the same call for
// google.com correctly returns Google's "G". Bundled locally instead of
// depending on that external service for this one slug.
const SEARCH_ENGINE_LOCAL_ICON_SLUGS = new Set(["bing"]);

// Central place mapping a (menuType, value) pair to an icon URL, shared by
// real-time bubbles, historical bubbles, and the sidebar's real-time list.
// Returns undefined for anything without a good icon (an unrecognized
// value/menuType) so callers fall back to the plain solid color exactly
// like an unresolvable country already did.
// A value with no bundled icon and no verified favicon still gets a real icon: the bundled "unknown" vector. In icon mode a bubble with no icon was drawn as a plain
// coloured square (and the sidebar as a bare dot) - Chris, 2026-10-16: "NO MORE SQUARE BOXES IN ICON MODE".
const GENERIC_ICON_VALUE = "unknown";
// the grey "?" for anything with no value: the compact badge (its disc is the size of its ring), in the fixed slate grey every "not there" thing wears
const noDataIcon = (): string => badgeIconDataUri(GENERIC_ICON_VALUE, "#8A93A0");
// The filters whose icons are round badges: the circle takes the CATEGORY'S OWN colour (getCategoryColor - the colour of its sidebar dot and its bubble, which is also the
// colour of the banner on the drawer you open by clicking it, and of its tooltip), so the icon, the bubble, the banner and the tooltip all agree. Chris, 2026-10-17.
const BADGE_MENUS = new Set(["traffic_source", "visitor_type", "visitor_category", "device"]);
const badgeColourFor = (name: string, menuType?: string): string => String(setColor(colourIndexFor(menuType, name)));
const resolveIconUrl = (menuType: string | undefined, rawValue: any): string | undefined => {
  // The "no value" bucket is ONE grey icon everywhere (stage bubble, dock, sidebar row) - Chris, 2026-10-17: the list showed it purple while the stage showed grey. A stage bubble
  // with no value reaches here as an empty value and takes the plain grey file below; the sidebar and dock reach here with the bucket's name, so it must take the same file.
  if (rawValue === NO_CATEGORY_DATA) {
    return noDataIcon();
  }
  if (menuType && BADGE_MENUS.has(menuType) && rawValue) {
    const key = String(rawValue).toLowerCase();
    if (hasBadge(key)) {
      return badgeIconDataUri(key, badgeColourFor(String(rawValue), menuType));
    }
  }
  const found = resolveIconUrlOrNothing(menuType, rawValue);
  if (found) {
    return found;
  }

  // nothing better for this value: the generic badge, in the category's own colour too
  return rawValue ? badgeIconDataUri(GENERIC_ICON_VALUE, badgeColourFor(String(rawValue), menuType)) : noDataIcon();
};

const resolveIconUrlOrNothing = (
  menuType: string | undefined,
  rawValue: any
): string | undefined => {
  if (!rawValue) {
    return undefined;
  }
  const value = String(rawValue);

  // The "no value for this filter" bucket is not a real category value, so
  // it has no icon by definition. Short-circuited here mainly to keep it out
  // of the "referrer" branch below, which would otherwise hand this literal
  // string to getFaviconUrl and fire a pointless external favicon request
  // that comes back as a generic placeholder.
  if (value === NO_CATEGORY_DATA) {
    return undefined;
  }

  if (menuType === "country_name") {
    const code = COUNTRY_NAME_TO_CODE[value.toLowerCase()];
    return code ? getFlagIconUrl(code) : undefined;
  }
  if (
    menuType === "os" ||
    menuType === "browser" ||
    menuType === "device" ||
    // traffic_source ('Direct'/'Referred' - see mapper.ts), visitor_type
    // ('new'/'return' - see SessionApi/Service/session.ts), and
    // visitor_category ('human'/'search_engine_bot'/'ai_bot'/'other_bot' -
    // see Common/Helper/visitorClassification.ts) all resolve the exact
    // same way as os/browser/device: a bundled local SVG named after the
    // raw value, lowercased. "none" (the "No Filter" collapse) works the
    // same way too - its one possible value is always the literal
    // ALL_VISITORS_CATEGORY string ("All visitors"), matching the bundled
    // "all visitors.svg".
    menuType === "traffic_source" ||
    menuType === "visitor_type" ||
    menuType === "visitor_category" ||
    menuType === "none"
  ) {
    return getLocalIconUrl(value);
  }
  if (menuType === "social_network") {
    const domain = SOCIAL_NETWORK_DOMAINS[value.toLowerCase()];

    return domain ? getVerifiedFaviconUrl(domain, "social") : getLocalIconUrl("social");
  }
  if (menuType === "search_engine") {
    const slug = value.toLowerCase();
    if (SEARCH_ENGINE_LOCAL_ICON_SLUGS.has(slug)) {
      return getLocalIconUrl(slug);
    }
    const domain = SEARCH_ENGINE_DOMAINS[slug];
    // 'Other' is a real, deliberate fallback bucket (mapper.ts/metrics.ts -
    // "had a referrer, but it didn't match a known search engine domain"),
    // not a null/unrecognized value - give it its own bundled icon instead
    // of falling through to the plain dot every unrecognized value gets.
    return domain ? getVerifiedFaviconUrl(domain) : getLocalIconUrl(value);
  }
  if (menuType === "referrer") {
    // '/' is this filter's own "no real referrer" bucket (mapper.ts/
    // metrics.ts both use it for direct traffic), not a domain at all -
    // getDomain("/") resolves to an empty string, so this fell through to
    // getFaviconUrl("/"), which Google's favicon service answers with its
    // generic "no favicon found" placeholder - indistinguishable from the
    // plain "unrecognized value" dot at a glance, reported as looking like
    // a stray null icon. Reuses the same direct.svg already bundled for the
    // Traffic Source filter's "Direct" value - same real-world concept.
    if (value === "/") {
      return getLocalIconUrl("direct");
    }
    const domain = getDomain(value) || value;
    // Same bundled-icon override as the "search_engine" branch above (see
    // SEARCH_ENGINE_LOCAL_ICON_SLUGS's comment) - a referrer whose domain is
    // bing.com (or a regional subdomain like cn.bing.com) hits the same
    // unreliable external favicon lookup, so it needs the same fix here too.
    if (domain?.includes("bing.")) {
      return getLocalIconUrl("bing");
    }
    return domain ? getVerifiedFaviconUrl(domain) : undefined;
  }
  return undefined;
};

// Real-time bubbles are keyed by country_code (an ISO code, not the display
// name resolveIconUrl expects for "country_name") for the flag CDN, so that
// one category is special-cased; every other category reads straight off the
// bubble's own sessionData field matching the active filter.
export const getBubbleIconUrl = (d: any): string | undefined => {
  if (!useIcons) {
    return undefined;
  }
  const menuType = selectedFilter?.menuType;
  if (menuType === "country_name") {
    const code = d?.sessionData?.country_code;
    return (code && code !== "UNKNOWN" ? getFlagIconUrl(code) : undefined) ?? noDataIcon();
  }
  // "none" isn't a real sessionData field (same special-case
  // resolveRealtimeCategory already needs - see its own comment) - a raw
  // d?.sessionData?.["none"] lookup is always undefined, so the spawn burst/
  // bubble never got an icon for the "No Filter" view and silently fell
  // back to the plain colored square.
  if (menuType === "none") {
    return resolveIconUrl(menuType, ALL_VISITORS_CATEGORY);
  }
  const rawValue = normalizeBrowserValue(menuType, d?.sessionData?.[menuType]);
  // A visit with no referrer is the "/" (Direct) bucket - the same rule resolveRealtimeCategory applies for the sidebar row and the dock. Reading the raw empty value here gave
  // those bubbles the generic icon while the list beside them showed Direct's own.
  return resolveIconUrl(menuType, menuType === "referrer" && (rawValue === undefined || rawValue === null || rawValue === "") ? "/" : rawValue);
};

/**
 * The icon for a value of ANY filter, for places that list several attributes side by side (the drawer's breakdown, the Sessions rows). The round-badge filters (device, visitor
 * type, new/return, traffic source) are plain dark line art there - colour carries no meaning in a row of attributes; every other icon (browsers, systems, flags) is the same
 * brand icon as everywhere. Always an icon, whatever the icons switch says.
 */
export const getPlainCategoryIconUrl = (menuType: string | undefined, value: string): string | undefined => {
  const key = String(value ?? "").toLowerCase();
  if (menuType && BADGE_MENUS.has(menuType) && hasBadge(key)) {
    return plainIconDataUri(key);
  }

  return resolveIconUrl(menuType, value);
};

// Historical "menu-node" bubbles are keyed by whatever display value the
// active filter groups by (a country name, an OS name, a search engine slug,
// a referrer hostname, etc.) via d.menuName.
export const getHistoricalIconUrl = (d: any): string | undefined => {
  if (!useIcons) {
    return undefined;
  }
  return resolveIconUrl(selectedFilter?.menuType, d?.menuName);
};

// Used by the sidebar's real-time list, which groups by the same value as
// historical bubbles but doesn't have a "d" bubble object - just the raw name.
export const getListItemIconUrl = (
  menuType: string | undefined,
  name: string
): string | undefined => {
  if (!useIcons) {
    return undefined;
  }
  return resolveIconUrl(menuType, name);
};

const appendIconImage = (
  parentNode: Element,
  d: any,
  iconUrl: string,
  width: number,
  aspect: number,
  markerAttr: "realTime" | "historical",
  idAttr: string,
  idValue: string,
  // New real-time bubbles render their rect at a hardcoded off-screen start
  // point (-130, 100) rather than d.x/d.y, purely as a transition starting
  // value - independent of the shared datum. Pass the same literal here so
  // the image starts at the same spot; omit for cases (historical redraws,
  // existing on-load bubbles) where the rect really is positioned from d.x/d.y.
  initialX: number = d.x ?? 0,
  initialY: number = d.y ?? 0
) => {
  const height = width * aspect;
  const parent = d3.select(parentNode.parentNode as Element);

  /*
   * One icon per bubble, enforced HERE rather than trusted to every caller.
   *
   * This function is the single funnel all five append sites pass through, and
   * it used to append unconditionally - so any two paths that both ran for the
   * same session (an on-load draw plus a redraw, say) left TWO <image>
   * elements stacked on the same bubble. Measured live in the browser
   * (2026-07-29): 38 images for 30 real-time shapes, with the same visitor_id
   * appearing twice.
   *
   * Invisible, because paintRealtimeBubblePositions dutifully moves both to the
   * same spot - but every duplicate is another element repositioned on EVERY
   * force tick, and the surplus grows with session count. That is precisely the
   * per-tick cost perf note 5 is about.
   *
   * Scoped to [data-bubble-flag] so this only ever replaces a previous ICON.
   * The converted-session star badge is a separate element on the same bubble
   * (data-goal-badge) and must survive untouched - removing by marker + id
   * alone would delete the star whenever an icon was redrawn.
   */
  /*
   * Matched with a PREDICATE, never an interpolated attribute selector.
   *
   * The first version of this built `[${idAttr}='${idValue}']`, and idValue is
   * a category name straight from the data - so "Côte d'Ivoire" terminated the
   * quoted string, produced an invalid selector, and THREW. Because this runs
   * inside a d3 .each() loop, that exception aborted the whole loop: every
   * bubble after the offending name silently rendered with no icon at all
   * (measured live: 17 of 45 historical bubbles had flags, and the missing ones
   * included Germany and Japan, which obviously exist in flag-icons - the names
   * were never the problem, the loop dying was).
   *
   * A predicate compares attribute values as plain strings, so no name can ever
   * be syntactically meaningful here - apostrophes, quotes, brackets, anything.
   */
  /*
   * Search the whole SVG, not just this parent group.
   *
   * Scoping the search to `parent` only deduped icons that happened to land in
   * the SAME group - but the historical dock has more than one append path, and
   * they use different parents, so each bubble ended up with two icons that
   * could never see each other. Measured live 2026-07-30: 159 images for 101
   * historical shapes, with exactly 60 country names holding 2 apiece, while
   * real-time (a single append path, one parent) had zero duplicates.
   *
   * markerAttr + idAttr/idValue already identify one bubble uniquely
   * (visitor_id for real-time, category name for historical), so widening the
   * search cannot remove a legitimately different icon - only the older copy of
   * this same one.
   */
  const searchRoot = d3.select(
    (parentNode as SVGElement).ownerSVGElement || (parentNode.parentNode as Element)
  );

  searchRoot
    .selectAll("image[data-bubble-flag]")
    .filter(function () {
      const el = this as Element;
      return (
        el.getAttribute(markerAttr) !== null && el.getAttribute(idAttr) === idValue
      );
    })
    .remove();

  const appended = parent
    .append("image")
    // Elements appended outside a .data()/.enter() join don't automatically
    // get a bound datum - without this, every tick handler's accessor
    // functions (which read d.x, d.pageName, etc.) would throw on this
    // element. Binding the *same* object the sibling rect uses (not a copy)
    // means any mutation the rect's handlers make (e.g. the d3-force
    // simulation updating d.x/d.y every tick, or clamping it in place) is
    // automatically visible here too.
    .datum(d)
    .attr("data-bubble-flag", true)
    .attr(markerAttr, true)
    .attr(idAttr, idValue)
    .attr("pageName", d.pageName ?? null)
    .attr("width", width)
    .attr("height", height)
    .attr("x", initialX - width / 2)
    .attr("y", initialY - height / 2)
    .attr("href", iconUrl)
    .attr("pointer-events", "none")
    .attr("remove", "no")
    .style(
      "filter",
      markerAttr === "historical" ? "url(#historical-bubble-shadow)" : null
    );
  // A moving icon is shown from a bitmap, not by replaying its SVG on every repaint (iconBitmaps.ts: the GPU's biggest single load with icons on). The SVG shows until it is ready.
  if (markerAttr === "realTime" && !iconBitmapsSwitchedOff) {
    const node = appended.node();
    if (node) useBitmapFor(node, iconUrl, width, height);
  }
};

// A plain top-of-file ES import (not getLocalIconUrl's runtime require())
// since this is a single fixed asset, not a dynamic per-category lookup -
// a runtime require() of an SVG under this project's webpack/TS config
// returns the module namespace object, not the plain URL string, which
// rendered as a broken-image icon on the page-box badge (confirmed live:
// href ended up literally "[object Module]"). A static import gets the
// correct string, same as RightMenu.tsx's already-working GoalStarIcon.
// Exported so tooltipService.ts's "Converted" badge can reuse the exact
// same asset the bubble's own star badge uses, instead of a second
// duplicate icon reference.
export const getGoalStarIconUrl = (): string => goalStarIconAsset;

// Real-time bubbles all share one fixed per-mode radius (getBubbleRadius) -
// historical dock bubbles instead carry their own already-computed radius,
// scaled per category by visitor count (see getHistoricalBubbleRadius).
// Badge sizing needs to read whichever one actually applies to the bubble
// being badged, or a historical badge would either dwarf a small category's
// bubble or look lost on a large one.
const resolveGoalBadgeRadius = (markerAttr: "realTime" | "historical", d?: any) =>
  markerAttr === "historical" ? +d?.radius || 0 : getBubbleRadius(d);

// A converted session's bubble should visibly "become" a star bubble for
// the rest of its session - a white star sitting INSIDE the bubble's own
// (now larger, see getBubbleRadius) spawn color, not replacing/covering it -
// the category color is the whole point of the bubble, the star just marks
// "converted" on top of it. d is passed through so this reads the SAME
// already-bumped radius the bubble itself was actually drawn at.
const getGoalBadgeSize = (radius: number) => radius * 1.3;

// Icon mode's badge is deliberately smaller and corner-offset rather than
// the full centered size above - a badge that size sitting dead-center
// would sit directly on top of the icon's own glyph (the whole point of
// icon mode), not just accent it. Half the icon's own rendered half-width
// as the offset magnitude tucks the badge into the icon's bottom-right
// corner - big enough to clearly read as "over here, not covering the
// middle" without pushing it mostly off the bubble entirely.
const getIconModeGoalBadgeSize = (radius: number) => radius * 1.05;
const getIconModeGoalBadgeOffset = (radius: number, markerAttr: "realTime" | "historical") => {
  const iconWidth = getRectSizeFromRadius(radius, getActiveIconImageAspect()).width;
  const fullOffset = iconWidth / 2 - getIconModeGoalBadgeSize(radius) / 2;
  // Pulled in from the full bottom-right-corner offset - direct request to
  // nudge the REAL-TIME badge specifically up and to the left "just a
  // little" rather than all the way into the corner. Historical dock
  // badges keep the original full corner placement.
  return markerAttr === "realTime" ? fullOffset * 0.8 : fullOffset;
};

// A converted session's star badge - deliberately reuses appendIconImage's
// exact idAttr/idValue/markerAttr convention (same visitor_id/name and
// realTime/historical tagging) rather than inventing separate positioning
// logic: paintRealtimeBubblePositions's tick handler and every dock-
// transition/removal/reset selector in this file already operate generically
// on "circle[X], rect[X], image[X]" for a given marker/id, so tagging this
// image the same way gets it tick-synced, carried through the historical
// dock transition, and cleaned up on departure/reset for free - no new
// per-badge lifecycle code needed. data-offset-x/y (see rectXFromCenter/
// rectYFromCenter) carries the icon-mode corner offset through every
// subsequent tick too, not just this initial append - otherwise the very
// next tick would recenter it, undoing the corner placement immediately.
const appendGoalBadge = (
  parentNode: Element,
  d: any,
  markerAttr: "realTime" | "historical",
  idAttr: string,
  idValue: string,
  initialX: number = d.x ?? 0,
  initialY: number = d.y ?? 0
) => {
  // Was previously skipped for real-time badges while single-view mode
  // ("arrow" icon in the dashboard-active-btn row, pageViewDisabled=true)
  // was active - reversed per direct request: a converted session's star
  // is a persistent state marker, not tied to page-slot layout, and should
  // stay visible whether the view is collapsed into one cluster or not
  // (unlike the transient celebration effect - see
  // celebrateConversionAtGoalFlag's own pageViewDisabled guard - or the
  // page-box goal flag, which has no box to attach to when collapsed).
  const iconMode = useIcons;
  const radius = resolveGoalBadgeRadius(markerAttr, d);
  const size = iconMode ? getIconModeGoalBadgeSize(radius) : getGoalBadgeSize(radius);
  const offset = iconMode ? getIconModeGoalBadgeOffset(radius, markerAttr) : 0;
  d3.select(parentNode.parentNode as Element)
    .append("image")
    .datum(d)
    .attr("data-goal-badge", true)
    .attr(markerAttr, true)
    .attr(idAttr, idValue)
    .attr("pageName", d.pageName ?? null)
    .attr("width", size)
    .attr("height", size)
    .attr("data-offset-x", offset)
    .attr("data-offset-y", offset)
    .attr("x", initialX - size / 2 + offset)
    .attr("y", initialY - size / 2 + offset)
    .attr("href", getGoalStarIconUrl())
    .attr("pointer-events", "none")
    .attr("remove", "no")
    // Gold was tried here (tying the converted-bubble star back to the goal
    // page-box's own flag color) and flagged directly as not working at
    // all once seen live - reverted to the original plain white star.
    // brightness(0) flattens the bundled asset's own baked-in navy to
    // black, invert(1) flips that to white. Icon mode's own drop shadow
    // (dropped along with the color, then flagged as missing) is restored -
    // the star sits over a real, visually busy icon there (not a flat
    // bubble color), so it needs its own separation to read as a badge
    // floating above the icon rather than blending into it.
    .style(
      "filter",
      iconMode
        ? "brightness(0) invert(1) drop-shadow(0 1px 2px rgba(0, 0, 0, 0.6))"
        : "brightness(0) invert(1)"
    );
};

// "Unidentified" visitor badge - a UA string that neither self-identifies as
// a bot NOR matches any real browser's signature (Common/Helper/
// visitorClassification.ts). Deliberately mirrors appendGoalBadge's
// structure (same marker/idAttr tagging so it gets tick-sync, historical
// dock carry-through, and departure cleanup for free via the same generic
// "circle[X], rect[X], image[X]" selectors used throughout this file) but
// pinned to the BOTTOM-LEFT corner rather than goalReached's bottom-right,
// and top-left is already the away glyph's - all three can coexist on one
// bubble without overlapping.
//
// Unlike the goal star, this is set once at arrival and never changes mid-
// session (see the sessionData.visitor_category comment in drawUserBubble),
// so there's no separate "flip true later" call site to also wire up - the
// three append call sites (initial draw, bulk icon/filter-mode redraw,
// historical dock) are the only places this needs to be checked.
const appendUnidentifiedBadge = (
  parentNode: Element,
  d: any,
  markerAttr: "realTime" | "historical",
  idAttr: string,
  idValue: string,
  initialX: number = d.x ?? 0,
  initialY: number = d.y ?? 0
) => {
  const iconMode = useIcons;
  // In icon mode with "By Browser" as the active filter, the bubble's own
  // icon (getBubbleIconUrl, via the same normalizeBrowserValue this badge's
  // classification is built on) already IS the unidentified glyph - the
  // corner badge would be the identical icon stacked on top of itself.
  // Every other combination (dot/bubble mode always, or icon mode under any
  // OTHER filter - country/OS/device/etc, where the bubble's icon is
  // something unrelated) keeps the badge, since it's the only indicator of
  // "unidentified" on screen in those cases.
  if (iconMode && selectedFilter?.menuType === "browser") {
    return;
  }
  const radius = resolveGoalBadgeRadius(markerAttr, d);
  // NOT reusing getIconModeGoalBadgeSize/Offset - those return offset=0 in
  // dot/circle mode (useIcons=false, the app's default view), because the
  // goal star is INTENTIONALLY centered there (it replaces the bubble's
  // look entirely - see getGoalBadgeSize's own comment). This badge needs a
  // real corner in EVERY mode, so it borrows the away-glyph's own always-on
  // radius-relative proportions (applyInteractionPulse's badgeR/dx/dy)
  // instead, which is why it looks right in dot mode too, not just icon mode.
  const size = Math.max(9, radius * 0.84);
  const offset = radius * 0.78;
  d3.select(parentNode.parentNode as Element)
    .append("image")
    .datum(d)
    .attr("data-unidentified-badge", true)
    .attr(markerAttr, true)
    .attr(idAttr, idValue)
    .attr("pageName", d.pageName ?? null)
    .attr("width", size)
    .attr("height", size)
    // Negative X (left) + positive Y (down, since SVG Y increases downward) -
    // bottom-left, distinct from goalReached's bottom-right (+offset, +offset)
    // and the away glyph's top-left, so all three can appear on one bubble
    // without overlapping. (Originally top-right; moved per direct
    // instruction so it doesn't compete for the same corner as either
    // existing glyph.)
    .attr("data-offset-x", -offset)
    .attr("data-offset-y", offset)
    .attr("x", initialX - size / 2 - offset)
    .attr("y", initialY - size / 2 + offset)
    .attr("href", unidentifiedBadgeIconAsset)
    .attr("pointer-events", "none")
    .attr("remove", "no")
    .style(
      "filter",
      iconMode ? "drop-shadow(0 1px 2px rgba(0, 0, 0, 0.6))" : "none"
    );
};

// RETURNING-VISITOR mark (2026-10-11, "E1"): two arrows chasing each other around the bubble, drawn by code (see returnMark.ts for the numbers and
// why they sit where they do). Mirrors appendUnidentifiedBadge's recipe EXACTLY so it gets the same treatment from every generic selector in this
// file for free: tagged realTime + visitor_id, so paintRealtimeBubblePositions keeps it centred on the bubble every tick (data-offset 0), the
// density-cloud hide pass hides it with the rest of the visitor's decorations, and every departure/redraw sweep that removes
// "circle/rect/image[visitor_id=...]" removes it.
//
// Dot mode draws the orbit; icon mode draws a corner badge (an icon-mode bubble is a 19px rect, and a circle that clears its corners is wider than
// the hover/ripple ring: 13.4px vs 11.05px). Gated on sessionData.visitor_type, NOT d.visitor_type: unlike visitor_category, visitor_type is only
// ever on the datum's sessionData.
//
// Sized from the bubble's CURRENT radius, so a visitor who converts later (the bubble grows 1.6x) needs it redrawn: see the conversion path in
// handleVisitorMove, which removes and re-appends it.
// The pulse glyphs a bubble can wear (away, click, typing, idle, reading, watching, scroll). One list, so the bulk redraw's lift and appendReturnMark's cannot drift.
const INTERACTION_DECORATION_SELECTOR =
  "[data-pulse-away=true], [data-pulse-click=true], [data-pulse-typing=true], [data-pulse-idle=true], [data-pulse-reading=true], [data-pulse-watching=true], [data-pulse-scroll=true]";

// LAYERING (Chris, 2026-10-12: "make all glyphs sit ABOVE the return bubble and circle arrows"). SVG paint order is document order, so:
//  - DOT mode: the orbit is inserted right after its own dot, which puts it above the dot and BELOW everything appended later (the goal star, the "?" badge and
//    every pulse glyph), wherever and whenever those are created.
//  - ICON mode: the corner badge must sit above the icon image (which is appended after the bubble shape), so it is appended; this visitor's pulse glyphs are then
//    lifted back over it, because this runs again when a converted bubble grows and the mark is re-created AFTER glyphs that are already on screen.
const appendReturnMark = (
  parentNode: Element,
  d: any,
  idValue: string,
  initialX: number = d.x ?? 0,
  initialY: number = d.y ?? 0
) => {
  if (d?.sessionData?.visitor_type !== "return") {
    return;
  }
  // Icon mode: a black-and-white arrow badge on the icon's top-right corner instead of the orbit (see RETURN_BADGE). Skipped when the active filter
  // is New vs Returning: the bubble's own icon is already the return arrow (resolveIconUrl, "return.svg"), so the badge would be that arrow twice
  // - the same reasoning as appendUnidentifiedBadge skipping its badge under the Browser filter.
  if (useIcons && selectedFilter?.menuType === "visitor_type") {
    return;
  }
  // Dot mode: the arrows take the BUBBLE'S OWN colour (Chris, 2026-10-12: black "is not helping"; the same colour, but further out). The icon-mode corner
  // badge has no bubble colour to take (the bubble is an icon) and stays black with its white outline. One rule for the grid and the zoomed view: returnMarkFor.
  const mark = returnMarkFor(useIcons, getBubbleRadius(d), String(setColor(d.color)));
  const offsetX = mark.offsetX;
  const offsetY = mark.offsetY;
  const host = parentNode.parentNode as Element;
  const returnMarkNode = d3
    .select(host)
    .insert("image", () => (useIcons ? null : (parentNode.nextSibling as Element | null)))
    .datum(d)
    .attr("data-return-mark", true)
    .attr("realTime", true)
    .attr("visitor_id", idValue)
    .attr("pageName", d.pageName ?? null)
    .attr("width", mark.size)
    .attr("height", mark.size)
    .attr("data-offset-x", offsetX)
    .attr("data-offset-y", offsetY)
    .attr("x", initialX - mark.size / 2 + offsetX)
    .attr("y", initialY - mark.size / 2 + offsetY)
    .attr("href", mark.uri)
    .attr("pointer-events", "none")
    .attr("remove", "no")
    .node();
  // moves with its bubble: shown from a bitmap, like the bubble's own icon (iconBitmaps.ts)
  if (returnMarkNode && !iconBitmapsSwitchedOff) useBitmapFor(returnMarkNode as Element, mark.uri, mark.size, mark.size);
  if (useIcons) {
    d3.select(host)
      .selectAll(INTERACTION_DECORATION_SELECTOR)
      .filter(function () {
        return (this as Element).getAttribute("visitor_id") === idValue;
      })
      .raise();
  }
};

// Bubbles are plain <circle> elements so all existing d3-force tick/movement
// code keeps working untouched; icon mode only swaps the fill to transparent
// and lets a companion <image> (see appendIconImage) show the actual icon.
export const getBubbleFill = (d: any) => {
  return getBubbleIconUrl(d) ? "transparent" : setColor(d.color);
};

// A colored stroke around an icon image just looks like a stray outline, not
// a deliberate design choice - drop it whenever icon mode is on (regardless
// of whether this particular bubble ended up with a real icon or fell back
// to a solid color, so a mixed "some outlined, some not" cluster doesn't
// happen).
export const getBubbleStroke = (d: any) =>
  useIcons ? "none" : setColor(d.color);

let highlightedVisitorId: string | null = null;

// WHO IS ON THE SITE RIGHT NOW, by browser: visitorKey -> the live session's id. Read from the live bubble list (a departed visitor's bubble is spliced out of
// it), so it cannot go stale the way an event-driven set could if one departure event were missed. The Visitors tab polls it while open to show its
// "here now" dot, and uses the session id to light that visitor's bubble when the row is hovered.
/** who is on each address of a page group right now: the live bubbles on the group, counted by the address they really have (a visitor whose address is not known is left out, so the rows can add up to less than the group's own count, never more) */
export const getGroupAddressCounts = (groupKey: string): Map<string, number> => {
  const counts = new Map<string, number>();
  for (const bubble of bubblesData as any[]) {
    if (bubble?.realTime && bubble.pageName === groupKey && bubble.rawAddress) {
      counts.set(String(bubble.rawAddress), (counts.get(String(bubble.rawAddress)) || 0) + 1);
    }
  }

  return counts;
};

export const getOnsiteVisitors = (): Map<string, string> => {
  const onsite = new Map<string, string>();
  for (const bubble of bubblesData as any[]) {
    if (bubble?.realTime && bubble.visitorKey && bubble.visitor_id) {
      onsite.set(String(bubble.visitorKey), String(bubble.visitor_id));
    }
  }

  return onsite;
};

export const isSessionHighlighted = (visitorId: string) =>
  highlightedVisitorId === visitorId;

// Two rings, animation-delayed half a cycle apart (see
// .bubble-highlight-ripple in AnalyticsView.scss), read as one continuous
// outward ripple rather than a single pulse that resets abruptly.
const HIGHLIGHT_RIPPLE_COUNT = 2;

// Selecting a session in RightMenu.tsx's Sessions tab (via clicking a bubble
// OR clicking a row directly) highlights the matching real-time bubble with
// a continuous outward ripple, so the correlation this session/that bubble
// reads clearly in both directions, not just bubble-to-row. Neutral
// (white/black, not a new hue) so it doesn't compete with the category-color
// system already used for bubble fill/sidebar/row bars - an earlier static
// gold stroke was flagged directly as too heavy and an unwanted extra color.
// Historical dock bubbles are aggregated per category (no single
// visitor_id), so this selector simply won't match anything for a session
// that's already departed - accepted, not worth a separate treatment for v1.
export const setSessionHighlight = (visitorId: string | null) => {
  svg?.selectAll("circle[data-highlight-ripple=true]").remove();
  highlightedVisitorId = visitorId;
  if (!visitorId) {
    densityCloud.setRowSelection(null);
    return;
  }
  const shapeNode = svg
    ?.select(
      `circle[realTime=true][visitor_id="${visitorId}"], rect[realTime=true][visitor_id="${visitorId}"]`
    )
    .node() as SVGGraphicsElement | null;
  const shapeDatum = shapeNode && (shapeNode as any).__data__;
  // A visitor that is a DOT in a density cloud has no visible SVG bubble: everything carrying its
  // visitor_id is hidden (applyCloudBubbleVisibility), this ripple included, so a ripple drawn here
  // would be invisible. The cloud draws the ripple on its canvas instead, in the same colour; it
  // draws only while the visitor has a dot there, so a visitor that lands in (or leaves) a cloud
  // while its row is selected hands the ripple over by itself.
  densityCloud.setRowSelection(visitorId, shapeDatum ? (setColor(shapeDatum.color) as any) : undefined);
  if (!shapeNode || !shapeDatum || cloudedVisitors.has(visitorId)) {
    return;
  }
  // 1.15x PLUS a small fixed pixel offset, not just a multiplier - a pure
  // proportional gap shrinks to almost nothing on small bubbles. The fixed
  // +3px floor keeps a visible gap regardless of bubble size. (Was 1.3x+6 -
  // confirmed that read as too far out; dialed back while keeping a floor
  // for small bubbles.)
  const radius = getBubbleRadius(shapeDatum) * 1.15 + 3;
  const parent = shapeNode.parentNode as Element | null;
  // Trying the bubble's own category color instead of a fixed neutral -
  // mix-blend-mode: difference (AnalyticsView.scss) already guarantees
  // contrast against whatever's underneath regardless of source color, so
  // the earlier "must be neutral to stay visible" constraint no longer
  // applies. Ties the ripple more directly to "this specific bubble" using
  // the same color language as its fill/sidebar/row bar. Revert to the
  // plain white default (just delete this .style call) if it doesn't read
  // well in practice.
  const rippleColor = setColor(shapeDatum.color);
  for (let i = 0; i < HIGHLIGHT_RIPPLE_COUNT; i++) {
    d3.select(parent ?? (svg?.node() as any))
      .append("circle")
      .attr("data-highlight-ripple", "true")
      .attr("visitor_id", visitorId)
      .attr("cx", shapeDatum.x)
      .attr("cy", shapeDatum.y)
      .attr("r", radius)
      .style("stroke", rippleColor as any)
      .attr("class", "bubble-highlight-ripple")
      .style("animation-delay", `${(i * 1.6) / HIGHLIGHT_RIPPLE_COUNT}s`);
  }
};

// Hovering a session row (not clicking - that's setSessionHighlight above)
// gets a lighter-weight, purely static yellow glow ring, so pointing at rows
// to see which bubble they refer to doesn't fight visually with the
// click-selection ripple's own animated white/category-color rings - both
// can be present at once (e.g. hovering a row other than the selected one)
// since they're on separate data-* tags. Same "historical dock has no single
// visitor_id to match" acceptance as setSessionHighlight - a hover over an
// already-departed session's row simply finds nothing and no-ops.
// Sessions-tab rows fire mouseenter/mouseleave at native mouse-move
// frequency while the cursor travels down the list (confirmed live - a
// synthetic stress test of ~90 hover events over 20s produced ~90 Long
// Tasks, essentially 1:1, and was the actual cause of a reported slowdown).
// remove()-then-append() a fresh pair of SVG elements on every single one of
// those, as an earlier version of this function did, is real, repeated
// DOM-node churn at that frequency. Keeping one persistent pair of circles
// across the whole session and just moving/hiding them (display:none when
// not hovering) removes that churn entirely - only the first-ever hover
// pays a one-time append cost.
let hoverHighlightOuter: any = null;
let hoverHighlightInner: any = null;

export const setSessionHoverHighlight = (visitorId: string | null) => {
  // the cloud draws the yellow ring for a visitor that is a dot (see setSessionHighlight)
  densityCloud.setRowHover(visitorId);
  if (!visitorId) {
    hoverHighlightOuter?.style("display", "none");
    hoverHighlightInner?.style("display", "none");
    return;
  }
  const shapeNode = svg
    ?.select(
      `circle[realTime=true][visitor_id="${visitorId}"], rect[realTime=true][visitor_id="${visitorId}"]`
    )
    .node() as SVGGraphicsElement | null;
  const shapeDatum = shapeNode && (shapeNode as any).__data__;
  if (!shapeNode || !shapeDatum || cloudedVisitors.has(visitorId)) {
    hoverHighlightOuter?.style("display", "none");
    hoverHighlightInner?.style("display", "none");
    return;
  }
  const radius = getBubbleRadius(shapeDatum) * 1.15 + 3;
  const parent = shapeNode.parentNode as Element | null;
  const parentSelection = d3.select(parent ?? (svg?.node() as any));
  // Two plain strokes (wide/faint outer + thin/solid inner) instead of a
  // single ring with filter:drop-shadow - see the matching CSS comment in
  // AnalyticsView.scss for why the blur filter was dropped (also confirmed
  // as part of the same reported slowdown).
  if (!hoverHighlightOuter || !(hoverHighlightOuter.node() as Element)?.isConnected) {
    hoverHighlightOuter = parentSelection
      .append("circle")
      .attr("data-hover-highlight", "true")
      .attr("class", "bubble-hover-highlight-outer");
  }
  if (!hoverHighlightInner || !(hoverHighlightInner.node() as Element)?.isConnected) {
    hoverHighlightInner = parentSelection
      .append("circle")
      .attr("data-hover-highlight", "true")
      .attr("class", "bubble-hover-highlight-inner");
  }
  [hoverHighlightOuter, hoverHighlightInner].forEach((sel) => {
    sel
      .style("display", null)
      .attr("visitor_id", visitorId)
      .attr("cx", shapeDatum.x)
      .attr("cy", shapeDatum.y)
      .attr("r", radius);
  });
};

// Ephemeral click/typing/idle activity pulses (see the mockup review this
// session) - purely decorative siblings appended next to the real-time
// bubble shape, tagged data-pulse-<type>="true" + visitor_id so
// paintRealtimeBubblePositions can keep them synced to the bubble's live x/y
// every tick, same recipe as icon images and highlight ripples above.
// Live-only (no DB persistence, no playback replay) - a deliberate v1 scope
// cut, confirmed with the user.
const CLICK_PULSE_DURATION_MS = 2400;
const TYPING_PULSE_DURATION_MS = 1200;
// Slightly longer than the tracking script's own IDLE_EMIT_INTERVAL_MS (4s)
// so a normal re-emit arrives before this client-side timeout would clear
// the Zzz's - only actually fires once idle events genuinely stop.
const IDLE_PULSE_CLEAR_MS = 9000;
// Longer than the tracking script's TYPING_EMIT_THROTTLE_MS (600ms), so
// continuous typing keeps re-arming this and the "still typing" state (see
// sessionTypingChanged) stays on for the whole burst - only clears once a
// real pause in typing happens, same shape as idle's own clear timeout.
const TYPING_STATE_CLEAR_MS = 1800;
// Same shape again, keyed off the tracking script's own SCROLL_EMIT_THROTTLE_MS
// (600ms) - a scroll burst keeps the looping scan-line element alive without
// restarting its animation on every individual scroll event (only the FIRST
// scroll event in a burst appends it - see the "scroll" branch below).
const SCROLL_PULSE_CLEAR_MS = 1800;

const interactionPulseTimeouts = new Map<string, number>();

const clearInteractionPulseTimeout = (key: string) => {
  const existing = interactionPulseTimeouts.get(key);
  if (existing) {
    window.clearTimeout(existing);
    interactionPulseTimeouts.delete(key);
  }
};

let cloudVisibilityTimer = 0;
/**
 * The dashboard is leaving the screen (another route): its density clouds are canvases on the
 * document body, so unless they are torn down they stay painted over the next page (a cloud was
 * seen on Settings). Coming back builds everything afresh (createStaticComponents).
 */
export const destroyDensityClouds = () => {
  if (cloudVisibilityTimer) { window.clearInterval(cloudVisibilityTimer); cloudVisibilityTimer = 0; }
  cloudedVisitors = new Set<string>();
  try {
    densityCloud.destroy();
  } catch (error) {
    console.error("[density-cloud] destroy failed while leaving the dashboard", error);
  }
};
let zoomLeaverRaf = 0;
const SLOT_TOTAL_BAR_H = 6;
const SLOT_TOTAL_ZONE_PAD = 3;
let slotTotalsTimer: ReturnType<typeof setInterval> | null = null;
let slotTotalsQueued = false;
let totalsHoverSlot: number | null = null;

/** draws the (hidden until updateSlotTotals shows it) bar into a slot's own group, and lets the box's own pointer events reach the tooltip: only the bar's strip is the hover target */
const appendSlotTotalBar = (svgInstance: any, rectSel: any, key: number, left: number, width: number, bottom: number) => {
  if (!SLOT_TOTALS) return;
  const g = svgInstance.append("g").attr("class", "slot-total").attr("data-slot", key).attr("display", "none").style("pointer-events", "none");
  g.append("rect").attr("class", "slot-total-track").attr("x", left).attr("y", bottom - SLOT_TOTAL_BAR_H).attr("width", width).attr("height", SLOT_TOTAL_BAR_H).style("fill", "rgba(11, 80, 110, 0.12)");
  g.append("rect").attr("class", "slot-total-fill").attr("x", left).attr("y", bottom - SLOT_TOTAL_BAR_H).attr("width", 0).attr("height", SLOT_TOTAL_BAR_H).style("fill", "#0b506e");
  const node = g.node() as SVGGElement;
  const track = node.querySelector("rect.slot-total-track") as SVGRectElement;
  const inStrip = (e: MouseEvent): boolean => {
    if (node.getAttribute("display") === "none") return false;
    const r = track.getBoundingClientRect();

    return e.clientY >= r.top - SLOT_TOTAL_ZONE_PAD && e.clientY <= r.bottom + SLOT_TOTAL_ZONE_PAD && e.clientX >= r.left && e.clientX <= r.right;
  };
  rectSel
    .on("mousemove.totals", (e: MouseEvent) => {
      if (slotDrag.isSlotDragActive()) return;
      if (inStrip(e)) {
        if (totalsHoverSlot !== key) {
          totalsHoverSlot = key;
          scheduleSlotHint(node.getAttribute("data-total-text") || "", e.clientX, e.clientY, true);
        } else {
          moveSlotHint(e.clientX, e.clientY);
        }
      } else if (totalsHoverSlot === key) {
        totalsHoverSlot = null;
        hideSlotHint();
      }
    })
    .on("mouseleave.totals", () => {
      if (totalsHoverSlot === key) {
        totalsHoverSlot = null;
        hideSlotHint();
      }
    });
};

/** sets every bar from the Pages tab's own numbers (the same list, the same range); hides them all where they do not apply (playback replays a day that is still being counted; the one-cluster view has no boxes) */
const updateSlotTotals = () => {
  const root = svg?.node() as SVGSVGElement | undefined;
  if (!root || !SLOT_TOTALS) return;
  const groups = root.querySelectorAll<SVGGElement>("g.slot-total");
  const inPlayback = playbackModeService.mode === "playback";
  const on = slotTotalsEnabled && !pageViewDisabled;
  if (!on) {
    groups.forEach((g) => { if (g.getAttribute("display") !== "none") g.setAttribute("display", "none"); });
    if (totalsHoverSlot !== null) { totalsHoverSlot = null; hideSlotHint(); }

    return;
  }
  const names: Array<string | undefined> = [];
  for (let idx = 0; idx < UNKNOWN_URL_CLUSER_INDEX; idx++) names[idx] = clusterTextInstances[idx]?.attr?.("real-text") || undefined;
  // in a replay the bars are the share of page views from 12:00 AM up to the moment shown, and move as it plays or is scrubbed
  const totals = computeSlotTotals(inPlayback ? playbackModeService.playbackPageViews() : data || [], names);
  const rangeName = inPlayback ? flowPeriodLabel({ inPlayback: true, priorLabel: playbackModeService.priorLabel, playbackDate: playbackModeService.playbackDate }) : selectedDateRange?.name || "";
  groups.forEach((g) => {
    const idx = Number(g.getAttribute("data-slot"));
    const t = idx === UNKNOWN_URL_CLUSER_INDEX ? totals.other : totals.bySlot.get(idx);
    if (!t) {
      if (g.getAttribute("display") !== "none") g.setAttribute("display", "none");

      return;
    }
    if (g.getAttribute("display") === "none") g.removeAttribute("display");
    const track = g.querySelector("rect.slot-total-track") as SVGRectElement;
    const fill = g.querySelector("rect.slot-total-fill") as SVGRectElement;
    const w = String(barFillWidth(t.share, t.views, Number(track.getAttribute("width"))));
    if (fill.getAttribute("width") !== w) fill.setAttribute("width", w);
    const text = slotTotalsText(idx === UNKNOWN_URL_CLUSER_INDEX ? "All other pages" : names[idx] || "", rangeName, t);
    if (g.getAttribute("data-total-text") !== text) g.setAttribute("data-total-text", text);
  });
};
/** coalesced: a burst of events asks once */
const scheduleSlotTotals = () => {
  if (!SLOT_TOTALS || slotTotalsQueued) return;
  slotTotalsQueued = true;
  setTimeout(() => { slotTotalsQueued = false; updateSlotTotals(); }, 120);
};

// "N on page", outside each page box on the right of its label. Same counts the
// density clouds use (the real bubbles in that slot, departing ones excluded).
const updateSlotCountLabels = (counts: Map<number, number>) => {
  const root = svg?.node() as SVGSVGElement | undefined;
  if (!root) return;
  root.querySelectorAll<SVGTextElement>("text.slot-count").forEach((el) => {
    const idx = Number(el.getAttribute("data-slot"));
    // A slot has a box exactly when it is showing a page: the label drawn in it
    // (set by fillClusterSelected, cleared by fillClusterEmpty). Reading `data`
    // instead meant a slot that was given a page later never got its count.
    // ...and never in the single-cluster view, which has no page boxes at all
    const hasBox = !pageViewDisabled && !!clusterTextInstances[idx]?.attr?.("real-text");
    const n = hasBox ? counts.get(idx) || 0 : -1;
    if (el.getAttribute("data-count") === String(n)) return;
    el.setAttribute("data-count", String(n));
    // The number and its words are ONE regular, mid-grey run (size, weight and colour are set on the
    // element itself): the bold, dark page name beside it is what is read first.
    // "onsite" already means the whole site (the total, top left); this is per page
    el.textContent = "";
    if (n < 0) return;
    el.appendChild(document.createTextNode(n.toLocaleString("en-US")));
    const words = document.createElementNS("http://www.w3.org/2000/svg", "tspan");
    words.textContent = idx === DEFAULT_VALUE.UNKNOWN_URL_CLUSER_INDEX ? " on other pages" : " on page";
    el.appendChild(words);
  });
};

let cloudSyncQueued = false;
let lastSlotCounts = new Map<number, number>();
// the single-cluster view's cloud: a box of its own (there is no page box), sized for the one big cluster
const SINGLE_CLOUD_W = 460;
const SINGLE_CLOUD_H = 340;
const syncCloudCounts = () => {
  if (cloudSyncQueued) return;
  cloudSyncQueued = true;
  requestAnimationFrame(() => {
    cloudSyncQueued = false;
    // Count by the real bubbles' own slot (their `cluster`), so "All other
    // pages" counts like any other slot.
    const counts = new Map<number, number>();
    for (const b of bubblesData) {
      if (b.realTime && !departingSessionIds.has(b.visitor_id) && typeof b.cluster === "number") {
        counts.set(b.cluster, (counts.get(b.cluster) || 0) + 1);
      }
    }
    lastSlotCounts = counts;
    updateSlotCountLabels(counts);
    scheduleSlotTotals();
    if (pageViewDisabled) {
      // one cluster, everyone in it
      const c = clusterCenterCoords?.[DEFAULT_VALUE.ALL_CLUSTER_INDEX];
      if (c) {
        let everyone = 0;
        counts.forEach((n) => { everyone += n; });
        densityCloud.notifyParticleCount(DEFAULT_VALUE.ALL_CLUSTER_INDEX, "All pages", everyone, c.x, c.y, SINGLE_CLOUD_W, SINGLE_CLOUD_H);
      }
    } else {
      for (let idx = 0; idx <= DEFAULT_VALUE.UNKNOWN_URL_CLUSER_INDEX; idx++) {
        const c = clusterCenterCoords?.[idx];
        if (!c) continue;
        let label = "";
        let w = boxWidth;
        if (idx === DEFAULT_VALUE.UNKNOWN_URL_CLUSER_INDEX) {
          label = "All other pages";
          const rectW = Number(clusterRectInstances?.[idx]?.attr?.("width"));
          if (rectW > 0) w = rectW;
        } else {
          label = data.find((pg: any) => pg?.selected && pg.slotIndex === idx)?.name ?? "";
        }
        densityCloud.notifyParticleCount(idx, label, counts.get(idx) || 0, c.x, c.y, w, boxHeight);
      }
    }
    applyCloudBubbleVisibility();
  });
};

// STAGING DIAGNOSTIC (read from the build stamp): for every real bubble that is
// VISIBLE inside a cloud's box, say why. A bubble flying in is normal; one that stays
// is a bug, and which of these it is tells where to look:
//   flying  = the cloud is waiting for it and it is still moving to its spot
//   waiting = the cloud has been waiting >2.5s (it should have landed by now)
//   dotted  = the cloud HAS a dot for this visitor but the real bubble still shows
//   unknown = the cloud has never heard of this visitor
//   style   = marked hidden but its style says visible (something rewrote it)
const stuckBubbleReport = (): string => {
  const svgNode = svg?.node() as SVGSVGElement | undefined;
  if (!svgNode) return "";
  const els = new Map<string, SVGElement>();
  svgNode.querySelectorAll<SVGElement>("[data-bubble][realTime]").forEach((el) => {
    const id = el.getAttribute("visitor_id");
    if (id) els.set(id, el);
  });
  let flying = 0, waiting = 0, dotted = 0, unknown = 0, style = 0, worst = 0;
  let detail = "";
  svg?.selectAll("text.stuck-diag").remove();   // (labels drawn beside each stuck bubble in earlier builds)
  for (const b of bubblesData) {
    if (!b.realTime || typeof b.x !== "number" || typeof b.cluster !== "number") continue;
    // (in the single-cluster view everyone belongs to its one cloud)
    const slot = pageViewDisabled ? DEFAULT_VALUE.ALL_CLUSTER_INDEX : b.cluster;
    if (!densityCloud.isCloudActive(slot) || (!pageViewDisabled && slot >= DEFAULT_VALUE.UNKNOWN_URL_CLUSER_INDEX)) continue;
    const c = clusterCenterCoords?.[slot];
    const halfW = pageViewDisabled ? SINGLE_CLOUD_W / 2 : boxHalfWidth;
    const halfH = pageViewDisabled ? SINGLE_CLOUD_H / 2 : boxHalfHeight;
    if (!c || Math.abs(b.x - c.x) > halfW || Math.abs(b.y - c.y) > halfH) continue;
    const el = els.get(b.visitor_id);
    if (!el) continue;
    const shown = el.style.visibility !== "hidden" && el.style.opacity !== "0";
    if (el.getAttribute("data-cloud-hidden") === "1" && shown) { style++; continue; }
    if (!shown) continue;
    const info = densityCloud.getVisitorCloudInfo(b.visitor_id);
    const note = () =>
      `${b.visitor_id.slice(0, 6)} ${info.state} age ${(info.ageMs / 1000).toFixed(1)}s dist ${Math.round(info.distToTarget)} ` +
      `clouded ${cloudedVisitors.has(b.visitor_id)} vis "${el.style.visibility}" attr ${el.getAttribute("data-cloud-hidden")} ` +
      `cluster ${b.cluster} sim ${Math.round(b.x)},${Math.round(b.y)}`;
    const tag = `${info.state} ${(info.ageMs / 1000).toFixed(1)}s d${Math.round(info.distToTarget)}${cloudedVisitors.has(b.visitor_id) ? " clouded" : ""}`;
    if (info.state === "landed") { dotted++; detail = note(); }
    else if (info.state === "none") { unknown++; detail = note(); }
    else if (info.state === "pending") {
      if (info.ageMs > 2500) { waiting++; if (info.ageMs > worst) { worst = info.ageMs; detail = note(); } } else flying++;
    }
  }
  return `bubbles in clouds: flying ${flying}, waiting ${waiting}${waiting ? ` (longest ${(worst / 1000).toFixed(1)}s)` : ""}, dotted ${dotted}, unknown ${unknown}, style ${style}${detail ? ` | e.g. ${detail}` : ""}`;
};

// A slot showing a cloud must not ALSO show its real bubbles, pulses and
// badges. Everything tied to a visitor carries a visitor_id attribute, so map
// each visitor to the slot it is in and hide exactly those.
const applyCloudBubbleVisibility = () => {
  const svgNode = svg?.node() as SVGSVGElement | undefined;
  if (!svgNode) return;
  // the count labels are state too: re-checked here, never left to one event
  updateSlotCountLabels(lastSlotCounts);
  const slotOf = new Map<string, number>();
  for (const b of bubblesData) {
    if (b.realTime) slotOf.set(b.visitor_id, pageViewDisabled ? DEFAULT_VALUE.ALL_CLUSTER_INDEX : b.cluster);
  }
  // Only visitors whose real bubble has LANDED in the cloud (they have a dot)
  // are handed off; a bubble still flying in keeps showing as the real thing.
  const landed = densityCloud.getLandedVisitorIds();
  const nextClouded = new Set<string>();
  slotOf.forEach((slot, vid) => {
    if (densityCloud.isCloudActive(slot) && landed.has(vid)) nextClouded.add(vid);
  });
  let changed = nextClouded.size !== cloudedVisitors.size;
  if (!changed) {
    nextClouded.forEach((v) => { if (!cloudedVisitors.has(v)) changed = true; });
  }
  if (changed) {
    const selectedWasDot = highlightedVisitorId ? cloudedVisitors.has(highlightedVisitorId) : false;
    cloudedVisitors = nextClouded;
    // the selected row's visitor just turned back into a real bubble (its cloud dissolved): the SVG
    // ripple was never drawn while it was a dot, so draw it now
    if (highlightedVisitorId && selectedWasDot && !cloudedVisitors.has(highlightedVisitorId)) {
      setSessionHighlight(highlightedVisitorId);
    }
    // forces read the set when (re)initialised, so nudge the simulation: the
    // neighbours relax back and the hidden bubbles stop pushing on them
    reheatRealtimeSimulation(false);
  }
  svgNode.querySelectorAll<SVGElement>("[visitor_id]").forEach((el) => {
    const hide = cloudedVisitors.has(el.getAttribute("visitor_id") || "");
    // judged by what is actually applied, not by the marker alone: if something
    // else rewrote the style the marker stays "1" and the element would show forever
    const isHidden = el.getAttribute("data-cloud-hidden") === "1" && el.style.visibility === "hidden";
    if (hide === isHidden) return;
    // fade (opacity only, cheap) rather than pop, and stop it catching the
    // pointer while invisible
    // visibility, not just opacity: the idle/typing badges run CSS keyframe
    // animations on opacity, and a running animation overrides an inline
    // opacity. visibility flips at the END of the fade (delay = duration).
    // An instant swap, never a crossfade: a fading real bubble beside its new
    // dot is two copies of one visitor.
    // (never touch `transition`: the app eases every real bubble's movement with a
    // stylesheet transition, and an inline override would switch that off for good)
    if (hide) {
      el.setAttribute("data-cloud-hidden", "1");
      el.style.opacity = "0";
      el.style.visibility = "hidden";
      el.style.pointerEvents = "none";
    } else {
      el.removeAttribute("data-cloud-hidden");
      el.style.visibility = "";
      el.style.opacity = "";
      el.style.pointerEvents = "";
    }
  });
};

const CLOUD_TRANSIENT_MS: Record<string, number> = {
  clicking: CLICK_PULSE_DURATION_MS,
  typing: TYPING_STATE_CLEAR_MS,
  scrolling: SCROLL_PULSE_CLEAR_MS,
};
const CLOUD_PULSE_STATE: Record<string, string> = {
  click: "clicking", typing: "typing", idle: "idle", reading: "reading",
  watching: "watching", active: "focused", scroll: "scrolling", away: "away", return: "focused",
};

const notifyCloudInteraction = (sessionId: string, type: string) => {
  const state = CLOUD_PULSE_STATE[type];
  if (!state) return;
  densityCloud.notifyInteractionState(sessionId, state);
  const ttl = CLOUD_TRANSIENT_MS[state];
  if (ttl) {
    const t0 = densityCloud.getStateT0(sessionId);
    window.setTimeout(() => {
      // a newer event of any kind restarts the clock; only revert if this
      // exact event is still the latest
      if (
        densityCloud.getInteractionState(sessionId) === state &&
        densityCloud.getStateT0(sessionId) === t0
      ) {
        densityCloud.notifyInteractionState(sessionId, "focused");
      }
    }, ttl);
  }
};

export const applyInteractionPulse = ({
  sessionId,
  type,
}: {
  sessionId: string;
  type: "click" | "typing" | "idle" | "reading" | "watching" | "active" | "scroll" | "away" | "return";
}) => {
  /*
   * 🔴 AWAY SUPERSEDES IDLE. ENFORCED HERE BECAUSE THE CLIENT CANNOT BE TRUSTED
   * TO STOP (2026-09-13).
   *
   * Seen on production: a bubble wearing the away badge AND the Zzz at once.
   * Measured from the live socket stream - the visitor was away and the
   * tracking script kept emitting `idle` anyway:
   *
   *     0.0s  observing production
   *     2.4s  IDLE session=4121b2
   *    30.4s  IDLE session=4121b2
   *
   * The cause is in analytics.js: `handleActivityReset` is bound to MOUSEMOVE
   * and re-arms the idle timer. A window that is VISIBLE BUT NOT FOCUSED - you
   * alt-tabbed to another app - still receives mousemove, so moving the mouse
   * across the screen re-armed idle behind the visitor's back. (The 28 second
   * gap is Chrome throttling timers in a hidden tab.)
   *
   * That is fixed in the script too, but 🔴 analytics.js CANNOT BE VERSIONED OR
   * CACHE-BUSTED - its URL is hardcoded in snippets on customer sites forever,
   * and stale copies have persisted for MONTHS. So the client fix reaches real
   * visitors slowly, and THIS guard is what fixes the dashboard now, for every
   * visitor still running an old script.
   *
   * PULSE_VOCABULARY.md's precedence is away > watching > reading > idle. An
   * idle arriving for a visitor already known to be away is not news, it is
   * noise from a timer nobody stopped.
   */
  if (
    (type === "idle" || type === "reading" || type === "watching" || type === "active") &&
    awaySessionIds.has(sessionId)
  ) {
    return;
  }

  notifyCloudInteraction(sessionId, type);
  syncCloudCounts();

  const shapeNode = svg
    ?.select(
      `circle[realTime=true][visitor_id="${sessionId}"], rect[realTime=true][visitor_id="${sessionId}"]`
    )
    .node() as SVGGraphicsElement | null;
  const shapeDatum = shapeNode && (shapeNode as any).__data__;
  if (!shapeNode || !shapeDatum) {
    return;
  }

  const parent = shapeNode.parentNode as Element | null;
  const parentSelection = d3.select(parent ?? (svg?.node() as any));
  const radius = getBubbleRadius(shapeDatum);
  const isIconSize = useIcons;

  // Any click/typing/scroll is proof the visitor is back, so the idle state
  // has to end IMMEDIATELY rather than waiting out IDLE_PULSE_CLEAR_MS.
  //
  // That timeout is a "no news" fallback, not a liveness signal: it exists
  // only to expire an idle streak once the tracking script STOPS re-emitting
  // `idle` every IDLE_EMIT_INTERVAL_MS (4s), which is why it has to be longer
  // than that interval. It says nothing about a visitor who has actively done
  // something - so left to it alone, a bubble kept snoring for up to 9 seconds
  // while visibly clicking and typing.
  //
  // The emit is guarded on `wasIdle` deliberately: without it every single
  // throttled click/scroll event would push a `sessionIdleChanged` into the
  // Sessions tab, which is exactly the per-event fan-out that CLAUDE.md's perf
  // note 8 is about. This only fires on a real idle -> awake transition.
  /*
   * 🔴 AN ACTION RETURNS THE BUBBLE TO NAKED.
   *
   * click / typing are proof the visitor is doing something OTHER than reading,
   * so neither resting state is true any more and BOTH glyphs come off. The
   * resting state re-establishes itself on its own once they go quiet again -
   * reading if they scrolled recently, idle after the quiet window.
   *
   * 🔴 SCROLL IS DELIBERATELY NOT IN THIS LIST. Scrolling IS reading behaviour -
   * it is the very evidence the reading state is derived from - so it SETS the
   * paragraph rather than clearing it (see below). Chris on staging: "the
   * reading glyph doesn't show up right after a scroll... maybe up to 6-8s".
   * That was the dashboard waiting for the client's resting-state tick, which
   * only fires after 8s of stillness. The dashboard already has the evidence
   * the moment the scroll arrives.
   *
   * Also excludes `reading` itself, which is a resting state arriving, not an
   * action: it clears idle (below) but must not clear itself.
   */
  if (type === "click" || type === "typing") {
    const readingPulses = parentSelection.selectAll(
      `[data-pulse-reading="true"][visitor_id="${sessionId}"]`
    );
    clearInteractionPulseTimeout(`reading-delay-${sessionId}`);
    if (!readingPulses.empty()) {
      readingPulses.remove();
      eventEmitter.emit("sessionReadingChanged", { sessionId, reading: false });
    }
  }

  /*
   * Drawing the paragraph lives here so BOTH the `reading` resting state and a
   * `scroll` can raise it. A scroll is the evidence; waiting for the client's
   * resting-state tick to relay that back was what made the glyph lag 8s.
   */
  const showReadingBadge = () => {
    if (parentSelection.selectAll(`[data-pulse-reading="true"][visitor_id="${sessionId}"]`).empty()) {
        /*
         * 🔴 DRAWN LIKE THE AWAY BADGE, BECAUSE THAT ONE IS READABLE.
         *
         * The first attempt was three thin rects filled in the BUBBLE'S OWN
         * colour, at the bubble's centre. Chris, looking at it on staging:
         * "Terrible. Can't even see it." He was right - a dark glyph on a dark
         * bubble is invisible, and the centre is where the icon already is.
         *
         * The away badge solves this by drawing every shape TWICE: a wide dark
         * UNDER stroke as a halo, then a narrow WHITE mark on top. That is what
         * makes it legible against any bubble colour. Same trick here, same
         * top-left position, same badgeR scale - so the two states read as
         * siblings rather than two unrelated decorations.
         *
         * Sharing the position is safe: away SUPERSEDES reading, so they can
         * never be on screen at the same moment.
         */
        /*
         * 🔴 SIZED AND WEIGHTED AGAINST THE AWAY BADGE, WHICH IS THE BAR.
         *
         * Third attempt. The first was invisible; the second had the right
         * shape but too little weight - Chris: "It's NOT as prominent as the
         * away glyph."
         *
         * Measured against the thing being compared to rather than guessed at
         * again: the away badge is a circle of radius badgeR drawn with stroke
         * widths badgeR*0.86 (dark halo) and badgeR*0.42 (white mark) - a very
         * chunky ring. Mine were 0.40 and 0.22, about HALF the weight, which is
         * why it read as a smudge beside it.
         *
         * FIFTH pass, and the shape finally changed rather than the scale.
         * Chris: "it's looking too BIG now. The lines need to be thicker
         * VERTICALLY... maybe getting rid of the middle line and then thicken
         * the 2 remaining lines."
         *
         * Exactly right, and it is the squint test from PULSE_VOCABULARY.md
         * doing its job: at this size a third line is detail nobody can
         * resolve, and it forces every stroke thinner to fit. Two thick lines
         * read as text; three thin ones read as noise. Box back down to roughly
         * the away badge's size, weight up.
         *
         * The earlier lesson, kept because it was also true -
         * Chris: "The glyph IS larger, but... I would like to see THICKER
         * lines. They're too thin." Enlarging the box made three hairlines
         * bigger, not bolder.
         *
         * So the WEIGHT moved instead: the white mark went 0.32 -> 0.46 of
         * badgeR, with the dark halo at 0.70 so the strokes sit in a solid
         * block rather than floating. Widened to 2.3*badgeR across as well, so
         * it reads as a paragraph rather than a square of bars.
         */
        const badgeR = Math.max(4.5, radius * 0.40);
        const dx = -radius * 0.78;
        const dy = -radius * 0.78;

        const badge = parentSelection
          .append("g")
          .attr("data-pulse-reading", "true")
          .attr("visitor_id", sessionId)
          .attr("class", "pulse-reading-badge")
          .attr("pointer-events", "none")
          .attr("data-badge-dx", dx)
          .attr("data-badge-dy", dy)
          .attr("transform", `translate(${shapeDatum.x + dx}, ${shapeDatum.y + dy})`);

        /*
         * Sized to the SAME FOOTPRINT as the away badge: that one is a circle of
         * radius badgeR, so the paragraph spans 2*badgeR across. The first two
         * attempts were smaller and thinner than the away glyph and read as
         * smudges - Chris, twice: "Can't even see it", then "still too small".
         */
        const half = badgeR * 0.95;
        const gap = badgeR * 1.08;   // separation between the two lines

        [
          { cls: "pulse-reading-badge-under", w: badgeR * 0.86 },
          { cls: "pulse-reading-badge-mark", w: badgeR * 0.52 },
        ].forEach(({ cls, w }) => {
          /*
           * TWO lines, not three - Chris's call, and it is the right one. At
           * bubble scale a third line only buys detail nobody can resolve,
           * while forcing every stroke thinner to fit. Two lets each one be
           * genuinely THICK, which is what makes it legible.
           */
          [-gap / 2, gap / 2].forEach((y, i) => {
            // The lower line is SHORT, the way a paragraph's last line is.
            // Without it two equal bars read as an equals sign, not as text.
            const lineHalf = i === 1 ? half * 0.6 : half;
            badge
              .append("line")
              .attr("class", cls)
              .attr("x1", -half)
              .attr("y1", y)
              .attr("x2", -half + lineHalf * 2)
              .attr("y2", y)
              .attr("stroke-width", w);
          });
        });
      eventEmitter.emit("sessionReadingChanged", { sessionId, reading: true });
    }
  };

  /*
   * A scroll raises the paragraph IMMEDIATELY. It is the evidence the reading
   * state is derived from, so there is nothing to wait for - and the client's
   * own resting-state tick cannot report it for another 8 seconds, because
   * that timer only fires once the visitor has gone still.
   */
  if (type === "scroll") {
    /*
     * 🔴 A NEW SCROLL TAKES THE PARAGRAPH DOWN FIRST, THEN BRINGS IT BACK.
     *
     * The delay below was only ever applied to the FIRST appearance, so once
     * the paragraph was up, scrolling again left it sitting there while the
     * scroll pulse played over it - the same two-marks-at-once glitch Chris
     * objected to, just reached from a different direction.
     *
     * An action interrupts its resting state. That has to be true every time,
     * not only the first time, or the rule is really "sometimes".
     */
    const existing = parentSelection.selectAll(
      `[data-pulse-reading="true"][visitor_id="${sessionId}"]`
    );
    if (!existing.empty()) {
      existing.remove();
      eventEmitter.emit("sessionReadingChanged", { sessionId, reading: false });
    }
  }

  if (type === "scroll" && !awaySessionIds.has(sessionId)) {
    /*
     * 🔴 LET THE SCROLL ANIMATION FINISH FIRST (2026-09-14).
     *
     * Chris: "the reading icon shows up INSTANTLY when I scroll. The animation
     * of the scroll hasn't ended yet and the reading icon is there."
     *
     * Two marks on one bubble at once reads as a glitch rather than a sequence.
     * The scroll pulse is a transient ACTION and the paragraph is the RESTING
     * STATE underneath it, so the state should settle in as the action fades -
     * not race it.
     *
     * Delayed by the scroll pulse's own lifetime rather than a number picked to
     * feel right: SCROLL_PULSE_CLEAR_MS is when that element is removed, so the
     * paragraph arrives just after. Tying it to the same constant means the two
     * cannot drift apart if the animation is ever retuned.
     *
     * Each scroll RESETS the delay, so continuous scrolling shows only the
     * scroll pulse and the paragraph appears once they actually pause.
     */
    dropBadge(parentSelection, "idle", sessionId, emitBadgeEvent);

    clearInteractionPulseTimeout(`reading-delay-${sessionId}`);
    interactionPulseTimeouts.set(
      `reading-delay-${sessionId}`,
      window.setTimeout(() => {
        interactionPulseTimeouts.delete(`reading-delay-${sessionId}`);

        // Re-check on arrival, not just on scheduling: in the meantime she may
        // have looked away, or the bubble may have gone entirely.
        if (awaySessionIds.has(sessionId)) {
          return;
        }

        /*
         * 🔴 WATCHING OUTRANKS READING, AND THIS PATH IS THE ONE THAT INFERS
         * READING LOCALLY (2026-09-14).
         *
         * Everywhere else the resting state arrives from the tracking script,
         * which already applies the whole precedence in computeRestingState:
         * media playing wins over "scrolled recently". THIS callback is the
         * exception - it promotes a scroll to reading on the dashboard's own
         * initiative, and it was checking only `away`, so it enforced half of
         * an order that has four terms.
         *
         * Reported by Chris on staging: play a video, then scroll, and the
         * paragraph stacked on top of the play triangle.
         *
         * The triangle in the DOM is the authority here: it is there because
         * the client asserted `watching`, and a local inference must never
         * overrule a state the visitor's own browser actually reported. When
         * playback stops the client sends `reading` or `active`, and those
         * paths displace it properly.
         */
        const isWatching = !parentSelection
          .selectAll(`[data-pulse-watching="true"][visitor_id="${sessionId}"]`)
          .empty();
        if (isWatching) {
          return;
        }
        const stillThere = svg
          ?.select(
            `circle[realTime=true][visitor_id="${sessionId}"], rect[realTime=true][visitor_id="${sessionId}"]`
          )
          .node();
        if (!stillThere) {
          return;
        }

        showReadingBadge();
      }, SCROLL_PULSE_CLEAR_MS + 120)
    );
  }

  if (type !== "idle") {
    const idlePulses = parentSelection.selectAll(
      `[data-pulse-idle="true"][visitor_id="${sessionId}"]`
    );
    const wasIdle = !idlePulses.empty();
    idlePulses.remove();
    clearInteractionPulseTimeout(`idle-${sessionId}`);
    if (wasIdle) {
      eventEmitter.emit("sessionIdleChanged", { sessionId, idle: false });
    }
  }

  if (type === "click") {
    parentSelection.selectAll(`[data-pulse-click="true"][visitor_id="${sessionId}"]`).remove();
    shapeNode.classList.remove("pulse-click-flash");
    // eslint-disable-next-line no-unused-expressions
    (shapeNode as any).offsetWidth; // force reflow so a re-triggered class restarts the animation
    shapeNode.classList.add("pulse-click-flash");

    parentSelection
      .append("circle")
      .attr("data-pulse-click", "true")
      .attr("visitor_id", sessionId)
      .attr("class", "pulse-click-ring")
      .attr("cx", shapeDatum.x)
      .attr("cy", shapeDatum.y)
      .attr("r", radius);

    parentSelection
      .append("circle")
      .attr("data-pulse-click", "true")
      .attr("visitor_id", sessionId)
      .attr("class", "pulse-click-burst")
      .attr("cx", shapeDatum.x)
      .attr("cy", shapeDatum.y)
      .attr("r", radius * 0.55);

    // Shown at every bubble size, including plain small circles - now that
    // the cursor is a brief entrance/click/exit that fully disappears
    // before the flash/ring/burst even start (sequential, not overlapping),
    // it doesn't have the earlier "permanently swallows an 8px bubble"
    // problem a static always-on triangle had. Sized deliberately larger
    // than the bubble itself so it reads clearly during its brief visible
    // window, rather than trying to stay contained within it.
    //
    // Wrapped in a <g> rather than animating the <path> directly: the CSS
    // "slide in, click, disappear" animation below needs to animate
    // `transform` on the cursor itself, but this element's POSITION is also
    // driven by a JS-set `transform` ATTRIBUTE every tick (see
    // paintRealtimeBubblePositions) - a CSS `transform` on the same element
    // would completely replace that attribute rather than combine with it
    // (presentation attributes lose to any CSS transform outright), snapping
    // the cursor to the wrong spot every frame. Splitting position (outer
    // <g>, JS-driven, tick-synced) from the entrance animation (inner
    // <path>, CSS-driven, local coordinates) avoids that conflict entirely -
    // standard SVG nested-transform composition, not a special case.
    {
      const triSize = radius * 4.2;
      const cursorGroup = parentSelection
        .append("g")
        .attr("data-pulse-click", "true")
        .attr("visitor_id", sessionId)
        .attr("class", "pulse-click-cursor-group")
        .attr("data-tri-offset", triSize / 2)
        .attr("data-tri-scale", triSize / 20)
        .attr(
          "transform",
          `translate(${shapeDatum.x - triSize / 2}, ${shapeDatum.y - triSize / 2}) scale(${
            triSize / 20
          })`
        );
      cursorGroup
        .append("path")
        .attr("class", "pulse-click-cursor-path")
        .attr("d", "M3 2 L17 10 L10 12 L7 18 Z");
    }

    clearInteractionPulseTimeout(`click-${sessionId}`);
    interactionPulseTimeouts.set(
      `click-${sessionId}`,
      window.setTimeout(() => {
        parentSelection.selectAll(`[data-pulse-click="true"][visitor_id="${sessionId}"]`).remove();
        shapeNode.classList.remove("pulse-click-flash");
        interactionPulseTimeouts.delete(`click-${sessionId}`);
      }, CLICK_PULSE_DURATION_MS)
    );

    // Every single click, not just a start/stop transition (unlike idle and
    // typing's own sustained-state events) - RightMenu.tsx only listens for
    // this while the matching session's row is the one currently open in the
    // drawer, so the fan-out is naturally capped at one row regardless of
    // how many sessions are onsite.
    eventEmitter.emit("sessionClickPulse", { sessionId });
  } else if (type === "typing") {
    parentSelection.selectAll(`[data-pulse-typing="true"][visitor_id="${sessionId}"]`).remove();

    const caretHeight = radius * 2.1;
    const caretWidth = Math.max(2, radius * 0.36);
    parentSelection
      .append("rect")
      .attr("data-pulse-typing", "true")
      .attr("visitor_id", sessionId)
      .attr("class", "pulse-typing-caret")
      .attr("x", shapeDatum.x - caretWidth / 2)
      .attr("y", shapeDatum.y - caretHeight / 2)
      .attr("width", caretWidth)
      .attr("height", caretHeight)
      .attr("rx", 1);

    clearInteractionPulseTimeout(`typing-${sessionId}`);
    interactionPulseTimeouts.set(
      `typing-${sessionId}`,
      window.setTimeout(() => {
        parentSelection.selectAll(`[data-pulse-typing="true"][visitor_id="${sessionId}"]`).remove();
        interactionPulseTimeouts.delete(`typing-${sessionId}`);
      }, TYPING_PULSE_DURATION_MS)
    );

    // Sustained "still typing" state, separate from the short-lived caret
    // pulse above - only transitions on/off, doesn't re-emit on every single
    // keystroke event within an ongoing typing burst.
    if (!interactionPulseTimeouts.has(`typing-state-${sessionId}`)) {
      eventEmitter.emit("sessionTypingChanged", { sessionId, typing: true });
    }
    clearInteractionPulseTimeout(`typing-state-${sessionId}`);
    interactionPulseTimeouts.set(
      `typing-state-${sessionId}`,
      window.setTimeout(() => {
        interactionPulseTimeouts.delete(`typing-state-${sessionId}`);
        eventEmitter.emit("sessionTypingChanged", { sessionId, typing: false });
      }, TYPING_STATE_CLEAR_MS)
    );
  } else if (type === "watching") {
    /*
     * 🔴 WATCHING - a play triangle, drawn with the away badge's two-pass
     * treatment so it is legible on a bubble of any colour (2026-09-14).
     *
     * PULSE_VOCABULARY.md's squint test: "Zzz is idle, a paragraph is reading,
     * a play triangle is watching." The set has to read without teaching.
     *
     * Same corner and same badgeR as reading, because the two are mutually
     * exclusive resting states - watching OUTRANKS reading, and the client
     * only ever emits one of them.
     *
     * "Never appear together" was the original claim here and it was WRONG:
     * the client emitting one of them is not the same as only one being drawn.
     * The dashboard also INFERS reading from a scroll (the reading-delay
     * timeout above), and that inference bypassed the client entirely, so
     * scrolling during playback stacked a paragraph on the triangle. Sharing a
     * corner made it unmissable. The guard lives in that callback.
     */
    if (parentSelection.selectAll(`[data-pulse-watching="true"][visitor_id="${sessionId}"]`).empty()) {
      const badgeR = Math.max(4.5, radius * 0.40);
      const dx = -radius * 0.78;
      const dy = -radius * 0.78;

      const badge = parentSelection
        .append("g")
        .attr("data-pulse-watching", "true")
        .attr("visitor_id", sessionId)
        .attr("class", "pulse-watching-badge")
        .attr("pointer-events", "none")
        .attr("data-badge-dx", dx)
        .attr("data-badge-dy", dy)
        .attr("transform", `translate(${shapeDatum.x + dx}, ${shapeDatum.y + dy})`);

      // A right-pointing triangle, nudged left of centre so its visual mass
      // sits where the eye expects the badge to be - a triangle's centroid is
      // not its bounding-box centre.
      const r = badgeR * 0.95;
      const points = `${-r * 0.55},${-r} ${r * 0.85},0 ${-r * 0.55},${r}`;

      [
        { cls: "pulse-watching-badge-under", w: badgeR * 0.9 },
        { cls: "pulse-watching-badge-mark", w: 0 },
      ].forEach(({ cls, w }) => {
        const tri = badge.append("polygon").attr("class", cls).attr("points", points);
        if (w) {
          // The halo is a thick STROKE around the same shape, so the triangle
          // keeps its silhouette instead of being boxed in.
          tri.attr("stroke-width", w).attr("stroke-linejoin", "round");
        }
      });

      eventEmitter.emit("sessionWatchingChanged", { sessionId, watching: true });
    }

    // The resting states are mutually exclusive - arriving at one takes the
    // others down.
    dropBadge(parentSelection, "idle", sessionId, emitBadgeEvent);
    dropBadge(parentSelection, "reading", sessionId, emitBadgeEvent);
    clearInteractionPulseTimeout(`idle-${sessionId}`);
    clearInteractionPulseTimeout(`reading-delay-${sessionId}`);

  } else if (type === "reading") {
    showReadingBadge();
    dropBadge(parentSelection, "watching", sessionId, emitBadgeEvent);

    // Reading and idle are mutually exclusive resting states, so arriving at
    // one must clear the other. Without this a visitor who stops scrolling
    // would wear a paragraph AND a Zzz - the same clash as away plus idle.
    dropBadge(parentSelection, "idle", sessionId, emitBadgeEvent);
    clearInteractionPulseTimeout(`idle-${sessionId}`);

    /*
     * 🔴 NO EXPIRY. A RESTING STATE PERSISTS UNTIL SOMETHING REPLACES IT.
     *
     * This used to expire after IDLE_PULSE_CLEAR_MS like a transient pulse, and
     * that is what made the glyph flicker on staging - reported as "the reading
     * icon kept appearing and disappearing".
     *
     * The mechanism: every mousemove calls resetIdleTimer, which CLEARS THE
     * EMIT INTERVAL. So the moment a visitor twitches the mouse, the tracking
     * script stops re-asserting the resting state, and nine seconds later this
     * timeout removed the glyph - even though nothing about the visitor had
     * changed. Then they went still, and eight seconds later it came back.
     *
     * PULSE_VOCABULARY.md already draws this distinction: ACTIONS are transient
     * moments that decay, RESTING STATES persist. `away` was always modelled
     * that way. reading and idle now are too: replaced by another resting
     * state, cleared by a real action, and never expired by a stopwatch.
     */
  } else if (type === "active") {
    /*
     * 🔴 THE RETRACTION. "active" means present and engaged with nothing to
     * claim - a NAKED bubble (2026-09-14).
     *
     * Every other resting state can only be displaced by a different one, so
     * without this the client had no way to say "that state is over". Pausing
     * a video left the play triangle on the bubble until `idle` happened to
     * displace it thirty seconds later.
     *
     * It draws nothing. It only takes things away.
     */
    dropBadge(parentSelection, "watching", sessionId, emitBadgeEvent);
    dropBadge(parentSelection, "reading", sessionId, emitBadgeEvent);
    dropBadge(parentSelection, "idle", sessionId, emitBadgeEvent);
    clearInteractionPulseTimeout(`reading-${sessionId}`);
    clearInteractionPulseTimeout(`idle-${sessionId}`);
    clearInteractionPulseTimeout(`reading-delay-${sessionId}`);

  } else if (type === "idle") {
    dropBadge(parentSelection, "watching", sessionId, emitBadgeEvent);

    // The other half of the mutual exclusion: decaying from reading back to
    // idle must take the paragraph away.
    dropBadge(parentSelection, "reading", sessionId, emitBadgeEvent);
    clearInteractionPulseTimeout(`reading-${sessionId}`);

    if (parentSelection.selectAll(`[data-pulse-idle="true"][visitor_id="${sessionId}"]`).empty()) {
      const zzzColor = setColor(shapeDatum.color);
      const sizes = isIconSize ? [11, 15] : [9, 12];
      sizes.forEach((size, i) => {
        parentSelection
          .append("text")
          .attr("data-pulse-idle", "true")
          .attr("visitor_id", sessionId)
          .attr("class", `pulse-idle-zzz pulse-idle-zzz-${i + 1}`)
          .attr("x", shapeDatum.x)
          .attr("y", shapeDatum.y)
          .attr("font-size", size)
          .style("fill", zzzColor as any)
          .text(i === 0 ? "z" : "Z");
      });
      // Only emit on the actual not-idle -> idle transition (this block only
      // runs once per idle streak, since subsequent idle events within
      // IDLE_PULSE_CLEAR_MS find the selector non-empty and skip re-creating
      // the text elements) - the Sessions tab row badge mirrors this same
      // transition rather than re-rendering on every repeated idle event.
      eventEmitter.emit("sessionIdleChanged", { sessionId, idle: true });
    }

    // Same reasoning as the reading branch above: a resting state persists
    // until something replaces it. The old "no news" expiry existed because the
    // script stops re-emitting, which is exactly the condition that made the
    // glyph flicker rather than a signal that the visitor woke up.
  } else if (type === "away" || type === "return") {
    /*
     * 🔴 REMEMBER WHO IS AWAY (2026-09-10).
     *
     * Away is the one interaction state that PERSISTS - every other pulse is a
     * moment that clears itself. It arrives as a transition, so nothing
     * re-asserts it later, and any repaint that re-creates the bubble DOM
     * silently drops it.
     *
     * drawUserBubble already restores it from `sessionData.away` for a bubble
     * being drawn fresh, but drawExistingBubble - the repaint used on every
     * resize - appends circles directly through a d3 enter() join and never
     * goes near that code. So resizing the window rebuilt the grid and the
     * away glyph vanished, reported directly: "I have the away glyph on and
     * then change the viewport/window sizing and the flicker comes back".
     *
     * Tracked here, at the single point where the state actually changes, so
     * the repaint path has something authoritative to restore from.
     */
    if (type === "away") {
      awaySessionIds.add(sessionId);
    } else {
      awaySessionIds.delete(sessionId);
    }

    /*
     * Tab visibility, deliberately styled as a STATE on the bubble itself
     * rather than another badge: "away" is not an event that happens, it's a
     * condition that persists, and the visualization already has four badges
     * competing for space around a small circle.
     *
     * Opacity ONLY - no `filter: grayscale()`, however much better it would
     * look. Real-time bubbles move on every d3 tick, and per perf note 2 in
     * the project CLAUDE.md a CSS filter on a moving SVG element forces the
     * compositor onto a rasterized path. Opacity is compositor-only, so this
     * costs effectively nothing no matter how many visitors are away at once.
     *
     * No timeout to clear it, unlike every other pulse: away persists until
     * the visitor actually comes back. `clearInteractionPulses` still removes
     * it when the session ends, so it cannot outlive its bubble.
     */
    shapeNode.classList.toggle("pulse-away", type === "away");

    parentSelection.selectAll(`[data-pulse-away="true"][visitor_id="${sessionId}"]`).remove();

    if (type === "away") {
      /*
       * A "no entry" glyph (circle + diagonal slash) in the bubble's TOP-LEFT
       * corner. Drawn in SVG rather than using the literal emoji: an emoji
       * renders differently per platform, can't be recoloured to contrast with
       * whatever colour the bubble underneath happens to be, and sits on the
       * text baseline instead of centring on a point.
       *
       * Top-left specifically because the goal star badge offsets toward the
       * BOTTOM-RIGHT in icon mode (appendGoalBadge's data-offset-x/y are both
       * positive), so this is the one corner that is reliably free.
       *
       * The dark under-stroke is a deliberate substitute for a drop-shadow:
       * per perf note 2 in the project CLAUDE.md, a CSS filter on an element
       * that moves every tick forces the compositor onto a rasterised path.
       * Two stacked strokes fake the same separation for free.
       */
      const badgeR = Math.max(4.5, radius * 0.42);
      const dx = -radius * 0.78;
      const dy = -radius * 0.78;
      const slash = badgeR * 0.62;

      const badge = parentSelection
        .append("g")
        .attr("data-pulse-away", "true")
        .attr("visitor_id", sessionId)
        .attr("class", "pulse-away-badge")
        .attr("pointer-events", "none")
        // Read back by paintRealtimeBubblePositions every tick to keep the
        // badge pinned to its bubble's corner as the simulation moves it.
        .attr("data-badge-dx", dx)
        .attr("data-badge-dy", dy)
        .attr("transform", `translate(${shapeDatum.x + dx}, ${shapeDatum.y + dy})`);

      [
        { cls: "pulse-away-badge-under", w: badgeR * 0.86 },
        { cls: "pulse-away-badge-mark", w: badgeR * 0.42 },
      ].forEach(({ cls, w }) => {
        badge.append("circle").attr("class", cls).attr("r", badgeR).attr("stroke-width", w);
        badge
          .append("line")
          .attr("class", cls)
          .attr("x1", -slash)
          .attr("y1", slash)
          .attr("x2", slash)
          .attr("y2", -slash)
          .attr("stroke-width", w);
      });
    }

    // Away supersedes the transient pulses - a hidden tab is not typing or
    // scrolling, and leaving those glyphs on a faded bubble reads as a bug.
    if (type === "away") {
      /*
       * 🔴 AWAY SUPERSEDES IDLE, AND IDLE WAS MISSING FROM THIS LIST
       * (2026-09-11).
       *
       * Reported directly, with a screenshot of a bubble wearing both glyphs at
       * once: "away AND asleep? I thought asleep was when the user was
       * focused?" - which is exactly right. PULSE_VOCABULARY.md sets the
       * precedence as away -> watching -> reading -> idle, and the two states
       * are mutually exclusive by definition: idle means present but not doing
       * anything, away means not looking at all.
       *
       * Typing and scroll were already cleared here. Idle was not, so a visitor
       * who went idle and THEN away kept the Zzz alongside the away glyph until
       * its own 9s timer happened to expire.
       *
       * The timeout is cleared too, not just the element: leaving it armed
       * means it fires later and emits sessionIdleChanged(false) for a session
       * that has not been idle for some time - harmless today, but the kind of
       * stale event that makes a row badge flicker for no reason.
       */
      clearInteractionPulseTimeout(`idle-${sessionId}`);
      parentSelection.selectAll(`[data-pulse-idle="true"][visitor_id="${sessionId}"]`).remove();
      eventEmitter.emit("sessionIdleChanged", { sessionId, idle: false });

      /*
       * 🔴 AND READING, FOR THE SAME REASON - reported with a screenshot of the
       * two glyphs STACKED (2026-09-14).
       *
       * The guard at the top of this function stops a reading pulse being ADDED
       * while away, which is necessary but not sufficient: a visitor who was
       * already reading and THEN looked away kept the paragraph, and since both
       * badges are pinned to the same top-left corner they simply drew on top
       * of each other.
       *
       * Blocking the new is not the same as clearing the existing. The away
       * branch has to actively take down every state it supersedes.
       */
      clearInteractionPulseTimeout(`reading-delay-${sessionId}`);
      parentSelection.selectAll(`[data-pulse-reading="true"][visitor_id="${sessionId}"]`).remove();
      eventEmitter.emit("sessionReadingChanged", { sessionId, reading: false });
      parentSelection.selectAll(`[data-pulse-watching="true"][visitor_id="${sessionId}"]`).remove();
      eventEmitter.emit("sessionWatchingChanged", { sessionId, watching: false });
      parentSelection.selectAll(`[data-pulse-typing="true"][visitor_id="${sessionId}"]`).remove();
      parentSelection.selectAll(`[data-pulse-scroll="true"][visitor_id="${sessionId}"]`).remove();
      ["typing", "typing-state", "scroll"].forEach((key) =>
        clearInteractionPulseTimeout(`${key}-${sessionId}`)
      );
    }

    eventEmitter.emit("sessionAwayChanged", { sessionId, away: type === "away" });
  } else if (type === "scroll") {
    // Sustained, looping treatment (like idle/typing), not a single-shot
    // retrigger (like click) - a real scroll burst fires many throttled
    // events in a row, and restarting a 2.6s sweep animation on every one
    // would never let it complete a full cycle. Only the first event in a
    // burst appends the line; later events just extend the clear timeout.
    if (parentSelection.selectAll(`[data-pulse-scroll="true"][visitor_id="${sessionId}"]`).empty()) {
      // Same width formula as the typing caret (Math.max(2, radius * 0.36))
      // so the two glyphs read as the same visual weight, per direct
      // request. Base position is vertically centered on the bubble - the
      // existing generic tick-sync in paintRealtimeBubblePositions already
      // recenters any tagged rect sibling to shapeDatum.y - height/2 every
      // frame (same mechanism the typing caret relies on), so the CSS
      // animation only needs to add a symmetric +/- translateY offset on
      // top of that, rather than tracking an absolute "top edge" position
      // itself (which the tick-sync would otherwise fight over every tick).
      const thickness = Math.max(2, radius * 0.36);
      const halfTravel = radius - thickness / 2;
      parentSelection
        .append("rect")
        .attr("data-pulse-scroll", "true")
        .attr("visitor_id", sessionId)
        .attr("class", "pulse-scroll-line")
        .attr("x", shapeDatum.x - radius)
        .attr("y", shapeDatum.y - thickness / 2)
        .attr("width", radius * 2)
        .attr("height", thickness)
        .attr("rx", 1)
        .style("--scan-half-travel", `${halfTravel}px`);
      eventEmitter.emit("sessionScrollChanged", { sessionId, scrolling: true });
    }

    clearInteractionPulseTimeout(`scroll-${sessionId}`);
    interactionPulseTimeouts.set(
      `scroll-${sessionId}`,
      window.setTimeout(() => {
        parentSelection.selectAll(`[data-pulse-scroll="true"][visitor_id="${sessionId}"]`).remove();
        interactionPulseTimeouts.delete(`scroll-${sessionId}`);
        eventEmitter.emit("sessionScrollChanged", { sessionId, scrolling: false });
      }, SCROLL_PULSE_CLEAR_MS)
    );
  }
};

export const clearInteractionPulses = (visitorId: string) => {
  const hadIdlePulse = !svg
    ?.selectAll(`[data-pulse-idle="true"][visitor_id="${visitorId}"]`)
    .empty();
  const hadTypingState = interactionPulseTimeouts.has(`typing-state-${visitorId}`);
  const hadScrollPulse = interactionPulseTimeouts.has(`scroll-${visitorId}`);
  svg?.selectAll(`[data-pulse-click="true"][visitor_id="${visitorId}"]`).remove();
  svg?.selectAll(`[data-pulse-typing="true"][visitor_id="${visitorId}"]`).remove();
  svg?.selectAll(`[data-pulse-idle="true"][visitor_id="${visitorId}"]`).remove();
  svg?.selectAll(`[data-pulse-scroll="true"][visitor_id="${visitorId}"]`).remove();
  // A departing visitor's reading / watching / away badges leave with the bubble, and the Sessions drawer is told, or the row keeps the glyph for good (production, 2026-10-15: the last
  // session of the day still wore READING hours after it had left). The away id is also forgotten here: it used to stay in this set for the life of the tab.
  const wasAway = awaySessionIds.delete(visitorId);
  const droppedBadges = svg ? dropAllBadges(svg as any, visitorId, emitBadgeEvent, ["reading", "watching", "away"]) : [];
  if (wasAway && !droppedBadges.includes("away")) {
    eventEmitter.emit("sessionAwayChanged", { sessionId: visitorId, away: false });
  }
  svg?.select(`circle[realTime=true][visitor_id="${visitorId}"], rect[realTime=true][visitor_id="${visitorId}"]`)
    .classed("pulse-click-flash", false);
  // The away state has no self-clearing timeout (it persists until the
  // visitor returns), so ending the session is the only thing that removes it.
  svg?.select(`circle[realTime=true][visitor_id="${visitorId}"], rect[realTime=true][visitor_id="${visitorId}"]`)
    .classed("pulse-away", false);
  ["click", "typing", "idle", "typing-state", "scroll"].forEach((type) =>
    clearInteractionPulseTimeout(`${type}-${visitorId}`)
  );
  if (hadIdlePulse) {
    eventEmitter.emit("sessionIdleChanged", { sessionId: visitorId, idle: false });
  }
  if (hadTypingState) {
    eventEmitter.emit("sessionTypingChanged", { sessionId: visitorId, typing: false });
  }
  if (hadScrollPulse) {
    eventEmitter.emit("sessionScrollChanged", { sessionId: visitorId, scrolling: false });
  }
};

const clearAllInteractionPulses = () => {
  // A full reset removes every badge at once; the drawer re-derives its row glyphs from what is on screen, so one nudge per state is enough (none is sent when nothing was up).
  const hadStateBadges = !!svg && !svg.selectAll("[data-pulse-idle=true], [data-pulse-reading=true], [data-pulse-watching=true], [data-pulse-away=true]").empty();
  svg?.selectAll("[data-pulse-click=true], [data-pulse-typing=true], [data-pulse-idle=true], [data-pulse-reading=true], [data-pulse-watching=true], [data-pulse-scroll=true], [data-pulse-away=true]").remove();
  svg?.selectAll("circle[realTime=true], rect[realTime=true]").classed("pulse-click-flash", false);
  interactionPulseTimeouts.forEach((id, key) => {
    window.clearTimeout(id);
    if (key.startsWith("idle-")) {
      eventEmitter.emit("sessionIdleChanged", { sessionId: key.slice("idle-".length), idle: false });
    } else if (key.startsWith("typing-state-")) {
      eventEmitter.emit("sessionTypingChanged", {
        sessionId: key.slice("typing-state-".length),
        typing: false,
      });
    } else if (key.startsWith("scroll-")) {
      eventEmitter.emit("sessionScrollChanged", {
        sessionId: key.slice("scroll-".length),
        scrolling: false,
      });
    } else if (key.startsWith("reading-delay-")) {
      eventEmitter.emit("sessionReadingChanged", {
        sessionId: key.slice("reading-delay-".length),
        reading: false,
      });
    }
  });
  interactionPulseTimeouts.clear();
  if (hadStateBadges) {
    ["sessionReadingChanged", "sessionWatchingChanged", "sessionIdleChanged", "sessionAwayChanged"].forEach((event) => eventEmitter.emit(event, {}));
  }
};

// Ground-truth reads of "which sessions are CURRENTLY idle/typing/scrolling
// right now", derived directly from interactionPulseTimeouts' own keys (a
// timeout for `${type}-${sessionId}` only exists while that state is
// actually active - the same bookkeeping the pulse logic above already
// relies on for its own clear/re-arm behavior). RightMenu.tsx's row badges
// were initially driven purely by the one-shot sessionIdleChanged/
// sessionTypingChanged/sessionScrollChanged transition events, which only
// fire once per streak - if a session was ALREADY mid-streak before its row
// existed/was being watched (e.g. clicking a bubble that's already Zzz'ing,
// per direct user report), that one-shot event had already fired and
// wouldn't fire again, so the row's local Set never learned about it despite
// the bubble still visibly animating. These getters let RightMenu
// periodically reconcile its row-level state against the real ground truth
// instead of relying solely on catching every transition live.

/*
 * Unlike the other three, away has NO entry in interactionPulseTimeouts - it
 * is not a decaying pulse but a state that persists until the visitor comes
 * back, so there is no timeout to enumerate. The DOM badge itself is the
 * ground truth. This runs only on reconcile (not per tick), so the query cost
 * is irrelevant here - see perf note 5 for why that distinction matters.
 */
/*
 * How many visitors are on the site right now.
 *
 * realtimeNodes is the array the d3 simulation itself holds (see its
 * declaration) - the same one every arrival pushes into and every departure
 * splices out of - so its length IS the live onsite count, with no DOM query
 * and no separate counter that could drift out of step with the bubbles.
 *
 * Deliberately a plain length read rather than a derived/incremented total:
 * per perf note 10, occupancy is a STATE and must be derived, never
 * accumulated, or a resync double-counts it.
 */
export const getOnsiteVisitorCount = (): number => realtimeNodes.length;

export const getActiveAwaySessionIds = (): string[] => {
  // Same cast paintRealtimeBubblePositions uses - d3's node() is loosely typed.
  const svgNode = svg?.node() as SVGSVGElement | undefined;
  if (!svgNode) {
    return [];
  }
  // Unquoted attribute value so the whole selector fits in double quotes -
  // matches the existing [data-pulse-click=true] style used by the per-tick
  // scan, and satisfies the repo's doublequote lint rule.
  const nodes = svgNode.querySelectorAll<SVGGraphicsElement>(
    "[data-pulse-away=true][visitor_id]"
  );
  return Array.from(nodes)
    .map((node) => node.getAttribute("visitor_id") || "")
    .filter(Boolean);
};

export const getActiveTypingSessionIds = (): string[] =>
  Array.from(interactionPulseTimeouts.keys())
    .filter((key) => key.startsWith("typing-state-"))
    .map((key) => key.slice("typing-state-".length));

export const getActiveScrollingSessionIds = (): string[] =>
  Array.from(interactionPulseTimeouts.keys())
    .filter((key) => key.startsWith("scroll-"))
    .map((key) => key.slice("scroll-".length));

// Derived from the badge actually on screen, like away above. The old version
// read the "reading-delay-" timer key, which is deleted the moment the delay
// fires - i.e. exactly when the badge appears - so a row could never show it.
const activeBadgeSessionIds = (selector: string): string[] => {
  const svgNode = svg?.node() as SVGSVGElement | undefined;
  if (!svgNode) {
    return [];
  }
  return Array.from(svgNode.querySelectorAll<SVGGraphicsElement>(selector))
    .map((node) => node.getAttribute("visitor_id") || "")
    .filter(Boolean);
};

// Idle is derived from the Zzz on screen too. It used to read an "idle-" timer key, but the idle
// branch stopped registering one when resting states lost their expiry (2026-09-14, "no expiry"),
// so this was EMPTY FOREVER: no Sessions row ever showed the Zzz, in any view. (Seen first on the
// density cloud, where a bubble wore the Z and its open row did not.)
export const getActiveIdleSessionIds = (): string[] =>
  activeBadgeSessionIds("[data-pulse-idle=true][visitor_id]");

export const getActiveReadingSessionIds = (): string[] =>
  activeBadgeSessionIds("[data-pulse-reading=true][visitor_id]");

export const getActiveWatchingSessionIds = (): string[] =>
  activeBadgeSessionIds("[data-pulse-watching=true][visitor_id]");

// Icons need a few more pixels than a solid-color dot to read clearly rather
// than a colored smudge, so bump the visual radius (not the collision radius
// used by the force simulations) while icon mode is on. A converted (goal-
// reached) session's WHOLE bubble is bumped larger too, not just its star
// badge - a bigger star crammed into the same small bubble just clipped/
// overflowed oddly; scaling the bubble itself up first, with the badge
// staying proportional to it (see getGoalBadgeSize), is what actually reads
// as "a bigger, more prominent star bubble."
export const getBubbleRadius = (d?: any) => {
  const base = useIcons ? 7 : 4;
  return d?.goalReached ? base * 1.6 : base;
};

// Icon mode renders bubbles as <rect> (matching the icon's own aspect ratio -
// 4:3 for flags, square for everything else) instead of <circle>. The shape
// is decided once, at creation time, from whatever mode is active then -
// toggling later re-colors/resizes existing bubbles in place (see
// setUseIcons) but doesn't reshape them, same as the existing live-restyle
// behavior.
export const getBubbleShapeTag = () => (useIcons ? "rect" : "circle");

const FLAG_RECT_ASPECT = 0.72; // height / width, close to flag-icons' 4x3 ratio
const FLAG_IMAGE_ASPECT = 0.75; // flag-icons' actual 4x3 ratio
const SQUARE_ICON_ASPECT = 1; // OS/browser/device/search-engine/referrer icons are square

// Aspect only depends on which filter is currently active (every bubble on
// screen shares it), not on any individual bubble - flags are the one
// rectangular case, everything else (OS, browser, device, search engine,
// referrer favicons) is square.
export const getActiveIconAspect = () =>
  selectedFilter?.menuType === "country_name"
    ? FLAG_RECT_ASPECT
    : SQUARE_ICON_ASPECT;

// The companion <image> is sized independently of the rect it sits on, so it
// can use the icon's exact aspect ratio (flags are exactly 4:3) rather than
// the rect's own slightly-approximate one.
export const getActiveIconImageAspect = () =>
  selectedFilter?.menuType === "country_name"
    ? FLAG_IMAGE_ASPECT
    : SQUARE_ICON_ASPECT;

export const getRectSizeFromRadius = (
  radius: number,
  aspect: number = SQUARE_ICON_ASPECT
) => {
  const width = radius * 2.7;
  return { width, height: width * aspect };
};

// d3's forceCollide only knows how to keep circles apart - it was being given
// `radius` itself, but the rendered rect can be ~2.7x wider than that, so
// rects were overlapping even though their (much smaller) collision circles
// weren't. Use the rect's half-diagonal as the effective collision radius in
// icon mode so the physics actually matches what's on screen.
export const getHistoricalCollisionRadius = (radius: number) => {
  if (!useIcons) {
    return radius;
  }
  const { width, height } = getRectSizeFromRadius(radius, getActiveIconAspect());
  // The full half-diagonal keeps even a rectangular flag clear at every angle, but it is ~41% bigger than a square icon's half-width, so
  // 4 browser icons sat a full icon-width and more apart (reported: "so spaced far apart even on the tightest setting"). It is now
  // the larger half-side plus HISTORICAL_ICON_CORNER_SHARE of the way to the half-diagonal: the icons sit close, and only the corners of
  // a wide flag can come within a hair of each other. This (with the padding below) is the knob for icon spacing.
  const halfSide = Math.max(width, height) / 2, halfDiag = Math.sqrt((width / 2) ** 2 + (height / 2) ** 2);
  return halfSide + HISTORICAL_ICON_CORNER_SHARE * (halfDiag - halfSide);
};
const HISTORICAL_ICON_CORNER_SHARE = 0.1;   // was 0.4, then tightened again at Chris's request

// The circle-mode cluster's tight spacing is exactly right and should stay
// untouched. Icons (especially flags) read as visually cramped/touching at
// that same small gap, even though nothing's technically overlapping - so
// icon mode gets a noticeably bigger buffer between bubbles. This is the one
// knob to turn if the icon cluster ever needs to feel looser/tighter again.
export const getHistoricalCollisionPadding = () => (useIcons ? 2 : 3);   // was 5 in icon mode: tightened with the collision radius above

// More relaxation passes were needed to fully resolve icon-mode's larger
// collision radii without residual overlap, but running those same extra
// passes on circle mode (which never needed them) subtly changed how it
// settles - not just its bubble sizes, but its resting layout. Keep circle
// mode's collision solver exactly as it always was.
// 6 passes for icon mode, as originally tuned - the larger icon radii need them
// to resolve without residual overlap. Briefly cut to 3 chasing the drag
// slowdown; measurement showed it made no difference (see maxRadius above), so
// the visual quality is worth keeping.
export const getHistoricalCollisionIterations = () => (useIcons ? 6 : 3);

// Historical bubbles should visibly reflect relative visitor counts (e.g. a
// country with 6 sessions should look noticeably bigger than one with 1), not
// just clear a fixed floor. Radius is scaled between a min and max based on
// this bubble's count relative to the largest count currently shown. An
// exponent > 1 (rather than sqrt) is used deliberately: it pushes low counts
// further toward the minimum so the smallest bubbles read as clearly smaller,
// not just "slightly less than everything else". The min/max range itself is
// kept modest so the whole cluster still fits its allotted area without
// bubbles overlapping or spilling past the visible boundary below it.
const getMaxHistoricalUserValue = () => {
  const historicalValues = bubblesData
    .filter((d: any) => d.historical && d.menuName)
    // `|| 0` already coerces NaN away here - kept explicit because the whole
    // scale is relative to this max, so one non-finite value would otherwise
    // resize every bubble on screen, not just its own.
    .map((d: any) => (Number.isFinite(+d.user) ? +d.user : 0));
  return historicalValues.length ? Math.max(...historicalValues, 1) : 1;
};

export const getHistoricalBubbleRadius = (
  userValue: number,
  maxUserValue?: number
) => {
  /*
   * The floor is the REAL-TIME bubble radius, not an arbitrary smaller
   * number. One historical session and one live visitor are the same single
   * visit, so they should be the same size - previously the floor was 3 (vs
   * a live bubble's 4), making a departed visitor render SMALLER than the
   * live bubble that represented them moments earlier.
   */
  /*
   * Guard against a non-finite count BEFORE any arithmetic.
   *
   * `+item.user` is NaN whenever a category was seeded client-side rather than
   * coming from the backend aggregate (drawHistoricalBubble's `userValue: 0`
   * placeholder path sets no `user` field at all). NaN then propagates through
   * the ratio into the radius, and an <image> or <rect> with width="NaN" is
   * invalid - the browser falls back to the flag asset's INTRINSIC size, which
   * is why Taiwan (1 session, 0 of them ended) and Poland rendered as some of
   * the largest icons on screen while Singapore's 52 sessions rendered small.
   * Size stopped tracking the data entirely.
   *
   * Treating an unknown count as 0 puts the bubble at the floor, which is the
   * honest representation of "we don't have a number for this yet".
   */
  const safeValue = Number.isFinite(+userValue) ? +userValue : 0;
  const minRadius = getBubbleRadius();
  /*
   * 28 for icon mode (was 22, widened 2026-08-02 - see below). Briefly
   * reduced to 14 on 2026-07-30 on the theory that larger collision radii
   * were behind the drag slowdown - MEASURED AND DISPROVED: dragging at
   * ~100 bubbles ran 96.6ms/frame with maxRadius 14 and 3 collision
   * iterations, versus 85.1ms with 22 and 6. The radius is not the
   * bottleneck; the per-frame cost is browser style/layout/paint over ~330
   * SVG elements (zero long tasks were recorded despite 90ms frames), not
   * physics - so widening the ceiling further is not expected to reintroduce
   * that regression, though it hasn't been separately re-measured at 28.
   *
   * Widened rather than touching the exponent below, deliberately - the
   * exponent has already been tuned through several painful rounds (1.6,
   * which collapsed the whole low end into one indistinguishable size; the
   * zero-count `: 1` fallback bug), and reopening that is exactly the kind
   * of change that's expensive to get wrong again. This is a narrower,
   * safer lever: reported directly that a 2-vs-4-session By Operating
   * System scene (Linux 4, Windows/macOS 2 each) rendered nearly the same
   * size - the 7-22px range compressed that real 2x difference down to 22px
   * vs 17px (77% radius, 60% area). Widening the CEILING only, with the
   * floor and exponent both unchanged, gives every ratio more physical
   * pixels to express itself in without changing the curve's shape - the
   * same scene now renders 28px vs 21px (75% radius, 56% area), a real if
   * modest improvement, with zero risk to the long-tail behavior the
   * exponent was tuned for.
   */
  const maxRadius = useIcons ? 28 : 16;
  const effectiveMax = Math.max(
    Number.isFinite(+(maxUserValue as any)) ? +(maxUserValue as any) : getMaxHistoricalUserValue(),
    safeValue,
    1
  );
  /*
   * Exponent BELOW 1, which is the reverse of what this used to do.
   *
   * It was 1.6, chosen to push low counts toward the minimum so the smallest
   * bubbles would read as clearly smaller. It overshot badly: with a max of
   * 43, counts of 1, 3 and 5 ALL rounded to the same radius, and 10 was only
   * one pixel bigger. The entire bottom half of the range collapsed into one
   * indistinguishable size - reported directly as being overwhelmed by "so
   * many small bubbles grouped together" on By Country.
   *
   * An exponent below 1 expands the low and middle instead of compressing
   * them, which is where almost all the values actually live: a long tail of
   * small counts under one or two large ones is the normal shape of this
   * data, so that tail is exactly what needs the resolution. 0.6 sits between
   * a pure sqrt (0.5, area-proportional) and linear - close enough to
   * area-proportional to stay honest about relative magnitude, while still
   * separating 1 from 5 from 17 at a glance.
   */
  /*
   * A count of ZERO is always the floor - never the ceiling.
   *
   * This used to be `effectiveMax > 1 ? pow(...) : 1`, and that `: 1` fallback
   * meant "if there is nothing to compare against, assume MAXIMUM size". A
   * brand-new category is seeded by drawHistoricalBubble with a single record
   * of `userValue: 0`, so the max derived from it is 0, effectiveMax clamps to
   * 1, the comparison fails, and ratio became 1 - giving a country with zero
   * sessions the largest bubble on screen. Reported repeatedly (Taiwan,
   * Poland, then "Turkey just came in with 0 and is huge"): the newest arrival
   * was always the biggest, which is precisely backwards.
   *
   * The `: 1` branch is still right when there IS a real count and no larger
   * peer to compare with - one category with 12 sessions is legitimately the
   * biggest thing on screen. It is only wrong for zero, so zero is now handled
   * before that question is even asked.
   */
  const ratio =
    safeValue <= 0
      ? 0
      : effectiveMax > 1
      ? Math.pow(safeValue / effectiveMax, 0.6)
      : 1;
  return Math.round(minRadius + (maxRadius - minRadius) * ratio);
};

export const getHistoricalBubbleFill = (d: any) => {
  return getHistoricalIconUrl(d) ? "transparent" : setColor(d.color);
};

// Circle vs. rect is a different SVG element type, so an existing bubble
// can't just be re-styled into the other shape - it has to be removed and
// re-appended. Both redraws rebind straight from bubblesData, which already
// holds each bubble's live x/y/color/radius, so nothing jumps or resets.
const redrawRealtimeBubbles = () => {
  svg?.selectAll("circle[realTime=true], rect[realTime=true], image[realTime=true]").remove();
  // Was `bubblesData.filter((item) => item.realTime)` - bubblesData can end
  // up holding a SEPARATE object for the same visitor_id that isn't the
  // live reference the force simulation actually tracks in realtimeNodes
  // (root cause not fully isolated, but confirmed live via direct
  // instrumentation: a freshly-redrawn rect's own bound datum had
  // `realtimeNodes.includes(datum) === false` despite realtimeNodes
  // otherwise having the expected count). Binding a redrawn bubble to that
  // orphaned copy means the simulation's own per-tick force calculations -
  // and therefore drag's spring-back, which relies on exactly those forces -
  // silently never touch it again (reported directly: dragging a bubble
  // after a filter change froze it in place forever, velocity staying
  // bit-for-bit zero even while other bubbles kept moving normally).
  // realtimeNodes IS the ground truth the simulation ticks - sourcing the
  // redraw from it directly instead of a separately-filtered copy of
  // bubblesData makes this class of divergence structurally impossible.
  const realtimeBubbles = realtimeNodes;

  svg
    ?.selectAll("circle[data-bubble], rect[data-bubble]")
    .data(realtimeBubbles, (d: any) => d.visitor_id)
    .enter()
    .append(getBubbleShapeTag())
    .attr("data-bubble", true)
    .attr("width", (d: any) => getRectSizeFromRadius(getBubbleRadius(d), getActiveIconAspect()).width)
    .attr("height", (d: any) => getRectSizeFromRadius(getBubbleRadius(d), getActiveIconAspect()).height)
    .attr("cx", (d: any) => d.x)
    .attr("cy", (d: any) => d.y)
    .attr("x", rectXFromCenter((d: any) => d.x))
    .attr("y", rectYFromCenter((d: any) => d.y))
    .attr("realTime", true)
    .attr("r", (d: any) => getBubbleRadius(d))
    .attr("id", (d: any, i: number) => i)
    .style("fill", (d: any) => getBubbleFill(d))
    .style("stroke", (d: any) => getBubbleStroke(d))
    .attr("visitor_id", (d: any) => d.visitor_id)
    .attr("show-menu", (d: any) => d.name)
    .attr("remove", "no")
    .attr("createdAt", (d: any) => d.createdAt)
    .attr("pageName", (d: any) => d.pageName)
    .on("mouseover", (d: any) => showRealtimeTooltip(d))
    .on("mouseout", () => hideRealtimeTooltip())
    .on("click", (event: any, d: any) => handleRealtimeBubbleClick(d))
    .call(realtimeDragBehavior())
    .each(function (d: any) {
      const iconUrl = getBubbleIconUrl(d);
      if (iconUrl) {
        appendIconImage(
          this,
          d,
          iconUrl,
          getRectSizeFromRadius(getBubbleRadius(d), getActiveIconAspect()).width,
          getActiveIconImageAspect(),
          "realTime",
          "visitor_id",
          d.visitor_id
        );
      }
      // Was missing here (unlike drawUserBubble's own .each()) - toggling
      // icon mode or switching the "By ..." filter runs this bulk redraw,
      // which recreates every circle/rect (and icon image) from scratch but
      // silently dropped any already-converted session's star badge, even
      // though d.goalReached was still correctly set on the in-memory datum.
      if (d.goalReached) {
        appendGoalBadge(this, d, "realTime", "visitor_id", d.visitor_id);
      }
      // Same "bulk redraw drops it" gap as goalReached above - d.visitor_category
      // is still correctly set on the in-memory datum, this redraw just
      // needs to re-check it same as the initial draw does.
      if (d.visitor_category === "unidentified") {
        appendUnidentifiedBadge(this, d, "realTime", "visitor_id", d.visitor_id);
      }
      appendReturnMark(this, d, d.visitor_id);
    });

  /*
   * Lift the interaction decorations back above the bubbles.
   *
   * SVG has no z-index - paint order IS document order, so the last sibling
   * wins. This redraw removes and re-appends every circle/rect/image, which
   * puts the new shapes AFTER any pulse decoration that was already on screen,
   * burying it. Reported directly when switching from icon to bubble mode: the
   * away glyph "goes to the back".
   *
   * The goal star avoids this by being re-appended inside the .each() above;
   * the pulses can't take that route because they're owned by
   * applyInteractionPulse and carry live animation state, so they're raised
   * rather than recreated - recreating them would restart every animation and
   * lose the away state entirely.
   *
   * Runs once per redraw (a mode toggle or filter switch), never per tick, so
   * the selector cost here is irrelevant - unlike the per-tick scan that perf
   * note 5 is about.
   */
  svg?.selectAll(INTERACTION_DECORATION_SELECTOR).raise();

  bubblesSelection = svg?.selectAll("circle[data-bubble], rect[data-bubble]");
};

/*
 * Re-measure every historical bubble against the current shared maximum.
 *
 * Radius is assigned when a bubble is drawn, so bubbles created before a new
 * larger category arrived were sized against a smaller denominator and stay
 * visually too big forever. Relative size only means anything if the whole set
 * is rescaled whenever the maximum moves. Cheap: a plain arithmetic pass over
 * the historical bubbles (a handful of categories), never per tick.
 */
export const renormaliseHistoricalRadii = () => {
  const max = getMaxHistoricalUserValue();
  bubblesData.forEach((item: any) => {
    if (item?.historical && item.menuName) {
      item.radius = getHistoricalBubbleRadius(+item.user, max);
    }
  });
};

const redrawHistoricalBubbles = () => {
  svg?.selectAll("circle[historical=true], rect[historical=true], image[historical=true]").remove();
  const historicalBubbles = bubblesData.filter(
    (item: any) => item?.historical === true && item.menuName
  );

  // d.radius is whatever it was the last time fresh backend data came in and
  // drawHistoricalBubble ran - which could've been under a different useIcons
  // state (a different min/max range) than the one we're toggling into right
  // now. Recompute it fresh here so toggling always reflects the *current*
  // mode's sizing, regardless of when the underlying data last refreshed.
  const maxUserValue = Math.max(
    ...historicalBubbles.map((item: any) => +item.user || 0),
    1
  );
  historicalBubbles.forEach((item: any) => {
    item.radius = getHistoricalBubbleRadius(+item.user, maxUserValue);
  });

  svg
    ?.selectAll("circle[data-bubble], rect[data-bubble]")
    .data(historicalBubbles, (d: any) => d.menuName)
    .enter()
    .append(getBubbleShapeTag())
    .attr("data-bubble", true)
    .attr("width", (d: any) => getRectSizeFromRadius(d.radius, getActiveIconAspect()).width)
    .attr("height", (d: any) => getRectSizeFromRadius(d.radius, getActiveIconAspect()).height)
    .attr("cx", (d: any) => d.x)
    .attr("cy", (d: any) => d.y)
    .attr("r", (d: any) => d.radius)
    .attr("x", rectXFromCenter((d: any) => d.x))
    .attr("y", rectYFromCenter((d: any) => d.y))
    .attr("rx", 1)
    .attr("id", (d: any, i: number) => i)
    .attr("historical", true)
    .style("filter", useIcons ? null : "url(#historical-bubble-shadow)")   // icon mode: this shape is transparent (the icon image carries the shadow), so its filter paints nothing and only costs frames - see the note above DOCK_SHADOW
    .attr("stroke-width", useIcons ? 0 : 1)
    .attr("class", "sub_circle")
    .attr("show-menu", (d: any) => d.name)
    .attr("user", (d: any) => d.user)
    .attr("sub-menu-flag", true)
    .attr("remove", "no")
    .style("stroke", (d: any) => getBubbleStroke(d))
    .attr("name", (d: any) => d.menuName)
    .style("fill", (d: any) => getHistoricalBubbleFill(d))
    .on("mouseover", (d: any) => showHistoricalTooltip(d))
    .on("mouseout", () => hideHistoricalTooltip())
    .on("click", (event: any, d: any) => handleHistoricalBubbleClick(d))
    .call(historicalDragBehavior())
    .each(function (d: any) {
      const iconUrl = getHistoricalIconUrl(d);
      if (iconUrl) {
        appendIconImage(
          this,
          d,
          iconUrl,
          getRectSizeFromRadius(d.radius, getActiveIconAspect()).width,
          getActiveIconImageAspect(),
          "historical",
          "name",
          d.menuName
        );
      }
      if (d.converted > 0) {
        appendGoalBadge(this, d, "historical", "name", d.menuName);
      }
    });

  bubblesSelection = svg?.selectAll("circle[data-bubble], rect[data-bubble]");

  // Re-skinning the shapes above reuses each bubble's existing d.x/d.y as-is,
  // which were laid out under whichever spacing rules (circle vs icon) were
  // active *before* this toggle - without re-running the collision
  // simulation, bubbles never actually move to the new spacing, they just
  // change appearance in place. This mirrors the simulation setup in
  // drawBubbleNow so toggling always re-lays-out the cluster correctly.
  const bubbleParametersData = returnBubbleParameterData();
  const clusterIndex = HISTORICAL_CLUSTER_INDEX;
  const historicalCoords =
    clusterCenterCoords && clusterCenterCoords[clusterIndex];

  if (historicalCoords) {
    const historicalSimulation = d3
      .forceSimulation()
      .force(
        "collision",
        d3
          .forceCollide()
          .radius(
            (d: any) =>
              getHistoricalCollisionRadius(+d.radius) +
              getHistoricalCollisionPadding() +
              clusterLabelExtra(d)
          )
          .strength(1)
          .iterations(getHistoricalCollisionIterations())
      )
      .force(
        "charge",
        d3
          .forceManyBody()
          // Historical cluster's own spacing setting - see
          // DEFAULT_D3CIRCLE_CONSTRAINT's own comment.
          .strength(
            bubbleParametersData && bubbleParametersData.historicalCharge
              ? parseInt(bubbleParametersData.historicalCharge)
              : DEFAULT_D3CIRCLE_CONSTRAINT.historicalCharge
          )
      )
      .force("gravity", d3.forceManyBody().strength(0.2))
      .force("x", d3.forceX(historicalCoords?.x).strength(0.05))
      .force("y", d3.forceY(historicalCoords?.y).strength(0.05))
      .on("tick", () => {
        svg
          ?.selectAll(
            "circle[historical=true], rect[historical=true], image[historical=true]"
          )
          .attr("cx", (d: any) => {
            d.x = clampBubbleCenter(d.x, d.radius, mainSvgWidth - 10);
            return d.x;
          })
          .attr("cy", (d: any) => {
            d.y = clampBubbleCenterRange(d.y, d.radius, historicalBandTop, dockFloorY());
            return d.y;
          })
          .attr("x", rectXFromCenter((d: any) => d.x))
          .attr("y", rectYFromCenter((d: any) => d.y));
      });

    stopPreviousSimulation("historical", historicalSimulation);
    historicalSimulation.nodes(historicalBubbles).alpha(0.9).restart();
    if (dockPlacesItself()) {
      historicalSimulation.stop();
      scheduleHistoricalRowsLayout();
    }

  // One icon per bubble - see dedupeHistoricalIcons for why this is enforced
  // here rather than trusted to the append path.
  dedupeHistoricalIcons();
}
};

export const setUseIcons = (value: boolean) => {
  useIcons = value;
  storage.set("useIcons", value ? "true" : "false");
  redrawRealtimeBubbles();
  redrawHistoricalBubbles();
};

export const setInteractionPulsesEnabled = (value: boolean) => {
  interactionPulsesEnabled = value;
  storage.set("interactionPulsesEnabled", value ? "true" : "false");
  // Toggle via CSS class rather than removing DOM elements. Removing clears
  // interactionPulseTimeouts, so toggle-on has no state left to restore and
  // glyphs only come back on the next interaction event — reported directly
  // as "reading glyph didn't come back." Hiding with CSS preserves every
  // badge in place; toggle-on removes the class and they instantly reappear.
  const svgNode = svg?.node() as SVGSVGElement | undefined;
  if (svgNode) {
    svgNode.classList.toggle("pulses-hidden", !value);
  }
  // Still sync the drawer row badges via the ground-truth getters so the
  // Sessions list stays consistent with whatever the bubbles show.
  eventEmitter.emit("sessionIdleChanged");
  eventEmitter.emit("sessionTypingChanged");
  eventEmitter.emit("sessionScrollChanged");
  eventEmitter.emit("sessionReadingChanged");
};

export const createRealtimeListItem = (name: string, totalUsers: number) => {
  const usersCount = bubblesData

    .filter((bubble: any) => {
      return !!bubble.sessionData;
    })
    .reduce((acc: any, bubble: any) => {
      if (resolveRealtimeCategory(bubble.sessionData) === name) {
        return ++acc;
      }

      return acc;
    }, 0);
  if (!items.filter((data) => data.name === name).length) {
    // hashNameToColorIndex alone is a pure function of the name, but with
    // realistic category counts (a "by country" list can have dozens of
    // values) hashing into even a large visually-distinct palette produces
    // frequent collisions purely from the birthday paradox - e.g. 18 names
    // into 22 colors already has a roughly even chance some pair collides,
    // and this was confirmed directly (Poland/Brazil/Sweden all hashed to
    // the same slot; Mexico/Germany to another). No palette size that
    // still looks visually distinct fixes this on its own. So the hash is
    // only a starting point: if that slot is already taken by a DIFFERENT
    // name that's currently active (in `items` right now), probe forward to
    // the next free slot instead of silently colliding - guaranteeing no
    // two simultaneously-visible categories ever share a color as long as
    // the active count doesn't exceed the palette size. Cached in
    // categoryColorAssignments as before, so a name keeps whichever slot it
    // resolved to for as long as it stays cached (persists across playback
    // seeks - see that map's own comment).
    const assignedColor = assignColorForCategory(name);
    items.push({
      name,
      currentUsers: usersCount,
      totalUsers,
      color: assignedColor,
      colorInfo: setColor(assignedColor),
      display: true,
    });

    items.sort((a, b) => {
      // Sort in descending order based on currentUsers, giving priority to currentUsers
      if (a.currentUsers > b.currentUsers) return -1;
      if (a.currentUsers < b.currentUsers) return 1;
    
      // If currentUsers are equal, then sort based on totalUsers
      if (a.totalUsers > b.totalUsers) return -1;
      if (a.totalUsers < b.totalUsers) return 1;
    
      // If both currentUsers and totalUsers are equal, maintain the original order
      return 0;
    });
  }
};

export const getItemByName = (itemName: string) => {
  return items && items.find((item) => item.name === itemName);
};
const returnBubbleParameterData = () => {
  return storage.getJSON("bubbleParameters");
};

// A new visitor should visibly emerge from its category's row in the
// sidebar's real-time list (the small color/flag dot next to the name),
// then travel to its destination cluster - matching how reference
// people-flow visualizations introduce a new dot from its origin group
// rather than having it simply appear at the destination. The sidebar list
// is a separate React tree with no shared coordinate space with the SVG, so
// this converts the dot's on-screen position into the SVG's own internal
// coordinate system via the standard screen-to-SVG-point transform. This
// same x/y is used for both the real bubble's spawn position AND the gooey
// burst (which renders into this same svg) - using one shared value for both
// is what guarantees they land in exactly the same place, instead of each
// being computed via its own separate lookup that could drift apart.
// The Real Time list's dots and the canvas's transform, read ONCE for a batch of arrivals (originSnapshot.ts: one layout read per bubble drawn was 17% of everything a Playback at 3x ran).
const sidebarOriginReader = createOriginReader<{ svgNode: SVGSVGElement; inverse: DOMMatrix } | null>({
  now: () => performance.now(),
  maxAgeMs: 120,
  read: () => {
    const svgNode = svg?.node?.() as SVGSVGElement | undefined;
    if (!svgNode) {
      return null;
    }
    const ctm = svgNode.getScreenCTM?.();
    const centres = new Map<string, { x: number; y: number } | null>();
    document.querySelectorAll(".filter-data li[data-name]").forEach((item) => {
      const rowName = item.getAttribute("data-name");
      const dot = item.querySelector(".color-status, .flag-status");
      if (!rowName || !dot || centres.has(rowName)) {
        return;
      }
      const rect = dot.getBoundingClientRect();
      // A zero-size rect means the dot isn't actually rendered/visible right now (e.g. its <li> is currently clipped out of the sidebar's own scrollable area, or the drawer is
      // closed) - the CTM math would still produce a "valid looking" number in that case, just one that doesn't correspond to anywhere actually on screen, so it is no origin.
      centres.set(rowName, rect.width === 0 && rect.height === 0 ? null : { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 });
    });

    return { centres, extra: ctm ? { svgNode, inverse: ctm.inverse() } : null };
  },
});

const getSidebarOriginCoords = (name: string) => {
  const found = sidebarOriginReader.lookup(name);
  if (!found || !found.centre || !found.extra) {
    return null;
  }
  const point = found.extra.svgNode.createSVGPoint();
  point.x = found.centre.x;
  point.y = found.centre.y;
  const svgPoint = point.matrixTransform(found.extra.inverse);

  // The sidebar sits to the left of the svg by design, so svgPoint.x is
  // routinely negative here - that's expected, not a bug. What we DO want to
  // guard against is the real bubble's (and burst's) position overshooting
  // past the svg's drawn right/bottom edge, which is what "spawns behind the
  // scrollbar / in the whitespace" looked like.
  const svgWidth = +svg?.attr?.("width") || mainSvgWidth;
  const svgHeight = +svg?.attr?.("height") || mainSvgHeight;
  if (svgPoint.x > svgWidth || svgPoint.y > svgHeight) {
    return null;
  }

  return { x: svgPoint.x, y: svgPoint.y };
};

export const drawUserBubble = async (sessionData, silent = false) => {
  const bubbleParametersData = returnBubbleParameterData();

  return new Promise(async(resolve) => {
    if (pendingDisconnectedSessions.has(sessionData._id)) {
      pendingDisconnectedSessions.delete(sessionData._id);
      resolve(undefined);
      return;
    }

    // Guards against double-drawing the same session as two separate
    // bubblesData entries (each with its own onsiteUser++), which is
    // possible even though only one DOM circle ever visibly appears -
    // AnalyticsView.tsx's real 'new-visitor' handler deliberately defers its
    // draw by 1s, and reconcileOnsiteVisitors (which resyncs against the
    // backend's already-recorded-instantly onsite list) can run during that
    // exact window and see the same session as "missing" and draw it too.
    // The second push still increments onsiteUser/currentUsers, but its DOM
    // append is a no-op (d3's data-join below keys on visitor_id, so it
    // matches the already-existing circle and enters nothing new) - the
    // net effect is the onsite counters run ahead of the number of bubbles
    // actually visible, exactly what was reported ("shows 12... only 2
    // bubbles onscreen").
    if (bubblesData.some((node) => node.realTime && node.visitor_id === sessionData._id)) {
      resolve(undefined);
      return;
    }

    let otherPage = false;
    let menuIndex = 0;
    const realtimeType: string = resolveRealtimeCategory(sessionData);

    if (!realtimeType) {
      // Was a bare `return;` with no resolve() - harmless as long as nobody
      // awaited this promise, but the playback feature now awaits
      // drawUserBubble sequentially (see playbackService.ts's applyAction),
      // so a session missing this filter's classification field used to
      // hang playback/seeking forever on this exact call.
      resolve(undefined);
      return;
    }
    if (!getItemByName(realtimeType)) {
      createRealtimeListItem(realtimeType, 0);
      drawHistoricalBubble([{ bubbleName: realtimeType, userValue: 0 }]);

      notifySidebarItemsUpdated();
    }

    const currentItemData = getItemByName(realtimeType);
    onsiteUser++;
    if (currentItemData) {
      currentItemData.currentUsers++;
    
    }


    for (let i = 0; i < items.length; i++) {
      if (items[i].name === realtimeType) {
        menuIndex = i;
      }
    }
    items.sort((a, b) => {
      // Sort in descending order based on currentUsers, giving priority to currentUsers
      if (a.currentUsers > b.currentUsers) return -1;
      if (a.currentUsers < b.currentUsers) return 1;
      
      // If currentUsers are equal, then sort based on totalUsers
      if (a.totalUsers > b.totalUsers) return -1;
      if (a.totalUsers < b.totalUsers) return 1;
      
      // If both currentUsers and totalUsers are equal, maintain the original order
      return 0;
    });

    const bubbleCoords = {
      x: 100,

      y: (menuIndex + 1) * 20 + 60,
    };
    const visitedPage = data.find(
      (page: any) => page.selected && page.name === sessionData.current_url_name
    );
    let clusterIndex = null;
    if (visitedPage) {
      clusterIndex = visitedPage.slotIndex;
    } else if (pageViewDisabled) {
      clusterIndex = DEFAULT_VALUE.ALL_CLUSTER_INDEX;
    } else {
      otherPage = true;
      clusterIndex = DEFAULT_VALUE.UNKNOWN_URL_CLUSER_INDEX;
    }

    if (pageViewDisabled) {
      bubbleCoords.x = clusterCenterCoords[DEFAULT_VALUE.ALL_CLUSTER_INDEX].x;
      bubbleCoords.y = clusterCenterCoords[DEFAULT_VALUE.ALL_CLUSTER_INDEX].y;
    } else {
      bubbleCoords.x = clusterCenterCoords[clusterIndex].x;
      bubbleCoords.y = clusterCenterCoords[clusterIndex].y;
    }

    // Skipped during silent seek reconstruction, same as the spawn burst -
    // a snapshot jump replaying every already-departed session's arrival
    // would otherwise inflate arrival-line weights with sessions that
    // aren't actually spawning on screen right now.
    // a visit already there when a replay clip begins was counted (landing and moves) in the clip's day-so-far flows
    if (!silent && !sessionData.carried) {
      recordArrivalTraversal(clusterIndex);
      tallyRoute("arrive", String(sessionData._id), sessionData.current_url_name);
    }

    if (
      pageWiseCount[sessionData.current_url_name] ||
      pageWiseCount[sessionData.current_url_name] === 0
    ) {
      pageWiseCount[sessionData.current_url_name] += 1;
    } else {
      pageWiseCount["allOtherPages"] += 1;
    }
    syncCloudCounts();

    // Silent (seek/mode-switch reconstruction) draws never use a sidebar
    // origin at all, even if one happens to already exist in the DOM -
    // deliberately skipping getSidebarOriginCoords entirely rather than
    // just skipping the "wait a frame" retry below. Reading whatever the
    // sidebar's CURRENT DOM position happens to be at the exact moment this
    // runs (mid-loop, chunked across animation frames by
    // replaySessionStates) is timing-dependent: switching between 1:1 and
    // compressed reconstructs the same onsite session set via two SEPARATE
    // reconstruction runs, and if a category's sidebar row is at a
    // different on-screen position (or hasn't painted yet) at the moment
    // each one reaches that session, the two runs hand the physics
    // simulation different starting x/y for what should be an identical
    // input - which the collision force then resolves into two visibly
    // different final resting layouts within the same box. There's no
    // visible travel to protect during a silent draw anyway (no burst, no
    // transition), so always starting exactly at the destination coords
    // keeps every reconstruction's starting state fully deterministic.
    let originCoords = silent ? null : getSidebarOriginCoords(realtimeType);
    if (!originCoords && !silent) {
      // realtimeType is a brand-new category: its sidebar <li> was just
      // queued via createRealtimeListItem()/notifySidebarItemsUpdated()
      // above, but React hasn't committed it to the DOM yet (that event ->
      // setState -> re-render is async), so the querySelector inside
      // getSidebarOriginCoords found nothing this tick. Waiting one frame
      // for React to paint it, THEN resolving the bubble's actual starting
      // position (not just the decorative burst), matters for two reasons
      // - both reported as "wrong" compared to every other bubble's normal
      // spawn: without this, the fallback below puts the new bubble
      // directly at its destination cluster with zero travel distance, so
      // it never visibly flies in from the sidebar like every other bubble
      // does; and every brand-new category's first-ever bubble would
      // fall back to that same non-origin path, so two different new
      // categories whose first sessions land on the same page cluster
      // would both spawn at the EXACT same point and get violently thrown
      // apart by the collision force on the very next physics tick
      // (visible as bubbles "behaving erratically" right after appearing).
      // This case is common early in a playback run, when most of the
      // day's distinct categories are being seen for the first time in
      // quick succession - which is also why the symptom fades as
      // playback progresses and every category's sidebar row already
      // exists.
      await new Promise((r) => requestAnimationFrame(r));
      // A frame has passed, so the list may well have drawn the row: read it AGAIN whatever the last read's age. Without this, on a 120 Hz screen (8.3 ms to a frame) the reader's "a read younger than
      // 16 ms is not repeated" rule answered "not there" from the OLD read, the bubble was put straight in its box and just appeared (a shared replay's first visitor, 2026-10-25).
      sidebarOriginReader.reset();
      originCoords = getSidebarOriginCoords(realtimeType);
    }

    // Push first, THEN derive the burst's color/icon decision from this
    // exact pushed object - not from the outer `sessionData`/`currentItemData`
    // read separately. Re-deriving from two independent sources used to be
    // able to disagree: getBubbleIconUrl({ sessionData }) checked the FULL,
    // untrimmed sessionData, while the real bubble's own icon-vs-solid check
    // (below, in .each) runs against the TRIMMED sessionData subset stored
    // here - normally the same fields, but any future field added to one and
    // not the other would silently desync burst vs. bubble again. Deriving
    // both the burst and the real bubble from the identical object makes
    // that mismatch structurally impossible rather than just currently
    // unlikely.
    bubblesData.push({
      x: originCoords?.x != null ? originCoords.x : +bubbleCoords?.x,
      y: originCoords?.y != null ? originCoords.y : +bubbleCoords?.y,
      radius: 4,
      realTime: true,
      color: currentItemData?.color,
      // Was hardcoded to UNKNOWN_URL_CLUSER_INDEX regardless of clusterIndex
      // (computed just above for this exact arrival's actual box) - the
      // bubble still visually rendered in the right box either way, since
      // positioning comes from bubbleCoords/pageName resolution elsewhere,
      // but this field itself never reflected where a bubble actually
      // arrived. handleVisitorMove reads it as "the box this session was in
      // before this move" for every SUBSEQUENT page change, so a session's
      // very first navigation always looked like it started from
      // "unknown," silently dropped by anything (like the flow-lines
      // feature) that only wants transitions between two real boxes.
      cluster: clusterIndex,
      id: clusterIndex,
      // where (svg coords) and when this bubble spawned: the zoomed view flies a new visitor in
      // from there
      spawnX: originCoords?.x ?? null,
      spawnY: originCoords?.y ?? null,
      spawnAt: performance.now(),
      // a silent draw is a reconstruction (a seek): the visitor was already there, so there is no arrival to fly
      spawnSilent: !!silent,
      number: bubblesData?.length ? bubblesData?.length : 0,
      pageUserCount: pageWiseCount[sessionData.current_url_name],
      visitor_id: sessionData._id,
      // Set by the backend's live goal-URL match (session.ts's
      // handleUrlChange) whenever this session's arrival page itself is a
      // goal destination - a first pageview landing directly on a goal URL
      // is a conversion just as much as reaching it on a later navigation.
      goalReached: !!sessionData.goalReached,
      // Set once at arrival by the backend's classifyVisitor (raw User-
      // Agent based, see Common/Helper/visitorClassification.ts) - unlike
      // goalReached this never changes mid-session, so (unlike the goal
      // star) there's no later "flip true" call site to also wire up.
      visitor_category: sessionData.visitor_category,
      // One key per browser, shared by all its visits (the server's visitorKey): lets the Visitors tab show who is on the site RIGHT NOW (getOnsiteVisitors).
      visitorKey: sessionData.visitorKey,
      name: realtimeType,
      pageName: sessionData.current_url_name,
      currentUrlName: sessionData.current_url_name,
      // the address the visitor really has, where the page is a group (its own events keep it beside the group's name): what "Current" under each address of a group counts
      rawAddress: Array.isArray(sessionData.events) && sessionData.events.length ? sessionData.events[sessionData.events.length - 1]?.rawPath : undefined,
      createdAt: new Date(sessionData.createdAt).toISOString(),

      sessionData: {
        browser: sessionData.browser,
        device: sessionData.device,
        visitor_type: sessionData.visitor_type,
        // which visit this is for its browser (1 = the first): the tooltip says "Return · 3rd visit"
        visitNumber: sessionData.visitNumber,
        country_name: sessionData.country_name,
        country_code: sessionData.country_code,
        city: sessionData.city,
        region_name: sessionData.region_name,
        os: sessionData.os,
        os_version: sessionData.os_version,
        traffic_source: sessionData.traffic_source,
        search_engine: sessionData.search_engine,
        social_network: sessionData.social_network,
        referrer: sessionData.referrer,
        visitor_category: sessionData.visitor_category,
      },
    });
    const newBubbleForBurst = bubblesData[bubblesData.length - 1];
    densityCloud.notifyVisitorData(sessionData._id, {
      browser: sessionData.browser,
      visitor_type: sessionData.visitor_type,
      goalReached: !!sessionData.goalReached,
      unidentified: sessionData.visitor_category === "unidentified",
      attrs: zoomAttrsOf(sessionData),
    });
    densityCloud.notifyVisitorPage(sessionData._id, sessionData.current_url_name);
    // The count sync further up runs BEFORE this bubble exists (it is queued for the next frame, and the bubble is only added after the
    // frame wait above), so the per-slot counts, the "N on page" labels and the moment a cloud opens trailed every arrival until the
    // next event synced again. Now that the bubble and its data are in, sync again (rAF-coalesced: at most one run per frame).
    syncCloudCounts();

    // The real bubble this burst represents will render as a flag/OS/etc.
    // icon (not a solid color) whenever icon mode is on and one resolves for
    // this visitor's data - getBubbleIconUrl derives that purely from the
    // visitor's own data (country code, OS, ...), completely independent of
    // the category's assigned accent color. Using that accent color for the
    // burst in that case guarantees a mismatch (an arbitrary "outer" color
    // around whatever the icon's own real colors are, "inner"), so fall back
    // to a neutral gray instead of asserting a color the bubble won't use.
    const spawnBurstColor = getBubbleIconUrl(newBubbleForBurst)
      ? GOOEY_NEUTRAL_BURST_COLOR
      : newBubbleForBurst.color != null
      ? String(setColor(newBubbleForBurst.color))
      : undefined;
    // Playback feature: when silently reconstructing a seek target, every
    // already-departed session's arrival+departure gets replayed back-to-
    // back (see playbackService.ts's replaySessionStates). Playing the
    // spawn-burst flash for each one renders as bubbles visibly "raining
    // down" from the sidebar in rapid succession before immediately
    // docking - correct data, completely wrong presentation for a snapshot
    // jump. Skipping just the burst (the bubble itself still gets created
    // normally below) removes that visual noise without touching the
    // bookkeeping.
    if (silent) {
      // no-op - intentionally skip both burst branches below
    } else if (originCoords) {
      // Use this exact snapshot for the burst - don't re-query the dot's
      // position later.
      //
      // NOTE: anchoring the burst to bubbleCoords (the bubble's cluster
      // DESTINATION) instead was tried and reverted - the burst fires
      // instantly at spawn time, but the bubble takes ~1.6-2.2s to
      // physically travel there via the force simulation, so the flash
      // finished and faded long before the bubble visually arrived,
      // reading as "no burst happening" at all. The two are positionally
      // decoupled from the timing that made this effect legible in the
      // first place, so it stays anchored to the origin.
      //
      // Pin this category's sidebar row in place for the burst's own
      // duration - see activeBurstRowNames' comment for why a resort
      // repainting this exact row underneath a still-playing burst is what
      // reads as "the burst is the wrong color."
      lockRowDuringBurst(
        realtimeType,
        GOOEY_TUNING.spawnDuration + GOOEY_TUNING.spawnFadeDuration
      );
      playGooeySpawnBurst(
        originCoords.x,
        originCoords.y,
        spawnBurstColor,
        getBubbleRadius(newBubbleForBurst),
        effectDim(sessionData._id, sessionData.current_url_name)
      );
    }
    // else: still no origin even after the one-frame wait above (e.g. the
    // sidebar drawer is closed) - no burst to play, and the push above
    // already fell back to spawning directly at the destination.

    // New bubbles join the single persistent real-time simulation (added by
    // reference, so mutating its fields later - e.g. on a page move -
    // immediately affects how its target is resolved) instead of each new
    // visitor spinning up its own one-off simulation that could conflict
    // with everyone else's. Starting position is the sidebar origin
    // resolved above (so it visibly travels from there to its cluster,
    // like every other bubble) - it only starts directly at the
    // destination in the rare case no origin could be resolved at all.
    const newBubbleObject = bubblesData[bubblesData.length - 1];
    realtimeNodes.push(newBubbleObject);
    reheatRealtimeSimulation(silent);

    svg
      ?.selectAll("circle[data-bubble][realTime=true], rect[data-bubble][realTime=true]")
      .data([newBubbleObject], (d: any) => d.visitor_id)
      .enter()
      .append(getBubbleShapeTag())
      .attr("data-bubble", true)
      .attr("width", (d: any) => getRectSizeFromRadius(getBubbleRadius(d), getActiveIconAspect()).width)
      .attr("height", (d: any) => getRectSizeFromRadius(getBubbleRadius(d), getActiveIconAspect()).height)
      .attr("cx", (d: any) => d.x)
      .attr("cy", (d: any) => d.y)
      .attr("x", rectXFromCenter((d: any) => d.x))
      .attr("y", rectYFromCenter((d: any) => d.y))
      .attr("realTime", true)
      .attr("r", (d: any) => getBubbleRadius(d))
      .attr("id", bubblesData.length - 1)
      .style("fill", (d: any) => getBubbleFill(d))
      .style("stroke", (d: any) => getBubbleStroke(d))
      .attr("visitor_id", sessionData._id)
      .attr("show-menu", (d) => d.name)
      .attr("remove", "no")
      .attr("createdAt", (d) => d.createdAt)
      .attr("pageName", sessionData.current_url_name)
      .on("mouseover", (d) => showRealtimeTooltip(d))
      .on("mouseout", () => hideRealtimeTooltip())
      .on("click", (event, d) => handleRealtimeBubbleClick(d))
      .call(realtimeDragBehavior())
      .each(function (d: any) {
        const iconUrl = getBubbleIconUrl(d);
        if (iconUrl) {
          appendIconImage(
            this,
            d,
            iconUrl,
            getRectSizeFromRadius(getBubbleRadius(d), getActiveIconAspect()).width,
            getActiveIconImageAspect(),
            "realTime",
            "visitor_id",
            sessionData._id,
            d.x,
            d.y
          );
        }
        if (d.goalReached) {
          appendGoalBadge(this, d, "realTime", "visitor_id", sessionData._id, d.x, d.y);
          // Was missing here - a session that converts ON ARRIVAL (its
          // first page IS the goal page, e.g. landing directly on /terms)
          // already got its badge via appendGoalBadge above, but the
          // confetti/border-flash celebration only ever fired from
          // handleVisitorMove's mid-visit conversion block, which never
          // runs for a brand-new arrival - reported directly as goal-page
          // arrivals getting the star badge but no celebration. Same
          // !silent guard as that other call site - skipped during
          // playback's silent seek reconstruction, where this isn't a
          // real "journey" happening live, just jumping straight to a
          // point in time.
          if (!silent) {
            celebrateConversionAtGoalFlag(sessionData.current_url_name, sessionData._id);
          }
        }
        if (d.visitor_category === "unidentified") {
          appendUnidentifiedBadge(this, d, "realTime", "visitor_id", sessionData._id, d.x, d.y);
        }
        appendReturnMark(this, d, sessionData._id, d.x, d.y);

        /*
         * Restore the "not focused" glyph for a visitor who was ALREADY away
         * when this bubble was drawn.
         *
         * Away is the one interaction state that outlives the moment it
         * happened, so it is the only one that can be true before the
         * dashboard is even looking. It arrives on the session itself (see
         * Session.ts's `away` field) rather than as a live event, so without
         * this the glyph was simply lost on every refresh, on reconnect
         * replay, and on any filter switch that redraws bubbles - the visitor
         * silently appeared focused while still being away.
         *
         * Applied unconditionally, NOT gated on `silent`: silent suppresses
         * ANIMATION for replayed/reconstructed sessions, and this is a state
         * being restored, not a transition being played. Skipping it here
         * would reintroduce exactly the bug this fixes.
         */
        if (sessionData.away) {
          applyInteractionPulse({ sessionId: sessionData._id, type: "away" });
        }
      });

  // `silent` here means this is NOT a live arrival - it's flushActiveSessions
  // replaying an already-onsite session after a reconnect, or playback
  // reconstructing state at a seek target. Neither is a new pageview, so
  // neither may increment the counters. Previously this ran unconditionally:
  // last session's fix made resyncs visually silent (no spawn burst) but the
  // pageview increment kept firing, so the count inflated invisibly on every
  // single reconnect. Playback's own totals are refetched authoritatively by
  // RightMenu's refreshPagesStatsForMode, so suppressing here loses nothing.
  await handleVisitorMovePagesModel(null, sessionData.current_url_name, !silent);
  if (silent) {
    // countPageview was false above, so occupancy was not incremented. Derive
    // it from the bubbles that actually exist. See scheduleCurrentVisitorsSync.
    scheduleCurrentVisitorsSync();
  }
    eventEmitter.emit("userCountChanged", onsiteUser);
    resolve("ok");
  });
};

const visitorLastPage = {};

export const handleVisitorMove = async(
  urlTrackingData,
  newVisitor = false,
  connectionType?: string,
  silent = false
) => {
  /*
   * 🔴 CHANGING PAGE RESETS THE RESTING STATE TO NAKED (2026-09-14).
   *
   * Chris: "the reading glyph stays on the bubble when switching tabs. That
   * doesn't make any sense. The user clicked another tab to go to another tab."
   * Right - the paragraph was EARNED on the page she left. She has provided no
   * evidence at all about the page she just arrived on, and a bubble that
   * arrives somewhere new claiming "reading" is the over-claim this whole state
   * was designed to avoid.
   *
   * Note this is the OPPOSITE of the repositioning fix in the same session, and
   * both are right: the glyph must FOLLOW the bubble as it drifts within a
   * page, and must CLEAR when the bubble changes page. One is the same context
   * moving, the other is a new context.
   *
   * Applies to a presence move (tab switch) and a real navigation alike - in
   * both cases she is somewhere she has not yet shown us anything about. The
   * resting state re-establishes itself within seconds on its own.
   */
  const movingSessionId = urlTrackingData?.sessionId;
  if (movingSessionId) {
    if (svg) {
      dropAllBadges(svg as any, movingSessionId, emitBadgeEvent, ["reading", "idle", "watching"]);
    }
  }

  const bubbleParametersData = returnBubbleParameterData();
  // `node` here only ever needs to be a scratch/local snapshot - its .id/
  // .cluster/.currentUrlName reassignments below are never written back
  // (the real bubblesData update happens separately via the
  // bubblesData.forEach a few lines down, matched fresh by visitor_id), and
  // its only reads are two plain scalar fields (pageName, cluster). Deep-
  // cloning the ENTIRE bubblesData array via JSON.parse(JSON.stringify(...))
  // - and then doing it AGAIN on the single found node - to obtain that was
  // confirmed live as a catastrophic allocation source: this function runs
  // on every pageview action, and during dense/fast playback that meant
  // ~58,000 JSON.stringify calls/sec producing ~93MB/s of string data,
  // directly causing the "slows down and goes in spurts" GC stutter with
  // many bubbles onscreen. A plain object literal copy of just the fields
  // actually read/reassigned below is enough - no deep clone needed since
  // nothing here is a nested object/array.
  const originalNode = bubblesData.find(
    (n: any) => n.visitor_id === urlTrackingData.sessionId
  );

  if (!originalNode) {
    return;
  }
  const node = { ...originalNode };
  const previousUrl = newVisitor ? null : node.pageName;
  // Read before node.cluster gets reassigned below - node is a snapshot
  // taken at the top of this function, so this is genuinely the box the
  // session was in immediately before this move (or null on a brand-new
  // arrival, which isn't a "path" between two boxes).
  const previousClusterIndex: number | null = newVisitor ? null : node.cluster;

 /*
  * 🔴 A PRESENCE MOVE IS NOT A PAGE VIEW.
  *
  * countPageview defaults to TRUE, and this same call is what inflated the live
  * Page Views counter to 28k against a real 17k (CLAUDE.md perf note 10) by
  * treating a REPAINT as an EVENT. `presenceOnly` is the server saying "the
  * visitor switched tabs" - no page was loaded, nothing was billed, and the
  * dashboard must not manufacture a view either. The bubble still moves; only
  * the counting is suppressed.
  */
 await handleVisitorMovePagesModel(
   previousUrl,
   urlTrackingData.pathName,
   !urlTrackingData?.presenceOnly
 );
 eventEmitter.emit("sessionChanged", urlTrackingData);
  // Must check page.selected, same as the other three lookups of this exact
  // shape (resolveRealtimeClusterCoords, drawUserBubble) - a page can exist
  // in `data` with a leftover/assigned slotIndex without currently being one
  // of the actually-selected/pinned boxes. Without this check, navigating to
  // such a page routed here (a pageview move, not an arrival) to that stale
  // slotIndex - landing on a box that was never given a real label (falls
  // back to a placeholder "Page N" in getBoxLabel) instead of correctly
  // falling through to "All other pages" like every other lookup already
  // does. This was the actual root cause of flow lines (and bubbles)
  // appearing to land on "empty" page slots - nothing to do with any of the
  // flow-line-specific fixes made earlier.
  const selectedPage = data.find(
    (page: any) => page.selected && page.name === urlTrackingData.pathName
  );

  if (
    selectedPage &&
    selectedPage.slotIndex < DEFAULT_VALUE.UNKNOWN_URL_CLUSER_INDEX
  ) {
    node.id = selectedPage.slotIndex;
    node.cluster = selectedPage.slotIndex;
    node.currentUrlName = urlTrackingData.pathName;
  } else {
    node.id = DEFAULT_VALUE.UNKNOWN_URL_CLUSER_INDEX;
    node.cluster = DEFAULT_VALUE.UNKNOWN_URL_CLUSER_INDEX;
    node.currentUrlName = urlTrackingData.pathName;
  }
  // Skipped during silent seek reconstruction (replaySessionStates) - that
  // path calls handleVisitorMove to snap a freshly-drawn bubble straight to
  // its net-state page at the seek target, which is NOT a real page-to-page
  // navigation the session actually made. Recording that as a path
  // traversal injected synthetic edges (from wherever the bubble happened
  // to spawn to the seek target) into pathWeights on every single scrub-bar
  // drag, since seeks deliberately never call resetFlowLines() so real
  // accumulated data survives scrubbing - same "silent" precedent already
  // used by recordArrivalTraversal below/drawUserBubble's spawn burst.
  if (!silent) {
    recordPathTraversal(previousClusterIndex, node.cluster);
    tallyRoute("move", urlTrackingData.sessionId, urlTrackingData.pathName);
  }

  if (previousUrl && urlTrackingData.pathName && !connectionType) {
    if (pageWiseCount[previousUrl] || pageWiseCount[previousUrl] === 0) {
      if (node.currentUrlName === urlTrackingData.pathName) {
        pageWiseCount[visitorLastPage[urlTrackingData.sessionId]] -= 1;
        pageWiseCount[urlTrackingData.pathName] += 1;
      }
      if (pageWiseCount[previousUrl] !== 0) {
        pageWiseCount[previousUrl] -= 1;
        pageWiseCount[urlTrackingData.pathName] += 1;
      }
    } else {
      if (pageWiseCount[previousUrl] !== 0) {
        pageWiseCount["allOtherPages"] += 1;
        pageWiseCount[previousUrl] -= 1;
      }
    }
  }
  syncCloudCounts();

  // new code end

  // Retarget the moving visitor's own bubble by mutating its pageName in
  // place - deliberately NOT touching x/y here. The persistent simulation's
  // per-node force target is resolved fresh from d.pageName every tick, so
  // this one field change is all it takes for that ONE bubble to ease
  // toward its new cluster over the following ticks, while every other
  // bubble (whose pageName hasn't changed) stays exactly where it is.
  // Snapping x/y to the destination here would just teleport it instead.
  bubblesData.forEach((item) => {
    if (item.visitor_id === urlTrackingData.sessionId) {
      item.pageName = urlTrackingData.pathName;
      item.currentUrlName = urlTrackingData.pathName;
      item.rawAddress = urlTrackingData.rawPath;
      densityCloud.notifyVisitorPage(urlTrackingData.sessionId, urlTrackingData.pathName);
      item.id =
        selectedPage && selectedPage.slotIndex < DEFAULT_VALUE.UNKNOWN_URL_CLUSER_INDEX
          ? selectedPage.slotIndex
          : DEFAULT_VALUE.UNKNOWN_URL_CLUSER_INDEX;
      item.cluster = item.id;
      // Once true, stays true - a session that already converted earlier in
      // its visit shouldn't "un-convert" just because a later navigation's
      // pathName doesn't happen to also be a goal URL.
      if (urlTrackingData.goalReached) {
        item.goalReached = true;
      }
    }
  });

  svg
    ?.selectAll(
      `circle[visitor_id='${urlTrackingData.sessionId}'], rect[visitor_id='${urlTrackingData.sessionId}'], image[visitor_id='${urlTrackingData.sessionId}']`
    )
    .attr("pageName", urlTrackingData.pathName);

  // FUNNEL (demo only): a funnel visitor who has just stepped from one step to the next rides the ribbon between the two boxes. Not for a reconstruction (silent) or a hidden tab (a ride
  // would be queued by the browser and replayed on return: the same trap the other animations here guard against).
  if (funnelHost && !silent && !document.hidden) {
    funnelHost.layer.onVisitorMoved(urlTrackingData.sessionId, previousUrl, urlTrackingData.pathName);
  }

  // A session converting mid-visit (not on arrival) has no badge yet -
  // drawUserBubble only ever checks d.goalReached once, at spawn time. Add
  // it now, appended next to the bubble's own shape so it inherits the same
  // tick-sync/dock-transition/cleanup behavior appendGoalBadge's own comment
  // describes. Guarded on "not already present" so re-navigating to another
  // goal URL later in the same visit doesn't stack duplicate badges.
  if (
    urlTrackingData.goalReached &&
    !svg?.select(`image[data-goal-badge][visitor_id='${urlTrackingData.sessionId}']`).node()
  ) {
    const shapeNode = svg
      ?.select(
        `circle[visitor_id='${urlTrackingData.sessionId}'], rect[visitor_id='${urlTrackingData.sessionId}']`
      )
      .node();
    // The LIVE bubblesData entry (mutated above), not the deep-cloned
    // snapshot `node` taken at the top of this function - appendGoalBadge
    // binds this as the badge's own datum, and paintRealtimeBubblePositions
    // reads d.x/d.y fresh every tick, so a stale clone would leave the badge
    // frozen at its append-time position instead of tracking the bubble.
    const liveItem = bubblesData.find(
      (item) => item.visitor_id === urlTrackingData.sessionId
    );
    if (shapeNode && liveItem) {
      // The shape itself was created BEFORE this conversion, at the
      // smaller pre-goal size - getBubbleRadius(liveItem) now returns the
      // larger goal size (liveItem.goalReached is already true, set above),
      // so without this the new badge would be sized for a bigger bubble
      // than the circle/rect still actually is, reading as mismatched/
      // oversized rather than "the whole bubble got bigger."
      const upsizedRadius = getBubbleRadius(liveItem);
      const upsizedRect = getRectSizeFromRadius(upsizedRadius, getActiveIconAspect());
      d3.select(shapeNode)
        .attr("r", upsizedRadius)
        .attr("width", upsizedRect.width)
        .attr("height", upsizedRect.height);
      appendGoalBadge(shapeNode, liveItem, "realTime", "visitor_id", urlTrackingData.sessionId);
      // The ZOOMED view keeps its own copy of what it knows about a visitor, set when they arrive and when the grid is rebuilt. A visitor who converts MID-VISIT changed neither, so the grid showed their star
      // and the zoomed view never did (found 2026-10-17, reported from the demo). Tell it now.
      densityCloud.notifyVisitorData(urlTrackingData.sessionId, {
        browser: liveItem.sessionData?.browser,
        visitor_type: liveItem.sessionData?.visitor_type,
        goalReached: true,
        unidentified: (liveItem.visitor_category ?? liveItem.sessionData?.visitor_category) === "unidentified",
        attrs: zoomAttrsOf(liveItem.sessionData),
      });
      // The bubble just grew (getBubbleRadius reads goalReached), and the returning mark was sized for the smaller one: redraw it.
      if (liveItem.sessionData?.visitor_type === "return") {
        svg?.selectAll(`image[data-return-mark][visitor_id='${urlTrackingData.sessionId}']`).remove();
        appendReturnMark(shapeNode, liveItem, urlTrackingData.sessionId);
      }
      // Skipped during silent seek reconstruction, same as the flowLines
      // block below - playConfettiBurst spins up its own untracked rAF loop
      // (~200 ticks, no seekGeneration check) with no cap on how many can run
      // concurrently, unlike playGooeyDockBurst's MAX_CONCURRENT_GOOEY_BURSTS.
      // replaySessionStates can call this once per already-converted session
      // while reconstructing a seek target on a busy day - each one a real
      // "journey" that never happened, exactly the reasoning drawUserBubble/
      // moveBubbleToHistoricalCluster's own `silent` params already apply to
      // their bursts. Left unguarded, a single scrub could kick off several
      // overlapping 3+s confetti loops (32 SVG paths each, updated every
      // frame) plus repeated goal-slot-flash reflows, well past whichever
      // seek triggered them - the same class of "slows down and goes in
      // spurts" symptom the seek-performance work upstream already fixed
      // once for a different cause.
      if (!silent) {
        celebrateConversionAtGoalFlag(urlTrackingData.pathName, urlTrackingData.sessionId);
      }
    }
    // sessionChanged (emitted above) has no Footer.tsx listener, so a
    // mid-visit conversion needs its own event or the Footer's
    // conversions/conversionRate stats go stale until a full page reload
    // re-fetches getMainMetrics.
    eventEmitter.emit("goalConverted", urlTrackingData);
  }

  syncRealtimeNodesFromBubblesData();
  reheatRealtimeSimulation();
};

export const handleVisitorLeave = (itemName: string) => {
  const foundItem = getItemByName(itemName);
  // A departing bubble's cached .name can go stale if the filter changed
  // while it was onsite and it had no value for the new filter's field
  // (e.g. no country_name under "By Country") - see the
  // "realtimeFilterChanged" listener's own fix for the actual source.
  // getItemByName then finds nothing for that stale name, and this used to
  // crash outright (`foundItem.currentUsers--` on undefined) rather than
  // just skip the sidebar-count update for a bubble that was never really
  // representing a category under the current filter anyway.
  if (!foundItem) {
    return;
  }

  foundItem.currentUsers--;

  foundItem.totalUsers++;
  items.sort((a, b) => {
    // Sort in descending order based on currentUsers, giving priority to currentUsers
    if (a.currentUsers > b.currentUsers) return -1;
    if (a.currentUsers < b.currentUsers) return 1;
    
    // If currentUsers are equal, then sort based on totalUsers
    if (a.totalUsers > b.totalUsers) return -1;
    if (a.totalUsers < b.totalUsers) return 1;
    
    // If both currentUsers and totalUsers are equal, maintain the original order
    return 0;
  });

};

let eventsEnabled: boolean;
export const handleFilterChange = async (filterSelected) => {
  categoryColorAssignments = {};
  selectedFilter = filterSelected;
  persistSelectedFilter(filterSelected);

  items = [];
  // A row's burst-pin (activeBurstRowNames) locks it to its LAST EMITTED
  // position by name (see notifySidebarItemsUpdatedFlush) so a resort can't
  // repaint it mid-animation - but neither that lock nor
  // lastEmittedRowOrder was ever cleared on a filter switch, only on the
  // lock's own setTimeout. A name left locked from the PREVIOUS filter (e.g.
  // "Referred" under Traffic Source) could keep pinning/reappearing in the
  // freshly rebuilt list for as long as that timeout was still pending,
  // alongside stale position data from a completely different category set -
  // reported directly as the sidebar list still showing a stale
  // name/position right after switching filters.
  activeBurstRowNames.clear();
  lastEmittedRowOrder = [];
  // The actual sidebar rebuild-from-onsite-visitors happens in the
  // "realtimeFilterChanged" listener below, triggered by this emit - see
  // that listener's own comment for the bug that made it a no-op for the
  // "None" filter specifically.
  if (eventsEnabled) {
    eventEmitter.emit("realtimeFilterChanged");
  }
  // Bubble shape/size/aspect (rect vs square vs flag-ratio) is only decided
  // at creation time - switching categories (e.g. Operating System back to
  // Country) otherwise leaves already-drawn real-time bubbles stuck with
  // whichever aspect was active when they were first drawn, even though
  // their fill/tooltip already reflect the new category. Reshape them here
  // too, same as toggling "Show icons" already does.
  if (useIcons) {
    redrawRealtimeBubbles();
  }
  // The historical dock is grouped by the OLD selectedFilter's categories
  // (menuName values, colors looked up via `items`, which was just wiped
  // above) - without this, switching "by X" left the dock's existing
  // circles pointing at now-nonexistent category entries and never
  // refetched/redrew them for the new grouping, reported as the historical
  // cluster disappearing entirely. getHistoricalBubblesData() is a LIVE-only
  // backend fetch (keyed off selectedDateRange, the live date filter) - it
  // has no idea what day playback is showing, so it either fetches the
  // wrong data or nothing at all during playback (confirmed: the same
  // "disappears" bug persisted in playback after this fix alone). Playback
  // mode's own listener (playbackService.ts's "realtimeFilterChanged"
  // handler) rebuilds the dock instead, from the departed sessions it
  // already has locally - no backend round trip needed or possible mid
  // playback.
  if (playbackModeService.mode !== "playback") {
    removeHistoricalBubbles();
    getHistoricalBubblesData();
  }
};

export const enableEvents = () => {
  eventsEnabled = true;
};

export const disableEvents = () => {
  eventsEnabled = false;
};

// date range filter change

eventEmitter.addListener("filterChanged", (info?: { quiet?: boolean; trigger?: string }) => {
  if (info?.quiet) {
    // an automatic resync: refresh the numbers, keep what is on screen (the dock, the lists)
    refreshHistoricalBubblesQuietly().then((handled) => {
      if (handled) {
        updateSiteInfo();
        getPagesStatistics();
        getMetricsData({
          startDate: selectedDateRange ? new Date(getEffectiveDateRangeValue()).toISOString() : new Date(new Date().setHours(0, 0, 0, 0)).toISOString(),
          endDate: selectedEndDateRange ? selectedEndDateRange : new Date().toISOString(),
        });
      } else {
        eventEmitter.emit("filterChanged");   // no dock to update in place: do the full rebuild
      }
    });

    return;
  }
  items = [];
  removeHistoricalBubbles();
  const realtimeMenuItems = new Set<string>();

  // See the "realtimeFilterChanged" listener's comment - same raw-lookup
  // bug (missed the "None" filter's special-casing), fixed the same way.
  bubblesData.forEach((bubble) => {
    const category = resolveRealtimeCategory(bubble.sessionData);
    // A live session genuinely missing this field (no geo lookup, unusual
    // UA, etc.) shouldn't get its own fake "null"/"undefined" sidebar
    // category - see getHistoricalBubblesData's matching guard. But it
    // still needs its STALE name/color from whichever filter was active
    // before explicitly cleared here, not left as-is - otherwise it kept
    // masquerading as belonging to its old category (reported as a phantom
    // "All visitors" entry surviving a switch to "By Country", and more
    // generally as "filters mixing" for any stale name).
    if (category) {
      realtimeMenuItems.add(category);
    } else {
      bubble.name = UNCATEGORIZED_CATEGORY;
      bubble.color = UNCATEGORIZED_COLOR_INDEX;
    }
  });
  for (const listItemName of realtimeMenuItems) {
    createRealtimeListItem(listItemName, 0);

    bubblesData
      .filter(
        (bubble) => resolveRealtimeCategory(bubble.sessionData) === listItemName
      )
      .forEach((bubble) => {
        const currentItem = getItemByName(listItemName);
        bubble.color = currentItem.color;
        bubble.name = listItemName;
      });
  }
  // Same stale-bound-datum issue as the "realtimeFilterChanged" listener's
  // own fix (see its comment) - removeHistoricalBubbles() above rebinds
  // bubblesSelection from a pre-mutation snapshot, so restyling needs to
  // rebind straight from realtimeNodes rather than trust whatever's
  // currently bound to the DOM.
  svg
  ?.selectAll("circle[realTime=true], rect[realTime=true], image[realTime=true]")
  .data(realtimeNodes, (d: any) => d.visitor_id)
  ?.style("fill", (d) => getBubbleFill(d))
  ?.style("stroke", (d) => getBubbleStroke(d));

  notifySidebarItemsUpdated();
  initialHistoryFlag = false;
  updateSiteInfo();
  getPagesStatistics();

  if (playbackModeService.mode === "playback") {
    // the dock is what the replay has built so far: the backend knows nothing of it (it would answer for the live range, and the dock would show only today's sessions)
    eventEmitter.emit("playbackDockRebuild");
  } else {
    getHistoricalBubblesData();
  }
  getMetricsData({
    startDate: selectedDateRange
    ? new Date(getEffectiveDateRangeValue()).toISOString()
    : new Date(new Date().setHours(0,0,0,0)).toISOString(),
    endDate: selectedEndDateRange
      ? selectedEndDateRange
      : new Date().toISOString(),
  });
});

// realtime filter change for clusters

export const realtimeFilterChangedForClusters = () => {
  categoryColorAssignments = {};
  selectedFilter = selectedFilter;
  items = [];
};

// Playback feature: wipes all bubble/state so playback always starts (and
// every seek always restarts) from a guaranteed-clean slate, and so exiting
// playback back to live mode doesn't leave stray playback bubbles behind.
// Mutates realtimeNodes IN PLACE (never reassign - the persistent D3
// simulation holds a reference to this exact array).
export const resetVisualizationState = () => {
  sidebarOriginReader.reset(); // the list is about to be rebuilt: where its dots were is no longer where they are
  // the clouds must forget the old visitors with the bubbles, or they keep them as ghost dots
  try {
    cloudedVisitors = new Set<string>();
    densityCloud.resetVisitors();
  } catch (error) {
    console.error("[density-cloud] reset failed while wiping the board", error);
  }
  bubblesData = [];
  realtimeNodes.length = 0;
  realtimeSimulation?.nodes(realtimeNodes);
  syncCloudCounts();
  // Halts d3's own internal, real-frame-driven timer so nothing autonomous
  // is running while the next reconstruction's bubbles accumulate one by
  // one across replaySessionStates' multi-frame loop (see
  // reheatRealtimeSimulation's silent-mode comment). settleRealtimeSimulation
  // explicitly seeds alpha and ticks it forward manually once the full
  // batch is in, so nothing is lost by starting stopped here.
  realtimeSimulation?.stop();
  Object.keys(pageWiseCount).forEach((key) => {
    pageWiseCount[key] = 0;
  });
  // pagesModel.ts's own `data[].currentVisitors` (the right-drawer Pages
  // tab's "Current" column) is a SEPARATE counter from pageWiseCount above -
  // this function runs on every single seek/scrub, not just a genuinely new
  // date, and the onsite-session reconstruction that follows only ever
  // INCREMENTS each currently-onsite session's page (via
  // handleVisitorMovePagesModel inside drawUserBubble/handleVisitorMove)
  // without this ever zeroing it first. Left unreset, every seek piled its
  // reconstruction's increments on top of the previous seek's already-
  // incremented values instead of replacing them - scrubbing back and forth
  // a few times made "Current" climb without bound, and since this same
  // reset also runs when exiting playback back to live, that drift carried
  // straight into live mode's Pages tab too until a full page reload wiped
  // it clean (reported directly: "off while I'm in playback... may be the
  // case in real-time too").
  data?.forEach((page: any) => {
    page.currentVisitors = 0;
  });
  items = [];
  onsiteUser = 0;
  svg
    ?.selectAll("circle[realTime=true], rect[realTime=true], image[realTime=true]")
    .remove();
  // The Sessions-tab-row highlight ripple (setSessionHighlight) isn't
  // tagged realTime=true, so the wipe above never touched it - going back
  // and forth between live/playback (or any seek, which also calls this)
  // left a stale ripple circle orphaned in the DOM, no longer tracking any
  // real bubble, plus highlightedVisitorId still pointing at a session that
  // may not even exist in the newly-loaded mode/day. Clearing both here,
  // at the single shared reset point every mode/seek transition already
  // goes through, means every caller gets this for free instead of each
  // one needing its own explicit setSessionHighlight(null) call.
  svg?.selectAll("circle[data-highlight-ripple=true]").remove();
  svg?.selectAll("circle[data-hover-highlight=true]").remove();
  highlightedVisitorId = null;
  // Same gap as the ripple/hover-ring fix above, for the interaction-pulse
  // badges (away/click/typing/idle/scroll) - this shared reset point never
  // called clearAllInteractionPulses(), so switching live -> playback (or
  // any seek) left a stale "not focused" glyph (or any other pulse badge)
  // orphaned on screen, no longer tracking a real bubble. Documented as a
  // known gap when this file's other cleanup fixes went in, but never
  // actually wired up until reported live in production. Reuses the same
  // function the settings-gear "disable interaction pulses" toggle already
  // calls, so this also correctly clears interactionPulseTimeouts and
  // emits the matching sessionIdleChanged/sessionTypingChanged/
  // sessionScrollChanged(false) events - without that, RightMenu's row-level
  // badges would keep showing idle/typing/scrolling too, not just the bubble
  // glyph.
  clearAllInteractionPulses();
  removeHistoricalBubbles();
  notifySidebarItemsUpdated();
  eventEmitter.emit("userCountChanged", onsiteUser);
  // Deliberately NOT calling resetFlowLines() here - this function runs on
  // EVERY seek, including the automatic snapshot seekTo() takes at the
  // natural end of a day's playback (see playbackService.ts's tick()) and
  // every scrub-bar drag, not just a genuinely new date being loaded. Flow
  // lines are meant to accumulate across a full playback run and persist
  // once it finishes, not vanish the moment the day ends or the user drags
  // the scrub bar - only loadDate() (a real, new date) should clear them;
  // see its own explicit resetFlowLines() call.
};

// Self-healing resync against the backend's ground truth for "who's
// actually onsite right now" (SessionApi's getOnsiteSessions). The real-time
// view is otherwise built up purely from accumulated socket deltas (arrival
// ++, departure --), which has no way to recover if a single delta is ever
// missed, duplicated, or arrives mid-reconnect - previously the only fix was
// a manual page reload.
//
// This must only touch the actual DIFFERENCE between what's currently shown
// and the truth - an earlier version wiped every real-time bubble and
// redrew all of them from scratch on every call, which is exactly what
// self-healing should NOT look like: since this runs on every
// connect/reconnect and every few minutes as a safety net, wiping
// everything meant the ENTIRE board would periodically "reset" - every
// already-correct bubble destroyed and recreated, all flying back in from
// the sidebar together, even though nothing was wrong with them (reported
// verbatim: bubbles "reset" and "came from the left" every couple of
// minutes). Bubbles/counts that already match the truth must be left
// completely alone.
let reconcilingOnsiteVisitors = false;
export const reconcileOnsiteVisitors = async () => {
  if (reconcilingOnsiteVisitors) {
    return;
  }
  reconcilingOnsiteVisitors = true;
  try {
    const trueOnsiteSessions = await getOnsiteSessions();
    const trueVisitorIds = new Set(
      trueOnsiteSessions.map((sessionData: any) => String(sessionData._id))
    );
    const knownVisitorIds = new Set(
      bubblesData
        .filter((node) => node.realTime)
        .map((node) => String(node.visitor_id))
    );

    // Sessions shown onsite here but actually already gone (their real
    // departure notification was the one that got missed) - remove them
    // via the normal departure path, silently (see drawUserBubble's silent
    // param), so a bunch of these correcting at once doesn't play a dock
    // animation per bubble. This still correctly applies the totalUsers/
    // historical-dock increments that departure should have applied the
    // first time.
    const staleVisitorIds = bubblesData
      .filter(
        (node) => node.realTime && !trueVisitorIds.has(String(node.visitor_id))
      )
      .map((node) => node.visitor_id);
    for (const visitorId of staleVisitorIds) {
      await moveBubbleToHistoricalCluster(visitorId, true);
    }

    // Sessions actually onsite but missing here (their arrival notification
    // was the one that got missed) - draw them in now, silently for the
    // same reason.
    for (const sessionData of trueOnsiteSessions) {
      if (!knownVisitorIds.has(String(sessionData._id))) {
        await drawUserBubble(sessionData, true);
      }
    }

    /*
     * Re-DERIVE the onsite count from the bubbles this function just corrected,
     * instead of leaving it to whatever the accumulated counter happens to hold.
     *
     * onsiteUser is only ever ++'d on arrival and --'d on departure, and the
     * only reset is the full visualization wipe - so it had NO self-healing path
     * at all. Reconcile fixed the bubbles and left the headline number wrong,
     * which meant a single missed departure was permanent and the error
     * compounded for as long as the tab stayed open. Measured 2026-07-30 after
     * an overnight run: the header read 461 TOTAL VISITORS ONSITE while the
     * backend held exactly 30 open sessions - the bubbles had been reconciled,
     * the number never had been.
     *
     * This is perf note 10's rule applied to the one place still breaking it:
     * occupancy is a STATE, so derive it. realtimeNodes is the array reconcile
     * has just brought in line with the backend's own onsite list, which makes
     * its length the closest thing to ground truth the client has - and it is
     * idempotent by construction, so running this twice cannot double-count.
     */
    const derivedOnsite = getOnsiteVisitorCount();
    if (onsiteUser !== derivedOnsite) {
      onsiteUser = derivedOnsite;
      eventEmitter.emit("userCountChanged", onsiteUser);
    }
  } finally {
    reconcilingOnsiteVisitors = false;
  }
};

export const removeHistoricalBubbles = () => {
  // Was a hardcoded `!== 11` - the historical cluster's own index moved
  // from 11 to 13 (HISTORICAL_CLUSTER_INDEX) when the 2 new real page slots
  // took over indices 10-11 (see UNKNOWN_URL_CLUSER_INDEX's own comment).
  // This function runs on nearly every filter change/reset, and with the
  // stale literal, it was wrongly matching and deleting REAL-TIME bubbles
  // on slot 11 (any session on whichever page occupies that slot) from
  // bubblesData on every single one of those calls - the actual root cause
  // of "/terms and /items-list" (the two new slots) losing their
  // bubblesData entry while their DOM circle stayed onscreen: stuck
  // bubbles, and their conversions never counted since bubblesData no
  // longer had anything to look up when they departed.
  bubblesData = bubblesData.filter((node) => node.cluster !== HISTORICAL_CLUSTER_INDEX);
  // DO NOT make chain from the actions below!

  bubblesSelection = bubblesSelection?.data(
    JSON.parse(JSON.stringify(bubblesData)),

    (d: any) => d.index
  );
  // d3.selectAll("circle[data-bubble], rect[data-bubble]").remove();
  svg?.selectAll("circle[historical=true], rect[historical=true], image[historical=true]").remove();
  const dockSvg = svg?.node() as SVGSVGElement | undefined;
  if (dockSvg) {
    // the bar chart and the labels belong to the bubbles they describe: rebuilt from the data right after, by computeHistoricalRows
    removeHistoricalCharts(dockSvg);
    removeHistoricalLabels(dockSvg);
    dockLabelsLive = false;
  }
};

const updateSiteInfo = async () => {
  const requestParams = {
    id: 0,
    jsonprc: "2.0",
    method: "update",
    params: {
      siteId:storage.get("siteId"),
      selectedViews: {
        showListView: true,
        selectedSource: selectedFilter,
        selectedTimeSource: selectedDateRange.name || "Today",
        selectedCustomTimeSource:
          selectedDateRange.name === "Custom Date"
            ? selectedDateRange.name
            : null,
      },
    },
  };
  const tempo = await site(requestParams);
  return tempo;
};

/**
 * The automatic resyncs (a socket reconnect, coming back to the tab, the 3-minute check) bring the dock's numbers up to date WITHOUT tearing it down: every bubble that exists is
 * resized in place, a category that is new is added, nothing is removed or redrawn. (They used to run the full rebuild: the whole dock vanished and came back, and the app paused
 * while it was fetched again - reported on the demo as "something is reloading".) Returns false when there is no dock to update, so the caller does the full rebuild instead.
 */
const refreshHistoricalBubblesQuietly = async (): Promise<boolean> => {
  if (!bubblesData.some((item) => item?.historical === true && item.menuName)) {
    return false;
  }
  const getHistoryData = await session({
    id: 0,
    jsonprc: "2.0",
    method: "getHistoricalBubblesData",
    params: {
      endDate: selectedEndDateRange ? selectedEndDateRange : new Date().toISOString(),
      startDate: getEffectiveDateRangeValue(),
      siteId: storage.get("siteId"),
      menuType: selectedFilter?.menuType,
    },
  });
  const data = getHistoryData?.data?.result;
  if (!data) {
    return true;   // nothing to apply; leave the dock as it is
  }
  let totalSessionCount = 0;
  let totalConvertedCount = 0;
  for (const name in data) {
    if (name === "null" || name === "undefined" || !name) {
      continue;
    }
    const { visitors, converted } = data[name];
    totalSessionCount += visitors;
    totalConvertedCount += converted || 0;
    if (bubblesData.some((item) => item?.historical === true && item.menuName === name)) {
      updateBubble(name, visitors, converted);
    } else {
      drawNewHistoricalBubble(name, visitors, converted);
    }
  }
  historical.totalSession = totalSessionCount;
  historical.totalConverted = totalConvertedCount;
  notifySidebarItemsUpdated();

  return true;
};

const getHistoricalBubblesData = async () => {
  const param = {
    id: 0,
    jsonprc: "2.0",
    method: "getHistoricalBubblesData",
    params: {
      endDate: selectedEndDateRange
        ? selectedEndDateRange
        : new Date().toISOString(),
      startDate: getEffectiveDateRangeValue(),
      siteId: storage.get("siteId"),
      menuType: selectedFilter?.menuType,
    },
  };

  const getHistoryData = await session(param);

  const bubbledData = getHistoryData?.data?.result;
  // Create realtime item bubbles data
  const newBubbledData = [];
  const historicalBubbleData = [];
  // See AnalyticsView.tsx's fetchData for the original fix this mirrors -
  // this is a separate call site (fires on "By..." filter switches, not
  // date-range changes) that had the same gap: historical.totalSession
  // (the tooltip percentage's denominator) was never set from this
  // response, only ever incremented by live departure events.
  let totalSessionCount = 0;
  let totalConvertedCount = 0;

  for (const bubbleName in bubbledData) {
    // Defense in depth against the backend's own null-exclusion
    // (metrics.repo.ts's countVisitorsByFiels) - `for...in` keys are always
    // strings, so a JS `null` group key from an older/uncleaned backend
    // response would otherwise arrive here as the literal string "null" and
    // render as if it were a real category.
    if (bubbleName === "null" || bubbleName === "undefined" || !bubbleName) {
      continue;
    }
    const currentItem = getItemByName(bubbleName);
    // Was a plain number - metrics.ts's getHistoricalBubblesData now
    // returns { visitors, converted } per category (same aggregation pass,
    // no extra round trip) so the historical tooltip can show each
    // category's own conversion metrics.
    const { visitors, converted } = bubbledData[bubbleName];

    currentItem
      ? currentItem.totalUsers++
      : createRealtimeListItem(bubbleName, visitors);

    newBubbledData.push(bubbleName);
    historicalBubbleData.push({
      bubbleName: bubbleName,
      userValue: visitors,
      converted,
    });
    totalSessionCount += visitors;
    totalConvertedCount += converted || 0;
  }
  historical.totalSession = totalSessionCount;
  historical.totalConverted = totalConvertedCount;
  removeHistoricalBubbles();
  drawHistoricalBubble(historicalBubbleData);
  notifySidebarItemsUpdated();

  // Was `clusterCenterCoords[9].x - 50 + 150 - (clusterCenterCoords[0].x - 50)`
  // - slot 9 is the rightmost column only on a five-column grid, and the
  // -50/+150 pair are stale 100px-box literals. This is the usable width the
  // historical dock lays its bubbles out across, so it is exactly the grid's
  // own span.
  const rectAllOtherPages = gridWideBoxEndX - gridFirstColumnLeftEdge;
  const historyIndex = Math.floor(rectAllOtherPages / 64);

  for (let i = 0; i < newBubbledData.length; i++) {
    const centerValue = 140 + i * 64 + 32;
    if (centerValue < rectAllOtherPages + 140 - 64) {
      historyIndexRectCenters[newBubbledData[i]] = {};
      historyIndexRectCenters[newBubbledData[i]].x = 140 + i * 64 + 32;
      historyIndexRectCenters[newBubbledData[i]].y = 0;
      //  {x: 140 + (i*64) + 32, y: 0};
    } else {
      historyIndexRectCenters[newBubbledData[i]] = {};
      historyIndexRectCenters[newBubbledData[i]].x =
        140 + (i - Math.floor(i / historyIndex) * historyIndex) * 64 + 32;
      historyIndexRectCenters[newBubbledData[i]].y =
        Math.floor(i / historyIndex) * 64;
    }
  }
  if (!initialHistoryFlag) {
    // Was an intersection with the historical (departed-sessions) response
    // for the selected date range - any category still holding onsite
    // visitors (currentUsers > 0) but with zero departures yet in that
    // range got wrongly dropped from the sidebar list entirely, even
    // though its live bubbles were still rendering. Keep an item if it
    // either has historical data OR still has live onsite users.
    const names = Object.keys(bubbledData);
    const tempoArray = items.filter(
      (element) => names.includes(element.name) || element.currentUsers > 0
    );
    items = tempoArray;
  }
};

export const DateRangeFilterSet = (dateRange: any, endDate?: any) => {
  // Was only ever SET here (Custom Date range picked), never cleared when
  // switching back to a normal range (Today, Last 7 Days, etc.) - every
  // fetch reading this shared module-level variable (Footer.tsx's
  // getMetricsDataInfo, this file's own getMetricsData/
  // getHistoricalBubblesData) unconditionally prefers it over a live
  // `new Date()` whenever it's truthy, with no gate on whether the
  // CURRENTLY selected range is even "Custom Date" anymore. Confirmed
  // directly: once a Custom Date was picked once, ALL later metrics
  // fetches - Footer's Sessions/Page Views/etc AND the segment-detail
  // panel's numbers - silently kept using that one frozen end timestamp
  // forever, so live traffic arriving after that moment stopped moving any
  // number on screen, with no error and no visible sign anything was wrong
  // short of comparing against the backend's own real count. Clearing it
  // here whenever a non-custom range is chosen keeps it scoped to exactly
  // the one case (this file's other reads) that's supposed to use it.
  selectedEndDateRange = endDate ? endDate.value : undefined;
  selectedDateRange = dateRange;
  saveHistoricalRange(dateRange);
  eventEmitter.emit("filterChanged");
};

export const moveBubbleToHistoricalCluster = async(sessionId: string, silent = false) => {
  // Claimed SYNCHRONOUSLY, before any await - see departingSessionIds' own
  // comment. A second near-simultaneous call for the same session (already
  // happening in practice, confirmed via live debug logging) bails out here
  // instead of racing the first call's still-pending removal.
  if (departingSessionIds.has(sessionId)) {
    return;
  }
  departingSessionIds.add(sessionId);
  // (its return value used to decide whether the dock burst played: every closing visitor now bursts in the dock, zoomed or not, as Chris
  // asked, so the value is no longer used)
  densityCloud.notifyVisitorDeparture(sessionId);
  syncCloudCounts();
  try {

  const bubbleParametersData = returnBubbleParameterData();

  const userBubble = bubblesData.find((node) => node.visitor_id === sessionId);

  if (!userBubble) {
    // A real DOM circle can exist for this visitor_id with no corresponding
    // bubblesData entry - confirmed directly (playback, live debug
    // inspection): the circle and its bound datum were both present and
    // correctly attributed, but bubblesData.find found nothing, so this
    // departure kept silently no-op'ing forever, leaving that bubble
    // permanently stuck onscreen ("3 bubbles never close" - the true
    // desync's root cause wasn't isolated in time, but the visible symptom
    // has a clear, safe fix regardless: if a matching circle genuinely
    // exists, remove it now rather than leaving it stranded). Only treat
    // this as the legitimate "disconnect arrived before the deferred
    // 'new-visitor' draw ran" race (the ORIGINAL reason
    // pendingDisconnectedSessions exists) when there's truly nothing to
    // remove.
    const orphanedSelector = `circle[visitor_id='${sessionId}'], rect[visitor_id='${sessionId}'], image[visitor_id='${sessionId}']`;
    const orphanedNode = svg?.select(orphanedSelector).node();
    if (orphanedNode) {
      clearInteractionPulses(sessionId);
      svg?.selectAll(orphanedSelector).remove();
      const orphanedRealtimeIndex = realtimeNodes.findIndex(
        (node: any) => node.visitor_id === sessionId
      );
      if (orphanedRealtimeIndex !== -1) {
        realtimeNodes.splice(orphanedRealtimeIndex, 1);
        realtimeSimulation?.nodes(realtimeNodes);
      }
      onsiteUser = Math.max(0, onsiteUser - 1);
      eventEmitter.emit("userCountChanged", onsiteUser);
    } else {
      // The bubble may not exist yet if this disconnect arrived before the
      // deferred 'new-visitor' draw ran - remember it so that draw can skip
      // itself instead of creating an orphaned bubble with no future removal.
      pendingDisconnectedSessions.add(sessionId);
    }
    return;
  }

  // The ripple correlates a Sessions-tab row to a LIVE bubble - once THIS
  // session departs, that bubble is about to stop existing (it's folding
  // into the aggregated historical dock), so the ripple has nothing left to
  // point at. Without this it just sat at the departure position forever,
  // reported directly as "stays when the session closes." Only clears if
  // the departing session is the one actually highlighted - an unrelated
  // session departing shouldn't touch someone else's active highlight.
  if (sessionId && isSessionHighlighted(sessionId)) {
    setSessionHighlight(null);
  }

  // Gated identically to recordArrivalTraversal/recordPathTraversal's own
  // `!silent` checks (counting no longer depends on the lines being shown) (see those calls) - `silent` here
  // means this departure is being reconstructed instantly during a playback
  // seek, not a real-time event, and the existing arrival/path recording
  // already excludes those for consistency. Uses the ORIGINAL `silent` flag,
  // not effectiveSilent below - a genuinely live-fired departure should
  // always count toward flow-line stats regardless of whether its own
  // animation gets shortened.
  if (!silent) {
    recordDepartureTraversal(userBubble.cluster);
    tallyRoute("leave", sessionId);
  }

  // `silent` (true only for playback seek reconstruction, jumping straight
  // to a point in time with no journey to animate) is the only thing that
  // skips the docking animation now. Earlier attempts also skipped it
  // whenever not enough real wall-clock time had passed since spawn
  // ("hasSpawnSettled"), reasoning that the force sim hadn't caught up to
  // its target yet - but the bubble is already spliced out of
  // realtimeNodes/realtimeSimulation above by the time this fires, so there
  // is no ongoing physics fight; the transition below just eases smoothly
  // from wherever the bubble currently sits (settled or not) to the dock.
  // Gating on settle time bought nothing but a structural failure mode:
  // most realistic visit lengths at typical playback compression never
  // satisfy any real-time threshold, so departures kept resolving to an
  // instant, un-animated vanish ("disappearing instead of closing") no
  // matter how that threshold or the force strength feeding it was tuned.
  // DOCK_TRANSITION_DURATION_MS below (mode-aware, much shorter in
  // playback) is what actually keeps the animation feeling proportionate to
  // playback's pace, instead of a settle-time gate deciding whether it
  // plays at all.
  // In the bar charts the dock's bubbles are not drawn, but the visitor's own bubble (or icon) still FLIES to its bar, and the bar changes when it lands (Chris, 2026-10-19: "I don't see bubbles or
  // icons closing INTO their respective bars and them changing. That's how these bar charts are supposed to work."). A first version made departures instant here on the reasoning that there was
  // nothing to fly to: that was wrong. Only a playback seek and a hidden tab (the `silent` flag) apply the net effect at once.
  // closing where it is (shrink, pop, sink, vanish): the departure is applied at once like a seek's, and a ghost plays the closing animation in the box
  const closingHere = !silent && closesInPlace();
  const effectiveSilent = silent || closingHere;

  // handleVisitorMove(userBubble.currentUrlName, null, "disconnect");
 await handleVisitorMovePagesModel(userBubble.pageName, null);

  // Use the category name captured when this bubble was created
  // (userBubble.name), not a fresh lookup against the CURRENT filter - if the
  // user switches the real-time filter (e.g. Country -> OS) while a visitor
  // is still onsite, recomputing from selectedFilter here would decrement a
  // completely different list item than the one that was incremented at
  // arrival, permanently skewing both (the arrival item stuck too high, the
  // departure item driven negative).
  handleVisitorLeave(userBubble.name);
  onsiteUser--;

  eventEmitter.emit("userCountChanged", onsiteUser);
  const userNodeIndex = bubblesData.findIndex(
    (node) => node.visitor_id === userBubble.visitor_id
  );

  bubblesData?.splice(userNodeIndex, 1);

  bubblesSelection[0]?.splice(userNodeIndex, 1);

  const realtimeNodeIndex = realtimeNodes.findIndex(
    (node) => node.visitor_id === userBubble.visitor_id
  );
  if (realtimeNodeIndex !== -1) {
    realtimeNodes.splice(realtimeNodeIndex, 1);
    realtimeSimulation?.nodes(realtimeNodes);
  }

  clearInteractionPulses(userBubble.visitor_id);

  const parsedVisitorId = sessionId.replace(/'/g, " ");

  const historicalNode = svg.select(
    `circle[name='${userBubble.name}'], rect[name='${userBubble.name}'], image[name='${userBubble.name}']`
  );

  if (effectiveSilent) {
    // Playback feature: reconstructing an already-departed session for a
    // seek target. The normal path below animates a 1600ms dock transition
    // + burst per session - fine for one live departure, but replaying
    // hundreds/thousands of them back-to-back (one right after each
    // silent-mode drawUserBubble arrival) is what rendered as bubbles
    // "raining down and immediately closing". Apply the exact same net
    // effect on the historical dock's data/DOM (increment its count, create
    // it if this is the first departure into this category) without the
    // moving bubble, transition, or burst - there's no visible "journey" to
    // animate when jumping straight to a point in time.
    if (closingHere) playLeaveGhost(`circle[visitor_id='${parsedVisitorId}'], rect[visitor_id='${parsedVisitorId}'], image[visitor_id='${parsedVisitorId}']`, leaveStyle);
    svg
      ?.selectAll(
        `circle[visitor_id='${parsedVisitorId}'], rect[visitor_id='${parsedVisitorId}'], image[visitor_id='${parsedVisitorId}']`
      )
      .remove();
    historical.totalSession++;
    if (userBubble.goalReached) {
      historical.totalConverted++;
    }
    if (historicalNode._groups[0][0]) {
      const newHistoricalValue =
        parseInt(historicalNode?._groups[0][0]?.getAttribute("user")) + 1;
      const existingDockBubble = bubblesData.find(
        (item) => item?.historical === true && item.menuName === userBubble.name
      );
      const newConvertedValue =
        (existingDockBubble?.converted || 0) + (userBubble.goalReached ? 1 : 0);
      updateBubble(userBubble.name, newHistoricalValue, newConvertedValue);
    } else {
      drawNewHistoricalBubble(userBubble.name, 1, userBubble.goalReached ? 1 : 0);
    }
    // the historical bubbles take the visitor in with their burst whichever way it closed (a visitor that closes where it is never flew there, so the burst is all the dock shows of it)
    if (closingHere) {
      const chartTipHere = dockIsChart() ? chartTips.get(userBubble.name) : undefined;
      const dockNodeNow = svg?.select(`circle[name='${userBubble.name}'], rect[name='${userBubble.name}'], image[name='${userBubble.name}']`)?.node();
      const here = dockNodeNow ? getShapeCenter(dockNodeNow) : null;
      const bx = chartTipHere ? chartTipHere.x : here ? here.cx : clusterCenterCoords?.[HISTORICAL_CLUSTER_INDEX]?.x;
      const by = chartTipHere ? chartTipHere.y : here ? here.cy : clusterCenterCoords?.[HISTORICAL_CLUSTER_INDEX]?.y;
      playGooeyDockBurst(
        bx,
        by,
        getHistoricalIconUrl({ menuName: userBubble.name })
          ? GOOEY_NEUTRAL_BURST_COLOR
          : userBubble.color != null
          ? String(setColor(userBubble.color))
          : undefined,
        chartTipHere ? CHART_ARRIVAL_RADIUS : dockNodeNow ? +((dockNodeNow as Element).getAttribute("r") || 0) || getHistoricalBubbleRadius(1) : getHistoricalBubbleRadius(1),
        effectDim(userBubble.visitor_id, userBubble.pageName, true)
      );
    }
    return;
  }

  // A visitor who is closing is dimmed in dim mode, whoever they are (Chris, 2026-10-18): their bubble and icon at once, and their burst at the dock.
  let dimBurst = effectDim(userBubble.visitor_id, userBubble.pageName, true);
  if (funnelHost) funnelHost.layer.visitorLeaving(userBubble.visitor_id);
  if (leaveStyle === "dim") {
    svg
      ?.selectAll(`circle[visitor_id='${parsedVisitorId}'], rect[visitor_id='${parsedVisitorId}'], image[visitor_id='${parsedVisitorId}']`)
      .style("opacity", DIM_LEAVING_OPACITY);
    dimBurst = true;
  }

  // Count increments and dock-bubble creation/update moved OUT of the
  // transition's "end" callback and made synchronous, immediately below -
  // they used to only happen ~dockTransitionDurationMs() (commonly
  // ~1600ms) later, inside "end", which is real STATE, not cosmetic
  // animation, and had no business waiting on an animation to finish. Since
  // the departingSessionIds guard above only stays claimed for this
  // function's synchronous body, a second departure for the same session
  // arriving anytime during that ~1600ms animation window sailed straight
  // past the guard and caused a real second increment - confirmed directly,
  // historical tooltip showing exactly double the true conversion count.
  // Only the purely cosmetic burst effect (playGooeyDockBurst) still runs
  // inside "end" - its timing doesn't affect correctness, and awaiting the
  // full transition here instead would have throttled playback throughput
  // (every subsequent action in the tick loop would block behind each
  // departure's full ~500-1600ms animation).
  if (historicalNode._groups[0][0]) {
    // in a bar chart the bubble flies to the tip of its bar and the bar takes it in on landing (see chartInFlight); otherwise to the dock bubble
    const chartTip = dockIsChart() ? chartTips.get(userBubble.name) : undefined;
    const historicalCenter = chartTip ? { cx: chartTip.x, cy: chartTip.y } : getShapeCenter(historicalNode?._groups[0][0]);
    historical.totalSession++;
    if (userBubble.goalReached) {
      historical.totalConverted++;
    }
    const newHistoricalValue =
      parseInt(historicalNode?._groups[0][0]?.getAttribute("user")) + 1;
    const existingDockBubble = bubblesData.find(
      (item) => item?.historical === true && item.menuName === userBubble.name
    );
    const newConvertedValue =
      (existingDockBubble?.converted || 0) + (userBubble.goalReached ? 1 : 0);
    // counted as in flight BEFORE the dock's count changes, so the chart never shows the visitor in the bar before the bubble gets there
    const departing = userBubble.name;
    if (chartTip) chartInFlight.set(departing, inFlightOf(departing) + 1);
    updateBubble(userBubble.name, newHistoricalValue, newConvertedValue);
    const targetRadius = chartTip
      ? CHART_ARRIVAL_RADIUS
      : +historicalNode?._groups[0][0]?.getAttribute("r") ||
        getHistoricalBubbleRadius(newHistoricalValue);
    let landed = false;
    const land = () => {
      if (landed) return;
      landed = true;
      if (chartTip) landChartArrival(departing);
    };
    // a bubble removed before its transition ends (a reset, a seek) would never land: the chart must not keep counting it as in flight
    if (chartTip) window.setTimeout(land, dockTransitionDurationMs() + 600);
    svg
      ?.selectAll(
        `circle[visitor_id='${parsedVisitorId}'], rect[visitor_id='${parsedVisitorId}'], image[visitor_id='${parsedVisitorId}']`
      )
      .transition()
      .duration(dockTransitionDurationMs())

      .attr(
        "transform",
        `translate(${-(userBubble.x - historicalCenter.cx)},${-(
          userBubble.y - historicalCenter.cy
        )})`
      )
      .on("end", () => {
        land();
        playGooeyDockBurst(
          historicalCenter.cx,
          historicalCenter.cy,
          getHistoricalIconUrl({ menuName: userBubble.name })
            ? GOOEY_NEUTRAL_BURST_COLOR
            : userBubble.color != null
            ? String(setColor(userBubble.color))
            : undefined,
          targetRadius,
          dimBurst
        );
      })
      .remove();
  } else if (shouldHideBucket(userBubble.name)) {
    // "Hide unknown": this visitor's group is not drawn in the dock, so there is no bar or bubble to land on and NO burst (a ring that bloomed over empty space was the artifact Chris reported,
    // 2026-10-20). But they still go to the dock: they fly to its centre like every other departure, and are gone on arrival (Chris, 2026-10-21: icons "just disappearing out of nowhere, not moving
    // into the historical dock ... They NEED to move into the historical dock somewhere"). The counts are the same as the branch below (state, not animation).
    historical.totalSession++;
    if (userBubble.goalReached) {
      historical.totalConverted++;
    }
    const dockPoint = clusterCenterCoords && clusterCenterCoords[HISTORICAL_CLUSTER_INDEX];
    const here = getShapeCenter(
      svg.select(`circle[visitor_id='${parsedVisitorId}'], rect[visitor_id='${parsedVisitorId}'], image[visitor_id='${parsedVisitorId}']`)?._groups[0][0]
    );
    const selection = svg?.selectAll(
      `circle[visitor_id='${parsedVisitorId}'], rect[visitor_id='${parsedVisitorId}'], image[visitor_id='${parsedVisitorId}']`
    );
    if (dockPoint && here) {
      selection
        .transition()
        .duration(dockTransitionDurationMs())
        .attr("transform", `translate(${dockPoint.x - here.cx},${dockPoint.y - here.cy})`)
        .transition()
        .duration(HIDDEN_GROUP_CLOSE_FADE_MS / 2)
        .style("opacity", 0)
        .remove();
    } else {
      selection
        .transition()
        .duration(Math.min(HIDDEN_GROUP_CLOSE_FADE_MS, dockTransitionDurationMs()))
        .style("opacity", 0)
        .remove();
    }
  } else {
    const currentBubblePosition = svg.select(
      `circle[visitor_id='${parsedVisitorId}'], rect[visitor_id='${parsedVisitorId}'], image[visitor_id='${parsedVisitorId}']`
    );
    const currentBubbleCenter = getShapeCenter(
      currentBubblePosition?._groups[0][0]
    );

    const clusterIndex = HISTORICAL_CLUSTER_INDEX;

    refreshHistoricalCoords();
    const historicalCoords =
      clusterCenterCoords && clusterCenterCoords[clusterIndex];

    historical.totalSession++;
    if (userBubble.goalReached) {
      historical.totalConverted++;
    }
    // a category seen for the first time: in a bar chart its bar appears at the next layout, so the bubble waits two frames to learn where the bar ends, then flies there (and the bar shows the
    // visitor on landing, like every other departure)
    const newInChart = dockIsChart();
    const newName = userBubble.name;
    if (newInChart) chartInFlight.set(newName, inFlightOf(newName) + 1);
    drawNewHistoricalBubble(userBubble.name, 1, userBubble.goalReached ? 1 : 0);
    let newLanded = false;
    const newLand = () => {
      if (newLanded) return;
      newLanded = true;
      if (newInChart) landChartArrival(newName);
    };
    if (newInChart) window.setTimeout(newLand, dockTransitionDurationMs() + 900);
    const flyNew = () => {
      const tip = newInChart ? chartTips.get(newName) : undefined;
      const destX = tip ? tip.x : historicalCoords.x;
      const destY = tip ? tip.y : historicalCoords.y;
      svg
      ?.selectAll(
        `circle[visitor_id='${parsedVisitorId}'], rect[visitor_id='${parsedVisitorId}'], image[visitor_id='${parsedVisitorId}']`
      )
      .transition()
      .duration(dockTransitionDurationMs())

      .attr(
        "transform",
        (d) =>
          `translate(${destX - currentBubbleCenter.cx},${
            destY - currentBubbleCenter.cy
          })`
      )
      .on("end", () => {
        newLand();
        playGooeyDockBurst(
          destX,
          destY,
          getHistoricalIconUrl({ menuName: userBubble.name })
            ? GOOEY_NEUTRAL_BURST_COLOR
            : userBubble.color != null
            ? String(setColor(userBubble.color))
            : undefined,
          tip ? CHART_ARRIVAL_RADIUS : getHistoricalBubbleRadius(1),
          dimBurst
        );
      })
      .remove();
    };
    if (newInChart) window.requestAnimationFrame(() => window.requestAnimationFrame(flyNew));
    else flyNew();
  }
  } finally {
    departingSessionIds.delete(sessionId);
  }
};

const clearClusterSelection = () => {
  // Was a hardcoded `< 10` - real page slots now go up to (not including)
  // UNKNOWN_URL_CLUSER_INDEX (12), so this needs to also clear the 2 new
  // slots (indices 10-11), not just the original 1-9.
  for (let i = 1; i < UNKNOWN_URL_CLUSER_INDEX; i++) {
    fillClusterEmpty(i);
  }
};

const drawNewHistoricalBubble = (name, value, converted = 0) => {
  if (shouldHideBucket(name)) {
    return;
  }
  refreshHistoricalCoords();
  // Unlike updateBubble (which calls this itself), nothing here previously
  // notified the sidebar - the very first departure into a brand new
  // category (the only path that reaches this function instead of
  // updateBubble's early-return) never fired "sideBarRealtimeItemsUpdated",
  // meaning any listener relying on that event to know the historical dock
  // just went from empty to non-empty would miss exactly that transition.
  notifySidebarItemsUpdated();
  // Guards the actual mutation point against every caller that decides
  // "no dock bubble exists yet for this category" from a DOM snapshot taken
  // before an async gap (moveBubbleToHistoricalCluster's 1600ms departure
  // transition, whose dock-creation only happens in its .on("end") callback).
  // Two departures for the same category racing in during that window both
  // see no DOM circle yet and both schedule a create - without this check,
  // both .on("end") callbacks would call this function and each would push
  // its own bubblesData entry, producing literal duplicate DOM circles all
  // pinned to the identical target point, which then fight forever via
  // collision/charge forces (visible as bubbles that "buzz"/"flutter" and
  // never settle). Folding a second create attempt into an update on the
  // entry the first attempt already created closes that race regardless of
  // which caller triggers it.
  const existingBubble = bubblesData.find(
    (item) => item?.historical === true && item.menuName === name
  );
  if (existingBubble) {
    updateBubble(
      name,
      (existingBubble.user || 0) + value,
      (existingBubble.converted || 0) + converted
    );
    return;
  }

  const bubbleParametersData = returnBubbleParameterData();
  const clusterIndex = HISTORICAL_CLUSTER_INDEX;

  const historicalCoords =
    clusterCenterCoords && clusterCenterCoords[clusterIndex];

  const currentBubble = getItemByName(name);

  const bubbleCreation = {
    x: historicalCoords?.x,
    // Was a hardcoded [10] - "All other pages" moved to index 12 when 2 new
    // real page slots took over indices 10-11 (see UNKNOWN_URL_CLUSER_INDEX's
    // own comment).
    y: clusterCenterCoords[UNKNOWN_URL_CLUSER_INDEX].y - 50 + 100 + 100 +40,
    radius: getHistoricalBubbleRadius(value),
    color: currentBubble?.color,
    colorInfo: currentBubble?.colorInfo,
    cluster: clusterIndex,
    historical: true,
    id: clusterIndex,
    menuName: name,
    user: value,
    converted,
    currentUrlName: "menu-nodes",
  };
  bubblesData.push(bubbleCreation);

  const historicalBubbleData = bubblesData.filter(
    (item) => item?.historical === true && item.menuName
  );

  const historicalSimulation = d3
    .forceSimulation()
    .force(
      "collision",
      d3
        .forceCollide()
        .radius(
          (d: any) =>
            getHistoricalCollisionRadius(+d.radius) +
            getHistoricalCollisionPadding() +
            clusterLabelExtra(d)
        )
        .strength(1)
        .iterations(getHistoricalCollisionIterations())
    )
    .force(
      "charge",
      d3
        .forceManyBody()
        // Historical cluster's own spacing setting, independent of the
        // page-cluster "charge" setting (see DEFAULT_D3CIRCLE_CONSTRAINT's
        // own comment).
        .strength(
          bubbleParametersData && bubbleParametersData.historicalCharge
            ? parseInt(bubbleParametersData.historicalCharge)
            : DEFAULT_D3CIRCLE_CONSTRAINT.historicalCharge
        )
    )
    .force("gravity", d3.forceManyBody().strength(0.2))
    .force("x", d3.forceX(historicalCoords?.x).strength(0.05))
    .force("y", d3.forceY(historicalCoords?.y).strength(0.05))
    .on("tick", () => tickHistoricalBubblePositions());

  stopPreviousSimulation("historical", historicalSimulation);
  historicalSimulation.nodes(historicalBubbleData).alpha(0.9).restart();
  if (dockPlacesItself()) {
    historicalSimulation.stop();   // the rows and bars layouts place the bubbles themselves; the physics stays out of it
    scheduleHistoricalRowsLayout();
  }

  svg
    ?.selectAll("circle[historical=true], rect[historical=true]")
    .data(historicalBubbleData, (d: any) => d.menuName)
    .enter()
    .append(getBubbleShapeTag())
    .attr("data-bubble", true)
    .attr("width", (d: any) => getRectSizeFromRadius(d.radius, getActiveIconAspect()).width)
    .attr("height", (d: any) => getRectSizeFromRadius(d.radius, getActiveIconAspect()).height)
    .attr("cx", historicalCoords?.x)
    .attr("cy", historicalCoords?.y)
    .attr("r", (d) => d.radius)
    .attr(
      "x",
      rectXFromCenter(() => historicalCoords?.x)
    )
    .attr(
      "y",
      rectYFromCenter(() => historicalCoords?.y)
    )
    .attr("rx", 1)
    .attr("id", historicalBubbleData.length - 1)
    .attr("historical", true)
    .style("filter", useIcons ? null : "url(#historical-bubble-shadow)")   // icon mode: this shape is transparent (the icon image carries the shadow), so its filter paints nothing and only costs frames - see the note above DOCK_SHADOW
    .attr("stroke-width", useIcons ? 0 : 1)
    .attr("class", "sub_circle")
    .attr("show-menu", (d) => d.name)
    .attr("user", (d) => d.user)
    .attr("sub-menu-flag", true)
    .attr("remove", "no")
    .style("stroke", (d) => getBubbleStroke(d))
    .attr("name", (d) => d.menuName)
    .style("fill", (d: any) => getHistoricalBubbleFill(d))
    .on("mouseover", (d) => showHistoricalTooltip(d))
    .on("mouseout", () => hideHistoricalTooltip())
    .on("click", (event, d) => handleHistoricalBubbleClick(d))
    .call(historicalDragBehavior())
    .each(function (d: any) {
      const iconUrl = getHistoricalIconUrl(d);
      if (iconUrl) {
        appendIconImage(
          this,
          d,
          iconUrl,
          getRectSizeFromRadius(d.radius, getActiveIconAspect()).width,
          getActiveIconImageAspect(),
          "historical",
          "name",
          d.menuName,
          historicalCoords?.x,
          historicalCoords?.y
        );
      }
      if (d.converted > 0) {
        appendGoalBadge(
          this,
          d,
          "historical",
          "name",
          d.menuName,
          historicalCoords?.x,
          historicalCoords?.y
        );
      }
    });
};

/**
 * A dock bubble GROWS as visitors depart into it (updateBubble), but the dock's physics is created with the category and cools in a few
 * seconds, so a bubble that grew afterwards sat on top of its neighbours for good (reported: the dock "starts small and then stays
 * compressed so tight that the bubbles overlap"). The overlap is the broken state, so it is the trigger: when any two dock bubbles
 * now overlap, re-initialise and wake the simulation so its collision force pushes them apart. Not on every growth: a dock that is
 * fine is left to rest.
 */
// ROWS: the targets every dock bubble is gliding to, and the one animation frame loop that moves them. Positions go straight onto each bubble's own d.x / d.y (what the physics
// layout writes too), then the same tickHistoricalBubblePositions places every element of the bubble - so nothing else about the dock changes between the two layouts.
let rowsTargets = new Map<any, { x: number; y: number }>();
let rowsFrame = 0;
let rowsQueued = false;
const glideRows = () => {
  rowsFrame = 0;
  let moving = false;
  rowsTargets.forEach((target, d) => {
    if (typeof d.x !== "number" || Number.isNaN(d.x) || typeof d.y !== "number" || Number.isNaN(d.y)) {
      d.x = target.x;
      d.y = target.y;
    }
    const dx = target.x - d.x;
    const dy = target.y - d.y;
    if (Math.abs(dx) < 0.4 && Math.abs(dy) < 0.4) {
      d.x = target.x;
      d.y = target.y;
    } else {
      d.x += dx * 0.2;
      d.y += dy * 0.2;
      moving = true;
    }
  });
  tickHistoricalBubblePositions();
  if (dockLabelsLive) {
    const labelSvg = svg?.node() as SVGSVGElement | undefined;
    if (labelSvg) positionHistoricalLabels(labelSvg);
  }
  if (moving) {
    rowsFrame = requestAnimationFrame(glideRows);
  }
};
// ── the dock's two text layouts (demo only, see DOCK_EXTRAS): labels under the rows' bubbles, and the bar chart ────────────────────────────────────────────────────────────────────
const DOCK_LABEL_MEASURE = (text: string): number => textWidth(text, LABEL_PX, LABEL_WEIGHT);
const BAR_TEXT_PX = 11;
const BAR_TEXT_WEIGHT = 500;
const barTextWidth = (text: string): number => textWidth(text, BAR_TEXT_PX, BAR_TEXT_WEIGHT);
/** a category's name as the dock's hover card writes it (the Referral Link filter's "/" bucket is Direct) */
const dockCategoryName = (d: any): string =>
  selectedFilter?.menuType === "referrer" && d.menuName === "/" ? "/ Direct" : readableLabel(selectedFilter?.menuType, d.menuName);
/** half the height a dock bubble is DRAWN at (what a label hangs below): the icon's, or the circle's */
const dockBubbleHalfHeight = (d: any): number =>
  useIcons ? (getRectSizeFromRadius(+d.radius, getActiveIconAspect()).width * getActiveIconImageAspect()) / 2 : +d.radius;
/** "31%": a share in the bar chart (a whole number from 10% up, one decimal below, so a small share is not shown as 0) */
const barShareText = (share: number): string => (share >= 10 ? `${Math.round(share)}%` : `${(Math.round(share * 10) / 10).toFixed(1)}%`);
let dockLabelsLive = false;
/**
 * The bar charts and a visitor who is leaving. The count (and so the dock's data) changes the moment the visitor departs, but the BAR should change when the visitor's bubble lands in it: so each
 * departure on its way is counted here, by category, and the chart draws the count without them until they land. `chartTips` is where each bar ENDS (the tip of a column, the end of a list bar):
 * the place a leaving bubble flies to, set by the last chart layout.
 */
const chartInFlight = new Map<string, number>();
const chartTips = new Map<string, { x: number; y: number }>();
/** the size a bubble has when it lands in a bar: small, so it reads as going INTO the bar */
const CHART_ARRIVAL_RADIUS = 7;
const inFlightOf = (name: string): number => chartInFlight.get(name) || 0;
const totalInFlight = (): number => {
  let total = 0;
  chartInFlight.forEach((n) => { total += n; });

  return total;
};
const landChartArrival = (name: string) => {
  const left = inFlightOf(name) - 1;
  if (left > 0) chartInFlight.set(name, left); else chartInFlight.delete(name);
  scheduleHistoricalRowsLayout(); // the bar takes the visitor in
  const chartSvg = svg?.node() as SVGSVGElement | undefined;
  if (chartSvg) pulseChartBar(chartSvg, name); // and shows it (a fraction of a pixel of growth cannot be seen)
};
/** the bubbles' own hover card and click, on a bar's row: the datum on the hit area is the bubble's, so they read exactly what they read on a bubble */
const dockChartHooks: ChartHooks = {
  onOver: (event: MouseEvent) => showHistoricalTooltip(event),
  onOut: () => hideHistoricalTooltip(),
  onClick: (datum: unknown) => handleHistoricalBubbleClick(datum),
  measure: barTextWidth,
};

/**
 * Puts every dock bubble back to the size its count says (Chris, 2026-10-24: clicking into Rows left some icons large). A bubble's size lives in two places, its data (radius) and its drawn shape, icon and
 * badge; they were written at different times against different "largest" values (see updateBubble, renormaliseHistoricalRadii and redrawHistoricalBubbles), so after a layout change they could disagree.
 * Recomputes every radius against the current largest, then writes it to the drawn elements. Writes only what differs.
 */
const resetDockBubbleSizes = () => {
  renormaliseHistoricalRadii();
  const nodes = bubblesData.filter((item: any) => item?.historical === true && item.menuName);
  const set = (el: Element, attrs: Record<string, number>) => {
    Object.entries(attrs).forEach(([k, v]) => {
      const text = String(Math.round(v * 100) / 100);
      if (el.getAttribute(k) !== text) el.setAttribute(k, text);
    });
  };
  nodes.forEach((d: any) => {
    const name = String(d.menuName);
    const rect = getRectSizeFromRadius(d.radius, getActiveIconAspect());
    const imageHeight = rect.width * getActiveIconImageAspect();
    svg?.selectAll(`circle[name='${name}'], rect[name='${name}']`).each(function (this: any) {
      const cx = +this.getAttribute("cx"), cy = +this.getAttribute("cy");
      if (this.tagName.toLowerCase() === "circle") set(this, { r: d.radius });
      else set(this, { width: rect.width, height: rect.height, x: cx - rect.width / 2, y: cy - rect.height / 2 });
    });
    svg?.selectAll(`image[name='${name}']:not([data-goal-badge])`).each(function (this: any) {
      const cx = +this.getAttribute("cx"), cy = +this.getAttribute("cy");
      set(this, { width: rect.width, height: imageHeight, x: cx - rect.width / 2, y: cy - imageHeight / 2 });
    });
  });
};
const computeHistoricalRows = () => {
  resetDockBubbleSizes();
  const nodes = bubblesData.filter((item) => item?.historical === true && item.menuName);
  const targets = new Map<any, { x: number; y: number }>();
  const svgNode = svg?.node() as SVGSVGElement | undefined;
  if (nodes.length === 0) {
    if (svgNode) {
      removeHistoricalCharts(svgNode);
      removeHistoricalLabels(svgNode);
      dockLabelsLive = false;
    }
  } else {
    // the grey panel's own width, as drawn: mainSvgWidth is the width at the last full redraw and goes stale when the window is resized
    const panelRight = Number(svg?.select("#maincluster").attr("width")) || mainSvgWidth - 10;
    // (the playback controls now live inside the scrub bar, under the stage, so nothing covers the lower left and the charts use the whole width)
    const chartInset = 0;
    const box = { left: chartInset, right: panelRight - chartInset, top: historicalBandTop, bottom: dockFloorY() };
    if (historicalLayout === "bars" && svgNode) {
      const base = Math.max(0, getHistoricalPercentBase() - totalInFlight());
      // the room under the baseline for the name (and, in icon mode, the icon above it)
      const labelZone = useIcons ? 38 : 22; // one line of name (and, in icon mode, the icon above it)
      const rows: ColumnRow[] = nodes.map((d) => {
        const count = Math.max(0, (+d.user || 0) - inFlightOf(String(d.menuName))); // visitors still flying in are not in the bar yet

        return {
          key: String(d.menuName),
          datum: d,
          name: dockCategoryName(d),
          count,
          valueText: barShareText(base > 0 ? (count / base) * 100 : 0),
          colour: String(setColor(d.color)),
          iconUrl: getHistoricalIconUrl(d),
        };
      });
      const layout = layoutHistoricalColumns(
        rows.map((r) => ({ id: r.key, value: r.count })),
        box,
        historicalRowsOrder,
        labelZone
      );
      syncHistoricalColumns(svgNode, rows, layout, labelZone, dockChartHooks, useIcons);
      removeHistoricalLabels(svgNode);
      dockLabelsLive = false;
      chartTips.clear();
      nodes.forEach((d) => {
        const slot = layout.slots.get(String(d.menuName));
        if (slot) {
          // a leaving bubble flies to the TIP of its column
          const tip = { x: slot.x, y: layout.baseline - slot.barH };
          chartTips.set(String(d.menuName), tip);
          targets.set(d, tip);
        }
      });
    } else if (historicalLayout === "list" && svgNode) {
      const base = Math.max(0, getHistoricalPercentBase() - totalInFlight());
      const rows: BarListRow[] = nodes.map((d) => {
        const count = Math.max(0, (+d.user || 0) - inFlightOf(String(d.menuName))); // visitors still flying in are not in the bar yet

        return {
          key: String(d.menuName),
          datum: d,
          name: dockCategoryName(d),
          count,
          valueText: `${count.toLocaleString()} · ${barShareText(base > 0 ? (count / base) * 100 : 0)}`,
          colour: String(setColor(d.color)),
          iconUrl: getHistoricalIconUrl(d),
        };
      });
      const layout = layoutHistoricalBarList(
        rows.map((r) => ({ id: r.key, value: r.count, labelWidth: barTextWidth(r.name), valueWidth: barTextWidth(r.valueText) })),
        box,
        historicalRowsOrder
      );
      syncHistoricalBarList(svgNode, rows, layout, dockChartHooks);
      removeHistoricalLabels(svgNode);
      dockLabelsLive = false;
      // the bubbles are hidden (the stylesheet) but sit at the END of their own bar, which is where a visitor who leaves flies to
      chartTips.clear();
      const largest = Math.max(0, ...rows.map((r) => r.count));
      nodes.forEach((d) => {
        const slot = layout.slots.get(String(d.menuName));
        const row = rows.find((r) => r.key === String(d.menuName));
        if (slot && row) {
          const tip = { x: slot.barX + barLength(row.count, largest, slot.barMaxW), y: slot.y };
          chartTips.set(String(d.menuName), tip);
          targets.set(d, tip);
        }
      });
    } else if (historicalLayout === "donut" && svgNode) {
      const rows: DonutRow[] = nodes.map((d) => {
        const count = Math.max(0, (+d.user || 0) - inFlightOf(String(d.menuName))); // visitors still flying in are not in the ring yet

        return { key: String(d.menuName), datum: d, name: dockCategoryName(d), count, valueText: "", colour: String(setColor(d.color)), iconUrl: getHistoricalIconUrl(d) };
      });
      const layout = layoutDonut(rows.map((r) => ({ id: r.key, value: r.count })), box, historicalRowsOrder, DOCK_EXTRAS && historicalLabels);
      rows.forEach((r) => { r.valueText = barShareText((layout.slices.get(r.key)?.share ?? 0) * 100); });
      syncHistoricalDonut(svgNode, rows, layout, dockChartHooks, layout.total.toLocaleString("en-US"), `${layout.otherCount} smaller ${layout.otherCount === 1 ? "group" : "groups"}`);
      removeHistoricalLabels(svgNode);
      dockLabelsLive = false;
      // the bubbles are hidden (the stylesheet) and a leaving visitor flies to the middle of its own slice
      chartTips.clear();
      nodes.forEach((d) => {
        const slice = layout.slices.get(String(d.menuName));
        if (slice) {
          const tip = { x: slice.tipX, y: slice.tipY };
          chartTips.set(String(d.menuName), tip);
          targets.set(d, tip);
        }
      });
    } else {
      if (svgNode) removeHistoricalCharts(svgNode);
      const labelled = !!svgNode && DOCK_EXTRAS && historicalLabels;
      const names = new Map(nodes.map((d) => [String(d.menuName), dockCategoryName(d)]));
      const rooms = new Map(nodes.map((d) => [String(d.menuName), labelled ? labelRoom(DOCK_LABEL_MEASURE(names.get(String(d.menuName)) as string)) : 0]));
      const slots = layoutHistoricalRows(
        nodes.map((d) => ({ id: String(d.menuName), value: +d.user || 0, size: getHistoricalCollisionRadius(+d.radius) * 2, room: rooms.get(String(d.menuName)) || undefined, drawn: dockBubbleHalfHeight(d) * 2 })),
        box,
        historicalRowsOrder,
        ROWS_GAP_X,
        ROWS_GAP_Y + (labelled ? LABEL_ROOM_Y : 0),
        undefined,
        labelled   // with names under the bubbles they STAND on a common line, so every name in a row is at one height and the big icon (Google) rises above the rest
      );
      nodes.forEach((d) => {
        const slot = slots.get(String(d.menuName));
        if (slot) {
          targets.set(d, { x: slot.x, y: slot.y });
        }
      });
      if (svgNode && labelled) {
        syncHistoricalLabels(
          svgNode,
          nodes.map((d) => ({ key: String(d.menuName), datum: d, name: names.get(String(d.menuName)) as string, halfHeight: dockBubbleHalfHeight(d), room: Math.max(rooms.get(String(d.menuName)) || 0, 1) })),
          DOCK_LABEL_MEASURE,
          LABEL_DROP
        );
        dockLabelsLive = true;
      } else if (svgNode) {
        removeHistoricalLabels(svgNode);
        dockLabelsLive = false;
      }
    }
  }
  rowsTargets = targets;
  if (!rowsFrame && targets.size > 0) {
    rowsFrame = requestAnimationFrame(glideRows);
  }
};
if (typeof window !== "undefined") {
  // the rows wrap to the panel's width, so they re-flow when it changes: once at once, and once more after the dashboard has finished redrawing itself for the new size
  window.addEventListener("resize", () => {
    scheduleHistoricalRowsLayout();
    window.setTimeout(scheduleHistoricalRowsLayout, 700);
  });
}
/** (Re)lays the dock out as rows on the next animation frame. Coalesced: any number of changes in one frame cost one layout. A no-op in cluster layout. */
export const scheduleHistoricalRowsLayout = () => {
  if (!dockPlacesItself() || rowsQueued) {
    return;
  }
  rowsQueued = true;
  requestAnimationFrame(() => {
    rowsQueued = false;
    if (dockPlacesItself()) {
      computeHistoricalRows();
    }
  });
};

// DOCK_SHADOW (measured 2026-10-17, dev demo, 27 dock bubbles, same drag test, interleaved, 3 rounds each): while the dock moves, frames averaged 20-21ms with the shadow filter on
// every dock element and 8.5-8.8ms with it off - a 2.5x cost that showed as "skipping" every time a close made the dock shift. The whole cost was the filter on the TRANSPARENT shape
// element (icon mode draws the icon as a separate <image>): leaving the shape unfiltered and the icon image filtered gave 8.7ms, identical to no filters at all, and the shadow
// everyone sees (the image's) is unchanged. Circle mode keeps its filter: there the shape IS the bubble.
const wakeHistoricalIfOverlapping = () => {
  if (dockPlacesItself()) {
    scheduleHistoricalRowsLayout();   // a count changed: the order (and sizes) may have too
    return;
  }
  const sim = activeSimulations.historical;
  const nodes: any[] = sim?.nodes?.() ?? [];
  const pad = getHistoricalCollisionPadding();
  for (let i = 0; i < nodes.length; i++) {
    for (let j = i + 1; j < nodes.length; j++) {
      const a = nodes[i], b = nodes[j];
      if (typeof a.x !== "number" || typeof b.x !== "number") continue;
      const need = getHistoricalCollisionRadius(+a.radius) + getHistoricalCollisionRadius(+b.radius) + 2 * pad;
      if (Math.hypot(a.x - b.x, a.y - b.y) < need - 1) {
        // sim.nodes(nodes) re-initialises every force. That is the point: d3's forceCollide reads each node's radius ONCE, when it is
        // initialised, and keeps it, so waking the simulation without this ran the collision force on the OLD, smaller radii (which saw
        // no overlap) and nothing moved.
        sim.nodes(nodes).alpha(Math.max(sim.alpha(), 0.5)).restart();
        return;
      }
    }
  }
};

// convertedValue is optional - callers that only know the new total
// visitor count (not conversions) leave the bubble's existing converted
// count untouched rather than clobbering it back to undefined.
const updateBubble = (name, value, convertedValue?: number) => {
  notifySidebarItemsUpdated();

  const newRadius = getHistoricalBubbleRadius(+value);

  const bubbleToUpdate = bubblesData.find((bubble) => bubble.menuName === name);

  if (bubbleToUpdate) {
    bubbleToUpdate.radius = newRadius;
    bubbleToUpdate.user = value;
    if (convertedValue !== undefined) {
      bubbleToUpdate.converted = convertedValue;
    }

    const clusterIndex = HISTORICAL_CLUSTER_INDEX;

    const historicalCoords =
      clusterCenterCoords && clusterCenterCoords[clusterIndex];

    bubblesData.forEach((item, index) => {
      if (item.menuName === name && item?.historical === true) {
        bubblesData[index].radius = newRadius;
        bubblesData[index].user = value;
        if (convertedValue !== undefined) {
          bubblesData[index].converted = convertedValue;
        }
      }
    });
    const historicalBubbleData = bubblesData.filter(
      (item) => item?.historical === true && item.menuName
    );

    const newRectSize = getRectSizeFromRadius(newRadius, getActiveIconAspect());
    svg
      ?.selectAll(`circle[name='${name}'], rect[name='${name}']`)
      ?.attr("r", newRadius)
      .attr("width", newRectSize.width)
      .attr("height", newRectSize.height)
      .attr("x", function (this: SVGGraphicsElement) {
        return +this.getAttribute("cx") - newRectSize.width / 2;
      })
      .attr("y", function (this: SVGGraphicsElement) {
        return +this.getAttribute("cy") - newRectSize.height / 2;
      })
      .attr("user", value);

    wakeHistoricalIfOverlapping();   // it just grew: if it now overlaps a neighbour, the dock's physics must run again

    // The companion icon image uses its own true-aspect size (see
    // appendIconImage), not the rect's approximate one, so it's resized
    // separately using its own cx bookkeeping.
    const newImageHeight = newRectSize.width * getActiveIconImageAspect();
    svg
      ?.selectAll(`image[name='${name}']:not([data-goal-badge])`)
      ?.attr("width", newRectSize.width)
      .attr("height", newImageHeight)
      .attr("x", function (this: SVGGraphicsElement) {
        return +this.getAttribute("cx") - newRectSize.width / 2;
      })
      .attr("y", function (this: SVGGraphicsElement) {
        return +this.getAttribute("cy") - newImageHeight / 2;
      });

    // A category's own star badge needs the SAME resize treatment as its
    // icon above (grown/shrunk to track the bubble's new radius) if it
    // already has one - separately, a category that just received its
    // FIRST-ever conversion (this update is what flips converted from 0 to
    // >0) has no badge yet at all, since drawHistoricalBubble/
    // drawNewHistoricalBubble only ever check d.converted at CREATION time.
    // Mirrors handleVisitorMove's identical real-time-bubble case (a session
    // converting mid-visit, not on arrival).
    const existingBadge = svg?.select(`image[data-goal-badge][name='${name}']`);
    if (existingBadge && !existingBadge.empty()) {
      const badgeSize = useIcons
        ? getIconModeGoalBadgeSize(newRadius)
        : getGoalBadgeSize(newRadius);
      existingBadge
        .attr("width", badgeSize)
        .attr("height", badgeSize)
        .attr("x", function (this: SVGGraphicsElement) {
          const offsetX = +this.getAttribute("data-offset-x") || 0;
          return +this.getAttribute("cx") - badgeSize / 2 + offsetX;
        })
        .attr("y", function (this: SVGGraphicsElement) {
          const offsetY = +this.getAttribute("data-offset-y") || 0;
          return +this.getAttribute("cy") - badgeSize / 2 + offsetY;
        });
    } else if ((bubbleToUpdate.converted || 0) > 0) {
      const shapeNode = svg
        ?.select(`circle[name='${name}'], rect[name='${name}']`)
        .node();
      if (shapeNode) {
        appendGoalBadge(shapeNode, bubbleToUpdate, "historical", "name", name);
      }
    }
  }
};

// Playback feature: applies the net effect of N already-departed sessions in
// one pass, for seek/scrub reconstruction (playbackService.ts's seekTo). A
// session that arrived AND departed before the seek target leaves exactly
// three traces: its category's sidebar totalUsers +1 (currentUsers nets to
// zero - see drawUserBubble's ++ and handleVisitorLeave's --), the
// historical dock's totalSession +1, and its category dock bubble's user
// count +1. Applying those as aggregated counts per category - instead of
// draw-bubble-then-immediately-dock per session - is what makes seeking a
// busy day near-instant, creates zero throwaway real-time DOM nodes, and
// can't trip the duplicate-dock-circle race (one dock create/update per
// category, sequentially, instead of thousands of interleaved ones).
/**
 * True while a SAMPLED day is being played back. The dock bubbles then count only the sessions the replay chose to play (and only
 * those departed so far), which is a different number from the day's real total in the footer and the drawer, so the dock tooltip
 * labels it "(sampled total)" (Chris: any sampled number that deviates from the main numbers must say so).
 */
let playbackSampled = false;
export const setPlaybackSampled = (on: boolean) => { playbackSampled = on; };
export const isSampledPlaybackView = (): boolean => playbackSampled && playbackModeService.mode === "playback";

export const applyDepartedSessionsBulk = (departedSessions: any[]) => {
  if (!departedSessions.length) {
    return;
  }

  const countsByCategory: { [name: string]: number } = {};
  const convertedByCategory: { [name: string]: number } = {};
  for (const sessionData of departedSessions) {
    const realtimeType = resolveRealtimeCategory(sessionData);
    if (!realtimeType) {
      continue;
    }
    countsByCategory[realtimeType] = (countsByCategory[realtimeType] || 0) + 1;
    if (sessionData.goalReached) {
      convertedByCategory[realtimeType] = (convertedByCategory[realtimeType] || 0) + 1;
    }
  }

  Object.keys(countsByCategory).forEach((name) => {
    const count = countsByCategory[name];
    const convertedCount = convertedByCategory[name] || 0;
    if (!getItemByName(name)) {
      createRealtimeListItem(name, 0);
    }
    const item = getItemByName(name);
    if (item) {
      item.totalUsers += count;
    }
    historical.totalSession += count;
    historical.totalConverted += convertedCount;

    const dockNode = svg?.select(
      `circle[name='${name}'], rect[name='${name}'], image[name='${name}']`
    );
    if (dockNode?._groups?.[0]?.[0]) {
      const newValue =
        parseInt(dockNode._groups[0][0].getAttribute("user") || "0", 10) + count;
      const existingDockBubble = bubblesData.find(
        (item) => item?.historical === true && item.menuName === name
      );
      const newConvertedValue = (existingDockBubble?.converted || 0) + convertedCount;
      updateBubble(name, newValue, newConvertedValue);
    } else {
      drawNewHistoricalBubble(name, count, convertedCount);
    }
  });

  items.sort((a, b) => {
    // Sort in descending order based on currentUsers, giving priority to currentUsers
    if (a.currentUsers > b.currentUsers) return -1;
    if (a.currentUsers < b.currentUsers) return 1;

    // If currentUsers are equal, then sort based on totalUsers
    if (a.totalUsers > b.totalUsers) return -1;
    if (a.totalUsers < b.totalUsers) return 1;

    // If both currentUsers and totalUsers are equal, maintain the original order
    return 0;
  });
  notifySidebarItemsUpdated();
};

eventEmitter.addListener("realtimeFilterChanged", () => {
  items = [];
  removeHistoricalBubbles();
  const realtimeMenuItems = new Set<string>();

  // Was a raw sessionData[selectedFilter.menuType] lookup - missed the
  // "None" filter's special-casing (menuType "none" isn't a real
  // sessionData field, so this always evaluated to undefined for it, and
  // for every other filter its behavior only coincidentally matched
  // resolveRealtimeCategory). This is the actual "By..." dropdown handler
  // (handleFilterChange emits this event) - fixed to match the already-
  // correct usage two lines below in this same function.
  bubblesData.forEach((bubble) => {
    const category = resolveRealtimeCategory(bubble.sessionData);
    // See the "filterChanged" listener's matching guard - don't manufacture
    // a fake "null"/"undefined" sidebar category for a session genuinely
    // missing this field. But its STALE name/color from whichever filter
    // was active before must be cleared here too, not left as-is - this is
    // the actual "By..." dropdown handler, so this is where a bubble
    // missing the new field's value (e.g. no country_name under "By
    // Country") was keeping its old cached name/color and masquerading as
    // that old category (reported as a phantom "All visitors" entry, and
    // more generally "filters mixing").
    if (category) {
      realtimeMenuItems.add(category);
    } else {
      bubble.name = UNCATEGORIZED_CATEGORY;
      bubble.color = UNCATEGORIZED_COLOR_INDEX;
    }
  });
  for (const listItemName of realtimeMenuItems) {
    createRealtimeListItem(listItemName, 0);

    bubblesData
      .filter(
        (bubble) => resolveRealtimeCategory(bubble.sessionData) === listItemName
      )
      .forEach((bubble) => {
        const currentItem = getItemByName(listItemName);
        bubble.color = currentItem.color;
        bubble.name = listItemName;
      });
  }

  notifySidebarItemsUpdated();
  // removeHistoricalBubbles() above rebinds bubblesSelection - which matches
  // circle[data-bubble]/rect[data-bubble], broad enough to include these same
  // real-time nodes - to a JSON-cloned snapshot of bubblesData taken BEFORE
  // the color-mutation loop above ran. That leaves the DOM's bound datum
  // holding stale pre-switch colors even though bubblesData/realtimeNodes
  // themselves are correct, so reading d.color off whatever's currently
  // bound would silently repaint with the OLD filter's colors (masked in
  // icon mode, where fill goes transparent under the icon overlay, but
  // directly visible in circle mode - reported as "colors remain from the
  // previous filter" after switching to No Filter with icons off). Rebinding
  // straight from realtimeNodes (the canonical live array, same objects the
  // mutation loop just updated) before restyling closes that gap regardless
  // of whatever removeHistoricalBubbles() clobbered a moment earlier.
  svg
    ?.selectAll("circle[realTime=true], rect[realTime=true], image[realTime=true]")
    .data(realtimeNodes, (d: any) => d.visitor_id)
    ?.style("fill", (d) => getBubbleFill(d))
    ?.style("stroke", (d) => getBubbleStroke(d));

  // Idle pulse Zzz glyphs and the row-selection highlight ripple are both
  // colored by category (setColor(shapeDatum.color) at creation time - see
  // applyInteractionPulse / setSessionHighlight) but never revisited after
  // creation, unlike the bubble fill/stroke restyled just above - so a
  // session that's mid-idle or currently ripple-highlighted through a filter
  // switch keeps showing the old filter's color until the decoration is next
  // recreated. Recolor any currently-live ones here, in lockstep.
  svg?.selectAll("[data-pulse-idle=true]").each(function (this: SVGGraphicsElement) {
    const visitorId = this.getAttribute("visitor_id");
    const bubble = realtimeNodes.find((n: any) => n.visitor_id === visitorId);
    if (bubble) {
      d3.select(this).style("fill", setColor(bubble.color) as any);
    }
  });
  svg?.selectAll("circle[data-highlight-ripple=true]").each(function (this: SVGGraphicsElement) {
    const visitorId = this.getAttribute("visitor_id");
    const bubble = realtimeNodes.find((n: any) => n.visitor_id === visitorId);
    if (bubble) {
      d3.select(this).style("stroke", setColor(bubble.color) as any);
    }
  });

  updateSiteInfo();
  // Live-only backend fetch (keyed off selectedDateRange) - see
  // handleFilterChange's own comment on why this can't run during playback.
  // Playback's dock rebuild is handled by playbackService.ts's own listener
  // on this same event.
  if (playbackModeService.mode !== "playback") {
    getHistoricalBubblesData();
  }
});

export const handleSubscriptionChange = (subscription: any) => {
  // restrictedRealtimeFilters/restrictedRangeFilters are now empty arrays
  // for every tier (see the backend's subscriptions.const.ts) - every plan
  // gets every feature, including every filter/date-range option; only data
  // volume/retention differ by tier. These .filter() calls are effectively
  // no-ops now, kept only because a genuinely restricted array (should the
  // product ever reintroduce one) would still be honored automatically.
  availableFilters = INITIAL_FILTERS.filter((filter) => {
    return !subscription.subscriptionData.restrictedRealtimeFilters.includes(
      filter?.menuType
    );
  });
  const dateRangeList = [];
  historyDataDateRange = DATEPICKER_RANGES.filter(
    (range) =>
      !subscription.subscriptionData.restrictedRangeFilters.includes(range)
  ).reduce((acc, rangeName) => {
    dateRangeList.push({ name: rangeName, value: getRangeDate(rangeName)[0] });
    return dateRangeList;
  }, []);
  // Custom Range used to be withheld from Base Jump specifically as a
  // feature lock - every tier gets every feature now, so this is always
  // offered; a Base Jump user picking a custom range just can't go further
  // back than their plan's actual storage window (enforced server-side, see
  // getPlaybackTimeline/formPeriodParams's own comments on
  // earliestAccessibleDate).
  historyDataDateRange.push({ name: "Custom Date", value: "Custom Date" });
  dropRestoredRangeIfRestricted();
};
const getRangeDate = (periodName) => {
  switch (periodName) {
    case "Today": {
      return [new Date().setHours(0, 0, 0, 0)];
    }
    case "Last 2 Days": {
      return [
        new Date(
          new Date().setHours(0, 0, 0, 0) - 1 * 24 * 60 * 60 * 1000
        ).getTime(),
      ];
    }
    case "Last 7 Days": {
      return [
        new Date(
          new Date().setHours(0, 0, 0, 0) - 6 * 24 * 60 * 60 * 1000
        ).getTime(),
      ];
    }
    case "Last 30 Days": {
      return [
        new Date(
          new Date().setHours(0, 0, 0, 0) - 29 * 24 * 60 * 60 * 1000
        ).getTime(),
      ];
    }
    case "All": {
      return [storage.get("userCreatedAt")];
    }
  }
};

// selectedDateRange.value for a relative preset ("Today", "Last 2 Days",
// etc.) is a raw epoch number computed ONCE - either at module load
// (initialHistoryDataDateRange above) or at the moment the user picked it
// (DateRangeFilterSet). Neither ever recomputes on its own. A tab left open
// across a midnight rollover (reported directly: "logged into production
// this morning" after the tab had been open since the day before) keeps
// that stale boundary forever while .name is still the literal string
// "Today" - nothing ever touches the string, so the label stays innocently
// correct while the query window silently grows to [yesterday's midnight,
// now], i.e. up to ~2 days. Every query site should call this instead of
// reading selectedDateRange.value directly, so a relative preset always
// reflects "today" as of RIGHT NOW rather than whenever the tab happened to
// load or the filter was picked. "All"/"Custom Date" are genuinely pinned
// to an absolute date the user chose - those must NOT be recomputed.
export const getEffectiveDateRangeValue = (): number => {
  // Always a USABLE time, never empty: callers build `new Date(x).toISOString()` from it, which throws on "" or undefined (the whole dashboard went blank on a fresh load
  // when "All" had no account creation date yet). Falls back to the start of today.
  const startOfToday = new Date().setHours(0, 0, 0, 0);
  if (!selectedDateRange || selectedDateRange.name === "All" || selectedDateRange.name === "Custom Date") {
    return usableTimeOr(selectedDateRange?.value, startOfToday);
  }
  const fresh = getRangeDate(selectedDateRange.name);

  return usableTimeOr(fresh ? fresh[0] : selectedDateRange.value, startOfToday);
};

export const setInitialFilter = (data) => {
  availableFilters = INITIAL_FILTERS.filter(
    (filter) =>
      !data?.subscriptionData?.restrictedRealtimeFilters?.includes(filter?.menuType)
  );
  if (!selectedFilter) {
    selectedFilter = availableFilters[0];
  }
};
// Called right after the account's own range list is built. The range restored at load came from the unrestricted initial list; if this account's plan
// does not offer it, fall back to the default (and forget it) rather than keep a range the person cannot pick. Never fires for a custom range.
const dropRestoredRangeIfRestricted = () => {
  if (
    selectedDateRange &&
    selectedDateRange.name !== "Custom Date" &&
    historyDataDateRange &&
    !historyDataDateRange.some((range) => range.name === selectedDateRange.name)
  ) {
    selectedDateRange = historyDataDateRange[0];
    saveHistoricalRange(selectedDateRange);
    eventEmitter.emit("filterChanged");
  }
};

export const setInitialDateFilter = (data) => {
  const dateRangeList = [];
  historyDataDateRange = DATEPICKER_RANGES.filter(
    (range) => !data?.subscriptionData?.restrictedRangeFilters?.includes(range)
  ).reduce((acc, rangeName) => {
    dateRangeList.push({ name: rangeName, value: getRangeDate(rangeName)[0] });
    return dateRangeList;
  }, []);
  // See handleSubscriptionChange's own comment - Custom Range is no longer
  // withheld from any tier.
  historyDataDateRange.push({ name: "Custom Date", value: "Custom Date" });
  dropRestoredRangeIfRestricted();
};


eventEmitter.addListener("pages-changed", (pages: any) => {
  clearClusterSelection();
  // The backend emits the raw pages array as this event's payload
  // (notification.ts's notifyPagesChanged: `.emit('pages-changed', pages)`),
  // but pagesModel.ts's setPages expects `{ siteId, pages }` and reads
  // `recods.pages` - passing the bare array here meant `recods.pages` was
  // always undefined, silently setting the whole module's `data` to
  // undefined (wiping every page's state) until some unrelated later fetch
  // happened to repaint it correctly. Wrapping it in the shape setPages
  // actually expects fixes that.
  // setPages builds fresh page records, so every page's "Current" count starts again from nothing while the visitors are still there: the next departure would take it to -1 (Chris's screenshot,
  // 2026-10-21: -3, -1 after making a group). The occupancy is DERIVED from the bubbles on screen, never accumulated, so derive it again as soon as the new list is in.
  setPages({ siteId, pages });
});
